import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Holidays from 'date-holidays';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { getPriceStockCalendar, getCities, getManagers, getProductTypes, getPriceReservationInfo, saveTourPriceInfo, getSupplierTaxCostItems, saveTourSelectedStock, saveTourSessionCloseSetting, getTourProductResourceList, getTourRatePlanList02, getItineraryType, getItineraryTypeSimpleList, saveSalePriceBulkByPriceNos, savePriceNameBulkByPriceNos, hideProductPricesByPriceNos, getPriceInfoForReservation, deleteTourSelectedEvents, getTourStockList, getSharedInventoryList, linkSharedSeriesToSelectedEvents, unlinkSharedSeriesFromSelectedEvents, getSeriesManagement, updateTourProductResource02, deleteTourProductResource, createTourProductResource02, updateTourRatePlan02, deleteTourRatePlan, createTourRatePlanDefault01, updateTourRatePlanOrder, getProductDetailExtra, saveProductDetailExtra, getPromotionOptions } from '../services/apiService';
import { getProductDetails, getProductList, saveProductBasicInfo } from '../services/productService';
import { getUserInfo } from '../utils/tokenManager';
import { useScreenTools } from '../services/ai/screenTools';
import { splitPromoNames } from '../utils/promotion';
import PromotionMappingModal from '../components/PromotionMappingModal';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import koLocale from '@fullcalendar/core/locales/ko';
import ProductSettingsModal from '../components/ProductSettingsModal';
import BulkPriceImportModal from '../components/BulkPriceImportModal';
import TourReservationModal from '../components/TourReservationModal';
import CalendarMonthGrid from '../components/CalendarMonthGrid';
import ProductSwitcherModal from '../components/ProductSwitcherModal';
import ProductPageHeader from '../components/shared/ProductPageHeader';
import SharedInventoryCreateModal from '../components/SharedInventoryCreateModal';
import MonthPicker from '../components/MonthPicker';

const normalizeNumber = (v) => {
  if (v == null) return 0;
  const s = String(v).trim();
  if (s === '') return 0;
  const n = Number(s.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Helpers
function startOfMonth(d) { const n = new Date(d.getFullYear(), d.getMonth(), 1); n.setHours(0, 0, 0, 0); return n; }
function endOfMonth(d) { const n = new Date(d.getFullYear(), d.getMonth() + 1, 0); n.setHours(0, 0, 0, 0); return n; }
function formatYMD(d) { const yyyy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${yyyy}-${mm}-${dd}`; }

/** Local calendar YYYY-MM-DD only; null if incomplete or invalid (avoids native date input month-segment typing bugs). */
function parseYMDStrict(s) {
  const t = String(s ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const y = Number(t.slice(0, 4));
  const m = Number(t.slice(5, 7));
  const d = Number(t.slice(8, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

function sanitizeBulkDateTyping(s) {
  return String(s ?? '').replace(/[^\d-]/g, '').slice(0, 10);
}

/**
 * Bulk 모달 prefill용: 재고를 모를 때는 `stock` 키를 넣지 않음.
 * (빈 문자열을 넣으면 `initialBulk.stock ?? prev.stock`가 `''`를 택해 사용자 입력이 지워짐)
 */
function bulkInitialStockField(stockVal) {
  if (stockVal == null) return {};
  const s = String(stockVal).trim();
  if (s === '') return {};
  return { stock: s };
}

// Currency code to symbol mapping
const currencySymbolMap = {
  KRW: '￦', CNY: '¥', JPY: '¥', USD: '$', EUR: '€', GBP: '£',
  THB: '฿', VND: '₫', PHP: '₱', SGD: 'S$', HKD: 'HK$', TWD: 'NT$',
  AUD: 'A$', CAD: 'C$', CHF: 'CHF', INR: '₹', MYR: 'RM', IDR: 'Rp',
};
function getCurrencySymbol(code) {
  if (!code) return '';
  const upper = String(code).toUpperCase().trim();
  // If already a symbol, return as-is
  if (/^[￦¥$€£฿₫₱₹]/.test(upper) || upper.length <= 2) return code;
  return currencySymbolMap[upper] || code;
}

function pickFirstValue(row, keys) {
  const list = Array.isArray(keys) ? keys : [];
  for (const k of list) {
    const v = row?.[k];
    if (v != null && String(v).trim() !== '') return v;
  }
  return '';
}

function splitTriplet(value) {
  if (value == null || String(value).trim() === '') return { adult: '', child: '', infant: '' };
  const parts = String(value).split('/').map(s => s.trim());
  return {
    adult: parts[0] || '',
    child: parts[1] || '',
    infant: parts[2] || '',
  };
}

function getTripletFromRow(row, { tripletKeys = [], adultKeys = [], childKeys = [], infantKeys = [] } = {}) {
  const tri = pickFirstValue(row, tripletKeys);
  if (tri) return splitTriplet(tri);
  const adult = pickFirstValue(row, adultKeys);
  const child = pickFirstValue(row, childKeys);
  const infant = pickFirstValue(row, infantKeys);
  return { adult: adult || '', child: child || '', infant: infant || '' };
}

// Range/Month helpers for multi-month view
function stripTime(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function minDate(a, b) { return a.getTime() <= b.getTime() ? a : b; }
function maxDate(a, b) { return a.getTime() >= b.getTime() ? a : b; }
function iterateMonths(start, end) {
  const s = startOfMonth(stripTime(start));
  const e = endOfMonth(stripTime(end));
  const out = [];
  const cur = new Date(s);
  cur.setDate(1);
  while (cur <= e) {
    out.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1, 1);
  }
  return out;
}
function getMonthMatrix(year, month) {
  // Returns 6x7 matrix of Date objects covering the month grid (including leading/trailing days)
  const first = new Date(year, month, 1); first.setHours(0, 0, 0, 0);
  const startDow = first.getDay();
  const gridStart = new Date(first); gridStart.setDate(1 - startDow);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart); d.setDate(gridStart.getDate() + i);
    d.setHours(0, 0, 0, 0);
    cells.push(d);
  }
  return cells;
}

const itineraryTintPalette = [
  { bg: '#F8E8E8', border: '#E9CACA', text: '#8F5A5A' },
  { bg: '#EAF4EC', border: '#CFE3D3', text: '#567A60' },
  { bg: '#EDF2FA', border: '#D4DEEE', text: '#5E7294' },
  { bg: '#F8F0E4', border: '#E8D9BF', text: '#8C6F45' },
  { bg: '#F2ECF7', border: '#DDD0E9', text: '#75608E' },
  { bg: '#E9F6F5', border: '#CBE4E0', text: '#4F7D76' },
  { bg: '#F7ECEF', border: '#E7D0D8', text: '#916572' },
  { bg: '#EEF3E6', border: '#D8E1C8', text: '#6B7C55' },
];

function normalizeItineraryLegendLabel(label) {
  return String(label || '').replace(/\s*\([^)]*\)\s*$/g, '').trim();
}

/** 상품 대기예약허용여부: 't' / true 등이면 대기 허용, 그 외('f' 등)는 마감 표기 */
function isWaitlistReservationAllowed(raw) {
  if (raw === true) return true;
  const s = String(raw ?? '').trim().toLowerCase();
  return s === 't' || s === 'true' || s === 'y' || s === '1';
}

/** 보유석(재고)과 판매석이 같고 재고가 1 이상이면 잔여 0으로 간주 */
function isCapacityFullySold(capacityNum, soldNum) {
  const c = Number(capacityNum);
  const s = Number(soldNum);
  return Number.isFinite(c) && c > 0 && Number.isFinite(s) && s === c;
}

/** renderDayContent / 툴팁과 동일 규칙으로 재고 소진율(0–100). 보유석≤0 또는 판매 불가 시 null */
function getSellThroughPercent(stockData, dayRow) {
  const v = dayRow || {};
  const sd = stockData || {};
  const nStockApi = (val) => {
    if (val == null || val === '') return NaN;
    const s = String(val).replace(/[^0-9.-]/g, '');
    if (s === '') return NaN;
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };
  const toNum = (x) => {
    const s = String(x ?? '').replace(/[^0-9.-]/g, '');
    return s ? Number(s) : NaN;
  };
  const capParsed = nStockApi(sd?.보유석);
  const remParsed = nStockApi(sd?.잔여석);
  const capacityNum = !Number.isNaN(capParsed) ? capParsed : toNum(v?.capacity);
  const remainNum = !Number.isNaN(remParsed) ? remParsed : toNum(v?.stock);
  const sharedSoldParsed = nStockApi(sd?.공유판매석);
  const soldParsed = !Number.isNaN(sharedSoldParsed) ? sharedSoldParsed : nStockApi(sd?.판매석);
  const soldNum = !Number.isNaN(soldParsed)
    ? soldParsed
    : (!Number.isNaN(capacityNum) && !Number.isNaN(remainNum) ? Math.max(0, capacityNum - remainNum) : NaN);
  if (Number.isNaN(capacityNum) || capacityNum <= 0 || Number.isNaN(soldNum)) return null;
  return Math.min(100, Math.max(0, Math.round((soldNum / capacityNum) * 100)));
}

/** TRP_S_투어재고리스트 응답 → 캘린더용 보유/판매/잔여. 판매석=상품판매, 공유판매석=공유재고판매 */
function parseStockEventsFromTourStockItem(item) {
  const events = Array.isArray(item?.이벤트) ? item.이벤트 : [];
  const 보유석Item = events.find((e) => e?.name === '보유석');
  const 판매석Item = events.find((e) => e?.name === '판매석');
  const 잔여석Item = events.find((e) => e?.name === '잔여석');
  const 공유판매Item = events.find((e) => e?.name === '공유판매');
  const toNum = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const ownSold = toNum(판매석Item?.value);
  const sharedSold = toNum(판매석Item?.공유판매석) ?? toNum(공유판매Item?.value);
  return {
    보유석: toNum(보유석Item?.value) ?? 0,
    판매석: ownSold ?? 0,
    공유판매석: sharedSold,
    잔여석: toNum(잔여석Item?.value) ?? 0,
  };
}

// Parse commission data from string format and determine type and values
const parseCommissionData = (commStr) => {
  if (!commStr || typeof commStr !== 'string') {
    return {
      hasCommission: false,
      commissionType: '정률',
      values: { adult: '', child: '', infant: '' }
    };
  }

  // Split by " / " to get individual values
  const parts = commStr.split(' / ').map(s => s.trim());
  if (parts.length !== 3) {
    return {
      hasCommission: false,
      commissionType: '정률',
      values: { adult: '', child: '', infant: '' }
    };
  }

  // Check if it's percentage (contains % and value < 100) or fixed amount (contains currency symbol or value >= 100)
  let commissionType = '정률'; // default to percentage
  const processedValues = { adult: '', child: '', infant: '' };
  const keys = ['adult', 'child', 'infant'];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const key = keys[i];

    // Extract numeric value
    const numericValue = parseFloat(part.replace(/[^0-9.-]/g, ''));

    if (isNaN(numericValue)) {
      processedValues[key] = '';
      continue;
    }

    // Check for currency symbols (￦, $, €, etc.) or high values
    const hasCurrencySymbol = /[￦$€¥£]/.test(part);
    const isPercentage = part.includes('%');

    if (hasCurrencySymbol || (!isPercentage && numericValue >= 100)) {
      // Fixed amount (정액)
      commissionType = '정액';
      processedValues[key] = numericValue.toLocaleString();
    } else if (isPercentage || numericValue < 100) {
      // Percentage (정률)
      processedValues[key] = numericValue.toString();
    } else {
      // Default to fixed amount for unclear cases
      commissionType = '정액';
      processedValues[key] = numericValue.toLocaleString();
    }
  }

  return {
    hasCommission: Object.values(processedValues).some(v => v !== ''),
    commissionType,
    values: processedValues
  };
};

// Single-day inline modal removed: we now use the right-side bulk modal for both single-day and range edits.

// Left side sheet modal for bulk apply
const BulkApplySideModal = ({ open, onClose, onApply, onDelete, onPreviewChange, initialRange, initialBulk, priceType, inline = false, productId, sharedSeriesNoByDate, resourceNo, onClearSelection, onRequestRefresh, onSyncSelection, useAgeGroup = true, onChangeUseAgeGroup, savingAgeGroup = false, multiMonthView = false, virtualRecruitInitial, onVirtualRecruitChange, itineraryTypes, selectedItineraryTypeNo, onSelectedItineraryTypeNoChange, promoSelection = [], onOpenPromoMapping, hasSelectablePromos = false }) => {
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [isContiguousSelection, setIsContiguousSelection] = useState(true);
  const [bulk, setBulk] = useState({ pa: '', pc: '', pi: '', ta: '', tc: '', ti: '', ca: '', cc: '', ci: '', ma: '', mc: '', mi: '', stock: '', closed: false });
  const [virtualRecruit, setVirtualRecruit] = useState({ use: false, count: '0', daysBefore: '0' });
  // Animation/control states (match ProductSettingsModal UX)
  const [mountedVisible, setMountedVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const backdropMouseDownRef = useRef(false);
  const [hasDOM, setHasDOM] = useState(false);
  const [priceSymbol, setPriceSymbol] = useState('');
  const [costSymbol, setCostSymbol] = useState('');
  // Seat status state for shared inventory modal
  const [seatStatusLoading, setSeatStatusLoading] = useState(false);
  const [seatStatus, setSeatStatus] = useState([]); // [{ 날짜, 이벤트:[{id,name,value}], 공유재고명, 공유재고색상 }]
  const [seatStatusMeta, setSeatStatusMeta] = useState({ seriesNo: null, seriesName: '', color: '' });
  const [seatStatusQueried, setSeatStatusQueried] = useState(false);
  const [costDivision, setCostDivision] = useState(''); // 원가구분
  const [detailCosts, setDetailCosts] = useState([]); // [{ 원가항목, 통화기호, 성인, 어린이, 영유아 }]
  const [taxCostItems, setTaxCostItems] = useState([]); // [{ 코드, 코드명 }]
  const [taxCostLoading, setTaxCostLoading] = useState(false);
  // Shared inventory (공유재고)
  const [useSharedStock, setUseSharedStock] = useState(false);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedList, setSharedList] = useState([]); // rows: { 시리즈번호, 시리즈명, 공유색상, 종류, 공급사, ... }
  const [selectedSeriesNo, setSelectedSeriesNo] = useState(null);
  const [sharedModalOpen, setSharedModalOpen] = useState(false);
  const [sharedSelectTemp, setSharedSelectTemp] = useState(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [sharedFilters, setSharedFilters] = useState({ name: '', supplier: '', kind: '' });
  const [sharedCreateOpen, setSharedCreateOpen] = useState(false);
  const [ageGroupSettingsOpen, setAgeGroupSettingsOpen] = useState(false);
  const [ageGroupSettingsTemp, setAgeGroupSettingsTemp] = useState(true);
  const [ageRangeTemp, setAgeRangeTemp] = useState({ infantEnd: 3, childEnd: 12, childStart: 3, adultStart: 12 });
  const [ageRangeInitial, setAgeRangeInitial] = useState(null);
  const filteredSharedList = useMemo(() => {
    const name = String(sharedFilters.name || '').toLowerCase();
    const supplier = String(sharedFilters.supplier || '').toLowerCase();
    const kind = String(sharedFilters.kind || '').toLowerCase();
    return (Array.isArray(sharedList) ? sharedList : []).filter(row => {
      const nm = String(row?.시리즈명 || '').toLowerCase();
      const sp = String(row?.공급사 || '').toLowerCase();
      const kd = String(row?.종류 || '').toLowerCase();
      return (!name || nm.includes(name)) && (!supplier || sp.includes(supplier)) && (!kind || kd.includes(kind));
    });
  }, [sharedList, sharedFilters]);
  // Ensure we seed one default detail row ("기타") once per modal open
  const seededDetailRef = useRef(false);
  // UI Tab: 판매가 수정 / 원가 항목
  const [activeTab, setActiveTab] = useState('price'); // 'price' | 'cost'
  useEffect(() => {
    if (multiMonthView && activeTab === 'cost') setActiveTab('price');
  }, [multiMonthView, activeTab]);

  useEffect(() => {
    if (!open) return;
    try {
      if (virtualRecruitInitial && typeof virtualRecruitInitial === 'object') {
        setVirtualRecruit({
          use: !!virtualRecruitInitial.use,
          count: String(virtualRecruitInitial.count ?? '0'),
          daysBefore: String(virtualRecruitInitial.daysBefore ?? '0'),
        });
      }
    } catch (_) { }
  }, [open, virtualRecruitInitial]);
  const [totalPriceModalOpen, setTotalPriceModalOpen] = useState(false);
  const [totalPriceModalTarget, setTotalPriceModalTarget] = useState('adult'); // 'adult' | 'child' | 'infant'
  // Commission (판매 커미션 설정) 표시 여부
  const [showCommission, setShowCommission] = useState(false);
  // Commission type: '정률' (percentage) or '정액' (fixed amount)
  const [commissionType, setCommissionType] = useState('정률');
  // Store previous commission type to detect changes
  const prevCommissionTypeRef = useRef('정률');
  // Suppress clearing when commissionType is updated programmatically from prefill
  const suppressCommissionClearRef = useRef(false);
  // Weekday selection for 적용기간 (default all selected)
  const weekdayOrder = ['월', '화', '수', '목', '금', '토', '일'];
  const [weekdaySelected, setWeekdaySelected] = useState(() => {
    const map = {};
    weekdayOrder.forEach((d) => { map[d] = true; });
    return map;
  });

  const parseNumber = (s) => {
    const n = Number(String(s ?? '').toString().replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  // (removed: handleMovePlanOrder moved to main component scope)

  // Emit live preview to parent so calendar can highlight matching cells
  useEffect(() => {
    if (!open) {
      onPreviewChange && onPreviewChange(null);
      return;
    }

    const nameToDow = { '일': 0, '월': 1, '화': 2, '수': 3, '목': 4, '금': 5, '토': 6 };
    const set = new Set(
      weekdayOrder
        .filter((n) => !!weekdaySelected[n])
        .map((n) => nameToDow[n])
        .filter((v) => v !== undefined)
    );

    // Prefer explicit range (continuous) when both ends are complete valid YYYY-MM-DD
    const startD = parseYMDStrict(rangeStart);
    const endD = parseYMDStrict(rangeEnd);
    const hasValidRange = !!(startD && endD);
    if (hasValidRange) {
      let s = new Date(startD);
      let e = new Date(endD);
      if (s.getTime() > e.getTime()) { const t = s; s = e; e = t; }
      onPreviewChange && onPreviewChange({ start: s, end: e, weekdays: set });
      return;
    }

    // Otherwise, if we have selectedDates (multi-date or discontiguous selection), use them directly
    if (initialRange?.selectedDates && Array.isArray(initialRange.selectedDates) && initialRange.selectedDates.length > 0) {
      const filteredDates = initialRange.selectedDates.filter(dateStr => {
        try {
          const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
          return !isNaN(d.getTime()) && set.has(d.getDay());
        } catch (_) {
          return false;
        }
      });
      onPreviewChange && onPreviewChange({ selectedDates: filteredDates, weekdays: set });
      return;
    }

    // Nothing to preview
    onPreviewChange && onPreviewChange(null);
  }, [open, rangeStart, rangeEnd, weekdaySelected, initialRange?.selectedDates]);

  const emitContiguousRangeIfComplete = useCallback((rs, re) => {
    const sDate = parseYMDStrict(rs);
    const eDate = parseYMDStrict(re);
    if (!sDate || !eDate) return;
    let a = new Date(sDate);
    let b = new Date(eDate);
    if (a.getTime() > b.getTime()) { const t = a; a = b; b = t; }
    const list = [];
    for (let d = new Date(a); d.getTime() <= b.getTime(); d.setDate(d.getDate() + 1)) list.push(formatYMD(d));
    if (typeof onSyncSelection === 'function') onSyncSelection(list);
    setIsContiguousSelection(true);
  }, [onSyncSelection]);

  // Format input with thousands separators while typing
  const formatInput = (s) => {
    const digits = String(s || '').replace(/[^0-9]/g, '');
    if (!digits) return '';
    const n = Number(digits);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString();
  };

  // Format percentage input 0-100 (no thousands separators)
  const formatPercent = (s) => {
    const digits = String(s || '').replace(/[^0-9]/g, '');
    if (!digits) return '';
    let n = Number(digits);
    if (!Number.isFinite(n)) return '';
    if (n > 100) n = 100;
    if (n < 0) n = 0;
    return String(n);
  };

  // Format commission input based on type (percentage or fixed amount)
  const formatCommission = (s) => {
    if (commissionType === '정률') {
      return formatPercent(s);
    } else {
      return formatInput(s);
    }
  };

  // Clear commission values when switching between types (user-driven only)
  useEffect(() => {
    if (!open) return;
    if (prevCommissionTypeRef.current !== commissionType) {
      if (suppressCommissionClearRef.current) {
        // Programmatic change from prefill; do not clear
        prevCommissionTypeRef.current = commissionType;
        suppressCommissionClearRef.current = false;
        return;
      }
      // User-driven change: clear commission inputs
      setBulk(prev => ({
        ...prev,
        ma: '',
        mc: '',
        mi: ''
      }));
      prevCommissionTypeRef.current = commissionType;
    }
  }, [commissionType, open]);

  // Handlers for detailed costs editing/add/remove and auto-total sync
  const recalcTotalsFromDetails = (list) => {
    try {
      const sumA = (Array.isArray(list) ? list : []).reduce((acc, it) => acc + parseNumber(it?.성인), 0);
      const sumC = (Array.isArray(list) ? list : []).reduce((acc, it) => acc + parseNumber(it?.어린이), 0);
      const sumI = (Array.isArray(list) ? list : []).reduce((acc, it) => acc + parseNumber(it?.영유아), 0);
      setBulk(prev => ({
        ...prev,
        ca: formatInput(sumA),
        cc: formatInput(sumC),
        ci: formatInput(sumI),
      }));
    } catch (_) { }
  };

  const handleDetailChange = (idx, field, value) => {
    setDetailCosts(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const item = { ...(next[idx] || {}) };
      if (field === '원가항목') {
        item.원가항목 = value;
        // try to backfill code from list
        const found = (taxCostItems || []).find(x => String(x.코드명) === String(value));
        if (found) item.원가항목코드 = String(found.코드);
      } else if (field === '원가항목코드') {
        item.원가항목코드 = value;
        const found = (taxCostItems || []).find(x => String(x.코드) === String(value));
        if (found) item.원가항목 = String(found.코드명);
      } else if (field === '성인') {
        item.성인 = formatInput(value);
      } else if (field === '어린이') {
        item.어린이 = formatInput(value);
      } else if (field === '영유아') {
        item.영유아 = formatInput(value);
      }
      // keep currency stored for each item (not shown as a column)
      if (!item.통화기호) item.통화기호 = costSymbol || priceSymbol || '';
      next[idx] = item;
      // Auto-sync totals to equals sum of details
      setTimeout(() => recalcTotalsFromDetails(next), 0);
      return next;
    });
  };

  const addDetailRow = () => {
    setDetailCosts(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const preferredCode = '233013';
      const fallbackName = '통합원가';
      const defaultOpt = (taxCostItems || []).find(x => String(x.코드) === preferredCode)
        || (taxCostItems || []).find(x => String(x.코드명) === fallbackName)
        || (taxCostItems || []).find(x => String(x.코드명) === '기타');
      const defaultName = defaultOpt ? String(defaultOpt.코드명) : fallbackName;
      // If items not loaded or not found, still set preferredCode so the select binds once options arrive
      const defaultCode = defaultOpt ? String(defaultOpt.코드) : preferredCode;
      next.push({ 원가항목: defaultName, 원가항목코드: defaultCode, 통화기호: costSymbol || priceSymbol || '', 성인: '', 어린이: '', 영유아: '' });
      setTimeout(() => recalcTotalsFromDetails(next), 0);
      return next;
    });
  };

  const removeDetailRow = (idx) => {
    setDetailCosts(prev => {
      const next = (Array.isArray(prev) ? prev : []).filter((_, i) => i !== idx);
      setTimeout(() => recalcTotalsFromDetails(next), 0);
      return next;
    });
  };

  // When detailCosts change, keep totals in sync (always on)
  useEffect(() => {
    recalcTotalsFromDetails(detailCosts || []);
  }, [detailCosts]);

  useEffect(() => {
    if (open) {
      // reset when opened
      let startPrefill = '';
      let endPrefill = '';
      // Check if this is multi-date selection mode
      if (initialRange?.selectedDates && Array.isArray(initialRange.selectedDates) && initialRange.selectedDates.length > 0) {
        // Multi-date mode: if contiguous, treat as range to enable weekday preview; otherwise show as list
        try {
          const sorted = [...initialRange.selectedDates].sort();
          const contiguous = (() => {
            if (sorted.length <= 1) return true;
            for (let i = 1; i < sorted.length; i++) {
              const prev = new Date(sorted[i - 1] + 'T00:00:00');
              const cur = new Date(sorted[i] + 'T00:00:00');
              const nextPrev = new Date(prev); nextPrev.setDate(prev.getDate() + 1);
              if (nextPrev.getTime() !== cur.getTime()) return false;
            }
            return true;
          })();
          if (contiguous) {
            setRangeStart(sorted[0]);
            setRangeEnd(sorted[sorted.length - 1]);
            setIsContiguousSelection(true);
          } else {
            setRangeStart('');
            setRangeEnd('');
            setIsContiguousSelection(false);
          }
        } catch (_) {
          setRangeStart('');
          setRangeEnd('');
          setIsContiguousSelection(false);
        }
      } else {
        // Range mode: set start/end dates
        if (initialRange?.start instanceof Date) {
          startPrefill = formatYMD(initialRange.start);
        } else if (initialRange?.startStr) {
          startPrefill = String(initialRange.startStr).slice(0, 10);
        }
        if (initialRange?.end instanceof Date) {
          // FullCalendar select end is exclusive; subtract 1 day for inclusive UX
          const d = new Date(initialRange.end);
          d.setDate(d.getDate() - 1);
          endPrefill = formatYMD(d);
        } else if (initialRange?.endStr) {
          // endStr is exclusive; convert to inclusive
          const d = new Date(String(initialRange.endStr).slice(0, 10) + 'T00:00:00');
          d.setDate(d.getDate() - 1);
          endPrefill = formatYMD(d);
        }
        setRangeStart(startPrefill);
        setRangeEnd(endPrefill || startPrefill);
        setIsContiguousSelection(true);
      }

      // prefill bulk fields if provided
      if (initialBulk && typeof initialBulk === 'object') {
        setBulk({
          pa: initialBulk.pa ?? '',
          pc: initialBulk.pc ?? '',
          pi: initialBulk.pi ?? '',
          ta: initialBulk.ta ?? '',
          tc: initialBulk.tc ?? '',
          ti: initialBulk.ti ?? '',
          ca: initialBulk.ca ?? '',
          cc: initialBulk.cc ?? '',
          ci: initialBulk.ci ?? '',
          ma: initialBulk.ma ?? '',
          mc: initialBulk.mc ?? '',
          mi: initialBulk.mi ?? '',
          stock: initialBulk.stock ?? '',
          closed: !!initialBulk.closed,
        });
        // Ensure symbols (initialBulk.pcur/ccur may be currency codes like KRW)
        setPriceSymbol(getCurrencySymbol(initialBulk.pcur ?? ''));
        setCostSymbol(getCurrencySymbol(initialBulk.ccur ?? (initialBulk.pcur ?? '')));
        setCostDivision(initialBulk.costDivision ?? '');
        setDetailCosts(Array.isArray(initialBulk.detailCosts) ? initialBulk.detailCosts : []);
        const loadedCommissionType = initialBulk.commissionType ?? '정률';
        setCommissionType(loadedCommissionType);
        prevCommissionTypeRef.current = loadedCommissionType;
        // Prefill commission checkbox: prefer explicit flag; otherwise infer from values
        if (typeof initialBulk.showCommission !== 'undefined') {
          setShowCommission(!!initialBulk.showCommission);
        } else {
          try {
            const hasCommission = ['ma', 'mc', 'mi'].some((k) => {
              const raw = initialBulk?.[k];
              const s = String(raw ?? '').trim();
              if (!s) return false;
              const n = Number(s.replace(/[^0-9.-]/g, ''));
              return Number.isFinite(n) ? n > 0 : true;
            });
            setShowCommission(hasCommission);
          } catch (_) {
            setShowCommission(false);
          }
        }
      } else {
        setBulk({ pa: '', pc: '', pi: '', ta: '', tc: '', ti: '', ca: '', cc: '', ci: '', ma: '', mc: '', mi: '', stock: '', closed: false });
        setShowCommission(false);
        setPriceSymbol('');
        setCostSymbol('');
        setCostDivision('');
        setDetailCosts([]);
        setUseSharedStock(false);
        setSharedList([]);
        setSelectedSeriesNo(null);
        // Reset weekday selection to all selected when opening fresh
        const map = {};
        weekdayOrder.forEach((d) => { map[d] = true; });
        setWeekdaySelected(map);
      }
      // fetch tax cost items when opening
      (async () => {
        try {
          setTaxCostLoading(true);
          const { companyNo } = getUserInfo() || {};
          if (companyNo) {
            const items = await getSupplierTaxCostItems({ companyNo });
            setTaxCostItems(Array.isArray(items) ? items : []);
          } else {
            setTaxCostItems([]);
          }
        } catch (e) {
          try { console.warn('세무 원가 항목 조회 실패', e); } catch (_) { }
          setTaxCostItems([]);
        } finally {
          setTaxCostLoading(false);
        }
      })();
      // trigger enter animation
      setAnimatingOut(false);
      const id = requestAnimationFrame(() => setMountedVisible(true));
      return () => cancelAnimationFrame(id);
    }
  }, [open, initialRange, initialBulk]);

  // When shared inventory is needed (checkbox on or modal open), fetch list once
  useEffect(() => {
    (async () => {
      try {
        if (!open) return;
        if (!useSharedStock && !sharedModalOpen) return;
        if (!productId) return;
        setSharedLoading(true);
        const rows = await getSharedInventoryList({ productNo: Number(productId) });
        setSharedList(Array.isArray(rows) ? rows : []);
      } catch (e) {
        try { console.warn('공유재고 조회 실패', e); } catch (_) { }
        setSharedList([]);
      } finally {
        setSharedLoading(false);
      }
    })();
  }, [useSharedStock, sharedModalOpen, open, productId]);

  // Prefill shared toggle/selection if selected dates include a shared series
  useEffect(() => {
    try {
      if (!open) return;
      const arr = initialRange?.selectedDates;
      if (!Array.isArray(arr) || arr.length === 0) return;
      const map = sharedSeriesNoByDate || {};
      const found = arr.find(d => map[String(d)] != null);
      if (found != null) {
        setSelectedSeriesNo(map[String(found)]);
      }
    } catch (_) { }
  }, [open, initialRange, sharedSeriesNoByDate]);

  // Keep shared stock toggle in sync with selection
  useEffect(() => {
    setUseSharedStock(!!selectedSeriesNo);
  }, [selectedSeriesNo]);

  // Backfill selected series name when list is loaded
  useEffect(() => {
    if (!selectedSeriesNo) { setSelectedSeriesName(''); return; }
    const found = (Array.isArray(sharedList) ? sharedList : []).find(row => String(row?.시리즈번호) === String(selectedSeriesNo));
    setSelectedSeriesName(found?.시리즈명 || '');
  }, [selectedSeriesNo, sharedList]);

  // Keep date range inputs synced when external selection changes while modal is open
  useEffect(() => {
    if (!open) return;
    const arr = initialRange?.selectedDates;
    if (!Array.isArray(arr) || arr.length === 0) return;
    try {
      const sorted = [...arr].sort();
      let contiguous = true;
      for (let i = 1; i < sorted.length; i++) {
        const prev = new Date(sorted[i - 1] + 'T00:00:00');
        const cur = new Date(sorted[i] + 'T00:00:00');
        const nextPrev = new Date(prev); nextPrev.setDate(prev.getDate() + 1);
        if (nextPrev.getTime() !== cur.getTime()) { contiguous = false; break; }
      }
      if (contiguous) {
        setRangeStart(sorted[0]);
        setRangeEnd(sorted[sorted.length - 1]);
        setIsContiguousSelection(true);
      } else {
        setRangeStart('');
        setRangeEnd('');
        setIsContiguousSelection(false);
      }
    } catch (_) {
      // ignore parse errors
    }
  }, [open, initialRange?.selectedDates]);

  // When modal opens, if there are no detail rows, seed one default row preferring '통합원가' (233013)
  useEffect(() => {
    if (!open) {
      seededDetailRef.current = false; // reset for next open
      return;
    }
    if (!seededDetailRef.current && (!detailCosts || detailCosts.length === 0)) {
      const preferredCode = '233013';
      const fallbackName = '통합원가';
      const defaultOpt = (taxCostItems || []).find(x => String(x.코드) === preferredCode)
        || (taxCostItems || []).find(x => String(x.코드명) === fallbackName)
        || (taxCostItems || []).find(x => String(x.코드명) === '기타');
      const defaultName = defaultOpt ? String(defaultOpt.코드명) : fallbackName;
      // If items not loaded or not found, still set preferredCode so the select binds once options arrive
      const defaultCode = defaultOpt ? String(defaultOpt.코드) : preferredCode;
      setDetailCosts([{ 원가항목: defaultName, 원가항목코드: defaultCode, 통화기호: costSymbol || priceSymbol || '', 성인: '', 어린이: '', 영유아: '' }]);
      seededDetailRef.current = true;
    }
  }, [open, taxCostItems, detailCosts]);

  // Backfill or correct codes when taxCostItems arrives/changes
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(detailCosts) || detailCosts.length === 0) return;
    const preferredCode = '233013';
    const byCode = (taxCostItems || []).find(x => String(x.코드) === preferredCode);
    const byName = (taxCostItems || []).find(x => String(x.코드명) === '통합원가');
    const 기타 = (taxCostItems || []).find(x => String(x.코드명) === '기타');
    const validCodes = new Set((taxCostItems || []).map(x => String(x.코드)));
    if ((taxCostItems || []).length === 0) return;
    setDetailCosts(prev => {
      const next = (prev || []).map(it => {
        if (!it) return it;
        const current = String(it.원가항목코드 || '');
        const currentValid = current && validCodes.has(current);
        if (currentValid) return it;
        // Choose best available default in order: 233013 -> 통합원가 -> 기타
        const chosen = byCode || byName || 기타;
        if (chosen) {
          return { ...it, 원가항목코드: String(chosen.코드), 원가항목: String(chosen.코드명) };
        }
        return { ...it, 원가항목코드: '', 원가항목: it.원가항목 || '' };
      });
      return next;
    });
  }, [open, taxCostItems]);

  // If initialBulk changes while open, update fields gracefully
  useEffect(() => {
    if (open && initialBulk && typeof initialBulk === 'object') {
      const toDisp = (v) => {
        const n = Number(String(v ?? '').toString().replace(/[^0-9.-]/g, ''));
        return Number.isFinite(n) ? n.toLocaleString() : '';
      };
      const formatCommissionForType = (val, type) => {
        if (!val && val !== 0) return '';
        if ((type || commissionType) === '정률') {
          const digits = String(val || '').replace(/[^0-9]/g, '');
          return digits ? String(Math.min(100, Number(digits))) : '';
        }
        return toDisp(val);
      };
      setBulk(prev => ({
        pa: toDisp(initialBulk.pa ?? prev.pa),
        pc: toDisp(initialBulk.pc ?? prev.pc),
        pi: toDisp(initialBulk.pi ?? prev.pi),
        ta: toDisp(initialBulk.ta ?? prev.ta),
        tc: toDisp(initialBulk.tc ?? prev.tc),
        ti: toDisp(initialBulk.ti ?? prev.ti),
        ca: toDisp(initialBulk.ca ?? prev.ca),
        cc: toDisp(initialBulk.cc ?? prev.cc),
        ci: toDisp(initialBulk.ci ?? prev.ci),
        ma: formatCommissionForType(initialBulk.ma ?? prev.ma, initialBulk.commissionType),
        mc: formatCommissionForType(initialBulk.mc ?? prev.mc, initialBulk.commissionType),
        mi: formatCommissionForType(initialBulk.mi ?? prev.mi, initialBulk.commissionType),
        stock: ('stock' in initialBulk)
          ? String(initialBulk.stock ?? '')
          : String(prev.stock ?? ''),
        // Ensure closed switch reflects prefilled value when it arrives asynchronously
        closed: (typeof initialBulk.closed !== 'undefined') ? !!initialBulk.closed : !!prev.closed,
      }));
      if (typeof initialBulk.pcur !== 'undefined') {
        setPriceSymbol(getCurrencySymbol(initialBulk.pcur || ''));
      }
      if (typeof initialBulk.ccur !== 'undefined') {
        setCostSymbol(getCurrencySymbol(initialBulk.ccur || initialBulk.pcur || ''));
      }
      if (typeof initialBulk.costDivision !== 'undefined') {
        const v = String(initialBulk.costDivision || '').trim();
        setCostDivision(v);
        try { console.debug('[BulkModal] costDivision set from initialBulk:', v); } catch (_) { }
      }
      if (Array.isArray(initialBulk.detailCosts)) setDetailCosts(initialBulk.detailCosts);
      if (typeof initialBulk.commissionType !== 'undefined') {
        const loadedType = initialBulk.commissionType || '정률';
        // Prevent clearing inputs during programmatic update
        suppressCommissionClearRef.current = true;
        setCommissionType(loadedType);
        prevCommissionTypeRef.current = loadedType;
      }
      // Prefill commission checkbox: prefer explicit flag; otherwise infer from values
      if (typeof initialBulk.showCommission !== 'undefined') {
        setShowCommission(!!initialBulk.showCommission);
      } else {
        try {
          const hasCommission = ['ma', 'mc', 'mi'].some((k) => {
            const raw = initialBulk?.[k];
            const s = String(raw ?? '').trim();
            if (!s) return false;
            const n = Number(s.replace(/[^0-9.-]/g, ''));
            return Number.isFinite(n) ? n > 0 : true;
          });
          setShowCommission(hasCommission);
        } catch (_) {
          setShowCommission(false);
        }
      }
    }
  }, [open, initialBulk]);

  useEffect(() => {
    setHasDOM(typeof document !== 'undefined' && !!document.body);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadAgeRangeForModal = async () => {
      if (!ageGroupSettingsOpen) return;
      if (!productId) return;
      try {
        const res = await getProductDetailExtra(productId);
        const d = res?.detail || {};
        if (!mounted) return;
        const infantEnd = Math.max(0, Number(d.연령유아끝 ?? 3));
        const childEnd = Math.max(infantEnd, Number(d.연령소아끝 ?? 12));
        const next = {
          infantEnd,
          childEnd,
          childStart: infantEnd,
          adultStart: childEnd,
        };
        setAgeGroupSettingsTemp(d.연령구분여부 !== false);
        setAgeRangeTemp(next);
        setAgeRangeInitial({ infantEnd, childEnd });
      } catch (_) {
        if (!mounted) return;
        setAgeRangeInitial((prev) => prev || { infantEnd: ageRangeTemp.infantEnd, childEnd: ageRangeTemp.childEnd });
      }
    };
    loadAgeRangeForModal();
    return () => { mounted = false; };
  }, [ageGroupSettingsOpen, productId]);

  const isTotalPrice = String(priceType || '').trim() === '합계요금';
  // 연령구분 미사용 시 성인 필드만 표시 (isTotalPrice와 동일한 UI)
  const showAdultOnly = isTotalPrice || !useAgeGroup;

  if (!open) return null;

  const requestClose = () => {
    setAnimatingOut(true);
    setMountedVisible(false);
    window.setTimeout(() => {
      onClose && onClose();
    }, 220);
  };

  /** 선택한 날짜들의 기획전 현황 — 툴바의 요금제 전체 집계와 달리 "선택 범위"만 본다.
   *  이름 문자열 기반이라 표시 전용이며, 실제 매핑 판단은 모달이 사전 역매핑으로 다시 한다. */
  const promoStats = useMemo(() => {
    const list = Array.isArray(promoSelection) ? promoSelection : [];
    const counts = new Map();
    list.forEach((item) => {
      splitPromoNames(item?.promo).forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
    });
    return {
      total: list.length,
      noPrice: list.filter((item) => !(Number(item?.priceNo) > 0)).length,
      names: Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([name, days]) => ({ name, days })),
    };
  }, [promoSelection]);

  /** 벌크 저장/삭제 공통: 기간·요일·멀티선택 날짜 컨텍스트 (저장과 동일한 규칙으로 삭제에도 사용) */
  const getBulkSelectionContext = () => {
    const selectedDays = weekdayOrder.filter((d) => !!weekdaySelected[d]);
    const mask = weekdayOrder.map((d) => (weekdaySelected[d] ? 'Y' : 'N')).join('');
    const rs = parseYMDStrict(rangeStart);
    const re = parseYMDStrict(rangeEnd);
    const hasExplicitRange = !!(rs && re);
    let selectedDates = undefined;
    if (!hasExplicitRange) {
      let dates = initialRange?.selectedDates;
      if (Array.isArray(dates)) {
        const nameToDow = { '일': 0, '월': 1, '화': 2, '수': 3, '목': 4, '금': 5, '토': 6 };
        const allowed = new Set(selectedDays.map(n => nameToDow[n]).filter(v => v !== undefined));
        dates = dates.filter(ds => {
          const d = new Date(String(ds).slice(0, 10) + 'T00:00:00');
          return !Number.isNaN(d.getTime()) ? allowed.has(d.getDay()) : true;
        });
        selectedDates = dates;
      }
    }
    return {
      rangeStart: hasExplicitRange ? formatYMD(rs) : rangeStart,
      rangeEnd: hasExplicitRange ? formatYMD(re) : rangeEnd,
      weekdays: { names: selectedDays, mask },
      selectedDates,
    };
  };

  const handleApply = async () => {
    try {
      // Decide currency to display use
      const firstCur = (bulk.pa || bulk.ca) ? (priceSymbol || costSymbol) : (costSymbol || priceSymbol);
      const displayCur = firstCur || costSymbol || priceSymbol || '';
      const sel = getBulkSelectionContext();
      const sharedSelected = useSharedStock ? (sharedList || []).find(r => String(r?.시리즈번호) === String(selectedSeriesNo)) : null;
      try { onVirtualRecruitChange && onVirtualRecruitChange(virtualRecruit); } catch (_) { }
      onApply && onApply({ ...sel, bulk, detailCosts, costDivision, currency: displayCur, closed: !!bulk.closed, useSharedStock: !!useSharedStock, sharedSeriesNo: sharedSelected ? String(sharedSelected.시리즈번호) : null, sharedSeries: sharedSelected || null, commissionType, virtualRecruit });
    } catch (_) { }
  };

  if (!hasDOM) return null;

  const ageGroupModal = (!ageGroupSettingsOpen ? null : createPortal(
    <div
      className="fixed inset-0 z-[120] transition-opacity"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setAgeGroupSettingsOpen(false); }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-gray-900">연령구분 설정</div>
              
            </div>
            <button type="button" className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50" onClick={() => setAgeGroupSettingsOpen(false)}>닫기</button>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-800 mb-2">연령구분 사용여부</div>
              <div className="flex items-center gap-4" role="radiogroup" aria-label="연령구분 사용여부">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ageGroupUsed_common"
                    value="true"
                    checked={ageGroupSettingsTemp !== false}
                    disabled={!!savingAgeGroup}
                    onChange={() => setAgeGroupSettingsTemp(true)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm font-medium ${savingAgeGroup ? 'text-gray-400' : 'text-gray-700'}`}>연령구분 사용</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ageGroupUsed_common"
                    value="false"
                    checked={ageGroupSettingsTemp === false}
                    disabled={!!savingAgeGroup}
                    onChange={() => setAgeGroupSettingsTemp(false)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm font-medium ${savingAgeGroup ? 'text-gray-400' : 'text-gray-700'}`}>연령구분 미사용</span>
                </label>
              </div>
              
            </div>

            {ageGroupSettingsTemp !== false && (
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-800 mb-2">연령 구간 설정</div>

                <div className="space-y-2">
                  <div className="grid grid-cols-[78px_1fr] items-center gap-2">
                    <span className="text-[11px] text-gray-600 font-medium">유아</span>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled className="w-16 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm" value={0} />
                      <span className="text-[12px] text-gray-500">~</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-20 px-2 py-1 border rounded text-sm"
                        value={String(ageRangeTemp.infantEnd ?? '')}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                          if (allowed.includes(e.key)) return;
                          if (e.ctrlKey || e.metaKey) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const rawStr = String(e.target.value || '').replace(/[^0-9]/g, '');
                          const end = rawStr === '' ? '' : Math.max(0, Number(rawStr || 0));
                          setAgeRangeTemp((prev) => {
                            const inf = end;
                            const childEnd = prev.childEnd === '' ? '' : Math.max(Number(prev.childEnd || 0), Number(inf || 0));
                            return {
                              ...prev,
                              infantEnd: inf,
                              childStart: inf === '' ? prev.childStart : inf,
                              childEnd,
                              adultStart: childEnd === '' ? prev.adultStart : childEnd,
                            };
                          });
                        }}
                      />
                      <span className="text-[11px] text-gray-500">세</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-[78px_1fr] items-center gap-2">
                    <span className="text-[11px] text-gray-600 font-medium">소아</span>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled className="w-16 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm" value={ageRangeTemp.childStart ?? (ageRangeTemp.infantEnd ?? 3)} />
                      <span className="text-[12px] text-gray-500">~</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-20 px-2 py-1 border rounded text-sm"
                        value={String(ageRangeTemp.childEnd ?? '')}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                          if (allowed.includes(e.key)) return;
                          if (e.ctrlKey || e.metaKey) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const rawStr = String(e.target.value || '').replace(/[^0-9]/g, '');
                          const end = rawStr === '' ? '' : Math.max(0, Number(rawStr || 0));
                          setAgeRangeTemp((prev) => {
                            const inf = prev.infantEnd === '' ? 0 : Number(prev.infantEnd || 0);
                            const chEnd = end === '' ? '' : Math.max(inf, end);
                            return {
                              ...prev,
                              childEnd: chEnd,
                              adultStart: chEnd === '' ? prev.adultStart : chEnd,
                            };
                          });
                        }}
                      />
                      <span className="text-[11px] text-gray-500">세</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-[78px_1fr] items-center gap-2">
                    <span className="text-[11px] text-gray-600 font-medium">성인</span>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled className="w-16 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm" value={ageRangeTemp.adultStart ?? (ageRangeTemp.childEnd ?? 12)} />
                      <span className="text-[11px] text-gray-500">세 이상</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
            <button type="button" className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50" disabled={!!savingAgeGroup} onClick={() => setAgeGroupSettingsOpen(false)}>취소</button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={(() => {
                if (!!savingAgeGroup) return true;
                const useSame = (ageGroupSettingsTemp !== false) === (useAgeGroup !== false);
                if (ageGroupSettingsTemp === false) return useSame;
                const init = ageRangeInitial;
                const rangesSame = !!init && Number(init.infantEnd) === Number(ageRangeTemp.infantEnd) && Number(init.childEnd) === Number(ageRangeTemp.childEnd);
                return useSame && rangesSame;
              })()}
              onClick={async () => {
                const next = ageGroupSettingsTemp !== false;
                const ok = window.confirm('이 설정은 상품 전체(모든 날짜)에 적용됩니다. 변경할까요?');
                if (!ok) return;
                let success = true;
                try {
                  if (typeof onChangeUseAgeGroup === 'function') {
                    const res = await onChangeUseAgeGroup({
                      useAgeGroup: next,
                      ranges: {
                        infantEnd: ageRangeTemp.infantEnd,
                        childEnd: ageRangeTemp.childEnd,
                      },
                    });
                    success = (typeof res === 'boolean') ? res : true;
                  }
                } catch (_) {
                  success = false;
                }
                if (success) setAgeGroupSettingsOpen(false);
              }}
            >적용</button>
          </div>
        </div>
      </div>
    </div>, document.body));

  const totalPriceModal = (!totalPriceModalOpen ? null : createPortal(
    <div
      className="fixed inset-0 z-[120] transition-opacity"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setTotalPriceModalOpen(false); }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-900">판매가 설정</div>
              {Array.isArray(itineraryTypes) && itineraryTypes.length > 1 && (
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-[11px] text-gray-600">일정타입</span>
                  <select
                    className="px-2 py-1 text-xs border rounded bg-white"
                    value={selectedItineraryTypeNo || ''}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (typeof onSelectedItineraryTypeNoChange === 'function') {
                        onSelectedItineraryTypeNoChange(v);
                      }
                    }}
                  >
                    {itineraryTypes.map((t) => (
                      <option key={t.일정타입번호} value={t.일정타입번호}>{t.일정타입명}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <button type="button" className="text-xs px-2 py-1 hover:bg-gray-50" onClick={() => setTotalPriceModalOpen(false)}>✕</button>
          </div>

          <div className="p-4 space-y-3">
            {!showAdultOnly ? (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2 items-center">
                  <div />
                  <div className="text-[11px] font-semibold text-gray-700 text-center">성인</div>
                  <div className="text-[11px] font-semibold text-gray-700 text-center">소아</div>
                  <div className="text-[11px] font-semibold text-gray-700 text-center">유아</div>
                </div>

                <div className="grid grid-cols-4 gap-2 items-center">
                  <div className="text-xs font-semibold text-gray-700">기본 판매가{priceSymbol ? ` (${priceSymbol})` : ''}</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="font-semibold w-full border border-gray-200 rounded-lg transition text-right px-2 py-2 text-sm bg-white"
                    value={bulk.pa}
                    onChange={(e) => setBulk(prev => ({ ...prev, pa: formatInput(e.target.value) }))}
                    placeholder="0"
                    aria-label="성인 기본 판매가"
                    autoFocus={totalPriceModalTarget === 'adult'}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    className="font-semibold w-full border border-gray-200 rounded-lg transition text-right px-2 py-2 text-sm bg-white"
                    value={bulk.pc}
                    onChange={(e) => setBulk(prev => ({ ...prev, pc: formatInput(e.target.value) }))}
                    placeholder="0"
                    aria-label="소아 기본 판매가"
                    autoFocus={totalPriceModalTarget === 'child'}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    className="font-semibold w-full border border-gray-200 rounded-lg transition text-right px-2 py-2 text-sm bg-white"
                    value={bulk.pi}
                    onChange={(e) => setBulk(prev => ({ ...prev, pi: formatInput(e.target.value) }))}
                    placeholder="0"
                    aria-label="유아 기본 판매가"
                    autoFocus={totalPriceModalTarget === 'infant'}
                  />
                </div>

                <div className="grid grid-cols-4 gap-2 items-center">
                  <div className="text-xs font-semibold text-gray-700">Tax/유류{priceSymbol ? ` (${priceSymbol})` : ''}</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="font-semibold w-full border border-gray-200 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition text-right px-2 py-2 text-sm bg-white"
                    value={bulk.ta}
                    onChange={(e) => setBulk(prev => ({ ...prev, ta: formatInput(e.target.value) }))}
                    placeholder="0"
                    aria-label="성인 Tax 및 유류할증료"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    className="font-semibold w-full border border-gray-200 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition text-right px-2 py-2 text-sm bg-white"
                    value={bulk.tc}
                    onChange={(e) => setBulk(prev => ({ ...prev, tc: formatInput(e.target.value) }))}
                    placeholder="0"
                    aria-label="소아 Tax 및 유류할증료"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    className="font-semibold w-full border border-gray-200 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition text-right px-2 py-2 text-sm bg-white"
                    value={bulk.ti}
                    onChange={(e) => setBulk(prev => ({ ...prev, ti: formatInput(e.target.value) }))}
                    placeholder="0"
                    aria-label="유아 Tax 및 유류할증료"
                  />
                </div>

                <div className="h-px bg-gray-200" />

                <div className="grid grid-cols-4 gap-2 items-center">
                  <div className="text-[16px] font-semibold text-blue-800">총 판매가</div>
                  <input
                    type="text"
                    className="w-full text-right px-2 py-2 text-[16px] font-semibold text-blue-700 bg-white"
                    value={formatInput(String(parseNumber(bulk.pa) + parseNumber(bulk.ta)))}
                    readOnly
                    disabled
                    aria-label="성인 총 판매가"
                  />
                  <input
                    type="text"
                    className="w-full text-right px-2 py-2 text-[16px] font-semibold text-blue-700 bg-white"
                    value={formatInput(String(parseNumber(bulk.pc) + parseNumber(bulk.tc)))}
                    readOnly
                    disabled
                    aria-label="소아 총 판매가"
                  />
                  <input
                    type="text"
                    className="w-full text-right px-2 py-2 text-[16px] font-semibold text-blue-700 bg-white"
                    value={formatInput(String(parseNumber(bulk.pi) + parseNumber(bulk.ti)))}
                    readOnly
                    disabled
                    aria-label="유아 총 판매가"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 items-center">
                  <div className="text-xs font-semibold text-gray-700">기본 판매가{priceSymbol ? ` (${priceSymbol})` : ''}</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition text-right text-sm px-2 py-2 bg-white"
                    value={bulk.pa}
                    onChange={(e) => {
                      const v = formatInput(e.target.value);
                      setBulk(prev => ({ ...prev, pa: v, pc: v, pi: v }));
                    }}
                    placeholder={isTotalPrice ? "합계" : "성인"}
                    aria-label={isTotalPrice ? "합계 판매가" : "성인 판매가"}
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-2 gap-2 items-center">
                  <div className="text-xs font-semibold text-gray-700">Tax/유류{priceSymbol ? ` (${priceSymbol})` : ''}</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full border border-gray-200 rounded-lg shadow-sm focus:ring-amber-500 focus:border-amber-500 transition text-right text-sm px-2 py-2 bg-white"
                    value={bulk.ta}
                    onChange={(e) => {
                      const v = formatInput(e.target.value);
                      setBulk(prev => ({ ...prev, ta: v, tc: v, ti: v }));
                    }}
                    placeholder={isTotalPrice ? "합계" : "성인"}
                    aria-label={isTotalPrice ? "합계 Tax 및 유류할증료" : "성인 Tax 및 유류할증료"}
                  />
                </div>

                <div className="h-px bg-gray-200" />

                <div className="grid grid-cols-2 gap-2 items-center">
                  <div className="text-xs font-semibold text-blue-800">총 판매가</div>
                  <input
                    type="text"
                    className="w-full text-right px-2 py-2 text-sm font-semibold text-blue-700 bg-white"
                    value={formatInput(String(parseNumber(bulk.pa) + parseNumber(bulk.ta)))}
                    readOnly
                    disabled
                    aria-label={isTotalPrice ? "합계 총 판매가" : "성인 총 판매가"}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="px-4 py-3 flex items-center justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
              onClick={() => {
                handleApply();
                setTotalPriceModalOpen(false);
              }}
            >확인</button>
          </div>
        </div>
      </div>
    </div>, document.body));

  const panelContent = (
    <div className="h-full flex flex-col">


      {/* Tab Bar - Improved Design */}
      {!multiMonthView && (
        <div className="px-0 py-2.5">
          <div className="flex w-full items-center gap-1" role="tablist" aria-label="가격 및 재고 수정 탭">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'price'}
              onClick={() => setActiveTab('price')}
              className={`flex-1 text-center px-3 py-2 text-sm font-semibold rounded-lg focus:outline-none transition-all ${activeTab === 'price'
                  ? 'bg-white text-primary-600 shadow-md border border-blue-200'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-white/50'
                }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0 0118 0z" />
                </svg>
                판매가
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'cost'}
              onClick={() => setActiveTab('cost')}
              className={`flex-1 text-center px-3 py-2 text-sm font-semibold rounded-lg focus:outline-none transition-all ${activeTab === 'cost'
                  ? 'bg-white text-amber-600 shadow-md border border-amber-200'
                  : 'text-gray-600 hover:text-amber-600 hover:bg-white/50'
                }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                원가
              </span>
            </button>
          </div>
        </div>
      )}




      {/* Body */}
      <main className={`flex-1 px-0 space-y-2 ${inline ? 'overflow-visible' : 'overflow-y-auto'}`}>
        {Array.isArray(itineraryTypes) && itineraryTypes.length > 1 && (
          <section className="bg-white border border-gray-200 rounded-xl shadow-sm px-2 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-gray-700">일정타입</div>
              <select
                className="px-2 py-1 text-sm border rounded"
                value={selectedItineraryTypeNo || ''}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (typeof onSelectedItineraryTypeNoChange === 'function') {
                    onSelectedItineraryTypeNoChange(v);
                  }
                }}
              >
                {itineraryTypes.map((t) => (
                  <option key={t.일정타입번호} value={t.일정타입번호}>{t.일정타입명}</option>
                ))}
              </select>
            </div>
          </section>
        )}
        {/* 기간 입력: 항상 표시하고, 유효하면 캘린더 선택과 동기화 */}
        <section className="bg-gray-50 p-2 rounded-xl shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">시작일</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                placeholder="YYYY-MM-DD"
                maxLength={10}
                title="YYYY-MM-DD"
                className={`text-[12px] text-center w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition ps-date ${isContiguousSelection && rangeStart ? '' : 'ps-empty'}`}
                value={isContiguousSelection ? rangeStart : ''}
                onChange={(e) => {
                  const v = sanitizeBulkDateTyping(e.target.value);
                  setRangeStart(v);
                  emitContiguousRangeIfComplete(v, rangeEnd);
                }}
                onBlur={() => {
                  const d = parseYMDStrict(rangeStart);
                  if (d) {
                    const n = formatYMD(d);
                    if (n !== rangeStart) setRangeStart(n);
                    emitContiguousRangeIfComplete(n, rangeEnd);
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">종료일</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                placeholder="YYYY-MM-DD"
                maxLength={10}
                title="YYYY-MM-DD"
                className={`text-[12px] text-center w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition ps-date ${isContiguousSelection && rangeEnd ? '' : 'ps-empty'}`}
                value={isContiguousSelection ? rangeEnd : ''}
                onChange={(e) => {
                  const v = sanitizeBulkDateTyping(e.target.value);
                  setRangeEnd(v);
                  emitContiguousRangeIfComplete(rangeStart, v);
                }}
                onBlur={() => {
                  const d = parseYMDStrict(rangeEnd);
                  if (d) {
                    const n = formatYMD(d);
                    if (n !== rangeEnd) setRangeEnd(n);
                    emitContiguousRangeIfComplete(rangeStart, n);
                  }
                }}
              />
            </div>
          </div>

          <div className="mt-2 mb-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">적용 요일</label>
            <div className="grid grid-cols-7 gap-2 text-[11px]">
              {weekdayOrder.map((d) => (
                <label key={d} className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={!!weekdaySelected[d]}
                    onChange={(e) => setWeekdaySelected((prev) => ({ ...prev, [d]: e.target.checked }))}
                  />
                  <span>{d}</span>
                </label>
              ))}
            </div>

          </div>
        </section>

        {!multiMonthView && (
          <section className="bg-white border border-gray-200 rounded-xl shadow-sm px-2 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                
                <div className="text-[11px] text-gray-600 truncate">
                  연령구분: <span className={`font-semibold ${useAgeGroup === false ? 'text-gray-800' : 'text-blue-700'}`}>{useAgeGroup === false ? '미사용' : '사용'}</span>
                </div>
              </div>
              <button
                type="button"
                className="text-[11px] px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60 shrink-0"
                disabled={!!savingAgeGroup}
                onClick={() => {
                  setAgeGroupSettingsTemp(useAgeGroup !== false);
                  setAgeRangeInitial(null);
                  setAgeGroupSettingsOpen(true);
                }}
              >연령구분 변경</button>
            </div>
          </section>
        )}

        {/* 판매가, Tax, 재고 / 공유재고 */}
        {activeTab === 'price' && (
          <section className="p-0 space-y-2">

            {/* 판매가 + Tax/유류 + 총 판매가 */}
             <div className="bg-white border border-blue-100 rounded-lg p-2 space-y-1 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-blue-700 flex items-center gap-1.5">
            
                 총 판매가 {priceSymbol ? ` (${priceSymbol})` : ''}
                </span>
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 shrink-0"
                  onClick={() => {
                    setTotalPriceModalTarget('adult');
                    setTotalPriceModalOpen(true);
                  }}
                >판매가 설정</button>
              </div>

              {/* 판매가 */}
              <div className="p-1.5">
              
                {showAdultOnly ? (
                  <div
                    className="w-full border border-blue-200 rounded-lg shadow-sm text-right text-sm px-2 py-1.5 bg-gray-50 text-blue-700 font-semibold"
                    aria-label={isTotalPrice ? "합계 총 판매가" : "성인 총 판매가"}
                  >{formatInput(String(parseNumber(bulk.pa) + parseNumber(bulk.ta)))}</div>
                ) : (
                  <div className="grid grid-cols-3 gap-1">
                    <div className="text-left">
                      <span className="text-[10px] text-gray-500 font-medium block mb-0.5">성인</span>
                      <div
                        className="font-semibold w-full rounded-lg text-left px-1.5 py-1 text-xs text-blue-700"
                        aria-label="성인 총 판매가"
                        title="성인"
                      >{formatInput(String(parseNumber(bulk.pa) + parseNumber(bulk.ta)))}</div>
                    </div>
                    <div className="text-left">
                      <span className="text-[10px] text-gray-500 font-medium block mb-0.5">소아</span>
                      <div
                        className="font-semibold w-full rounded-lg text-left px-1.5 py-1 text-xs text-blue-700"
                        aria-label="소아 총 판매가"
                        title="소아"
                      >{formatInput(String(parseNumber(bulk.pc) + parseNumber(bulk.tc)))}</div>
                    </div>
                    <div className="text-left">
                      <span className="text-[10px] text-gray-500 font-medium block mb-0.5">유아</span>
                      <div
                        className="font-semibold w-full rounded-lg text-left px-1.5 py-1 text-xs text-blue-700"
                        aria-label="유아 총 판매가"
                        title="유아"
                      >{formatInput(String(parseNumber(bulk.pi) + parseNumber(bulk.ti)))}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Tax/유류 (조건부 표시) */}
              {/* 총 판매가 (Tax/유류 입력 시에만 표시) */}
            </div>

                 {/* 판매 커미션 설정 */}
            <div className="border border-emerald-200 rounded-lg p-2" data-tour="ps-commission">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
                  <input
                    type="checkbox"
                    className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    checked={!!showCommission}
                    onChange={(e) => setShowCommission(!!e.target.checked)}
                  />
                  <span className="flex items-center gap-1">
                    
                    판매 커미션 설정
                  </span>
                </label>
                {showCommission && (
                  <div className="flex items-center gap-1.5 bg-white/60 rounded-md px-2 py-2">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="commissionType"
                        value="정률"
                        checked={commissionType === '정률'}
                        onChange={(e) => setCommissionType(e.target.value)}
                        className="w-3 h-3 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] text-gray-600">정률(%)</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="commissionType"
                        value="정액"
                        checked={commissionType === '정액'}
                        onChange={(e) => setCommissionType(e.target.value)}
                        className="w-3 h-3 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] text-gray-600">정액</span>
                    </label>
                  </div>
                )}
              </div>
              {showCommission && (
                <div className="space-y-1.5">
                  {showAdultOnly ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-emerald-600 font-medium w-8">{isTotalPrice ? '합계' : '성인'}</span>
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1.5 bg-white pr-8"
                          value={bulk.ma}
                          onChange={(e) => {
                            const v = formatCommission(e.target.value);
                            setBulk(prev => ({ ...prev, ma: v, mc: v, mi: v }));
                          }}
                          placeholder="0"
                          aria-label={isTotalPrice ? "커미션 합계" : "성인 커미션"}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-emerald-500 font-medium">
                          {commissionType === '정률' ? '%' : (priceSymbol || costSymbol || '￦')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                      <div>
                        <span className="text-[10px] text-emerald-600 font-medium block mb-0.5 text-left">성인</span>
                        <div className="relative">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1 bg-white pr-6"
                            value={bulk.ma}
                            onChange={(e) => setBulk(prev => ({ ...prev, ma: formatCommission(e.target.value) }))}
                            placeholder="0"
                            aria-label="성인 커미션"
                          />
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500">
                            {commissionType === '정률' ? '%' : ''}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-[10px] text-emerald-600 font-medium block mb-0.5 text-left">소아</span>
                        <div className="relative">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1 bg-white pr-6"
                            value={bulk.mc}
                            onChange={(e) => setBulk(prev => ({ ...prev, mc: formatCommission(e.target.value) }))}
                            placeholder="0"
                            aria-label="소아 커미션"
                          />
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500">
                            {commissionType === '정률' ? '%' : ''}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-[10px] text-emerald-600 font-medium block mb-0.5 text-left">유아</span>
                        <div className="relative">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1 bg-white pr-6"
                            value={bulk.mi}
                            onChange={(e) => setBulk(prev => ({ ...prev, mi: formatCommission(e.target.value) }))}
                            placeholder="0"
                            aria-label="유아 커미션"
                          />
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500">
                            {commissionType === '정률' ? '%' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
        
            {/* 가상모객 설정 (판매 커미션과 유사한 패턴) */}
            <div className="bg-gradient-to-r from-sky-50 to-cyan-50 border border-sky-200 rounded-lg p-2" data-tour="ps-virtual-recruit">
              <label className="flex items-center gap-2 text-xs font-semibold text-sky-700">
                <input
                  type="checkbox"
                  className="rounded border-sky-300 text-sky-600 focus:ring-sky-500"
                  checked={!!virtualRecruit.use}
                  onChange={(e) => {
                    setVirtualRecruit(prev => {
                      const next = { ...prev, use: !!e.target.checked };
                      try { onVirtualRecruitChange && onVirtualRecruitChange(next); } catch (_) { }
                      return next;
                    });
                  }}
                />
                <span className="flex items-center gap-1">
                
                  가상모객 설정
                </span>
              </label>
              {virtualRecruit.use && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] text-gray-600 font-medium mb-0.5">인원</div>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-full border border-slate-200 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 transition text-right px-2 py-1 text-xs bg-white"
                      value={virtualRecruit.count}
                      onChange={(e) => setVirtualRecruit(prev => {
                        const next = { ...prev, count: String(e.target.value || '').replace(/[^0-9]/g, '') };
                        try { onVirtualRecruitChange && onVirtualRecruitChange(next); } catch (_) { }
                        return next;
                      })}
                      placeholder="0"
                      aria-label="가상모객 인원"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-600 font-medium mb-0.5">가상 모객수 노출 종료 (출발 n일 전)</div>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-full border border-slate-200 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 transition text-right px-2 py-1 text-xs bg-white"
                      value={virtualRecruit.daysBefore}
                      onChange={(e) => setVirtualRecruit(prev => {
                        const next = { ...prev, daysBefore: String(e.target.value || '').replace(/[^0-9]/g, '') };
                        try { onVirtualRecruitChange && onVirtualRecruitChange(next); } catch (_) { }
                        return next;
                      })}
                      placeholder="0"
                      aria-label="가상모객 출발 몇일전"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 재고 & 마감 */}
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-lg p-2.5 mt-2">
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                
                재고 관리
              </label>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`font-semibold w-24 border border-primary-200 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 transition text-right px-2 py-1 text-xs ${useSharedStock ? 'bg-gray-100 opacity-60' : 'bg-white'}`}
                    value={bulk.stock}
                    onChange={(e) => setBulk(prev => ({ ...prev, stock: formatInput(e.target.value) }))}
                    onFocus={(e) => {
                      const digits = String(bulk.stock ?? '').replace(/[^\d]/g, '');
                      if (digits === '0') {
                        setBulk((prev) => ({ ...prev, stock: '' }));
                        requestAnimationFrame(() => {
                          try {
                            e.target.setSelectionRange(0, 0);
                          } catch (_) { /* read-only/disabled */ }
                        });
                      }
                    }}
                    placeholder="좌석 수"
                    disabled={useSharedStock}
                    aria-label="재고"
                  />
                  <label className="flex items-center gap-2 text-xs text-gray-700 whitespace-nowrap">
                    <span>마감</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={bulk.closed ? 'true' : 'false'}
                      onClick={() => setBulk(prev => ({ ...prev, closed: !prev.closed }))}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${bulk.closed ? 'bg-primary-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${bulk.closed ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-purple-700 ml-2" data-tour="ps-shared-stock">
                    <input
                      type="checkbox"
                      className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                      checked={useSharedStock}
                      onChange={async (e) => {
                        const next = !!e.target.checked;
                        if (!next) {
                          // Uncheck: unlink and clear selection
                          try {
                            const arr = initialRange?.selectedDates;
                            const { userNo, companyNo } = getUserInfo() || {};
                            if (Array.isArray(arr) && arr.length > 0 && productId && companyNo && userNo) {
                              const rn = Number(resourceNo);
                              if (Number.isFinite(rn) && rn > 0) {
                                const events = arr.map(k => ({ 날짜: String(k), 시간: '00:00', 자원번호: rn }));
                                await unlinkSharedSeriesFromSelectedEvents({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), events, calendarUse: true });
                                try { if (typeof onRequestRefresh === 'function') await onRequestRefresh(); } catch (_) { }
                              }
                            }
                          } catch (ex) {
                            try { console.error('공유재고 해제(체크박스) 중 오류', ex); } catch (_) { }
                          }
                          setSelectedSeriesNo(null);
                          setSelectedSeriesName('');
                          setSharedSelectTemp(null);
                          setUseSharedStock(false);
                          setBulk(prev => ({ ...prev, stock: '' }));
                          return;
                        }
                        // Check on: require selection; open modal, keep checkbox off until selection made
                        if (!selectedSeriesNo) {
                          setSharedSelectTemp(null);
                          setSharedModalOpen(true);
                          return;
                        }
                        setUseSharedStock(true);
                      }}
                    />
                    <span>공유재고 사용</span>
                  </label>
                </div>
              </div>
              {selectedSeriesName && (
                <div className="mt-1 text-[11px] text-purple-700 font-semibold">
                  선택된 공유재고: <span className="text-purple-800">{selectedSeriesName}</span>
                </div>
              )}
            </div>

            {/* 원가 */}
        {activeTab === 'cost' && !multiMonthView && (
          <section className="bg-white p-3 space-y-2.5">
            <div className="flex justify-between items-center">
              <h4 className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                원가 정보
              </h4>
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-gray-600">통화</label>
                <input
                  type="text"
                  className="border border-gray-300 rounded-lg shadow-sm focus:ring-amber-500 focus:border-amber-500 transition w-14 text-center text-xs py-1"
                  placeholder="기호"
                  value={costSymbol}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const symbol = getCurrencySymbol(raw);
                    setCostSymbol(symbol);
                    setDetailCosts(prev => (Array.isArray(prev) ? prev.map(it => ({ ...it, 통화기호: symbol })) : []));
                  }}
                  aria-label="원가 통화"
                />
              </div>
            </div>

            {/* 세부 원가 */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h5 className="text-xs font-semibold text-gray-600">세부 원가 내역</h5>
                <button type="button" className="px-3 py-1.5 text-xs font-semibold rounded-lg text-blue-700 hover:bg-blue-100 transition-colors flex items-center gap-2" onClick={addDetailRow}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                  항목 추가
                </button>
              </div>

              <div className="space-y-2.5">
                {detailCosts && detailCosts.length > 0 ? (
                  detailCosts.map((it, idx) => (
                    <div key={idx} className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {/* 원가 항목 선택/입력 */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">원가 항목</label>
                          <div className="flex items-center gap-1.5">
                            <select
                              className="text-[12px] px-1 py-1 flex-1 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition min-h-[30px]"
                              value={it.원가항목코드 || ''}
                              onChange={(e) => handleDetailChange(idx, '원가항목코드', e.target.value)}
                              disabled={taxCostLoading}
                            >
                              <option value="">항목 선택</option>
                              {(taxCostItems || []).map(opt => (
                                <option key={String(opt.코드)} value={String(opt.코드)}>
                                  {String(opt.코드명)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {/* 금액 입력 */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">금액</label>
                          {showAdultOnly ? (
                            <div className="grid grid-cols-1 gap-3">
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-1">{isTotalPrice ? '합계' : '성인'}</label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  className="text-[12px] w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition text-right px-2 py-1"
                                  value={formatInput(it.성인 || '')}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    // mirror to all age groups so totals logic stays consistent
                                    handleDetailChange(idx, '성인', v);
                                    handleDetailChange(idx, '어린이', v);
                                    handleDetailChange(idx, '영유아', v);
                                  }}
                                  aria-label={isTotalPrice ? "합계 금액" : "성인 금액"}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-1">성인</label>
                                <input type="text" inputMode="numeric" className="font-semibold text-[13px] w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition text-right px-2 py-1" value={formatInput(it.성인 || '0')} onChange={(e) => handleDetailChange(idx, '성인', e.target.value)} aria-label="성인 금액" />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-1">소아</label>
                                <input type="text" inputMode="numeric" className="font-semibold text-[13px] w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition text-right px-2 py-1" value={formatInput(it.어린이 || '0')} onChange={(e) => handleDetailChange(idx, '어린이', e.target.value)} aria-label="소아 금액" />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-1">유아</label>
                                <input type="text" inputMode="numeric" className="font-semibold text-[13px] w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition text-right px-2 py-1" value={formatInput(it.영유아 || '0')} onChange={(e) => handleDetailChange(idx, '영유아', e.target.value)} aria-label="유아 금액" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end mt-1.5">
                        <button type="button" className="px-2.5 py-1 text-xs font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 transition-colors" onClick={() => removeDetailRow(idx)}>삭제</button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-center text-gray-500 py-6">세부 원가 항목이 없습니다.</div>
                )}
              </div>
            </div>

            {/* 총 원가 */}
            <div className="p-2.5">
              <label className="block text-xs font-semibold text-amber-800 mb-1.5">💰 원가 합계{costSymbol ? ` (${costSymbol})` : ''}</label>
              {showAdultOnly ? (
                <input type="text" className="w-full bg-white border border-amber-300 rounded-lg shadow-sm text-right px-2 py-1.5 text-sm font-semibold text-amber-600" value={bulk.ca} readOnly disabled aria-label={isTotalPrice ? "합계 총 원가" : "성인 총 원가"} />
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  <div className="text-center">
                    <input type="text" className="w-full rounded-lg shadow-sm text-right px-1.5 py-1 text-xs font-semibold text-amber-600" value={bulk.ca} readOnly disabled aria-label="성인 총 원가" />
                    <span className="text-xs text-amber-600 font-medium mt-0.5 block">성인</span>
                  </div>
                  <div className="text-center">
                    <input type="text" className="w-full rounded-lg shadow-sm text-right px-1.5 py-1 text-xs font-semibold text-amber-600" value={bulk.cc} readOnly disabled aria-label="소아 총 원가" />
                    <span className="text-xs text-amber-600 font-medium mt-0.5 block">소아</span>
                  </div>
                  <div className="text-center">
                    <input type="text" className="w-full rounded-lg shadow-sm text-right px-1.5 py-1 text-xs font-semibold text-amber-600" value={bulk.ci} readOnly disabled aria-label="유아 총 원가" />
                    <span className="text-xs text-amber-600 font-medium mt-0.5 block">유아</span>
                  </div>
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-1.5">위 세부 원가 내역의 합계로 자동 계산됩니다.</p>
            </div>

          </section>
        )}

        {/* 기획전 매핑 — 선택 가능한 기획전이 있을 때만 표시 (현재 매핑 유무와 무관) */}
        {typeof onOpenPromoMapping === 'function' && hasSelectablePromos && (
          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200 rounded-lg p-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-purple-800">기획전</span>
              <button
                type="button"
                className="px-2.5 py-1 text-xs font-medium rounded-md border border-purple-400 bg-white text-purple-800 hover:bg-purple-100 transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                onClick={() => onOpenPromoMapping()}
                disabled={promoStats.total === 0}
                title={promoStats.total === 0 ? '먼저 달력에서 날짜를 선택하세요.' : `선택한 ${promoStats.total}개 날짜를 기획전에 매핑`}
              >
                기획전 변경
              </button>
            </div>

            {promoStats.names.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {promoStats.names.map((p) => (
                  <span
                    key={p.name}
                    className="inline-flex items-center rounded-full border border-purple-300 bg-white px-2 py-0.5 text-[11px] font-medium text-purple-800"
                    title={`선택한 ${promoStats.total}개 날짜 중 ${p.days}일이 이 기획전에 매핑되어 있습니다`}
                  >
                    {p.name}
                  </span>
                ))}
              </div>
            )}

            {promoStats.noPrice > 0 && (
              <p className="mt-1.5 text-[11px] text-gray-500">
                가격 미등록 {promoStats.noPrice}건은 매핑 대상에서 제외됩니다.
              </p>
            )}
          </div>
        )}

      </main>

      <footer className="px-4 py-2 flex items-center justify-between gap-2 sticky bottom-0 z-10">
        <div>

        </div>
        <div className="flex items-center gap-2">
          {typeof onDelete === 'function' && (
            <button
              type="button"
              className="px-2 py-1 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors shadow-sm"
              onClick={() => onDelete && onDelete(getBulkSelectionContext())}
            >삭제</button>
          )}
          <button className="px-3 py-1 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-blue-700 transition-colors shadow-sm" onClick={handleApply}>저장</button>
        </div>
      </footer>
    </div>
  );

  const handleCloseSharedModal = () => {
    setSharedModalOpen(false);
    setSharedSelectTemp(null);
    if (!selectedSeriesNo) {
      setUseSharedStock(false);
      setSelectedSeriesName('');
    }
  };

  const querySeatStatusForSeries = async (seriesNo, seriesName, color) => {
    try {
      const pad = (n) => String(n).padStart(2, '0');
      // 조회일: 오늘, 종료일: 조회일로부터 1년 뒤
      const today = new Date();
      const oneYearLater = new Date(today);
      oneYearLater.setFullYear(today.getFullYear() + 1);
      const sDate = today;
      const eDate = oneYearLater;
      const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const startDate = toYmd(sDate);
      const endDate = toYmd(eDate);
      setSeatStatusLoading(true);
      setSeatStatus([]);
      setSeatStatusQueried(true);
      setSeatStatusMeta({ seriesNo: seriesNo ?? null, seriesName: seriesName ?? '', color: color ?? '' });
      const list = await getSeriesManagement({
        startDate,
        endDate,
        searchCriteria: '출발일',
        seriesNo: Number(seriesNo),
      });
      setSeatStatus(Array.isArray(list) ? list : []);
    } catch (ex) {
      try { console.error('좌석현황 조회 실패', ex); } catch (_) { }
      try { alert('좌석현황 조회 중 오류가 발생했습니다.'); } catch (_) { }
    } finally {
      setSeatStatusLoading(false);
    }
  };

  const sharedCreateModal = (!sharedCreateOpen ? null : (
    <SharedInventoryCreateModal
      open={sharedCreateOpen}
      onClose={() => setSharedCreateOpen(false)}
      initialSeriesType="347001"
      onCreated={(created) => {
        setSharedCreateOpen(false);
        (async () => {
          try {
            if (!productId) return;
            setSharedLoading(true);
            const rows = await getSharedInventoryList({ productNo: Number(productId) });
            setSharedList(Array.isArray(rows) ? rows : []);
            if (created && created.seriesNo) {
              const seriesNoStr = String(created.seriesNo);
              setSharedSelectTemp(seriesNoStr);
              const arr = Array.isArray(rows) ? rows : [];
              const target = arr.find(r => String(r?.시리즈번호) === seriesNoStr);
              await querySeatStatusForSeries(seriesNoStr, target?.시리즈명 || '', target?.공유색상 || '');
            }
          } catch (e) {
            try { console.warn('공유재고 목록 새로고침 실패', e); } catch (_) { }
          } finally {
            setSharedLoading(false);
          }
        })();
      }}
    />
  ));

  // Shared Inventory selection modal
  const sharedModal = (!sharedModalOpen ? null : createPortal(
    <div
      className={`fixed inset-0 z-[100] ${animatingOut ? 'opacity-0' : 'opacity-100'} transition-opacity`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseSharedModal(); }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">공유재고 선택</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setSharedCreateOpen(true)}
              >
                공유재고 추가
              </button>
              <button className="text-xs px-2 py-1 hover:bg-gray-50" onClick={handleCloseSharedModal}>✕</button>
            </div>
          </div>
          <div className="p-3 space-y-3">
            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">시리즈명</label>
                <input type="text" className="w-full border rounded px-2 py-1 text-sm" value={sharedFilters.name} onChange={(e) => setSharedFilters(prev => ({ ...prev, name: e.target.value }))} placeholder="예: 방콕" />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">공급사</label>
                <input type="text" className="w-full border rounded px-2 py-1 text-sm" value={sharedFilters.supplier} onChange={(e) => setSharedFilters(prev => ({ ...prev, supplier: e.target.value }))} placeholder="예: 교원투어" />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">종류</label>
                <input type="text" className="w-full border rounded px-2 py-1 text-sm" value={sharedFilters.kind} onChange={(e) => setSharedFilters(prev => ({ ...prev, kind: e.target.value }))} placeholder="예: 호텔" />
              </div>
            </div>
            {/* List + Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border rounded-md max-h-80 overflow-y-auto">
                {sharedLoading ? (
                  <div className="p-4 text-sm text-gray-500">로딩 중...</div>
                ) : (!filteredSharedList || filteredSharedList.length === 0) ? (
                  <div className="p-4 text-sm text-gray-500">검색 결과가 없습니다.</div>
                ) : (
                  <ul className="divide-y">
                    {filteredSharedList.map((row, idx) => (
                      <li key={idx} className="p-2 text-[12px] flex items-center gap-2">
                        <input
                          type="radio"
                          name="shared-series-modal"
                          className="mt-0.5"
                          checked={String(sharedSelectTemp ?? selectedSeriesNo ?? '') === String(row?.시리즈번호)}
                          onChange={async () => {
                            setSharedSelectTemp(row?.시리즈번호);
                            await querySeatStatusForSeries(row?.시리즈번호, row?.시리즈명, row?.공유색상);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 truncate">{row?.시리즈명}</span>
                           
                          </div>
                          <div className="text-gray-500 truncate">#{row?.시리즈번호} · {row?.종류} · {row?.공급사}</div>
                        </div>

                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border rounded-md max-h-80 overflow-y-auto p-2">
                {seatStatusLoading ? (
                  <div className="p-2 text-sm text-gray-500">좌석현황 불러오는 중...</div>
                ) : (seatStatus && seatStatus.length > 0) ? (
                  <div className="space-y-2 text-[12px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{seatStatusMeta.seriesName}</span>
                     
                      <span className="text-gray-500">#{seatStatusMeta.seriesNo}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-1">
                      {seatStatus.map((d, i) => {
                        const departDate = String(d?.['출발일'] ?? '');
                        const weekday = String(d?.['요일'] ?? '');
                        const block = String(d?.['블럭수'] ?? '');
                        const sales = String(d?.['판매인원'] ?? '');
                        const fare = String(d?.['항공료'] ?? '');
                        return (
                          <div key={i} className="px-2 py-1 grid items-center" style={{ gridTemplateColumns: '110px repeat(4, 1fr)' }}>
                            <div className="truncate text-[12px] font-semibold text-gray-800 pr-2">{departDate}</div>
                            <div className="text-right text-[11px] text-gray-700 pr-2">요일: <b>{weekday || '-'}</b></div>
                            <div className="text-right text-[11px] text-gray-700 pr-2">블럭수: <b>{block || '-'}</b></div>
                            <div className="text-right text-[11px] text-gray-700 pr-2">판매인원: <b>{sales || '-'}</b></div>
                            <div className="text-right text-[11px] text-gray-700">항공료: <b>{fare || '-'}</b></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : seatStatusQueried ? (
                  <div className="p-2 text-sm text-gray-500">좌석이 없습니다.</div>
                ) : (
                  <div className="p-2 text-sm text-gray-500 text-center">우측에서 공유재고를 선택한 뒤 좌석현황을 확인해주세요.</div>
                )}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
            <button type="button" className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50" onClick={handleCloseSharedModal}>취소</button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={!sharedSelectTemp && !selectedSeriesNo}
              onClick={async () => {
                const chosen = sharedSelectTemp ?? selectedSeriesNo ?? null;
                setSelectedSeriesNo(chosen);
                if (chosen) {
                  const found = (Array.isArray(sharedList) ? sharedList : []).find(row => String(row?.시리즈번호) === String(chosen));
                  setSelectedSeriesName(found?.시리즈명 || '');
                  setUseSharedStock(true);
                } else {
                  setUseSharedStock(false);
                }
                setSharedModalOpen(false);
                // Immediately link the selected shared inventory to currently selected dates (if any)
                try {
                  if (chosen) {
                    const arr = initialRange?.selectedDates;
                    const { userNo, companyNo } = getUserInfo() || {};
                    const rn = Number(resourceNo);
                    if (Array.isArray(arr) && arr.length > 0 && productId && companyNo && userNo && Number.isFinite(rn) && rn > 0) {
                      const events = arr.map(k => ({ 날짜: String(k), 시간: '00:00', 자원번호: rn }));
                      await linkSharedSeriesToSelectedEvents({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), events, sharedSeriesNo: Number(chosen), calendarUse: true });
                      try { if (typeof onRequestRefresh === 'function') await onRequestRefresh(); } catch (_) { }
                    }
                  }
                } catch (ex) {
                  try { console.error('공유재고 즉시 연동 중 오류', ex); } catch (_) { }
                }
              }}
            >선택</button>
          </div>
        </div>
      </div>
    </div>, document.body));

  // Inline side panel mode (no backdrop/portal)
  if (inline) {
    return (
      <div>
        {panelContent}
        {sharedModal}
        {sharedCreateModal}
        {ageGroupModal}
        {totalPriceModal}
      </div>
    );
  }

  // Default: portal with backdrop (overlay)
  return (
    <>
      {sharedModal}
      {sharedCreateModal}
      {ageGroupModal}
      {totalPriceModal}
      {createPortal(
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-200 ${animatingOut ? 'ease-in opacity-0' : 'ease-out'} ${mountedVisible && !animatingOut ? 'opacity-100' : 'opacity-0'}`}
          onMouseDown={(e) => {
            backdropMouseDownRef.current = (e.target === e.currentTarget);
          }}
          onMouseUp={(e) => {
            if (backdropMouseDownRef.current && e.target === e.currentTarget) {
              requestClose();
            }
            backdropMouseDownRef.current = false;
          }}
        >
          {/* Panel */}
          <div
            className={`fixed top-0 right-0 h-full w-1/3 bg-gray-50 shadow-2xl z-50 transform transition-transform duration-300 ${animatingOut ? 'ease-in translate-x-full' : 'ease-out'} ${mountedVisible && !animatingOut ? 'translate-x-0' : 'translate-x-full'}`}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            {panelContent}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const PriceStockSettings = ({ ReservationModalComponent = TourReservationModal, headerTitle = '' }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const pendingPriceNameSyncRef = useRef(null);
  const syncingPriceNameRef = useRef(false);

  // Hierarchical: 항공패턴 -> 요금제 -> 날짜데이터
  // dataByPattern: { [patternName]: { [planName]: { [YYYY-MM-DD]: { price, cost, stock } } } }
  const [dataByPattern, setDataByPattern] = useState({});
  // Dropdown options: prefer wide-range fetched patterns/plans when available
  const [allPatterns, setAllPatterns] = useState([]); // array of pattern names
  const [allPlansByPattern, setAllPlansByPattern] = useState({}); // { [pattern]: string[] }
  const patterns = useMemo(() => {
    const wide = Array.isArray(allPatterns) ? allPatterns : [];
    if (wide.length) return wide;
    return Object.keys(dataByPattern || {});
  }, [allPatterns, dataByPattern]);
  const [selectedPattern, setSelectedPattern] = useState('');
  const plans = useMemo(() => {
    if (!selectedPattern) return [];
    const widePlans = allPlansByPattern?.[selectedPattern];
    if (Array.isArray(widePlans) && widePlans.length) return widePlans;
    return Object.keys(dataByPattern[selectedPattern] || {});
  }, [allPlansByPattern, dataByPattern, selectedPattern]);
  const [selectedPlan, setSelectedPlan] = useState('');
  // Loading state for wide list fetch (patterns/plans)
  const [listsLoading, setListsLoading] = useState(false);
  const renderData = useMemo(() => (dataByPattern[selectedPattern]?.[selectedPlan]) || {}, [dataByPattern, selectedPattern, selectedPlan]);
  /** 현재 캘린더에 로딩된 날짜들에 걸려 있는 기획전명 목록 (중복 제거, 매핑 날짜수 포함) */
  const promoSummary = useMemo(() => {
    const counts = new Map();
    let mappedDays = 0;
    Object.values(renderData || {}).forEach((day) => {
      const names = splitPromoNames(day?.promo);
      if (names.length === 0) return;
      mappedDays += 1;
      names.forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
    });
    return {
      mappedDays,
      names: Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([name, days]) => ({ name, days })),
    };
  }, [renderData]);
  const [hoverTooltipMM, setHoverTooltipMM] = useState(null); // { dateKey: string, x: number, y: number }
  const hoverTooltipTimerMMRef = useRef(null);
  const hoverTooltipPendingMMRef = useRef(null);
  // plan meta: { [pattern]: { [plan]: { costMethod, priceType } } }
  const [planMetaByPattern, setPlanMetaByPattern] = useState({});
  const selectedPlanMeta = useMemo(() => (planMetaByPattern?.[selectedPattern]?.[selectedPlan]) || null, [planMetaByPattern, selectedPattern, selectedPlan]);
  const hasPlan = useMemo(() => {
    if (!selectedPattern) return false;
    const planOptions = allPlansByPattern?.[selectedPattern];
    const opts = (Array.isArray(planOptions) && planOptions.length)
      ? planOptions
      : Object.keys(dataByPattern[selectedPattern] || {});
    return opts.length > 0;
  }, [selectedPattern, allPlansByPattern, dataByPattern]);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  // Track visible range for holiday calculation
  const [visibleRange, setVisibleRange] = useState({ start: null, end: null });
  // Live preview from right panel 적용기간 for calendar highlighting
  const [applyPreview, setApplyPreview] = useState(null); // { start: Date, end: Date, weekdays: Set<number> } | null
  // Readability controls
  const [showCompact, setShowCompact] = useState(true); // hide cost line by default
  // Cache last known planNo/resourceNo across months to support saving on months with no server rows
  const [planNoCache, setPlanNoCache] = useState({}); // { [pattern]: { [plan]: number } }
  const [resourceNoCache, setResourceNoCache] = useState({}); // { [pattern]: { [plan]: number } }
  // Stock data by date: { [YYYY-MM-DD]: { 보유석, 판매석, 잔여석 } }
  const [stockDataByDate, setStockDataByDate] = useState({});
  // Locally track dates linked to shared inventory (fallback visual until API includes flag)
  const [sharedLinkedDates, setSharedLinkedDates] = useState(new Set()); // Set<string YYYY-MM-DD>
  // Shared dates from API (상품공유번호 존재)
  const [sharedFromApiDates, setSharedFromApiDates] = useState(new Set());
  // Map of date -> 상품공유번호 for prefill
  const [sharedSeriesNoByDate, setSharedSeriesNoByDate] = useState({});
  // 일정타입 선택 (가격 저장 시 반영)
  const [priceItineraryTypes, setPriceItineraryTypes] = useState([]); // [{일정타입번호, 일정타입명}]
  const [selectedItineraryTypeNo, setSelectedItineraryTypeNo] = useState(null);
  const itineraryLegendItems = useMemo(() => {
    const ordered = [];
    const seen = new Set();
    const pushItem = (no, label) => {
      const cleanLabel = normalizeItineraryLegendLabel(label);
      if (!cleanLabel) return;
      const key = `${no == null ? 'null' : Number(no)}::${cleanLabel}`;
      if (seen.has(key)) return;
      seen.add(key);
      ordered.push({ no: no == null ? null : Number(no), label: cleanLabel });
    };
    (Array.isArray(priceItineraryTypes) ? priceItineraryTypes : []).forEach((item) => {
      pushItem(item?.일정타입번호, item?.일정타입명);
    });
    Object.values(renderData || {}).forEach((day) => {
      const list = Array.isArray(day?.itineraryTypes) ? day.itineraryTypes : [];
      list.forEach((item) => pushItem(item?.no, item?.label));
      if (list.length === 0) pushItem(day?.itineraryTypeNo, day?.itineraryLabel);
    });
    return ordered;
  }, [priceItineraryTypes, renderData]);
  const itineraryLegendMap = useMemo(() => {
    const map = new Map();
    itineraryLegendItems.forEach((item, idx) => {
      map.set(`${item.no == null ? 'null' : Number(item.no)}::${item.label}`, { ...item, ...itineraryTintPalette[idx % itineraryTintPalette.length] });
    });
    return map;
  }, [itineraryLegendItems]);
  const showItineraryLegend = itineraryLegendItems.length > 1;

  // 상품 변경 시 일정타입 리스트 로드
  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getItineraryTypeSimpleList({ productNo: Number(productId) });
        if (cancelled) return;
        const list = Array.isArray(resp?.table) ? resp.table : [];
        setPriceItineraryTypes(list);
        // 기본 선택: 첫번째 타입
        if (list.length > 0 && !selectedItineraryTypeNo) {
          setSelectedItineraryTypeNo(Number(list[0].일정타입번호));
        }
      } catch (e) {
        console.warn('일정타입 리스트 로드 실패:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  useEffect(() => {
    return () => {
      try {
        if (hoverTooltipTimerMMRef.current) clearTimeout(hoverTooltipTimerMMRef.current);
      } catch (_) { }
    };
  }, []);

  useEffect(() => {
    try {
      const st = location?.state;
      const newName = (st && st.newProductName != null) ? String(st.newProductName).trim() : '';
      if (st && st.autoSyncPriceName && newName) {
        pendingPriceNameSyncRef.current = { priceName: newName };
      }
    } catch (_) { }
  }, [location]);

  // FullCalendar control and custom header title
  const calendarRef = useRef(null);
  const lastRangeRef = useRef({ start: null, end: null });
  const goPrev = () => {
    const api = calendarRef.current?.getApi?.();
    if (api) api.prev();
  };
  const goNext = () => {
    const api = calendarRef.current?.getApi?.();
    if (api) api.next();
  };
  const ymTitle = useMemo(() => `${cursor.getFullYear()}년 ${cursor.getMonth() + 1}월`, [cursor]);

  // Multi-month view (Option A) states
  const [multiMonthView, setMultiMonthView] = useState(true);
  const [visibleStartMM, setVisibleStartMM] = useState(() => {
    const now = new Date();
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    s.setHours(0, 0, 0, 0);
    return s;
  });
  const [visibleEndMM, setVisibleEndMM] = useState(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 12, 0); // end of month +11 months
    end.setHours(0, 0, 0, 0);
    return end;
  });
  const monthsMM = useMemo(() => iterateMonths(visibleStartMM, visibleEndMM), [visibleStartMM, visibleEndMM]);
  const containerRefMM = useRef(null);
  const rightPanelRef = useRef(null);
  const [rightPanelStickyTop, setRightPanelStickyTop] = useState(null);
  const computeRightPanelStickyTop = useCallback(() => {
    try {
      const el = rightPanelRef.current;
      if (!el || typeof window === 'undefined' || typeof document === 'undefined') return;

      const getScrollParent = (node) => {
        let parent = node?.parentElement;
        while (parent) {
          const style = window.getComputedStyle(parent);
          const overflowY = String(style?.overflowY || '').toLowerCase();
          if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return parent;
          parent = parent.parentElement;
        }
        return window;
      };

      const scrollParent = getScrollParent(el);
      const elRect = el.getBoundingClientRect();
      const containerTop = scrollParent === window ? 0 : scrollParent.getBoundingClientRect().top;
      const nextTop = Math.max(0, Math.round(elRect.top - containerTop));
      setRightPanelStickyTop(nextTop);
    } catch (_) { }
  }, []);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      computeRightPanelStickyTop();
    });
    const onResize = () => computeRightPanelStickyTop();
    window.addEventListener('resize', onResize);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener('resize', onResize);
    };
  }, [computeRightPanelStickyTop, multiMonthView]);
  // Drag selection across months
  const [dragStartMM, setDragStartMM] = useState(null); // Date
  const [dragCurrentMM, setDragCurrentMM] = useState(null); // Date
  const [isDraggingMM, setIsDraggingMM] = useState(false);
  const [dragMovedMM, setDragMovedMM] = useState(false); // true if cursor moved to different day
  const lastDragEndRef = useRef(0); // timestamp to suppress background click right after drag
  const suppressNextDayClickRef = useRef(0); // timestamp to suppress day click right after drag
  // Multi-date selection (Ctrl+Click)
  const [selectedDates, setSelectedDates] = useState(new Set()); // Set<string> (YYYY-MM-DD)
  const [calendarRerenderTick, setCalendarRerenderTick] = useState(0);
  // 기획전 매핑된 날짜만 강조 (켜면 미매핑 날짜를 흐리게 처리)
  const [promoHighlightOnly, setPromoHighlightOnly] = useState(false);
  const [promoMappingOpen, setPromoMappingOpen] = useState(false);
  // 매핑 UI 노출용 — 진행중 기획전이 1개라도 있으면 사이드 패널에 진입점 표시
  const [hasSelectablePromos, setHasSelectablePromos] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const opts = await getPromotionOptions();
        if (!alive) return;
        setHasSelectablePromos(Array.isArray(opts) && opts.length > 0);
      } catch (_) {
        if (alive) setHasSelectablePromos(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  /** 기획전 매핑 모달에 넘길 선택 날짜 정보 — 캘린더 구현과 무관한 형태로 정규화.
   *  renderData/selectedDates 선언 이후에 두어야 한다(TDZ). */
  const promoSelection = useMemo(() => Array.from(selectedDates).sort().map((date) => {
    const day = renderData?.[date] || {};
    return { date, priceNo: day?.datePriceNo, promo: day?.promo || '' };
  }), [selectedDates, renderData]);
  const getMonthDates = useCallback((year, month) => {
    const start = new Date(year, month, 1); start.setHours(0, 0, 0, 0);
    const end = new Date(year, month + 1, 0); end.setHours(0, 0, 0, 0);
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(formatYMD(d));
    }
    return days;
  }, []);
  const getMonthSelectionState = useCallback((year, month) => {
    const days = getMonthDates(year, month);
    const total = days.length;
    if (total === 0) return { checked: false, indeterminate: false };
    let selectedCount = 0;
    for (const key of days) {
      if (selectedDates.has(key)) selectedCount += 1;
    }
    return {
      checked: selectedCount === total,
      indeterminate: selectedCount > 0 && selectedCount < total,
    };
  }, [getMonthDates, selectedDates]);
  const handleToggleMonth = useCallback((year, month, checked) => {
    const days = getMonthDates(year, month);
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (checked) {
        days.forEach((d) => next.add(d));
      } else {
        days.forEach((d) => next.delete(d));
      }
      const sorted = Array.from(next).sort();
      if (next.size === 0) {
        setApplyPreview(null);
        setBulkInitialRange(null);
        setBulkInitialValues(null);
        setBulkOpen(false);
      } else {
        setBulkInitialRange({ selectedDates: sorted });
        setBulkInitialValues(null);
        setBulkOpen(true);
      }
      return next;
    });
  }, [getMonthDates]);
  const normalizedRangeMM = useMemo(() => {
    if (!dragStartMM || !dragCurrentMM) return null;
    const a = stripTime(dragStartMM), b = stripTime(dragCurrentMM);
    const start = a <= b ? a : b;
    const end = a <= b ? b : a;
    return { start, end };
  }, [dragStartMM, dragCurrentMM]);

  // (Removed global mouseup listener; handled in container onMouseUp below)

  // Product name for header
  const [productName, setProductName] = useState('');
  // Product default currency symbol (from TRP_S_투어_기본정보)
  const [productCurrency, setProductCurrency] = useState('');
  /** getProductDetailExtra.detail.대기예약허용여부 — 재고 만석 시 (대기)/(마감) 문구 */
  const [waitlistReservationAllowed, setWaitlistReservationAllowed] = useState(false);

  // Build Korean public holiday map for the visible range (YYYY-MM-DD -> holiday name)
  const holidayMap = useMemo(() => {
    const map = new Map();
    try {
      // Support both FullCalendar single-month and multi-month visible ranges
      const s = multiMonthView ? visibleStartMM : visibleRange.start;
      const e = multiMonthView ? new Date(visibleEndMM.getFullYear(), visibleEndMM.getMonth(), visibleEndMM.getDate() + 1) : visibleRange.end; // end exclusive
      if (!s || !e) return map;
      const hd = new Holidays('KR');
      const start = new Date(s);
      const end = new Date(e); // exclusive
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const res = hd.isHoliday(new Date(d));
        if (res) {
          const names = Array.isArray(res) ? res.map(r => r && r.name).filter(Boolean).join(', ') : (res && res.name);
          if (names) {
            map.set(formatYMD(d), names);
          }
        }
      }
    } catch (_) { }
    return map;
  }, [visibleRange, multiMonthView, visibleStartMM, visibleEndMM]);

  // Bulk modal open
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkInitialRange, setBulkInitialRange] = useState(null);
  const [bulkInitialValues, setBulkInitialValues] = useState(null);

  // Reservation modal state
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPriceNo, setSelectedPriceNo] = useState('');
  // Track pending changes from bulk applies (to save via SP)
  const [pendingChanges, setPendingChanges] = useState([]);
  // Base price info loaded from RCT_S_%가격예약등록정보_01 (single object parsed from stringified array)
  const [basePriceInfo, setBasePriceInfo] = useState(null);
  const [baseLoading, setBaseLoading] = useState(false);
  // Shared ProductSettingsModal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Bulk price import modal state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportInitialText, setBulkImportInitialText] = useState('');
  const [bulkImportInitialInputTab, setBulkImportInitialInputTab] = useState('json'); // 'json' | 'text'
  // 연령구분 사용여부 (기본값 true, 상세설정에서 가져옴)
  const [useAgeGroup, setUseAgeGroup] = useState(true);
  const [savingAgeGroup, setSavingAgeGroup] = useState(false);
  const [virtualRecruit, setVirtualRecruit] = useState({ use: false, count: '0', daysBefore: '0' });
  const [ageGroupSettingsOpen, setAgeGroupSettingsOpen] = useState(false);
  const [ageGroupSettingsTemp, setAgeGroupSettingsTemp] = useState(true);
  const [ageRangeTemp, setAgeRangeTemp] = useState({ infantEnd: 3, childEnd: 12, childStart: 3, adultStart: 12 });
  const [ageRangeInitial, setAgeRangeInitial] = useState(null);
  const [hasDOM, setHasDOM] = useState(false);

  // Auto-open bulk import when coming from smart itinerary registration
  useEffect(() => {
    try {
      const state = location?.state || {};
      const numMap = state?.smartPriceStockNumMap;
      if (!numMap || typeof numMap !== 'object') return;
      const keys = Object.keys(numMap);
      if (!keys.length) return;

      // Prefill BulkPriceImportModal with the numbered key/value JSON (same format as df_cellMerge subData numeric map)
      const initialText = JSON.stringify(numMap);
      setBulkImportInitialText(initialText);
      // 스마트 상품 등록 경로에서는 텍스트 탭을 기본으로 사용 (붙여넣기 탭)
      setBulkImportInitialInputTab('text');
      setBulkImportOpen(true);

      // Clear state to prevent reopening on re-render/back
      navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: undefined });
    } catch (_) {
      // ignore
    }
  }, [location?.state, location?.pathname, location?.search, location?.hash, navigate]);

  const [patternManageOpen, setPatternManageOpen] = useState(false);
  const [planManageOpen, setPlanManageOpen] = useState(false);
  // 통합 모달 (재고구분 + 요금제 관리)
  const [unifiedManageOpen, setUnifiedManageOpen] = useState(false);
  const [expandedPatterns, setExpandedPatterns] = useState(new Set()); // 펼쳐진 재고구분
  const [tempPatternName, setTempPatternName] = useState('');
  const [tempPlanName, setTempPlanName] = useState('');
  const [renamingPattern, setRenamingPattern] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [planManageSelectedPattern, setPlanManageSelectedPattern] = useState('');
  const [planManageSelectedPlan, setPlanManageSelectedPlan] = useState('');
  const [planManageMode, setPlanManageMode] = useState('edit');
  const [planManageTempName, setPlanManageTempName] = useState('');
  const [renamingPlan, setRenamingPlan] = useState({ pattern: '', plan: '' });
  const [renamePlanValue, setRenamePlanValue] = useState('');
  const [planManageSaving, setPlanManageSaving] = useState(false);
  const [planManageOriginal, setPlanManageOriginal] = useState(null); // { name, meta }
  const [planManageCreatePriceMethod, setPlanManageCreatePriceMethod] = useState('');
  // DnD state for plan reordering in modal
  const [dragPlanPattern, setDragPlanPattern] = useState('');
  const [dragPlanIndex, setDragPlanIndex] = useState(null);

  // Commit reordered list to server using updateTourRatePlanOrder
  const commitPlanOrder = async (pattern, newOrderNames) => {
    const pat = String(pattern || '').trim();
    if (!pat) return;
    const orderList = (newOrderNames || [])
      .map((name, i) => {
        const priceNo = (planMetaByPattern?.[pat]?.[name]?.priceNo) ?? (planNoCache?.[pat]?.[name]);
        if (!priceNo) return null;
        return { 가격옵션번호: Number(priceNo), 순번: i + 1 };
      })
      .filter(Boolean);

    // Find resourceNo from first plan that has it
    let resourceNo = null;
    for (const name of newOrderNames) {
      const rno = (planMetaByPattern?.[pat]?.[name]?.resourceNo) ?? (resourceNoCache?.[pat]?.[name]);
      if (Number.isFinite(Number(rno)) && Number(rno) > 0) { resourceNo = Number(rno); break; }
    }
    const { companyNo, userNo } = getUserInfo() || {};
    if (!companyNo || !userNo || !productId || !resourceNo || orderList.length === 0) {
      console.warn('[PlanOrder] Missing info', { companyNo, userNo, productId, resourceNo, orderListLen: orderList.length });
      return;
    }
    setPlanManageSaving(true);
    try {
      await updateTourRatePlanOrder({
        companyNo: String(companyNo),
        productNo: Number(productId),
        userNo: String(userNo),
        resourceNo: Number(resourceNo),
        orderList,
      });
      // Refresh local order
      setAllPlansByPattern(prev => ({ ...(prev || {}), [pat]: [...newOrderNames] }));
      try { await refreshCalendarForCurrentMonth(); } catch (_) { }
    } catch (e) {
      console.error('요금제 순서 변경 실패:', e);
      alert('요금제 순서 변경에 실패했습니다.');
    } finally {
      setPlanManageSaving(false);
    }
  };

  const handlePlanDragStart = (pattern, index) => {
    setDragPlanPattern(String(pattern || ''));
    setDragPlanIndex(Number(index));
  };

  const handlePlanDragOver = (e) => {
    // allow drop
    try { e.preventDefault(); } catch (_) { }
  };

  const handlePlanDrop = async (pattern, dropIndex) => {
    const pat = String(pattern || '').trim();
    if (!pat || dragPlanPattern !== pat) { setDragPlanIndex(null); setDragPlanPattern(''); return; }
    const list = (() => {
      const wide = allPlansByPattern?.[pat];
      if (Array.isArray(wide) && wide.length) return [...wide];
      return Object.keys(dataByPattern?.[pat] || {});
    })();
    const from = Number(dragPlanIndex);
    const to = Number(dropIndex);
    setDragPlanIndex(null);
    setDragPlanPattern('');
    if (!Number.isInteger(from) || !Number.isInteger(to) || from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return;
    const reordered = [...list];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    await commitPlanOrder(pat, reordered);
  };

  const handleAddPattern = async () => {
    const name = String(tempPatternName || '').trim();
    if (!name) return;
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo || !productId) { alert('필수 정보(업체/유저/상품)가 없습니다.'); return; }
      await createTourProductResource02({
        resourceName: name,
        productNo: String(productId),
        companyNo: String(companyNo),
        userNo: String(userNo),
        대표항공코드: 'NULL',
        재고기준: true,
      });
      setAllPatterns((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        if (arr.includes(name)) return arr;
        return [...arr, name];
      });
      setAllPlansByPattern((prev) => ({ ...(prev || {}), [name]: [] }));
      setSelectedPattern(name);
      setTempPatternName('');
      alert('항공패턴을 추가했습니다.');
    } catch (e) {
      console.error('항공패턴 추가 실패', e);
      alert('항공패턴 추가에 실패했습니다.');
    }
  };

  const handleDeletePattern = async (name) => {
    if (!name) return;
    try {
      if (!confirm(`'${name}' 항공패턴을 삭제하시겠습니까?\n삭제 후에는 복구가 어렵습니다.`)) return;
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = productId;
      // Resolve resourceNo from any plan under this pattern
      let resourceNo = null;
      try {
        const planList = Array.isArray(allPlansByPattern?.[name]) && allPlansByPattern[name].length
          ? allPlansByPattern[name]
          : Object.keys(dataByPattern?.[name] || {});
        for (const planName of planList) {
          const rno = (
            planMetaByPattern?.[name]?.[planName]?.resourceNo ??
            resourceNoCache?.[name]?.[planName]
          );
          if (Number.isFinite(Number(rno)) && Number(rno) > 0) { resourceNo = Number(rno); break; }
        }
      } catch (_) { }
      // Fallback: resolve resourceNo by querying resource list when no plans exist yet
      if (!resourceNo) {
        resourceNo = await resolveResourceNo(name);
      }
      if (!companyNo || !userNo || !productNo || !resourceNo) {
        alert('필수 정보(업체/유저/상품/자원번호)를 확인할 수 없어 삭제할 수 없습니다.');
        return;
      }
      await deleteTourProductResource({
        companyNo: String(companyNo),
        productNo: String(productNo),
        resourceNo: Number(resourceNo),
        userNo: String(userNo),
      });
      // After successful delete, update local states
      setAllPatterns((prev) => (Array.isArray(prev) ? prev.filter((p) => p !== name) : []));
      setAllPlansByPattern((prev) => {
        const next = { ...(prev || {}) };
        delete next[name];
        return next;
      });
      setPlanMetaByPattern((prev) => {
        const next = { ...(prev || {}) };
        delete next[name];
        return next;
      });
      setPlanNoCache((prev) => {
        const next = { ...(prev || {}) };
        delete next[name];
        return next;
      });
      setResourceNoCache((prev) => {
        const next = { ...(prev || {}) };
        delete next[name];
        return next;
      });
      setDataByPattern((prev) => {
        const next = { ...(prev || {}) };
        delete next[name];
        return next;
      });
      if (selectedPattern === name) {
        setSelectedPattern('');
        setSelectedPlan('');
      }
      alert('항공패턴을 삭제했습니다.');
      try {
        await refreshCalendarForCurrentMonth();
      } catch (_) { }
    } catch (e) {
      console.error('항공패턴 삭제 실패', e);
      alert('항공패턴 삭제에 실패했습니다.');
    }
  };

  const handleAddPlan = () => {
    const name = String(tempPlanName || '').trim();
    if (!name || !selectedPattern) return;
    setAllPlansByPattern((prev) => {
      const next = { ...(prev || {}) };
      const arr = Array.isArray(next[selectedPattern]) ? next[selectedPattern] : [];
      if (!arr.includes(name)) next[selectedPattern] = [...arr, name];
      return next;
    });
    setTempPlanName('');
  };

  const handleDeletePlan = (name) => {
    if (!name || !selectedPattern) return;
    setAllPlansByPattern((prev) => {
      const next = { ...(prev || {}) };
      const arr = Array.isArray(next[selectedPattern]) ? next[selectedPattern] : [];
      next[selectedPattern] = arr.filter((p) => p !== name);
      return next;
    });
    if (selectedPlan === name) setSelectedPlan('');
  };

  const handleStartRenamePattern = (name) => {
    setRenamingPattern(String(name || ''));
    setRenameValue(String(name || ''));
  };

  const handleConfirmRenamePattern = async () => {
    const oldName = String(renamingPattern || '').trim();
    const newName = String(renameValue || '').trim();
    if (!oldName || !newName || oldName === newName) {
      setRenamingPattern('');
      setRenameValue('');
      return;
    }
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = productId;
      let resourceNo = null;
      try {
        const planList = Array.isArray(allPlansByPattern?.[oldName]) && allPlansByPattern[oldName].length
          ? allPlansByPattern[oldName]
          : Object.keys(dataByPattern?.[oldName] || {});
        for (const planName of planList) {
          const rno = (
            planMetaByPattern?.[oldName]?.[planName]?.resourceNo ??
            resourceNoCache?.[oldName]?.[planName]
          );
          if (Number.isFinite(Number(rno)) && Number(rno) > 0) { resourceNo = Number(rno); break; }
        }
      } catch (_) { }
      if (!companyNo || !userNo || !productNo || !resourceNo) {
        alert('필수 정보가 부족하여 항공패턴 이름을 수정할 수 없습니다.');
        setRenamingPattern('');
        setRenameValue('');
        return;
      }
      await updateTourProductResource02({
        resourceName: newName,
        productNo: String(productNo),
        companyNo: String(companyNo),
        userNo: String(userNo),
        resourceNo: Number(resourceNo),
        대표항공코드: 'NULL',
        재고기준: false,
      });
    } catch (e) {
      console.error('항공패턴 이름 수정 실패', e);
      alert('항공패턴 이름 수정에 실패했습니다.');
      setRenamingPattern('');
      setRenameValue('');
      return;
    }
    setAllPatterns((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const idx = arr.indexOf(oldName);
      if (idx === -1) return arr;
      const next = [...arr];
      next[idx] = newName;
      return next;
    });
    setAllPlansByPattern((prev) => {
      const next = { ...(prev || {}) };
      if (Object.prototype.hasOwnProperty.call(next, oldName)) {
        next[newName] = next[oldName];
        delete next[oldName];
      }
      return next;
    });
    setPlanMetaByPattern((prev) => {
      const next = { ...(prev || {}) };
      if (Object.prototype.hasOwnProperty.call(next, oldName)) {
        next[newName] = next[oldName];
        delete next[oldName];
      }
      return next;
    });
    setPlanNoCache((prev) => {
      const next = { ...(prev || {}) };
      if (Object.prototype.hasOwnProperty.call(next, oldName)) {
        next[newName] = next[oldName];
        delete next[oldName];
      }
      return next;
    });
    setResourceNoCache((prev) => {
      const next = { ...(prev || {}) };
      if (Object.prototype.hasOwnProperty.call(next, oldName)) {
        next[newName] = next[oldName];
        delete next[oldName];
      }
      return next;
    });
    setDataByPattern((prev) => {
      const next = { ...(prev || {}) };
      if (Object.prototype.hasOwnProperty.call(next, oldName)) {
        next[newName] = next[oldName];
        delete next[oldName];
      }
      return next;
    });
    if (selectedPattern === oldName) setSelectedPattern(newName);
    setRenamingPattern('');
    setRenameValue('');
  };

  const handleCancelRenamePattern = () => {
    setRenamingPattern('');
    setRenameValue('');
  };

  const getPlanMeta = (pat, plan) => (planMetaByPattern?.[pat]?.[plan]) || {};
  const updatePlanMeta = (pat, plan, updates) => {
    setPlanMetaByPattern((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      next[pat][plan] = { ...(next[pat][plan] || {}), ...(updates || {}) };
      return next;
    });
  };
  const handleAddPlanUnderPattern = (pattern, name) => {
    const pat = String(pattern || '').trim();
    const nm = String(name || '').trim();
    if (!pat || !nm) return;
    setAllPlansByPattern((prev) => {
      const next = { ...(prev || {}) };
      const arr = Array.isArray(next[pat]) ? next[pat] : [];
      if (!arr.includes(nm)) next[pat] = [...arr, nm];
      return next;
    });
    setPlanMetaByPattern((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      if (!next[pat][nm]) next[pat][nm] = { priceMethod: '', reserveControl: false, reserveMin: '', reserveMax: '', nonRefundable: false, autoConfirm: false, isPrivate: false };
      return next;
    });
    setDataByPattern((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      if (!Object.prototype.hasOwnProperty.call(next[pat], nm)) next[pat][nm] = {};
      return next;
    });
    setPlanManageSelectedPattern(pat);
    setPlanManageSelectedPlan(nm);
    setPlanManageTempName('');
  };
  const handleDeletePlanWithin = async (pattern, name) => {
    const pat = String(pattern || '').trim();
    const nm = String(name || '').trim();
    if (!pat || !nm) return;
    const confirmed = window.confirm('요금제를 삭제하시겠습니까?');
    if (!confirmed) return;
    const { companyNo, userNo } = getUserInfo() || {};
    const productNo = productId;
    const priceNo = (
      planMetaByPattern?.[pat]?.[nm]?.priceNo ??
      planNoCache?.[pat]?.[nm]
    );
    if (!companyNo || !userNo || !productNo || !Number.isFinite(Number(priceNo))) {
      alert('필수 정보가 부족하거나 가격옵션번호가 없습니다.');
      return;
    }
    try {
      const res = await deleteTourRatePlan({
        companyNo: String(companyNo),
        productNo: Number(productNo),
        priceOptionNo: Number(priceNo),
        userNo: String(userNo),
      });
      const successEmpty = res && typeof res === 'object' && Object.keys(res).length === 0;
      if (successEmpty) {
        alert('요금제가 삭제되었습니다.');
      }
    } catch (e) {
      console.error('요금제 삭제 실패', e);
      alert('요금제 삭제에 실패했습니다.');
      return;
    }
    setAllPlansByPattern((prev) => {
      const next = { ...(prev || {}) };
      const arr = Array.isArray(next[pat]) ? next[pat] : [];
      next[pat] = arr.filter((p) => p !== nm);
      return next;
    });
    setPlanMetaByPattern((prev) => {
      const next = { ...(prev || {}) };
      if (next?.[pat]) {
        const inner = { ...next[pat] };
        delete inner[nm];
        next[pat] = inner;
      }
      return next;
    });
    setPlanNoCache((prev) => {
      const next = { ...(prev || {}) };
      if (next?.[pat]) {
        const inner = { ...next[pat] };
        delete inner[nm];
        next[pat] = inner;
      }
      return next;
    });
    setResourceNoCache((prev) => {
      const next = { ...(prev || {}) };
      if (next?.[pat]) {
        const inner = { ...next[pat] };
        delete inner[nm];
        next[pat] = inner;
      }
      return next;
    });
    setDataByPattern((prev) => {
      const next = { ...(prev || {}) };
      if (next?.[pat]) {
        const inner = { ...next[pat] };
        delete inner[nm];
        next[pat] = inner;
      }
      return next;
    });
    if (planManageSelectedPattern === pat && planManageSelectedPlan === nm) {
      setPlanManageSelectedPlan('');
    }
    if (selectedPattern === pat && selectedPlan === nm) {
      setSelectedPlan('');
    }
    try {
      await refreshCalendarForCurrentMonth();
    } catch (_) { }
  };
  const handleStartRenamePlan = (pattern, name) => {
    setRenamingPlan({ pattern: String(pattern || ''), plan: String(name || '') });
    setRenamePlanValue(String(name || ''));
  };
  const handleConfirmRenamePlan = () => {
    const pat = String(renamingPlan?.pattern || '').trim();
    const oldName = String(renamingPlan?.plan || '').trim();
    const newName = String(renamePlanValue || '').trim();
    if (!pat || !oldName || !newName || oldName === newName) {
      setRenamingPlan({ pattern: '', plan: '' });
      setRenamePlanValue('');
      return;
    }
    setAllPlansByPattern((prev) => {
      const next = { ...(prev || {}) };
      const arr = Array.isArray(next[pat]) ? next[pat] : [];
      const idx = arr.indexOf(oldName);
      if (idx !== -1) {
        const arrNext = [...arr];
        if (!arrNext.includes(newName)) arrNext[idx] = newName; else arrNext.splice(idx, 1);
        next[pat] = arrNext;
      }
      return next;
    });
    setPlanMetaByPattern((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
        next[pat][newName] = next[pat][oldName];
        delete next[pat][oldName];
      }
      return next;
    });
    setPlanNoCache((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
        next[pat][newName] = next[pat][oldName];
        delete next[pat][oldName];
      }
      return next;
    });
    setResourceNoCache((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
        next[pat][newName] = next[pat][oldName];
        delete next[pat][oldName];
      }
      return next;
    });
    setDataByPattern((prev) => {
      const next = { ...(prev || {}) };
      next[pat] = { ...(next[pat] || {}) };
      if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
        next[pat][newName] = next[pat][oldName];
        delete next[pat][oldName];
      }
      return next;
    });
    if (planManageSelectedPattern === pat && planManageSelectedPlan === oldName) setPlanManageSelectedPlan(newName);
    if (selectedPattern === pat && selectedPlan === oldName) setSelectedPlan(newName);
    setRenamingPlan({ pattern: '', plan: '' });
    setRenamePlanValue('');
  };
  const handleCancelRenamePlan = () => {
    setRenamingPlan({ pattern: '', plan: '' });
    setRenamePlanValue('');
  };
  const resolveResourceNo = async (patternName) => {
    try {
      const pat = String(patternName || '').trim();
      if (!pat) return null;
      const plans = (Array.isArray(allPlansByPattern?.[pat]) && allPlansByPattern[pat].length)
        ? allPlansByPattern[pat]
        : Object.keys(dataByPattern?.[pat] || {});
      for (const pl of plans) {
        const rno = (
          planMetaByPattern?.[pat]?.[pl]?.resourceNo ??
          resourceNoCache?.[pat]?.[pl]
        );
        if (Number.isFinite(Number(rno)) && Number(rno) > 0) return Number(rno);
      }
      const { companyNo } = getUserInfo() || {};
      if (!companyNo || !productId) return null;
      const resList = await getTourProductResourceList({ companyNo: String(companyNo), productNo: Number(productId) });
      const row = (resList || []).find(r => String(r?.자원명 || '') === pat);
      const rno2 = row?.자원번호;
      return Number.isFinite(Number(rno2)) && Number(rno2) > 0 ? Number(rno2) : null;
    } catch (_) { return null; }
  };
  const handleCreateNewPlan = async () => {
    try {
      const pat = String(planManageSelectedPattern || '').trim();
      const name = String(renamePlanValue || '').trim();
      const method = String(planManageCreatePriceMethod || '').trim();
      if (!pat) { alert('패턴을 선택하세요.'); return; }
      if (!name) { alert('요금제명을 입력하세요.'); return; }
      if (!method) { alert('가격방식을 선택하세요.'); return; }
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo || !productId) { alert('업체/사용자/상품 정보가 없습니다.'); return; }
      const resourceNo = await resolveResourceNo(pat);
      if (!resourceNo) { alert('자원번호를 확인할 수 없습니다.'); return; }
      let planPolicyCode = '083001';
      if (method === '합산가격') planPolicyCode = '083002';
      else if (method === '단품') planPolicyCode = '083003';
      const resp = await createTourRatePlanDefault01({
        companyNo: String(companyNo),
        productNo: Number(productId),
        planName: name,
        planPolicyCode,
        userNo: String(userNo),
        resourceNo: Number(resourceNo),
      });
      const table = (resp && (resp.table || resp.Table || resp.table1 || resp.Table1)) || [];
      const created = table && table[0] ? table[0] : null;
      const priceNo = created?.가격옵션번호;
      const priceType = created?.가격정책 || (method === '합산가격' ? '합계요금' : method === '단품' ? '단품' : '개별요금');
      const reserveMin = created?.최소수량;
      const reserveMax = created?.최대수량;
      const nonRefundable = !!created?.환불불가;
      const autoConfirm = !!created?.자동확정;
      const reserveControl = !!created?.주문수량제어;
      const isPrivate = !!created?.프라이빗;
      setAllPlansByPattern((prev) => {
        const next = { ...(prev || {}) };
        const arr = Array.isArray(next[pat]) ? next[pat] : [];
        if (!arr.includes(name)) next[pat] = [...arr, name];
        return next;
      });
      setPlanMetaByPattern((prev) => {
        const next = { ...(prev || {}) };
        next[pat] = { ...(next[pat] || {}) };
        next[pat][name] = {
          priceMethod: method,
          priceType,
          reserveMin: (reserveMin ?? '') === '' ? '' : Number(reserveMin),
          reserveMax: (reserveMax ?? '') === '' ? '' : Number(reserveMax),
          isPrivate,
          nonRefundable,
          autoConfirm,
          reserveControl,
          priceNo: Number(priceNo),
          resourceNo: Number(resourceNo),
        };
        return next;
      });
      setPlanNoCache((prev) => ({ ...(prev || {}), [pat]: { ...(prev?.[pat] || {}), [name]: Number(priceNo) } }));
      setResourceNoCache((prev) => ({ ...(prev || {}), [pat]: { ...(prev?.[pat] || {}), [name]: Number(resourceNo) } }));
      setDataByPattern((prev) => ({ ...(prev || {}), [pat]: { ...(prev?.[pat] || {}), [name]: (prev?.[pat]?.[name] || {}) } }));
      setPlanManageMode('edit');
      setPlanManageSelectedPlan(name);
      setRenamePlanValue(name);
      setPlanManageOriginal({ name, meta: (getPlanMeta(pat, name) || {}) });
      alert('요금제를 생성했습니다.');
    } catch (e) {
      console.error('요금제 생성 실패', e);
      alert('요금제 생성에 실패했습니다.');
    }
  };
  const handleSavePlanManage = async () => {
    try {
      if (planManageMode !== 'edit') return;
      const pat = String(planManageSelectedPattern || '').trim();
      const pl = String(planManageSelectedPlan || '').trim();
      if (!pat || !pl) { alert('패턴과 요금제를 선택하세요.'); return; }
      const meta = getPlanMeta(pat, pl) || {};
      const priceOptionNo = meta?.priceNo || (planNoCache?.[pat]?.[pl]);
      if (!priceOptionNo) { alert('가격옵션번호를 확인할 수 없습니다.'); return; }
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo || !productId) { alert('업체/사용자/상품 정보가 없습니다.'); return; }
      if (planManageSaving) return;
      setPlanManageSaving(true);
      const nameToSave = (renamePlanValue || '').trim() || pl;
      const priceMethod = String(meta.priceMethod || '').trim();
      // 가격정책코드 매핑: 083001 개별요금, 083002 합계요금, 083003 단품
      let pricePolicyCode = '083001';
      let pricePolicyName = '개별요금';
      if (priceMethod === '합산가격') { pricePolicyCode = '083002'; pricePolicyName = '합계요금'; }
      else if (priceMethod === '단품') { pricePolicyCode = '083003'; pricePolicyName = '단품'; }
      // 가격유형코드 매핑: 082001 호텔, 082002 투어, 082003 단품 (이 화면은 투어이므로 기본 082002, 단품 선택 시 082003)
      let priceTypeCode = '082002';
      let priceTypeName = '투어';
      if (priceMethod === '단품') { priceTypeCode = '082003'; priceTypeName = '단품'; }
      const 기본 = {
        가격유형: priceTypeName,
        가격옵션번호: Number(priceOptionNo),
        가격유형코드: priceTypeCode,
        요금제명: nameToSave,
        비고: '',
        가격정책코드: pricePolicyCode,
        가격정책: pricePolicyName,
        최소수량: meta?.reserveControl ? String(meta?.reserveMin ?? '') : '',
        최대수량: meta?.reserveControl ? String(meta?.reserveMax ?? '') : '',
        프라이빗: !!meta?.isPrivate,
        테마: '테마없음',
        테마코드: '097200',
        순서: 1,
        기본요금: false,
        추가특전: '',
        환불불가: !!meta?.nonRefundable,
        자동확정: !!meta?.autoConfirm,
        주문수량제어: !!meta?.reserveControl,
      };
      await updateTourRatePlan02({
        companyNo: String(companyNo),
        productNo: Number(productId),
        priceOptionNo: Number(priceOptionNo),
        userNo: String(userNo),
        기본,
        // 판매조건은 apiService의 기본 예시 문자열을 사용 (사용자 요청: 예시 그대로)
        // 쇼핑은 기본 []
      });
      // 이름이 변경된 경우 로컬 상태도 동기화 (리스트/메타/캐시)
      if (nameToSave && nameToSave !== pl) {
        const pat = planManageSelectedPattern;
        const oldName = pl;
        const newName = nameToSave;
        setAllPlansByPattern((prev) => {
          const next = { ...(prev || {}) };
          const arr = Array.isArray(next[pat]) ? next[pat] : [];
          const idx = arr.indexOf(oldName);
          if (idx !== -1) {
            const arrNext = [...arr];
            if (!arrNext.includes(newName)) arrNext[idx] = newName; else arrNext.splice(idx, 1);
            next[pat] = arrNext;
          }
          return next;
        });
        setPlanMetaByPattern((prev) => {
          const next = { ...(prev || {}) };
          next[pat] = { ...(next[pat] || {}) };
          if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
            next[pat][newName] = next[pat][oldName];
            delete next[pat][oldName];
          }
          return next;
        });
        setPlanNoCache((prev) => {
          const next = { ...(prev || {}) };
          next[pat] = { ...(next[pat] || {}) };
          if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
            next[pat][newName] = next[pat][oldName];
            delete next[pat][oldName];
          }
          return next;
        });
        setResourceNoCache((prev) => {
          const next = { ...(prev || {}) };
          next[pat] = { ...(next[pat] || {}) };
          if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
            next[pat][newName] = next[pat][oldName];
            delete next[pat][oldName];
          }
          return next;
        });
        setDataByPattern((prev) => {
          const next = { ...(prev || {}) };
          next[pat] = { ...(next[pat] || {}) };
          if (Object.prototype.hasOwnProperty.call(next[pat], oldName)) {
            next[pat][newName] = next[pat][oldName];
            delete next[pat][oldName];
          }
          return next;
        });
        setPlanManageSelectedPlan(newName);
        setRenamePlanValue(newName);
      }
      // update snapshot after successful save
      try {
        const latestMeta = getPlanMeta(planManageSelectedPattern, (nameToSave && nameToSave !== pl) ? nameToSave : pl) || {};
        setPlanManageOriginal({ name: (nameToSave && nameToSave !== pl) ? nameToSave : pl, meta: { ...latestMeta } });
      } catch (_) { }
      alert('요금제 설정을 저장했습니다.');
      // Optionally keep modal open; plan name UI already updated via rename handlers
    } catch (e) {
      console.error('요금제 저장 실패', e);
      alert('요금제 저장에 실패했습니다.');
    } finally {
      setPlanManageSaving(false);
    }
  };
  // Start create mode: clear name input and prepare to add under selected pattern
  const startCreatePlan = () => {
    if (!planManageSelectedPattern) return;
    setPlanManageMode('create');
    setRenamingPlan({ pattern: planManageSelectedPattern, plan: '' });
    setRenamePlanValue('');
    setPlanManageCreatePriceMethod('');
  };
  // 선택된 항공패턴/요금제가 바뀌면 이전의 basePriceInfo를 초기화하여 잘못된 베이스가 재사용되지 않도록 함
  useEffect(() => {
    setBasePriceInfo(null);
  }, [selectedPattern, selectedPlan]);

  // 상품번호가 바뀌는 경우에도 베이스를 초기화하여 타 상품의 가격정보가 섞이지 않도록 함
  useEffect(() => {
    setBasePriceInfo(null);
  }, [productId]);

  const handleChangeUseAgeGroup = async (nextUse) => {
    const payload = (typeof nextUse === 'object' && nextUse != null) ? nextUse : null;
    const next = payload ? (payload.useAgeGroup !== false) : (nextUse !== false);
    const ranges = payload && payload.ranges ? payload.ranges : null;
    const prev = useAgeGroup;
    setUseAgeGroup(next);
    if (!productId) return;
    setSavingAgeGroup(true);
    try {
      const res = await getProductDetailExtra(productId);
      const rawDetail = res?.detail || {};
      const requestFields = Array.isArray(res?.requestFields) ? res.requestFields : [];
      const infantEnd = ranges && ranges.infantEnd !== '' && typeof ranges.infantEnd !== 'undefined'
        ? Math.max(0, Number(String(ranges.infantEnd).replace(/[^0-9]/g, '') || 0))
        : Math.max(0, Number(rawDetail?.연령유아끝 ?? 3));
      const childEnd = ranges && ranges.childEnd !== '' && typeof ranges.childEnd !== 'undefined'
        ? Math.max(infantEnd, Number(String(ranges.childEnd).replace(/[^0-9]/g, '') || 0))
        : Math.max(infantEnd, Number(rawDetail?.연령소아끝 ?? 12));
      const normalizedDetail = {
        ...(rawDetail || {}),
        연령구분여부: next,
        연령유아끝: infantEnd,
        연령소아시작: infantEnd,
        연령소아끝: childEnd,
        연령성인시작: childEnd,
        현지지불코드: String(rawDetail?.현지지불코드 ?? '0'),
        신청금: String(rawDetail?.신청금 ?? '100'),
        연령성인끝: rawDetail?.연령성인끝 ?? 100,
      };
      await saveProductDetailExtra({ productNo: productId, detail: normalizedDetail, requestFields });
      return true;
    } catch (e) {
      setUseAgeGroup(prev);
      try { console.error('연령구분여부 저장 실패:', e); } catch (_) { }
      alert('연령구분 설정 저장에 실패했습니다. 다시 시도해주세요.');
      return false;
    } finally {
      setSavingAgeGroup(false);
    }
  };

  useEffect(() => {
    // 연령구분여부 로드
    let mounted = true;
    const loadAgeGroupSetting = async () => {
      if (!productId) return;
      try {
        const res = await getProductDetailExtra(productId);
        if (!mounted) return;
        const detail = res?.detail || {};
        // 연령구분여부가 명시적으로 false인 경우만 false, 그 외는 true (기본값)
        setUseAgeGroup(detail.연령구분여부 !== false);
        setWaitlistReservationAllowed(isWaitlistReservationAllowed(detail?.대기예약허용여부));
      } catch (e) {
        // 에러 시 기본값 true 유지
        console.warn('연령구분여부 로드 실패:', e);
        setWaitlistReservationAllowed(false);
      }
    };
    loadAgeGroupSetting();
    return () => { mounted = false; };
  }, [productId]);

  useEffect(() => {
    setHasDOM(typeof document !== 'undefined' && !!document.body);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadAgeRangeForModal = async () => {
      if (!ageGroupSettingsOpen) return;
      if (!productId) return;
      try {
        const res = await getProductDetailExtra(productId);
        const d = res?.detail || {};
        if (!mounted) return;
        const infantEnd = Math.max(0, Number(d.연령유아끝 ?? 3));
        const childEnd = Math.max(infantEnd, Number(d.연령소아끝 ?? 12));
        const next = {
          infantEnd,
          childEnd,
          childStart: infantEnd,
          adultStart: childEnd,
        };
        setAgeGroupSettingsTemp(d.연령구분여부 !== false);
        setAgeRangeTemp(next);
        setAgeRangeInitial({ infantEnd, childEnd });
      } catch (_) {
        if (!mounted) return;
        setAgeRangeInitial((prev) => prev || { infantEnd: ageRangeTemp.infantEnd, childEnd: ageRangeTemp.childEnd });
      }
    };
    loadAgeRangeForModal();
    return () => { mounted = false; };
  }, [ageGroupSettingsOpen, productId]);

  const ageGroupModalMain = (!ageGroupSettingsOpen || !hasDOM ? null : createPortal(
    <div
      className="fixed inset-0 z-[120] transition-opacity"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setAgeGroupSettingsOpen(false); }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-gray-900">연령구분 설정</div>
              
            </div>
            <button type="button" className="text-xs px-2 py-1 hover:bg-gray-50" onClick={() => setAgeGroupSettingsOpen(false)}>✕</button>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-800 mb-2">연령구분 사용여부</div>
              <div className="flex items-center gap-4" role="radiogroup" aria-label="연령구분 사용여부">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ageGroupUsed_common"
                    value="true"
                    checked={ageGroupSettingsTemp !== false}
                    disabled={!!savingAgeGroup}
                    onChange={() => setAgeGroupSettingsTemp(true)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm font-medium ${savingAgeGroup ? 'text-gray-400' : 'text-gray-700'}`}>연령구분 사용</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ageGroupUsed_common"
                    value="false"
                    checked={ageGroupSettingsTemp === false}
                    disabled={!!savingAgeGroup}
                    onChange={() => setAgeGroupSettingsTemp(false)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm font-medium ${savingAgeGroup ? 'text-gray-400' : 'text-gray-700'}`}>연령구분 미사용</span>
                </label>
              </div>
              
            </div>

            {ageGroupSettingsTemp !== false && (
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-800 mb-2">연령 구간 설정</div>

                <div className="space-y-2">
                  <div className="grid grid-cols-[78px_1fr] items-center gap-2">
                    <span className="text-[11px] text-gray-600 font-medium">유아</span>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled className="w-16 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm" value={0} />
                      <span className="text-[12px] text-gray-500">~</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-20 px-2 py-1 border rounded text-sm"
                        value={String(ageRangeTemp.infantEnd ?? '')}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                          if (allowed.includes(e.key)) return;
                          if (e.ctrlKey || e.metaKey) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const rawStr = String(e.target.value || '').replace(/[^0-9]/g, '');
                          const end = rawStr === '' ? '' : Math.max(0, Number(rawStr || 0));
                          setAgeRangeTemp((prev) => {
                            const inf = end;
                            const childEnd = prev.childEnd === '' ? '' : Math.max(Number(prev.childEnd || 0), Number(inf || 0));
                            return {
                              ...prev,
                              infantEnd: inf,
                              childStart: inf === '' ? prev.childStart : inf,
                              childEnd,
                              adultStart: childEnd === '' ? prev.adultStart : childEnd,
                            };
                          });
                        }}
                      />
                      <span className="text-[11px] text-gray-500">세</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-[78px_1fr] items-center gap-2">
                    <span className="text-[11px] text-gray-600 font-medium">소아</span>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled className="w-16 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm" value={ageRangeTemp.childStart ?? (ageRangeTemp.infantEnd ?? 3)} />
                      <span className="text-[12px] text-gray-500">~</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-20 px-2 py-1 border rounded text-sm"
                        value={String(ageRangeTemp.childEnd ?? '')}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                          if (allowed.includes(e.key)) return;
                          if (e.ctrlKey || e.metaKey) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const rawStr = String(e.target.value || '').replace(/[^0-9]/g, '');
                          const end = rawStr === '' ? '' : Math.max(0, Number(rawStr || 0));
                          setAgeRangeTemp((prev) => {
                            const inf = prev.infantEnd === '' ? 0 : Number(prev.infantEnd || 0);
                            const chEnd = end === '' ? '' : Math.max(inf, end);
                            return {
                              ...prev,
                              childEnd: chEnd,
                              adultStart: chEnd === '' ? prev.adultStart : chEnd,
                            };
                          });
                        }}
                      />
                      <span className="text-[11px] text-gray-500">세</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-[78px_1fr] items-center gap-2">
                    <span className="text-[11px] text-gray-600 font-medium">성인</span>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled className="w-16 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-sm" value={ageRangeTemp.adultStart ?? (ageRangeTemp.childEnd ?? 12)} />
                      <span className="text-[11px] text-gray-500">세 이상</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
            <button type="button" className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50" disabled={!!savingAgeGroup} onClick={() => setAgeGroupSettingsOpen(false)}>취소</button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={(() => {
                if (!!savingAgeGroup) return true;
                const useSame = (ageGroupSettingsTemp !== false) === (useAgeGroup !== false);
                if (ageGroupSettingsTemp === false) return useSame;
                const init = ageRangeInitial;
                const rangesSame = !!init && Number(init.infantEnd) === Number(ageRangeTemp.infantEnd) && Number(init.childEnd) === Number(ageRangeTemp.childEnd);
                return useSame && rangesSame;
              })()}
              onClick={async () => {
                const next = ageGroupSettingsTemp !== false;
                const ok = window.confirm('이 설정은 상품 전체(모든 날짜)에 적용됩니다. 변경할까요?');
                if (!ok) return;
                let success = true;
                try {
                  const res = await handleChangeUseAgeGroup({
                    useAgeGroup: next,
                    ranges: {
                      infantEnd: ageRangeTemp.infantEnd,
                      childEnd: ageRangeTemp.childEnd,
                    },
                  });
                  success = (typeof res === 'boolean') ? res : true;
                } catch (_) {
                  success = false;
                }
                if (success) setAgeGroupSettingsOpen(false);
              }}
            >적용</button>
          </div>
        </div>
      </div>
    </div>, document.body));

  useEffect(() => {
    if (!planManageOpen && !unifiedManageOpen) return;
    const pat = selectedPattern && (patterns || []).includes(selectedPattern) ? selectedPattern : (patterns[0] || '');
    setPlanManageSelectedPattern((prev) => prev || pat);
    const plansForPat = Array.isArray(allPlansByPattern?.[pat]) && allPlansByPattern[pat].length
      ? allPlansByPattern[pat]
      : Object.keys(dataByPattern?.[pat] || {});
    const pl = (selectedPlan && plansForPat.includes(selectedPlan)) ? selectedPlan : (plansForPat[0] || '');
    setPlanManageSelectedPlan((prev) => prev || pl);
  }, [planManageOpen, unifiedManageOpen, selectedPattern, selectedPlan, patterns, allPlansByPattern, dataByPattern]);
  // Keep right-pane name input synced with the selected plan in edit mode
  useEffect(() => {
    if (!planManageOpen && !unifiedManageOpen) return;
    if (planManageMode !== 'edit') return;
    if (planManageSelectedPattern && planManageSelectedPlan) {
      setRenamingPlan({ pattern: planManageSelectedPattern, plan: planManageSelectedPlan });
      setRenamePlanValue(planManageSelectedPlan);
      // capture snapshot for reset
      const meta = getPlanMeta(planManageSelectedPattern, planManageSelectedPlan) || {};
      setPlanManageOriginal({ name: planManageSelectedPlan, meta: { ...meta } });
    }
  }, [planManageOpen, unifiedManageOpen, planManageSelectedPattern, planManageSelectedPlan, planManageMode]);
  // Prefill priceMethod from priceType/costMethod if empty when entering edit mode
  useEffect(() => {
    if (!planManageOpen && !unifiedManageOpen) return;
    if (planManageMode !== 'edit') return;
    const pat = planManageSelectedPattern;
    const pl = planManageSelectedPlan;
    if (!pat || !pl) return;
    const meta = getPlanMeta(pat, pl);
    const current = (meta && meta.priceMethod) || '';
    const raw = (meta && (meta.priceType || meta.costMethod)) || '';
    const candidate = (() => {
      const s = String(raw);
      // 가격정책 매핑: 합계요금 -> 합산가격, 개별요금 -> 1인가격, 단품 -> 단품
      if (/합계|합산/.test(s)) return '합산가격';
      if (/개별|1인|1\s*인/.test(s)) return '1인가격';
      if (/단품/.test(s)) return '단품';
      return s;
    })();
    if (!current && candidate) {
      updatePlanMeta(pat, pl, { priceMethod: candidate });
    }
  }, [planManageOpen, unifiedManageOpen, planManageMode, planManageSelectedPattern, planManageSelectedPlan, planMetaByPattern]);
  // Wide-range fetch to build pattern/plan dropdowns using dedicated procedures (resource and rate plan)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!productId) return;
        // Retry shortly to obtain companyNo in case token/user info is not yet hydrated
        let companyNo = (getUserInfo() || {}).companyNo;
        if (!companyNo) {
          for (let i = 0; i < 3 && !companyNo; i++) {
            await new Promise(r => setTimeout(r, 120));
            companyNo = (getUserInfo() || {}).companyNo;
          }
        }
        if (!companyNo) return;
        setListsLoading(true);
        // 1) Fetch resources (항공패턴 = 자원)
        const resList = await getTourProductResourceList({ companyNo: String(companyNo), productNo: Number(productId) });
        if (!mounted) return;
        const patternNames = [];
        const plansByPattern = {};
        const metaGrouped = {};
        // 2) For each resource, fetch its rate plans (in parallel, tolerate partial failures)
        const ratePlanPromises = (resList || []).map((r) => {
          const patternName = (r?.자원명 || '기초상품자원').toString();
          const resourceNo = Number(r?.자원번호);
          if (!patternName) return null;
          patternNames.push(patternName);
          plansByPattern[patternName] = [];
          if (!metaGrouped[patternName]) metaGrouped[patternName] = {};
          return getTourRatePlanList02({ companyNo: String(companyNo), productNo: Number(productId), resourceNo })
            .then(planRows => ({ patternName, resourceNo, planRows }))
            .catch(err => ({ patternName, resourceNo, error: err }));
        }).filter(Boolean);
        const settled = await Promise.allSettled(ratePlanPromises);
        if (!mounted) return;
        settled.forEach(res => {
          if (res.status !== 'fulfilled') {
            try { console.warn('요금제 병렬 조회 실패', res.reason); } catch (_) { }
            return;
          }
          const { patternName, resourceNo, planRows, error } = res.value || {};
          if (error) {
            try { console.warn('요금제 조회 실패', error); } catch (_) { }
            return;
          }
          (planRows || []).forEach(pl => {
            const rawName = pl?.요금제명;
            const priceNo = pl?.가격옵션번호;
            const nameTrimmed = typeof rawName === 'string' ? rawName.trim() : '';
            // If both name and option number are missing/null, skip this entry entirely
            if (!nameTrimmed && (priceNo == null || String(priceNo).trim() === '')) return;
            const planName = nameTrimmed || String(priceNo);
            const priceType = (pl?.가격정책 || '').toString();
            // Map additional meta fields from SP result
            const reserveMinRaw = pl?.최소수량;
            const reserveMaxRaw = pl?.최대수량;
            const isPrivateRaw = pl?.프라이빗;
            const nonRefundableRaw = pl?.환불불가;
            const autoConfirmRaw = pl?.자동확정;
            const reserveControlRaw = pl?.주문수량제어;
            const toNumOrEmpty = (v) => {
              const n = Number(v);
              return Number.isFinite(n) ? n : '';
            };
            if (!metaGrouped[patternName][planName]) metaGrouped[patternName][planName] = { costMethod: '', priceType };
            metaGrouped[patternName][planName].reserveMin = toNumOrEmpty(reserveMinRaw);
            metaGrouped[patternName][planName].reserveMax = toNumOrEmpty(reserveMaxRaw);
            metaGrouped[patternName][planName].isPrivate = !!isPrivateRaw;
            metaGrouped[patternName][planName].nonRefundable = !!nonRefundableRaw;
            metaGrouped[patternName][planName].autoConfirm = !!autoConfirmRaw;
            metaGrouped[patternName][planName].reserveControl = !!reserveControlRaw;
            if (priceNo != null && String(priceNo).trim() !== '') {
              metaGrouped[patternName][planName].priceNo = Number(priceNo);
            }
            metaGrouped[patternName][planName].resourceNo = resourceNo;
            if (planName && !plansByPattern[patternName].includes(planName)) plansByPattern[patternName].push(planName);
          });
        });
        if (!mounted) return;
        setAllPatterns(patternNames);
        setAllPlansByPattern(plansByPattern);
        setPlanMetaByPattern(metaGrouped);
        // Default selections from wide lists when empty or invalid
        try {
          let finalPattern = '';
          setSelectedPattern(prev => {
            finalPattern = (prev && patternNames.includes(prev)) ? prev : (patternNames[0] || '');
            return finalPattern;
          });
          setSelectedPlan(prev => {
            const currentPattern = finalPattern || (patternNames[0] || '');
            const availablePlans = Array.isArray(plansByPattern[currentPattern]) ? plansByPattern[currentPattern] : [];
            return (prev && availablePlans.includes(prev)) ? prev : (availablePlans[0] || '');
          });
        } catch (_) { }
        // Update caches from meta
        try {
          setPlanNoCache(prev => {
            const next = { ...(prev || {}) };
            Object.keys(metaGrouped || {}).forEach(pat => {
              next[pat] = { ...(next[pat] || {}) };
              Object.keys(metaGrouped[pat] || {}).forEach(plan => {
                const pn = metaGrouped?.[pat]?.[plan]?.priceNo;
                if (pn != null) next[pat][plan] = Number(pn);
              });
            });
            return next;
          });
          setResourceNoCache(prev => {
            const next = { ...(prev || {}) };
            Object.keys(metaGrouped || {}).forEach(pat => {
              next[pat] = { ...(next[pat] || {}) };
              Object.keys(metaGrouped[pat] || {}).forEach(plan => {
                const rn = metaGrouped?.[pat]?.[plan]?.resourceNo;
                if (rn != null) next[pat][plan] = Number(rn);
              });
            });
            return next;
          });
        } catch (_) { }
      } catch (e) {
        try { console.warn('패턴/요금제 전용 조회 실패', e); } catch (_) { }
      } finally {
        if (mounted) setListsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [productId]);

  // Ensure listsLoading is turned off after lists are populated or on error
  useEffect(() => {
    // Turn off when we have any pattern data or if productId changed
    if ((Array.isArray(allPatterns) && allPatterns.length) || !productId) {
      setListsLoading(false);
    }
  }, [allPatterns, productId]);

  // Ensure selectedPlan stays valid whenever selectedPattern or plans list changes
  useEffect(() => {
    if (!selectedPattern) return;
    const availablePlans = Array.isArray(allPlansByPattern?.[selectedPattern]) && allPlansByPattern[selectedPattern].length
      ? allPlansByPattern[selectedPattern]
      : Object.keys(dataByPattern[selectedPattern] || {});
    if (!availablePlans.length) {
      if (selectedPlan) setSelectedPlan('');
      return;
    }
    if (!selectedPlan || !availablePlans.includes(selectedPlan)) {
      setSelectedPlan(availablePlans[0]);
    }
  }, [selectedPattern, allPlansByPattern, dataByPattern, selectedPlan]);

  // Fetch month data whenever the visible month (cursor) changes
  useEffect(() => {
    let mounted = true;
    const loadMonth = async () => {
      try {
        if (!productId) return;
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;
        // If multi-month view is enabled, fetch for the full visible range
        const s = multiMonthView ? startOfMonth(visibleStartMM) : startOfMonth(cursor);
        const e = multiMonthView ? endOfMonth(visibleEndMM) : endOfMonth(cursor);
        const startDate = formatYMD(s);
        const endDate = formatYMD(e);
        const resp = await getPriceStockCalendar({
          productTypeName: '투어',
          productNo: Number(productId),
          companyNo: String(companyNo),
          startDate,
          endDate,
          판매여부1: true,
          판매여부2: true,
          판매된리스트만보기: false,
          키워드: '',
          구분: '3',
          플랫폼수수료반영: false,
          결제수수료반영: false,
        });
        if (!mounted) return;
        // Normalize possible table keys for safety
        const rows = (resp && (
          resp.table || resp.table1 || resp.Table1 || resp.Table || resp.Table0 || resp.empt0 || []
        )) || [];
        const grouped = {};
        const metaGrouped = {};
        rows.forEach((r) => {
          const rawDate = r?.행사시작일시 || r?.일자 || r?.날짜 || r?.판매일 || r?.date || r?.Date;
          if (!rawDate) return;
          const d = new Date(String(rawDate).slice(0, 10) + 'T00:00:00');
          if (Number.isNaN(d.getTime())) return;
          const key = formatYMD(d);
          const patternName = (r?.항공패턴 || r?.항공 || r?.패턴명 || r?.pattern || '기초상품자원').toString();
          const rawPlan = r?.요금제 ?? r?.가격명 ?? r?.pricePlan ?? r?.PlanName;
          const priceNo = r?.가격옵션번호 ?? r?.요금제번호 ?? r?.PlanNo ?? r?.priceNo;
          const planName = (typeof rawPlan === 'string' ? rawPlan.trim() : '') || (priceNo != null ? String(priceNo) : '');
          // If no plan name and no option number, treat as no rate plan and skip this row for plan grouping
          if (!planName) return;
          // Prefer 총판매가 (e.g., "￦300,000 / ￦200,000 / ￦100,000") then fallback to 개별 판매가 필드
          let priceAdult = '';
          let priceChild = '';
          let priceInfant = '';
          const totalPriceStr = r?.총판매가 || r?.TotalPrice || '';
          if (totalPriceStr) {
            const parts = String(totalPriceStr).split('/').map(s => s.trim());
            priceAdult = parts[0] || '';
            priceChild = parts[1] || '';
            priceInfant = parts[2] || '';
          } else {
            priceAdult = r?.판매가성인 ?? r?.판매가A ?? r?.AdultPrice ?? r?.priceAdult ?? '';
            priceChild = r?.판매가소아 ?? r?.판매가C ?? r?.ChildPrice ?? r?.priceChild ?? '';
            priceInfant = r?.판매가유아 ?? r?.판매가I ?? r?.InfantPrice ?? r?.priceInfant ?? '';
          }

          const sellTriplet = getTripletFromRow(r, {
            tripletKeys: ['판매가', 'SalePrice', 'SellPrice'],
            adultKeys: ['판매가성인', '판매가A', 'AdultPrice', 'priceAdult'],
            childKeys: ['판매가소아', '판매가C', 'ChildPrice', 'priceChild'],
            infantKeys: ['판매가유아', '판매가I', 'InfantPrice', 'priceInfant'],
          });
          const taxTriplet = getTripletFromRow(r, {
            tripletKeys: ['Tax및유류할증료', 'Tax및유류할증', '택스및유류할증료', '택스및유류할증', 'TaxFuel', 'FuelSurcharge', 'Tax'],
            adultKeys: ['Tax성인', 'TaxA', '택스성인', 'taxAdult'],
            childKeys: ['Tax소아', 'TaxC', '택스소아', 'taxChild'],
            infantKeys: ['Tax유아', 'TaxI', '택스유아', 'taxInfant'],
          });
          const commissionTriplet = getTripletFromRow(r, {
            tripletKeys: ['커미션', 'Commission'],
            adultKeys: ['커미션성인', '커미션A', 'CommissionAdult', 'commissionAdult'],
            childKeys: ['커미션소아', '커미션C', 'CommissionChild', 'commissionChild'],
            infantKeys: ['커미션유아', '커미션I', 'CommissionInfant', 'commissionInfant'],
          });
          let costAdult = r?.원가성인 ?? r?.원가A ?? r?.AdultCost ?? r?.costAdult ?? '';
          let costChild = r?.원가소아 ?? r?.원가C ?? r?.ChildCost ?? r?.costChild ?? '';
          let costInfant = r?.원가유아 ?? r?.원가I ?? r?.InfantCost ?? r?.costInfant ?? '';
          // Some datasets provide a single combined cost string field (e.g., "원가": "￦10,000 / ￦0 / ￦0")
          if (!(costAdult || costChild || costInfant)) {
            const totalCostStr = r?.원가 || r?.총원가 || r?.TotalCost || '';
            if (totalCostStr) {
              const cparts = String(totalCostStr).split('/').map(s => s.trim());
              costAdult = cparts[0] || '';
              costChild = cparts[1] || '';
              costInfant = cparts[2] || '';
            }
          }
          // Plan meta (same for same plan)
          const costMethod = (r?.원가방식 ?? r?.CostMethod ?? r?.원가방식명 ?? '').toString();
          const priceType = (r?.가격구분 ?? r?.PriceType ?? r?.가격방식 ?? '').toString();
          // Capture price option identifier strictly (avoid 혼용): prefer 가격옵션번호/요금제번호; do NOT use 가격번호
          // priceNo already computed above
          // Capture date-specific price record id (가격번호) separately for detail fetch
          const datePriceNo = (r?.가격번호 ?? r?.PriceNo ?? r?.priceId ?? r?.PriceId ?? r?.가격ID ?? r?.가격식별자);

          const virtualRecruitRaw = r?.가상모객 ?? r?.가상모집 ?? r?.가상모객수 ?? r?.VirtualRecruit ?? r?.virtualRecruit;
          let virtualGuest = null;
          if (virtualRecruitRaw != null && String(virtualRecruitRaw).trim() !== '') {
            const s = String(virtualRecruitRaw);
            const m = s.match(/(\d+)\s*명/) || s.match(/(\d+)/);
            const n = m ? Number(m[1]) : NaN;
            if (Number.isFinite(n) && n > 0) virtualGuest = n;
          }

          // Capacity / Remaining / Guest (sold) parsing
          // capacity (보유석) mapping: prefer explicit '보유석' when present
          const totalStockRaw = r?.보유석 ?? r?.총재고 ?? r?.총좌석 ?? r?.정원 ?? r?.TotalStock ?? '';
          const remainStockRaw = r?.잔여석 ?? r?.잔여 ?? r?.RemainingStock ?? '';
          const soldRaw = r?.모객수량 ?? r?.판매수량 ?? r?.예약수 ?? r?.Sold ?? '';
          const soldNetRaw = r?.공유제외한판매수량 ?? r?.판매수량공유제외 ?? r?.SoldNet ?? '';
          const closedRaw = (typeof r?.마감여부 !== 'undefined') ? r?.마감여부 : (typeof r?.Closed !== 'undefined' ? r?.Closed : undefined);
          const departConfirmedRaw = (typeof r?.출발확정 !== 'undefined') ? r?.출발확정 : (typeof r?.DepartConfirmed !== 'undefined' ? r?.DepartConfirmed : undefined);
          const scheduleConfirmedRaw = (typeof r?.일정확정 !== 'undefined') ? r?.일정확정 : (typeof r?.ScheduleConfirmed !== 'undefined' ? r?.ScheduleConfirmed : undefined);
          const toNum = (x) => {
            const s = String(x ?? '').replace(/[^0-9.-]/g, '');
            return s ? Number(s) : NaN;
          };
          const resourceNo = (r?.자원번호 ?? r?.ResourceNo ?? r?.resourceNo);
          const itineraryTypeNo = (r?.일정타입번호 ?? r?.itineraryTypeNo ?? r?.ItineraryTypeNo);
          const itineraryLabelRaw = r?.일정표 ?? r?.일정타입명 ?? r?.itineraryTypeName ?? r?.ItineraryTypeName;
          const itineraryLabel = String(itineraryLabelRaw ?? '').trim();
          const totalStockNum = toNum(totalStockRaw);
          const remainStockNum = toNum(remainStockRaw);
          const soldNum = toNum(soldRaw);
          const soldNetNum = toNum(soldNetRaw);
          const capacity = Number.isNaN(totalStockNum) ? null : totalStockNum;
          const remain = Number.isNaN(remainStockNum) ? null : remainStockNum;
          let guest = null;
          if (!Number.isNaN(soldNetNum)) {
            guest = soldNetNum;
          } else if (!Number.isNaN(soldNum)) {
            guest = soldNum;
          } else if (!Number.isNaN(totalStockNum) && !Number.isNaN(remainStockNum)) {
            guest = Math.max(0, totalStockNum - remainStockNum);
          }
          if (!grouped[patternName]) grouped[patternName] = {};
          if (!grouped[patternName][planName]) grouped[patternName][planName] = {};
          if (!metaGrouped[patternName]) metaGrouped[patternName] = {};
          if (!metaGrouped[patternName][planName]) metaGrouped[patternName][planName] = { costMethod, priceType, priceNo };
          // capture plan-level resourceNo if present in any row
          if (metaGrouped[patternName][planName] && (r?.자원번호 != null || r?.ResourceNo != null || r?.resourceNo != null)) {
            const rnoPlan = (r?.자원번호 ?? r?.ResourceNo ?? r?.resourceNo);
            if (rnoPlan != null && typeof metaGrouped[patternName][planName].resourceNo === 'undefined') {
              metaGrouped[patternName][planName].resourceNo = Number(rnoPlan);
            }
          }
          const toBool = (x) => {
            if (typeof x === 'boolean') return x;
            if (x == null) return false;
            // Accept truthy numeric values
            const num = Number(String(x).replace(/[^0-9.-]/g, ''));
            if (!Number.isNaN(num)) {
              return num > 0; // e.g., 1, 2 treated as true; 0 as false
            }
            const s = String(x).trim().toLowerCase();
            // Common truthy tokens
            const truthy = new Set([
              'true', 't', 'y', 'yes', '1',
              'closed', 'close', 'done',
              // Korean variants frequently seen
              '마감', '마감됨', '판매마감', '종료', '닫힘'
            ]);
            return truthy.has(s);
          };
          const prevDayData = grouped[patternName][planName][key] || {};
          const prevItineraries = Array.isArray(prevDayData.itineraryTypes) ? prevDayData.itineraryTypes : [];
          const nextItineraries = [...prevItineraries];
          if (itineraryLabel || itineraryTypeNo != null) {
            const exists = nextItineraries.some((item) => (
              Number(item?.no) === Number(itineraryTypeNo)
              || (itineraryLabel && String(item?.label || '') === itineraryLabel)
            ));
            if (!exists) {
              nextItineraries.push({
                no: itineraryTypeNo == null ? null : Number(itineraryTypeNo),
                label: itineraryLabel,
              });
            }
          }
          grouped[patternName][planName][key] = {
            ...prevDayData,
            price: { adult: priceAdult, child: priceChild, infant: priceInfant },
            sell: sellTriplet,
            tax: taxTriplet,
            commission: commissionTriplet,
            cost: { adult: costAdult, child: costChild, infant: costInfant },
            stock: remain ?? (remainStockRaw || ''),
            capacity: capacity ?? (totalStockRaw || ''),
            guest: guest ?? (soldNetRaw || soldRaw || ''),
            virtualGuest,
            closed: toBool(closedRaw),
            departConfirmed: toBool(departConfirmedRaw),
            scheduleConfirmed: toBool(scheduleConfirmedRaw),
            priceNo: (priceNo == null ? undefined : priceNo),
            datePriceNo: (datePriceNo == null ? undefined : Number(datePriceNo)),
            resourceNo: (resourceNo == null ? undefined : Number(resourceNo)),
            itineraryTypeNo: itineraryTypeNo == null ? (prevDayData.itineraryTypeNo ?? undefined) : Number(itineraryTypeNo),
            itineraryLabel: itineraryLabel || prevDayData.itineraryLabel || '',
            itineraryTypes: nextItineraries,
            // 이 날짜(가격번호)가 속한 기획전명을 콤마로 이어붙인 문자열.
            // RCT_S_가격예약_02가 STRING_AGG(기획전명,',')로 내려준다 — 기획전번호는 없다.
            promo: String(r?.기획전 ?? '').trim(),
          };
        });
        const patternList = Object.keys(grouped);
        console.debug('[PriceStock] rows:', rows.length, 'patterns:', patternList, 'plansByPattern:', Object.fromEntries(patternList.map(p => [p, Object.keys(grouped[p] || {})])));
        setDataByPattern(grouped);
        // Merge calendar-derived meta without overriding authoritative priceNo/resourceNo
        setPlanMetaByPattern(prev => {
          const merged = { ...(prev || {}) };
          Object.keys(metaGrouped || {}).forEach(pat => {
            merged[pat] = { ...(merged[pat] || {}) };
            const plans = metaGrouped[pat] || {};
            Object.keys(plans).forEach(plan => {
              const prevMeta = merged[pat][plan] || {};
              const fromCal = plans[plan] || {};
              merged[pat][plan] = {
                ...prevMeta,
                // keep existing priceNo/resourceNo; only merge descriptive fields from calendar
                costMethod: fromCal.costMethod ?? prevMeta.costMethod,
                priceType: fromCal.priceType ?? prevMeta.priceType,
              };
            });
          });
          return merged;
        });
        // Update caches for planNo/resourceNo when available
        try {
          setPlanNoCache(prev => {
            const next = { ...(prev || {}) };
            Object.keys(metaGrouped || {}).forEach(pat => {
              next[pat] = { ...(next[pat] || {}) };
              Object.keys(metaGrouped[pat] || {}).forEach(plan => {
                const pn = metaGrouped?.[pat]?.[plan]?.priceNo;
                if (pn != null) next[pat][plan] = Number(pn);
              });
            });
            return next;
          });
          setResourceNoCache(prev => {
            const next = { ...(prev || {}) };
            Object.keys(metaGrouped || {}).forEach(pat => {
              next[pat] = { ...(next[pat] || {}) };
              Object.keys(metaGrouped[pat] || {}).forEach(plan => {
                const rn = metaGrouped?.[pat]?.[plan]?.resourceNo;
                if (rn != null) next[pat][plan] = Number(rn);
              });
            });
            return next;
          });
        } catch (_) { }
        // Keep previous selections; if not in this month's grouped data, fall back to procedure-based lists before clearing
        setSelectedPattern(prevPat => {
          // Keep current pattern if valid in allPatterns (from wide fetch)
          if (prevPat && (allPatterns || []).includes(prevPat)) return prevPat;
          // Otherwise try grouped data
          if (prevPat && grouped[prevPat]) return prevPat;
          // Fallback to first pattern from wide fetch or grouped data
          if ((allPatterns || []).length) return allPatterns[0];
          return patternList[0] || prevPat || '';
        });
        // plan selection will be adjusted by effect below (which also prefers allPlansByPattern)
      } catch (e) {
        console.warn('가격/재고 월 데이터 로드 실패', e);
      }
    };
    loadMonth();
    return () => { mounted = false; };
  }, [cursor, productId, multiMonthView, visibleStartMM, visibleEndMM]);

  // Fetch stock data (보유석, 판매석, 잔여석) whenever pattern/plan/month changes
  useEffect(() => {
    let mounted = true;
    const loadStockData = async () => {
      try {
        if (!productId || !selectedPattern || !selectedPlan) return;
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;

        // Get resourceNo from cache or meta
        const resourceNo = resourceNoCache?.[selectedPattern]?.[selectedPlan]
          ?? selectedPlanMeta?.resourceNo;
        if (!resourceNo) {
          console.debug('[Stock] No resourceNo available for pattern/plan:', selectedPattern, selectedPlan);
          return;
        }

        const s = multiMonthView ? startOfMonth(visibleStartMM) : startOfMonth(cursor);
        const e = multiMonthView ? endOfMonth(visibleEndMM) : endOfMonth(cursor);
        const startDate = formatYMD(s);
        const endDate = formatYMD(e);

        const resp = await getTourStockList({
          companyNo: String(companyNo),
          productNo: Number(productId),
          startDate,
          endDate,
          startTime: '00:00',
          resourceNo: Number(resourceNo),
        });

        if (!mounted) return;

        // Parse response: { table: [{ 재고: JSON string }] }
        const rows = resp?.table ?? resp?.Table ?? [];
        if (!rows.length) {
          console.debug('[Stock] No stock data returned');
          return;
        }

        const stockField = rows[0]?.재고;
        if (!stockField && !Array.isArray(stockField)) {
          console.debug('[Stock] No 재고 field in response');
          return;
        }

        let stockArray = [];
        if (Array.isArray(stockField)) {
          stockArray = stockField;
        } else if (typeof stockField === 'string') {
          try { stockArray = JSON.parse(stockField); } catch (e) { console.warn('[Stock] Failed to parse 재고 JSON:', e); return; }
        } else if (stockField && typeof stockField === 'object') {
          // Some APIs may already return parsed object/array
          stockArray = Array.isArray(stockField) ? stockField : [];
        }

        // Build map: { [YYYY-MM-DD]: { 보유석, 판매석, 잔여석 } } and shared set
        const stockMap = {};
        const sharedMap = {};
        const sharedSet = new Set();
        (Array.isArray(stockArray) ? stockArray : []).forEach(item => {
          const dateStr = item?.날짜;
          if (!dateStr) return;

          stockMap[dateStr] = parseStockEventsFromTourStockItem(item);
          if (item?.상품공유번호 != null) {
            sharedSet.add(String(dateStr));
            sharedMap[String(dateStr)] = Number(item.상품공유번호);
          }
        });

        console.debug('[Stock] Loaded stock data for', Object.keys(stockMap).length, 'dates');
        setStockDataByDate(stockMap);
        setSharedFromApiDates(sharedSet);
        setSharedSeriesNoByDate(sharedMap);
      } catch (e) {
        console.warn('[Stock] Failed to load stock data:', e);
      }
    };
    loadStockData();
    return () => { mounted = false; };
  }, [cursor, productId, selectedPattern, selectedPlan, multiMonthView, visibleStartMM, visibleEndMM, resourceNoCache, selectedPlanMeta]);

  // Refresh calendar (same as loadMonth) on demand after saving
  const refreshCalendarForCurrentMonth = async () => {
    try {
      if (!productId) return;
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) return;
      // Refresh across visible range in multi-month mode
      const s = multiMonthView ? startOfMonth(visibleStartMM) : startOfMonth(cursor);
      const e = multiMonthView ? endOfMonth(visibleEndMM) : endOfMonth(cursor);
      const startDate = formatYMD(s);
      const endDate = formatYMD(e);
      const resp = await getPriceStockCalendar({
        productTypeName: '투어',
        productNo: Number(productId),
        companyNo: String(companyNo),
        startDate,
        endDate,
        판매여부1: true,
        판매여부2: true,
        판매된리스트만보기: false,
        키워드: '',
        구분: '3',
        플랫폼수수료반영: false,
        결제수수료반영: false,
      });
      const rows = (resp && (resp.table || resp.table1 || resp.Table1 || [])) || [];
      const grouped = {};
      const metaGrouped = {};
      rows.forEach((r) => {
        const rawDate = r?.행사시작일시 || r?.일자 || r?.날짜 || r?.판매일 || r?.date || r?.Date;
        if (!rawDate) return;
        const d = new Date(String(rawDate).slice(0, 10) + 'T00:00:00');
        if (Number.isNaN(d.getTime())) return;
        const key = formatYMD(d);
        const patternName = (r?.항공패턴 || r?.항공 || r?.패턴명 || r?.pattern || '기초상품자원').toString();
        const planName = (r?.요금제 || r?.가격명 || r?.pricePlan || r?.PlanName || '기본요금제').toString();
        const itineraryTypeNo = (r?.일정타입번호 ?? r?.itineraryTypeNo ?? r?.ItineraryTypeNo);
        const itineraryLabelRaw = r?.일정표 ?? r?.일정타입명 ?? r?.itineraryTypeName ?? r?.ItineraryTypeName;
        const itineraryLabel = String(itineraryLabelRaw ?? '').trim();
        let priceAdult = '';
        let priceChild = '';
        let priceInfant = '';
        const totalPriceStr = r?.총판매가 || r?.TotalPrice || '';
        if (totalPriceStr) {
          const parts = String(totalPriceStr).split('/').map(s => s.trim());
          priceAdult = parts[0] || '';
          priceChild = parts[1] || '';
          priceInfant = parts[2] || '';
        } else {
          priceAdult = r?.판매가성인 ?? r?.판매가A ?? r?.AdultPrice ?? r?.priceAdult ?? '';
          priceChild = r?.판매가소아 ?? r?.판매가C ?? r?.ChildPrice ?? r?.priceChild ?? '';
          priceInfant = r?.판매가유아 ?? r?.판매가I ?? r?.InfantPrice ?? r?.priceInfant ?? '';
        }
        const sellTriplet = getTripletFromRow(r, {
          tripletKeys: ['판매가', 'SalePrice', 'SellPrice'],
          adultKeys: ['판매가성인', '판매가A', 'AdultPrice', 'priceAdult'],
          childKeys: ['판매가소아', '판매가C', 'ChildPrice', 'priceChild'],
          infantKeys: ['판매가유아', '판매가I', 'InfantPrice', 'priceInfant'],
        });
        const taxTriplet = getTripletFromRow(r, {
          tripletKeys: ['Tax및유류할증료', 'Tax및유류할증', '택스및유류할증료', '택스및유류할증', 'TaxFuel', 'FuelSurcharge', 'Tax'],
          adultKeys: ['Tax성인', 'TaxA', '택스성인', 'taxAdult'],
          childKeys: ['Tax소아', 'TaxC', '택스소아', 'taxChild'],
          infantKeys: ['Tax유아', 'TaxI', '택스유아', 'taxInfant'],
        });
        const commissionTriplet = getTripletFromRow(r, {
          tripletKeys: ['커미션', 'Commission'],
          adultKeys: ['커미션성인', '커미션A', 'CommissionAdult', 'commissionAdult'],
          childKeys: ['커미션소아', '커미션C', 'CommissionChild', 'commissionChild'],
          infantKeys: ['커미션유아', '커미션I', 'CommissionInfant', 'commissionInfant'],
        });
        const costAdult = r?.원가성인 ?? r?.원가A ?? r?.AdultCost ?? r?.costAdult ?? '';
        const costChild = r?.원가소아 ?? r?.원가C ?? r?.ChildCost ?? r?.costChild ?? '';
        const costInfant = r?.원가유아 ?? r?.원가I ?? r?.InfantCost ?? r?.costInfant ?? '';
        const costMethod = (r?.원가방식 ?? r?.CostMethod ?? r?.원가방식명 ?? '').toString();
        const priceType = (r?.가격구분 ?? r?.PriceType ?? r?.가격방식 ?? '').toString();
        // Capture price option identifier strictly (avoid 혼용): prefer 가격옵션번호/요금제번호; do NOT use 가격번호
        const priceNo = r?.가격옵션번호 ?? r?.요금제번호 ?? r?.PlanNo ?? r?.priceNo;
        // Capture date-specific price record id (가격번호)
        const datePriceNo = (r?.가격번호 ?? r?.PriceNo ?? r?.priceId ?? r?.PriceId ?? r?.가격ID ?? r?.가격식별자);

        const virtualRecruitRaw = r?.가상모객 ?? r?.가상모집 ?? r?.가상모객수 ?? r?.VirtualRecruit ?? r?.virtualRecruit;
        let virtualGuest = null;
        if (virtualRecruitRaw != null && String(virtualRecruitRaw).trim() !== '') {
          const s = String(virtualRecruitRaw);
          const m = s.match(/(\d+)\s*명/) || s.match(/(\d+)/);
          const n = m ? Number(m[1]) : NaN;
          if (Number.isFinite(n) && n > 0) virtualGuest = n;
        }
        const totalStockRaw = r?.보유석 ?? r?.총재고 ?? r?.총좌석 ?? r?.정원 ?? r?.TotalStock ?? '';
        const remainStockRaw = r?.잔여석 ?? r?.잔여 ?? r?.RemainingStock ?? '';
        const soldRaw = r?.모객수량 ?? r?.판매수량 ?? r?.예약수 ?? r?.Sold ?? '';
        const soldNetRaw = r?.공유제외한판매수량 ?? r?.판매수량공유제외 ?? r?.SoldNet ?? '';
        const closedRaw = (typeof r?.마감여부 !== 'undefined') ? r?.마감여부 : (typeof r?.Closed !== 'undefined' ? r?.Closed : undefined);
        const departConfirmedRaw = (typeof r?.출발확정 !== 'undefined') ? r?.출발확정 : (typeof r?.DepartConfirmed !== 'undefined' ? r?.DepartConfirmed : undefined);
        const scheduleConfirmedRaw = (typeof r?.일정확정 !== 'undefined') ? r?.일정확정 : (typeof r?.ScheduleConfirmed !== 'undefined' ? r?.ScheduleConfirmed : undefined);
        const toNum = (x) => {
          const s = String(x ?? '').replace(/[^0-9.-]/g, '');
          return s ? Number(s) : NaN;
        };
        const resourceNo = (r?.자원번호 ?? r?.ResourceNo ?? r?.resourceNo);
        const totalStockNum = toNum(totalStockRaw);
        const remainStockNum = toNum(remainStockRaw);
        const soldNum = toNum(soldRaw);
        const soldNetNum = toNum(soldNetRaw);
        const capacity = Number.isNaN(totalStockNum) ? null : totalStockNum;
        const remain = Number.isNaN(remainStockNum) ? null : remainStockNum;
        let guest = null;
        if (!Number.isNaN(soldNetNum)) {
          guest = soldNetNum;
        } else if (!Number.isNaN(soldNum)) {
          guest = soldNum;
        } else if (!Number.isNaN(totalStockNum) && !Number.isNaN(remainStockNum)) {
          guest = Math.max(0, totalStockNum - remainStockNum);
        }
        if (!grouped[patternName]) grouped[patternName] = {};
        if (!grouped[patternName][planName]) grouped[patternName][planName] = {};
        if (!metaGrouped[patternName]) metaGrouped[patternName] = {};
        if (!metaGrouped[patternName][planName]) metaGrouped[patternName][planName] = { costMethod, priceType, priceNo };
        if (metaGrouped[patternName][planName] && resourceNo != null && typeof metaGrouped[patternName][planName].resourceNo === 'undefined') {
          metaGrouped[patternName][planName].resourceNo = Number(resourceNo);
        }
        const toBool = (x) => {
          if (typeof x === 'boolean') return x;
          if (x == null) return false;
          const num = Number(String(x).replace(/[^0-9.-]/g, ''));
          if (!Number.isNaN(num)) {
            return num > 0;
          }
          const s2 = String(x).trim().toLowerCase();
          const truthy = new Set(['true', 't', 'y', 'yes', '1', 'closed', 'close', 'done', '마감', '마감됨', '판매마감', '종료', '닫힘']);
          return truthy.has(s2);
        };
        const prevDayData = grouped[patternName][planName][key] || {};
        const prevItineraries = Array.isArray(prevDayData.itineraryTypes) ? prevDayData.itineraryTypes : [];
        const nextItineraries = [...prevItineraries];
        if (itineraryLabel || itineraryTypeNo != null) {
          const exists = nextItineraries.some((item) => (
            Number(item?.no) === Number(itineraryTypeNo)
            || (itineraryLabel && String(item?.label || '') === itineraryLabel)
          ));
          if (!exists) {
            nextItineraries.push({
              no: itineraryTypeNo == null ? null : Number(itineraryTypeNo),
              label: itineraryLabel,
            });
          }
        }
        grouped[patternName][planName][key] = {
          ...prevDayData,
          price: { adult: priceAdult, child: priceChild, infant: priceInfant },
          sell: sellTriplet,
          tax: taxTriplet,
          commission: commissionTriplet,
          cost: { adult: costAdult, child: costChild, infant: costInfant },
          stock: remain ?? (remainStockRaw || ''),
          capacity: capacity ?? (totalStockRaw || ''),
          guest: guest ?? (soldNetRaw || soldRaw || ''),
          virtualGuest,
          closed: toBool(closedRaw),
          departConfirmed: toBool(departConfirmedRaw),
          scheduleConfirmed: toBool(scheduleConfirmedRaw),
          priceNo: (priceNo == null ? undefined : priceNo),
          datePriceNo: (datePriceNo == null ? undefined : Number(datePriceNo)),
          resourceNo: (resourceNo == null ? undefined : Number(resourceNo)),
          itineraryTypeNo: itineraryTypeNo == null ? (prevDayData.itineraryTypeNo ?? undefined) : Number(itineraryTypeNo),
          itineraryLabel: itineraryLabel || prevDayData.itineraryLabel || '',
          itineraryTypes: nextItineraries,
          // 이 날짜(가격번호)가 속한 기획전명을 콤마로 이어붙인 문자열.
          // RCT_S_가격예약_02가 STRING_AGG(기획전명,',')로 내려준다 — 기획전번호는 없다.
          promo: String(r?.기획전 ?? '').trim(),
        };
      });
      const patternList = Object.keys(grouped);
      try { console.debug('[PriceStock] refresh rows:', rows.length, 'patterns:', patternList); } catch (_) { }
      // Build a list of date keys within current visible range to clean stale entries
      const rangeKeys = [];
      {
        const d0 = new Date(s);
        const d1 = new Date(e);
        for (let d = new Date(d0); d.getTime() <= d1.getTime(); d.setDate(d.getDate() + 1)) {
          rangeKeys.push(formatYMD(d));
        }
      }
      setDataByPattern(prev => {
        const next = { ...(prev || {}) };
        const serverPatterns = Object.keys(grouped);
        serverPatterns.forEach(pat => {
          next[pat] = { ...(next[pat] || {}) };
          const serverPlans = Object.keys(grouped[pat] || {});
          serverPlans.forEach(plan => {
            const prevDays = { ...(next[pat][plan] || {}) };
            // Remove any date within the current visible range that is not returned by server
            for (const k of rangeKeys) {
              if (Object.prototype.hasOwnProperty.call(prevDays, k) && !Object.prototype.hasOwnProperty.call(grouped[pat][plan], k)) {
                delete prevDays[k];
              }
            }
            // Apply/overwrite with server values
            Object.keys(grouped[pat][plan] || {}).forEach(dayKey => {
              const srv = grouped[pat][plan][dayKey] || {};
              const prevDay = prevDays[dayKey] || {};
              prevDays[dayKey] = {
                price: {
                  adult: srv?.price?.adult,
                  child: srv?.price?.child,
                  infant: srv?.price?.infant,
                },
                cost: {
                  adult: srv?.cost?.adult,
                  child: srv?.cost?.child,
                  infant: srv?.cost?.infant,
                },
                stock: srv?.stock,
                capacity: srv?.capacity,
                guest: srv?.guest,
                virtualGuest: srv?.virtualGuest,
                closed: srv?.closed,
                departConfirmed: srv?.departConfirmed,
                scheduleConfirmed: srv?.scheduleConfirmed,
                priceNo: srv?.priceNo,
                datePriceNo: srv?.datePriceNo,
                resourceNo: srv?.resourceNo,
                itineraryTypeNo: srv?.itineraryTypeNo ?? prevDay?.itineraryTypeNo,
                itineraryLabel: srv?.itineraryLabel ?? prevDay?.itineraryLabel ?? '',
                itineraryTypes: Array.isArray(srv?.itineraryTypes)
                  ? srv.itineraryTypes
                  : (Array.isArray(prevDay?.itineraryTypes) ? prevDay.itineraryTypes : []),
                // 기획전 매핑 — 저장 후 refresh 시 반드시 반영해야 캘린더 바가 갱신된다
                promo: Object.prototype.hasOwnProperty.call(srv, 'promo')
                  ? String(srv.promo ?? '').trim()
                  : (prevDay?.promo || ''),
              };
            });
            next[pat][plan] = prevDays;
          });
        });
        return next;
      });
      try { setCalendarRerenderTick((t) => t + 1); } catch (_) { }
      setPlanMetaByPattern(prev => {
        const mergedMeta = { ...(prev || {}) };
        Object.keys(metaGrouped || {}).forEach(pat => {
          mergedMeta[pat] = { ...(mergedMeta[pat] || {}) };
          const plans = metaGrouped[pat] || {};
          Object.keys(plans).forEach(plan => {
            const prevMeta = mergedMeta[pat][plan] || {};
            const fromCal = plans[plan] || {};
            mergedMeta[pat][plan] = {
              ...prevMeta,
              // Do NOT overwrite priceNo/resourceNo with calendar values
              costMethod: fromCal.costMethod ?? prevMeta.costMethod,
              priceType: fromCal.priceType ?? prevMeta.priceType,
            };
          });
        });
        return mergedMeta;
      });
      // Update caches with latest meta
      try {
        setPlanNoCache(prev => {
          const next = { ...(prev || {}) };
          Object.keys(metaGrouped || {}).forEach(pat => {
            next[pat] = { ...(next[pat] || {}) };
            Object.keys(metaGrouped[pat] || {}).forEach(plan => {
              const pn = metaGrouped?.[pat]?.[plan]?.priceNo;
              if (pn != null) next[pat][plan] = Number(pn);
            });
          });
          return next;
        });
        setResourceNoCache(prev => {
          const next = { ...(prev || {}) };
          Object.keys(metaGrouped || {}).forEach(pat => {
            next[pat] = { ...(next[pat] || {}) };
            Object.keys(metaGrouped[pat] || {}).forEach(plan => {
              const rn = metaGrouped?.[pat]?.[plan]?.resourceNo;
              if (rn != null) next[pat][plan] = Number(rn);
            });
          });
          return next;
        });
      } catch (_) { }
      setSelectedPattern(prevPat => {
        // Keep current pattern if valid in allPatterns (from wide fetch)
        if (prevPat && (allPatterns || []).includes(prevPat)) return prevPat;
        // Otherwise try grouped data
        if (prevPat && grouped[prevPat]) return prevPat;
        // Fallback to first pattern from wide fetch or grouped data
        if ((allPatterns || []).length) return allPatterns[0];
        return patternList[0] || prevPat || '';
      });

      // Refresh stock data as well
      try {
        if (selectedPattern && selectedPlan) {
          const resourceNo = resourceNoCache?.[selectedPattern]?.[selectedPlan]
            ?? metaGrouped?.[selectedPattern]?.[selectedPlan]?.resourceNo;
          if (resourceNo) {
            const stockResp = await getTourStockList({
              companyNo: String(companyNo),
              productNo: Number(productId),
              startDate,
              endDate,
              startTime: '00:00',
              resourceNo: Number(resourceNo),
            });
            const stockRows = stockResp?.table ?? stockResp?.Table ?? [];
            if (stockRows.length) {
              const stockStr = stockRows[0]?.재고;
              if (stockStr) {
                const stockField2 = stockStr;
                let stockArray = [];
                if (Array.isArray(stockField2)) {
                  stockArray = stockField2;
                } else if (typeof stockField2 === 'string') {
                  try { stockArray = JSON.parse(stockField2); } catch (_) { stockArray = []; }
                } else if (stockField2 && typeof stockField2 === 'object') {
                  stockArray = Array.isArray(stockField2) ? stockField2 : [];
                }
                const stockMap = {};
                const sharedMap2 = {};
                const sharedSet = new Set();
                (Array.isArray(stockArray) ? stockArray : []).forEach(item => {
                  const dateStr = item?.날짜;
                  if (!dateStr) return;
                  stockMap[dateStr] = parseStockEventsFromTourStockItem(item);
                  if (item?.상품공유번호 != null) {
                    sharedSet.add(String(dateStr));
                    sharedMap2[String(dateStr)] = Number(item.상품공유번호);
                  }
                });
                setStockDataByDate(stockMap);
                setSharedFromApiDates(sharedSet);
                setSharedSeriesNoByDate(sharedMap2);
              }
            }
          }
        }
      } catch (ex) {
        console.warn('재고 데이터 갱신 실패', ex);
      }
    } catch (e) {
      console.warn('가격/재고 월 데이터 갱신 실패', e);
    }
  };

  useEffect(() => {
    const pending = pendingPriceNameSyncRef.current;
    if (!pending || !pending.priceName) return;
    if (syncingPriceNameRef.current) return;
    if (!productId) return;
    if (!dataByPattern || !Object.keys(dataByPattern).length) return;

    const { companyNo, userNo } = getUserInfo() || {};
    if (!companyNo || !userNo) return;

    syncingPriceNameRef.current = true;
    (async () => {
      try {
        const byPriceNo = new Set();
        Object.values(dataByPattern || {}).forEach((plans) => {
          Object.values(plans || {}).forEach((days) => {
            Object.values(days || {}).forEach((cell) => {
              const n = Number(cell?.datePriceNo);
              if (Number.isFinite(n) && n > 0) byPriceNo.add(n);
            });
          });
        });
        const selected = Array.from(byPriceNo).map((n) => ({ 가격번호: n }));
        if (selected.length) {
          await savePriceNameBulkByPriceNos({
            productNo: Number(productId),
            companyNo: String(companyNo),
            userNo: String(userNo),
            selected,
            priceName: pending.priceName,
          });
          try { await refreshCalendarForCurrentMonth(); } catch (_) { }
        }
      } catch (e) {
        console.error('[PriceStockSettings] auto sync price name failed', e);
      } finally {
        syncingPriceNameRef.current = false;
        pendingPriceNameSyncRef.current = null;
        try {
          navigate(location.pathname, { replace: true, state: null });
        } catch (_) { }
      }
    })();
  }, [dataByPattern, productId, navigate, location.pathname]);

  // Handler for reservation button click
  // Always re-fetch the date-specific price number for the clicked date
  const handleReservationClick = async (date, _priceNoIgnored) => {
    try {
      const { companyNo } = getUserInfo() || {};
      if (!companyNo || !productId) {
        alert('업체 또는 상품 정보가 없습니다.');
        return;
      }

      // 1) Single-day calendar fetch to get the latest date-specific price record ID (가격번호)
      const startDate = String(date).slice(0, 10);
      const endDate = startDate;
      const resp = await getPriceStockCalendar({
        productTypeName: '투어',
        productNo: Number(productId),
        companyNo: String(companyNo),
        startDate,
        endDate,
      }).catch(() => null);

      const list = Array.isArray(resp?.table)
        ? resp.table
        : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.Table1) ? resp.Table1 : []));

      const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);

      // Optionally filter by the currently selected plan (가격옵션번호/요금제번호)
      const filtered = (list || []).filter((row) => {
        const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
        if (String(dateStr).slice(0, 10) !== startDate) return false;
        if (!selectedPlan) return true;
        const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo']);
        return optionNo == null || String(optionNo) === String(selectedPlan);
      });

      const targetRow = filtered[0] || (list && list[0]);
      const datePriceNo = targetRow ? tryGet(targetRow, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']) : undefined;
      // If there's no price number for the date, still proceed with 0 to get an empty structure from API
      const effectivePriceNo = Number.isFinite(Number(datePriceNo)) ? Number(datePriceNo) : 0;

      // 2) Use freshly fetched 가격번호 to get reservation price info
      const params = {
        companyNo,
        priceNo: Number(effectivePriceNo),
        nights: 1,
        adultCount: 1,
        ageArray: '',
        roomCount: 1,
      };
      const response = await getPriceInfoForReservation(params);

      // Inspect response: even if table exists, it may contain 정보 JSON with empty 객실상품
      const hasTable = response && Array.isArray(response.table) && response.table.length > 0;
      let roomsLen = 0;
      if (hasTable) {
        const infoStr = response.table[0]?.정보;
        try {
          const parsed = typeof infoStr === 'string' ? JSON.parse(infoStr) : (infoStr || {});
          const arr = Array.isArray(parsed?.객실상품) ? parsed.객실상품 : [];
          roomsLen = arr.length;
        } catch (_) {
          roomsLen = 0;
        }
      }

      if (roomsLen > 0) {
        setSelectedDate(startDate);
        setSelectedPriceNo(String(effectivePriceNo));
        setReservationModalOpen(true);
      } else {
        alert('재고가 없거나 마감이 되어 예약이 불가능 합니다');
      }
    } catch (error) {
      console.error('Failed to fetch reservation info:', error);
      alert('예약 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      // setLoading(false);
    }
  };

  // Attach the handler to the window object for the onclick event in the HTML string
  useEffect(() => {
    window.handleReservationClick = handleReservationClick;
    return () => {
      // Clean up when component unmounts
      delete window.handleReservationClick;
    };
  }, []);

  // When pattern changes or data changes, ensure selectedPlan is valid
  useEffect(() => {
    // Validate selectedPlan against wide-range plans first, fallback to current month data
    const planListWide = selectedPattern ? (allPlansByPattern[selectedPattern] || []) : [];
    if (planListWide && planListWide.length) {
      setSelectedPlan(prev => (prev && planListWide.includes(prev) ? prev : (planListWide[0] || '')));
      return;
    }
    const planList = selectedPattern ? Object.keys(dataByPattern[selectedPattern] || {}) : [];
    setSelectedPlan(prev => (prev && planList.includes(prev) ? prev : (planList[0] || '')));
  }, [selectedPattern, dataByPattern, allPlansByPattern]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (!productId) return;
        const { companyNo, userNo } = getUserInfo() || {};
        if (!companyNo) {
          console.warn('companyNo is missing from user info; product name may not load');
        }
        let detail = null;
        if (companyNo) {
          detail = await getProductDetails(productId, companyNo).catch(() => null);
        }
        if (!mounted) return;
        let name = detail?.상품명 || detail?.basic?.상품명 || '';
        // Extract currency symbol from product details (convert code to symbol)
        const currencyCode = detail?.통화 || detail?.basic?.통화 || '';
        const currency = getCurrencySymbol(currencyCode);
        if (!name) {
          // Fallback: search from product list
          try {
            if (companyNo && userNo) {
              const list = await getProductList(companyNo, userNo);
              const row = (Array.isArray(list) ? list : []).find(r => String(r?.상품번호) === String(productId));
              name = row?.상품명 || name;
            }
          } catch (e) {
            console.warn('Fallback getProductList failed', e);
          }
        }
        setProductName(name || '');
        setProductCurrency(currency || '');
      } catch (_) {
        if (!mounted) return;
        setProductName('');
      }
    };
    load();
    return () => { mounted = false; };
  }, [productId]);

  // No longer need manual days array when using FullCalendar

  // No per-day modal: we open bulk modal even for single day edits.

  // Open shared ProductSettingsModal (align with DetailPageSettings)
  const openBasicSettings = async () => {
    try {
      setModalLoading(true);
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(productId, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsModalOpen(true);
      } else {
        alert('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('상품 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setModalLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호 || productId;

      console.debug('[PriceStockSettings] handleSaveSettings start', {
        companyNo,
        userNo,
        productNo,
        productId,
      });

      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        getProductTypes().catch(() => []),
      ]);

      console.debug('[PriceStockSettings] dropdown lists loaded', {
        cities: Array.isArray(cities) ? cities.length : null,
        managers: Array.isArray(managers) ? managers.length : null,
        productTypes: Array.isArray(productTypes) ? productTypes.length : null,
      });

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;
      const original = selectedProduct || {};

      const nextProductName = (updatedProduct.상품명 ?? original.상품명 ?? '').toString();
      const prevProductName = (original.상품명 ?? '').toString();
      const productNameChanged = nextProductName.trim() !== prevProductName.trim();

      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr ? keywordStr.split(',').map(k => k.trim()).filter(Boolean) : [];

      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      const cityNo = cityItem?.코드 ?? original.도시번호;

      const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: nextProductName,
        도시번호: cityNo,
        도시: cityName,
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드,
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: original.통화코드 ?? '',
        통화: original.통화 ?? '',
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        카테고리: original.카테고리 ?? '',
        카테고리코드: original.카테고리코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
      };

      await saveProductBasicInfo({
        companyNo,
        userNo,
        productNo,
        basicInfo,
        keywords: keywordArr.join(','),
      });

      console.debug('[PriceStockSettings] saveProductBasicInfo success');

      // 상품명 변경 시: 현재 캘린더(멀티월뷰 포함)에 로딩된 모든 가격번호의 가격명을 상품명으로 일괄 업데이트
      if (productNameChanged && nextProductName.trim()) {
        try {
          const byPriceNo = new Set();
          Object.values(dataByPattern || {}).forEach((plans) => {
            Object.values(plans || {}).forEach((days) => {
              Object.values(days || {}).forEach((cell) => {
                const n = Number(cell?.datePriceNo);
                if (Number.isFinite(n) && n > 0) byPriceNo.add(n);
              });
            });
          });
          const selected = Array.from(byPriceNo).map((n) => ({ 가격번호: n }));
          if (selected.length) {
            await savePriceNameBulkByPriceNos({
              productNo: Number(productNo),
              companyNo: String(companyNo),
              userNo: String(userNo),
              selected,
              priceName: nextProductName,
            });
          }
        } catch (e) {
          console.error('[PriceStockSettings] bulk price name update failed', e);
        }
      }

      alert('성공적으로 저장되었습니다.');
      setIsModalOpen(false);
      setSelectedProduct(null);
      
      // 저장 후 연령구분여부 및 캘린더 데이터 갱신
      try {
        const res = await getProductDetailExtra(productId);
        const detail = res?.detail || {};
        setUseAgeGroup(detail.연령구분여부 !== false);
        setWaitlistReservationAllowed(isWaitlistReservationAllowed(detail?.대기예약허용여부));
      } catch (_) {}
      try {
        await refreshCalendarForCurrentMonth();
      } catch (_) {}

      return true;
    } catch (e) {
      console.error('Failed to save product details:', e);
      const detailMsg = e?.apiMessage || e?.message || '';
      alert(detailMsg ? `저장에 실패했습니다. 다시 시도해주세요.\n(${detailMsg})` : '저장에 실패했습니다. 다시 시도해주세요.');

      return false;
    } finally {
      setModalLoading(false);
    }
  };
  const applyBulk = async ({ rangeStart, rangeEnd, bulk, detailCosts: _detailCosts, costDivision: _costDivision, currency: _currency, weekdays, selectedDates: _selectedDates, useSharedStock, sharedSeriesNo, sharedSeries, commissionType, virtualRecruit: _virtualRecruit }) => {
// Handle multi-date selection
    let datesToApply = [];
    let isMultiDateMode = false;
    if (_selectedDates && Array.isArray(_selectedDates) && _selectedDates.length > 0) {
      // Multi-date selection mode
      isMultiDateMode = true;
datesToApply = _selectedDates.map(dateStr => new Date(dateStr + 'T00:00:00')).filter(d => !isNaN(d.getTime()));
      if (datesToApply.length === 0) {
        alert('선택된 날짜가 유효하지 않습니다.');
        return;
      }
      // For multi-date mode, use first and last date as rangeStart/rangeEnd for compatibility
      const sortedDates = [..._selectedDates].sort();
      rangeStart = sortedDates[0];
      rangeEnd = sortedDates[sortedDates.length - 1];
    } else {
      // Range selection mode
      if (!rangeStart || !rangeEnd) {
        alert('적용 기간(시작일/종료일)을 먼저 선택해주세요.');
        return;
      }
      const s = parseYMDStrict(String(rangeStart));
      const e = parseYMDStrict(String(rangeEnd));
      if (!s || !e) {
        alert('유효한 날짜 형식이 아닙니다. YYYY-MM-DD 형식으로 입력해 주세요.');
        return;
      }
      if (s.getTime() > e.getTime()) {
        alert('종료일은 시작일 이후여야 합니다.');
        return;
      }
      // Generate date array from range
      for (let d = new Date(s); d.getTime() <= e.getTime(); d.setDate(d.getDate() + 1)) {
        datesToApply.push(new Date(d));
      }
    }
    // Derive effective selections if user hasn't explicitly chosen yet
    let effectivePattern = selectedPattern;
    if (!effectivePattern) {
      const fallbackPatterns = (Array.isArray(allPatterns) && allPatterns.length)
        ? allPatterns
        : Object.keys(dataByPattern || {});
      if (fallbackPatterns && fallbackPatterns.length) {
        effectivePattern = fallbackPatterns[0];
        setSelectedPattern(effectivePattern);
      }
    }
    if (!effectivePattern) {
      alert('항공패턴을 선택해주세요.');
      return;
    }
    let effectivePlan = selectedPlan;
    if (!effectivePlan) {
      const planListWide = (allPlansByPattern?.[effectivePattern] || []);
      const planListNarrow = Object.keys(dataByPattern[effectivePattern] || {});
      const fallbackPlans = planListWide.length ? planListWide : planListNarrow;
      if (fallbackPlans && fallbackPlans.length) {
        effectivePlan = fallbackPlans[0];
        setSelectedPlan(effectivePlan);
      }
    }
    if (!effectivePlan) {
      alert('요금제를 선택해주세요.');
      return;
    }
    const current = dataByPattern[effectivePattern]?.[effectivePlan] || {};
    const next = { ...current };
    const dowNames = ['일', '월', '화', '수', '목', '금', '토'];
    const isSelectedDow = (dateObj) => {
      if (!weekdays || !Array.isArray(weekdays.names) || weekdays.names.length === 0) return true;
      const name = dowNames[dateObj.getDay()];
      return weekdays.names.includes(name);
    };
    // If using shared stock, call series linkage SP and refresh
    if (useSharedStock && sharedSeriesNo) {
      try {
        const { userNo, companyNo } = getUserInfo() || {};
        if (!productId || !companyNo || !userNo) { alert('필수 정보(상품/업체/유저)가 없습니다.'); return; }
        // Determine resourceNo as in delete flow
        const rno = (
          planMetaByPattern?.[effectivePattern]?.[effectivePlan]?.resourceNo ??
          resourceNoCache?.[effectivePattern]?.[effectivePlan]
        );
        if (!Number.isFinite(Number(rno)) || Number(rno) <= 0) {
          alert('자원번호를 확인할 수 없습니다. 요금제를 먼저 선택해주세요.');
          return;
        }
        const events = datesToApply.filter(d => isSelectedDow(d)).map(d => ({ 날짜: formatYMD(d), 시간: '00:00', 자원번호: Number(rno) }));
        if (!events.length) {
          alert('적용할 날짜가 없습니다. 요일 필터를 확인해주세요.');
          return;
        }
        await linkSharedSeriesToSelectedEvents({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), events, sharedSeriesNo: Number(sharedSeriesNo), calendarUse: true });
        // Mark locally for visual cue
        setSharedLinkedDates(prev => {
          const next = new Set(prev);
          events.forEach(ev => { if (ev?.날짜) next.add(String(ev.날짜)); });
          return next;
        });
        await refreshCalendarForCurrentMonth();
      } catch (e) {
        console.error('공유재고 시리즈 연동 실패', e);
        alert('공유재고 연동 중 오류가 발생했습니다.');
        return;
      }
    }

    // If shared is unchecked and some selected dates are currently linked to shared, unlink first then proceed
    if (!useSharedStock) {
      const keys = datesToApply.filter(d => isSelectedDow(d)).map(d => formatYMD(d));
      const needUnlink = keys.some(k => sharedFromApiDates?.has?.(k));
      if (needUnlink) {
        try {
          const { userNo, companyNo } = getUserInfo() || {};
          if (!productId || !companyNo || !userNo) { alert('필수 정보(상품/업체/유저)가 없습니다.'); return; }
          const rno = (
            planMetaByPattern?.[effectivePattern]?.[effectivePlan]?.resourceNo ??
            resourceNoCache?.[effectivePattern]?.[effectivePlan]
          );
          if (!Number.isFinite(Number(rno)) || Number(rno) <= 0) {
            alert('자원번호를 확인할 수 없습니다. 요금제를 먼저 선택해주세요.');
            return;
          }
          const events = keys.map(k => ({ 날짜: k, 시간: '00:00', 자원번호: Number(rno) }));
          await unlinkSharedSeriesFromSelectedEvents({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), events, calendarUse: true });
          // refresh stock/calendar flags before proceeding
          await refreshCalendarForCurrentMonth();
        } catch (ex) {
          console.error('공유재고 연동 해제 실패', ex);
          alert('공유재고 해제 중 오류가 발생했습니다.');
          return;
        }
      }
    }

    // Apply to all dates (either from multi-selection or range)
    datesToApply.forEach(d => {
      if (!isSelectedDow(d)) return;
      const key = formatYMD(d);
      next[key] = {
        ...next[key],
        price: {
          adult: normalizeNumber(bulk.pa),
          child: normalizeNumber(bulk.pc),
          infant: normalizeNumber(bulk.pi),
          taxAdult: normalizeNumber(bulk.ta),
          taxChild: normalizeNumber(bulk.tc),
          taxInfant: normalizeNumber(bulk.ti),
          costAdult: normalizeNumber(bulk.ca),
          costChild: normalizeNumber(bulk.cc),
          costInfant: normalizeNumber(bulk.ci),
          closed: !!bulk.closed,
          currency: _currency || '',
          costDivision: _costDivision || '',
        },
        stock: normalizeNumber(bulk.stock),
      };
    });
    // Optimistic update so inputs prefill immediately without full refresh
    try {
      const pat = effectivePattern;
      const pln = effectivePlan;
      if (pat && pln) {
        setDataByPattern((prev) => ({
          ...(prev || {}),
          [pat]: { ...(prev?.[pat] || {}), [pln]: next }
        }));
        // Also sync current modal initial values so open modal fields reflect immediately (display-formatted)
        const toDisp = (v) => {
          const n = Number(String(v ?? '').toString().replace(/[^0-9.-]/g, ''));
          return Number.isFinite(n) ? n.toLocaleString() : '';
        };
        const commTypeForPrefill = commissionType || '정률';
        const formatCommissionForType = (val) => {
          if (!val && val !== 0) return '';
          if (commTypeForPrefill === '정률') {
            // percentage: keep plain integer string 0-100
            const digits = String(val || '').replace(/[^0-9]/g, '');
            return digits ? String(Math.min(100, Number(digits))) : '';
          } else {
            // fixed amount: thousands-separated
            return toDisp(val);
          }
        };
        const hasCommissionInput = [bulk.ma, bulk.mc, bulk.mi]
          .some(v => Number(String(v ?? '').replace(/[^0-9.-]/g, '') || '0') !== 0);
        setBulkInitialValues({
          pa: toDisp(bulk.pa),
          pc: toDisp(bulk.pc),
          pi: toDisp(bulk.pi),
          ta: toDisp(bulk.ta),
          tc: toDisp(bulk.tc),
          ti: toDisp(bulk.ti),
          ca: toDisp(bulk.ca),
          cc: toDisp(bulk.cc),
          ci: toDisp(bulk.ci),
          ma: formatCommissionForType(bulk.ma),
          mc: formatCommissionForType(bulk.mc),
          mi: formatCommissionForType(bulk.mi),
          closed: !!bulk.closed,
          pcur: _currency || (basePriceInfo?.통화기호 || ''),
          ccur: _currency || (basePriceInfo?.통화기호 || ''),
          costDivision: (_detailCosts && _detailCosts.length > 0) ? '2' : '1',
          commissionType: commTypeForPrefill,
          showCommission: hasCommissionInput,
          ...bulkInitialStockField(bulk.stock),
        });
        // Force calendar repaint
        setCalendarRerenderTick((t) => t + 1);
      }
    } catch (_) { }
    // Immediately save via SPs
    try {
      const saveExtraMessages = [];
      // Determine planNo with cache fallback
      const planNoFromMetaOrCache = (
        planMetaByPattern?.[effectivePattern]?.[effectivePlan]?.priceNo ??
        planNoCache?.[effectivePattern]?.[effectivePlan]
      );
      const metaForPattern0 = planMetaByPattern?.[effectivePattern] || {};
      const validPriceNos0 = new Set(Object.values(metaForPattern0).map(m => Number(m?.priceNo)).filter(Number.isFinite));

      // Additional fallback: try to pick up priceNo from date-level data already loaded in calendar
      const dayLevelPlanNos = [];
      try {
        datesToApply.forEach(d => {
          if (!isSelectedDow(d)) return;
          const key = formatYMD(d);
          const day = (dataByPattern?.[effectivePattern]?.[effectivePlan]?.[key]) || {};
          const pn = Number(day?.priceNo);
          if (Number.isFinite(pn) && pn > 0) dayLevelPlanNos.push(pn);
        });
      } catch (_) {}

      const candidates = [
        Number(planNoFromMetaOrCache),
        ...(dayLevelPlanNos || []),
        ...Array.from(validPriceNos0),
      ].filter(n => Number.isFinite(n) && n > 0);

      let planNoToUse = candidates[0] ? Number(candidates[0]) : NaN;
      if (Number.isFinite(planNoToUse) && validPriceNos0.size > 0 && !validPriceNos0.has(planNoToUse)) {
        const fallbackNo = Array.from(validPriceNos0)[0];
        if (Number.isFinite(fallbackNo)) {
          try { console.warn('[PriceStock] planNo invalid, fallback applied', { effectivePattern, effectivePlan, planNoFromMetaOrCache, fallbackNo }); } catch (_) { }
          planNoToUse = Number(fallbackNo);
        }
      }

      let { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        for (let i = 0; i < 3 && (!companyNo || !userNo); i++) {
          await new Promise(r => setTimeout(r, 120));
          const ui = getUserInfo() || {};
          companyNo = companyNo || ui.companyNo;
          userNo = userNo || ui.userNo;
        }
      }

      if (!companyNo || !userNo || !Number.isFinite(planNoToUse) || planNoToUse <= 0) {
        alert('저장에 필요한 정보가 부족합니다. (회사/사용자/요금제번호)');
        return;
      }
      // 1) 재고수 변경 요청 (선택DATA 재고수정)
      const stockParsed = parseNum(bulk?.stock);
      const stockCountNum = Number.isFinite(stockParsed) ? Math.max(0, Math.floor(stockParsed)) : 0;
      const skipStockSave = !!useSharedStock && !!sharedSeriesNo;
      const userTypedStockDigits = /[0-9]/.test(String(bulk?.stock ?? ''));
      if (!skipStockSave && Number.isFinite(stockParsed) && stockParsed >= 0) {
        const events = [];
        const planLevelResourceNo = (
          planMetaByPattern?.[effectivePattern]?.[effectivePlan]?.resourceNo ??
          resourceNoCache?.[effectivePattern]?.[effectivePlan]
        );
        datesToApply.forEach(d => {
          if (!isSelectedDow(d)) return;
          const key = formatYMD(d);
          const day = (dataByPattern?.[effectivePattern]?.[effectivePlan]?.[key]) || {};
          const rno = (day?.resourceNo != null ? day.resourceNo : planLevelResourceNo);
          if (rno != null && Number.isFinite(Number(rno)) && Number(rno) > 0) {
            events.push({ 날짜: key, 시간: '', 자원번호: Number(rno) });
          }
        });
        if (events.length === 0) {
          try { console.warn('선택행사에 포함할 자원번호가 없습니다. (resourceNo 누락)'); } catch (_) { }
          if (userTypedStockDigits) {
            saveExtraMessages.push('재고는 저장되지 않았습니다. 선택 구간·요금제에 자원번호가 없어 재고 API를 호출하지 못했습니다. 요금제·자원 매핑을 확인하거나 데이터를 새로고침한 뒤 다시 시도해 주세요.');
          }
        } else {
          await saveTourSelectedStock({
            productNo: Number(productId),
            companyNo: String(companyNo),
            userNo: String(userNo),
            stockCount: String(stockCountNum),
            events,
            calendarUse: true,
            seriesUpdate: false,
          });
        }
      }
      // 1-b) 마감여부 변경 요청 (선택DATA 마감설정)
      try {
        const eventsForClose = [];
        const planLevelResourceNo = (
          planMetaByPattern?.[effectivePattern]?.[effectivePlan]?.resourceNo ??
          resourceNoCache?.[effectivePattern]?.[effectivePlan]
        );
        datesToApply.forEach(d => {
          if (!isSelectedDow(d)) return;
          const key = formatYMD(d);
          const day = (dataByPattern?.[effectivePattern]?.[effectivePlan]?.[key]) || {};
          const rno = (day?.resourceNo != null ? day.resourceNo : planLevelResourceNo);
          if (rno != null && Number.isFinite(Number(rno)) && Number(rno) > 0) {
            eventsForClose.push({ 날짜: key, 시간: '00:00', 자원번호: Number(rno) });
          }
        });
        if (eventsForClose.length > 0 && typeof bulk?.closed !== 'undefined') {
          await saveTourSessionCloseSetting({
            productNo: Number(productId),
            companyNo: String(companyNo),
            userNo: String(userNo),
            closed: !!bulk.closed,
            events: eventsForClose,
            calendarUse: true,
            seriesApply: true,
          });
        }
      } catch (e1) {
        console.warn('마감여부 저장 실패', e1);
      }
      // 2) 가격/원가 정보 저장 - 항상 현재 선택 요금제(planNo)의 베이스를 새로 로드하여 이전 요금제 데이터가 섞이지 않도록 함
      let base = await loadBasePriceInfo(planNoToUse);
      if (!base) {
        alert('기준 가격정보를 불러오지 못했습니다.');
        return;
      }
      const clone = JSON.parse(JSON.stringify(base));
      try {
        const vr = (typeof _virtualRecruit === 'object' && _virtualRecruit)
          ? _virtualRecruit
          : ((typeof virtualRecruit === 'object' && virtualRecruit) ? virtualRecruit : null);
        if (vr) {
          clone.가상모객사용 = !!vr.use;
          clone.가상모객인원 = parseNonNegativeInt(vr.count);
          clone.가상모객출발몇일전 = parseNonNegativeInt(vr.daysBefore);
        }
      } catch (_) { }
      const currency = _currency || clone.통화기호 || '';
      // 판매가: 항목 '판매가' 금액만 갱신 (코드/기호 등은 베이스 유지). 성인은 문자열 처리.
      const 판매가목록 = Array.isArray(clone.판매가) ? clone.판매가 : [];
      const 판매가Index = 판매가목록.findIndex(x => x.항목 === '판매가');
      const 판매가Base = 판매가Index >= 0 ? 판매가목록[판매가Index] : { 항목: '판매가', 번호: 1, 통화코드: clone.통화코드 || '', 통화기호: clone.통화기호 || currency };
      const 판매가Row = {
        ...판매가Base,
        성인: String(parseNum(bulk.pa)),
        어린이: parseNum(bulk.pc),
        영유아: parseNum(bulk.pi),
      };
      if (판매가Index >= 0) 판매가목록[판매가Index] = 판매가Row; else 판매가목록.push(판매가Row);
      // 판매가: Tax및유류할증료 항목 갱신/추가
      const 세금Index = 판매가목록.findIndex(x => x.항목 === 'Tax및유류할증료');
      const 세금Base = 세금Index >= 0 ? 판매가목록[세금Index] : { 항목: 'Tax및유류할증료', 번호: 2, 통화코드: clone.통화코드 || '', 통화기호: clone.통화기호 || currency };
      const 세금Row = {
        ...세금Base,
        성인: parseNum(bulk.ta),
        어린이: parseNum(bulk.tc),
        영유아: parseNum(bulk.ti),
      };
      if (세금Index >= 0) 판매가목록[세금Index] = 세금Row; else 판매가목록.push(세금Row);
      // 판매가: 커미션 항목 갱신/추가 (원가 탭 커미션 입력 반영)
      // 커미션 설정이 활성화되어 있고 값이 입력된 경우에만 저장
      const 커미션Index = 판매가목록.findIndex(x => x.항목 === '커미션');
      const hasCommissionInput = !!(bulk.ma || bulk.mc || bulk.mi);
      const 커미션Row = {
        ...(커미션Index >= 0 ? 판매가목록[커미션Index] : { 항목: '커미션', 번호: 3, 통화코드: clone.통화코드 || '', 통화기호: clone.통화기호 || currency }),
        // 커미션이 입력되어 있으면 해당 값 저장, 아니면 0
        // 정액 커미션일 때는 문자열로 저장, 정률일 때는 숫자로 저장
        성인: hasCommissionInput ? (commissionType === '정액' ? String(parseNum(bulk.ma)) : parseNum(bulk.ma)) : 0,
        어린이: hasCommissionInput ? (commissionType === '정액' ? String(parseNum(bulk.mc)) : parseNum(bulk.mc)) : 0,
        영유아: hasCommissionInput ? (commissionType === '정액' ? String(parseNum(bulk.mi)) : parseNum(bulk.mi)) : 0,
        통화코드: clone.통화코드 || '',
        통화기호: clone.통화기호 || currency,
      };
      if (커미션Index >= 0) 판매가목록[커미션Index] = 커미션Row; else 판매가목록.push(커미션Row);
      // 모든 판매가 행의 숫자 필드 정규화(null->0)
      // 단, 정상 페이로드와 동일하게 '판매가' 행의 성인은 문자열("10000")로 유지
      clone.판매가 = 판매가목록.map(r => {
        if (r && r.항목 === '판매가') {
          return {
            ...r,
            성인: String(r.성인 ?? ''),
            어린이: parseNum(r.어린이),
            영유아: parseNum(r.영유아),
          };
        }
        return {
          ...r,
          성인: parseNum(r.성인),
          어린이: parseNum(r.어린이),
          영유아: parseNum(r.영유아),
        };
      });
      // 원가 구분/총원가/세부원가 갱신 (베이스 정보 최대한 유지)
      // 세부원가 입력 유무에 따라 원가구분 동적 설정: 없으면 '1'(총원가), 있으면 '2'
      const hasDetailInput = Array.isArray(_detailCosts) && _detailCosts.length > 0;
      clone.원가구분 = hasDetailInput ? '2' : '1';
      // 총원가: 기존 첫 행이 있으면 금액만 갱신, 없으면 추가
      if (Array.isArray(clone.총원가) && clone.총원가.length > 0) {
        clone.총원가[0] = {
          ...clone.총원가[0],
          성인: parseNum(bulk.ca),
          어린이: parseNum(bulk.cc),
          영유아: parseNum(bulk.ci),
        };
      } else {
        clone.총원가 = [{ 구분: '총원가', 통화코드: clone.통화코드 || '', 통화기호: clone.통화기호 || currency, 성인: parseNum(bulk.ca), 어린이: parseNum(bulk.cc), 영유아: parseNum(bulk.ci) }];
      }
      // 세부원가: 항목명(원가항목) 기준 매칭하여 금액만 갱신, 없으면 기본값으로 생성
      const baseDetail = Array.isArray(clone.세부원가) ? clone.세부원가 : [];
      if (Array.isArray(_detailCosts)) {
        const byName = new Map(baseDetail.map((r) => [String(r.원가항목 || r.항목 || ''), r]));
        const merged = _detailCosts.map((it, idx) => {
          const key = String(it.원가항목 || '');
          const baseRow = byName.get(key) || {};
          return {
            ...baseRow,
            번호: baseRow.번호 || (idx + 1),
            원가항목: key || baseRow.원가항목,
            항목: key || baseRow.항목,
            원가항목코드: String(it.원가항목코드 || baseRow.원가항목코드 || ''),
            통화코드: baseRow.통화코드 || (clone.통화코드 || ''),
            통화기호: baseRow.통화기호 || it.통화기호 || clone.통화기호 || currency,
            환율적용방식: baseRow.환율적용방식 || '전일종가',
            환율적용방식코드: baseRow.환율적용방식코드 || '312001',
            적용환율: typeof baseRow.적용환율 !== 'undefined' ? baseRow.적용환율 : 1,
            성인: parseNum(it.성인),
            어린이: parseNum(it.어린이),
            영유아: parseNum(it.영유아),
          };
        });
        clone.세부원가 = merged && merged.length ? merged : [{ 번호: 1, 원가항목: null, 통화코드: clone.통화코드 || '', 통화기호: clone.통화기호 || currency, 성인: parseNum(bulk.ca), 어린이: parseNum(bulk.cc), 영유아: parseNum(bulk.ci) }];
      } else {
        // Ensure at least one detail row mirrors totals
        clone.세부원가 = [{ 번호: 1, 원가항목: null, 통화코드: clone.통화코드 || '', 통화기호: clone.통화기호 || currency, 성인: parseNum(bulk.ca), 어린이: parseNum(bulk.cc), 영유아: parseNum(bulk.ci) }];
      }
      // 적용기간: 멀티 날짜 선택 시 각 날짜별로 추가, 범위 선택 시 시작/종료일 설정
      if (_selectedDates && Array.isArray(_selectedDates) && _selectedDates.length > 0) {
        // Multi-date mode: add each date as separate period
        const _names = (weekdays && Array.isArray(weekdays.names) && weekdays.names.length) ? weekdays.names : ['월', '화', '수', '목', '금', '토', '일'];
        const _mask = (weekdays && typeof weekdays.mask === 'string' && weekdays.mask.length === 7) ? weekdays.mask : 'YYYYYYY';
        clone.적용기간 = _selectedDates.map(dateStr => ({
          시작일: dateStr,
          종료일: dateStr,
          요일: _names,
          요일2: _mask
        }));
      } else {
        // Range mode: single period with start/end
        if (Array.isArray(clone.적용기간) && clone.적용기간.length > 0) {
          clone.적용기간[0].시작일 = rangeStart;
          clone.적용기간[0].종료일 = rangeEnd;
          const _names = (weekdays && Array.isArray(weekdays.names) && weekdays.names.length) ? weekdays.names : ['월', '화', '수', '목', '금', '토', '일'];
          const _mask = (weekdays && typeof weekdays.mask === 'string' && weekdays.mask.length === 7) ? weekdays.mask : 'YYYYYYY';
          clone.적용기간[0].요일 = _names;
          clone.적용기간[0].요일2 = _mask;
        } else {
          const _names = (weekdays && Array.isArray(weekdays.names) && weekdays.names.length) ? weekdays.names : ['월', '화', '수', '목', '금', '토', '일'];
          const _mask = (weekdays && typeof weekdays.mask === 'string' && weekdays.mask.length === 7) ? weekdays.mask : 'YYYYYYY';
          clone.적용기간 = [{ 시작일: rangeStart, 종료일: rangeEnd, 요일: _names, 요일2: _mask }];
        }
      }
      // 선택요금제: planNo가 포함되도록 보장하고, 현재 저장 대상 이외의 요금제는 제거
      const 기존선택요금제 = Array.isArray(clone.선택요금제) ? clone.선택요금제 : [];
      const exists = 기존선택요금제.some(it => Number(it.가격옵션번호) === Number(planNoToUse));
      const next선택 = exists ? 기존선택요금제 : [...기존선택요금제, { 가격옵션번호: Number(planNoToUse), 합산가격여부: 'N' }];
      // 단일 저장이므로 대상 planNo만 유지
      clone.선택요금제 = next선택.filter(it => Number(it?.가격옵션번호) === Number(planNoToUse));

      // 요금제/상품자원요금제 구성 보강: 서버 기본 구조와 정합성 유지
      try {
        // 요금제 목록: 최소한 현재 planNo 반영
        const planNameForUI = String(selectedPlan || '기본요금제');
        const planList = Array.isArray(clone.요금제) ? clone.요금제 : [];
        const hasPlan = planList.some(p => Number(p?.요금제번호) === Number(planNoToUse));
        const mergedPlans = hasPlan ? planList : [...planList, { 요금제번호: Number(planNoToUse), 요금제명: planNameForUI }];
        clone.요금제 = mergedPlans.filter(Boolean);

        // 상품자원요금제: 현재 패턴/자원과 요금제 매핑 보강
        const resourceNoForPlan = (
          planMetaByPattern?.[effectivePattern]?.[effectivePlan]?.resourceNo ??
          resourceNoCache?.[effectivePattern]?.[effectivePlan]
        );
        const resourceName = String(effectivePattern || '기초상품자원');
        const sry = Array.isArray(clone.상품자원요금제) ? clone.상품자원요금제 : [];
        let group = sry.find(x => Number(x?.자원번호) === Number(resourceNoForPlan));
        if (!group) {
          group = { 자원번호: Number(resourceNoForPlan || 0), 자원명: resourceName, 요금제리스트: [] };
          sry.push(group);
        }
        const list = Array.isArray(group.요금제리스트) ? group.요금제리스트 : [];
        if (!list.some(x => Number(x?.가격옵션번호) === Number(planNoToUse))) {
          list.push({ 가격옵션번호: Number(planNoToUse), 가격옵션명: planNameForUI, 합산가격여부: 'N' });
        }
        group.요금제리스트 = list;
        clone.상품자원요금제 = sry;
      } catch (_) { }

      // 방어적 정리:
      // - 시작시간이 없으면 정상 페이로드처럼 처리
      //   * 행사시작시간: 값이 없거나 빈 배열이면 null
      //   * 선택시작시간: 값이 없거나 빈 배열이면 [{ 시간: '00:00' }]
      // - 일정타입에서 null 코드 제거
      if (!clone.행사시작시간 || (Array.isArray(clone.행사시작시간) && clone.행사시작시간.length === 0)) {
        clone.행사시작시간 = null;
      }
      if (!Array.isArray(clone.선택시작시간) || clone.선택시작시간.length === 0) {
        clone.선택시작시간 = [{ 시간: '00:00' }];
      }
      if (Array.isArray(clone.일정타입)) {
        clone.일정타입 = clone.일정타입.filter(it => it && it.코드 != null);
      }
      if (clone.선택일정타입 == null && Array.isArray(clone.일정타입) && clone.일정타입.length === 0) {
        // 선택 불가능하면 키만 비워둠(서버에서 기본 처리하도록)
        delete clone.선택일정타입;
      }
      // 가격 저장 시 일정타입 정보를 사용자 선택값 우선 반영, 없으면 API에서 조회
      let resolvedItineraryTypeNo;
      let resolvedItineraryTypeName = '';
      try {
        let typeNo = selectedItineraryTypeNo;
        let typeName = '';
        if (typeNo && priceItineraryTypes.length > 0) {
          const found = priceItineraryTypes.find(t => Number(t.일정타입번호) === Number(typeNo));
          typeName = found ? String(found.일정타입명 || '대표일정') : '대표일정';
        }
        if (!Number.isFinite(typeNo)) {
          const itinData = await getItineraryType({ productNo: Number(productId) });
          const row = (Array.isArray(itinData?.table) && itinData.table.length) ? itinData.table[0] : null;
          typeNo = row ? Number(row.일정타입번호 ?? row.itineraryTypeNo ?? row.번호) : undefined;
          typeName = row ? String(row.일정타입명 ?? row.itineraryTypeName ?? row.이름 ?? '대표일정') : '대표일정';
        }
        if (Number.isFinite(typeNo)) {
          clone.선택일정타입 = typeNo;
          clone.일정타입 = [{ 선택: false, 코드: typeNo, 코드명: typeName || '대표일정' }];
          resolvedItineraryTypeNo = Number(typeNo);
          resolvedItineraryTypeName = String(typeName || '대표일정');
        }
      } catch (_) {
        // ignore; keep existing if fetch fails
      }
      // Inject required meta fields (only if missing to avoid overwriting server-provided values)
      try {
        if (clone && typeof clone === 'object') {
          // 노출채널
          if (!Array.isArray(clone.노출채널) || clone.노출채널.length === 0) {
            clone.노출채널 = [
              { 선택: true, 코드: '웹노출여부', 코드명: '웹노출여부' },
              { 선택: true, 코드: '대리점노출여부', 코드명: '대리점노출여부' },
            ];
          }
          // 수수료 관련 (기본값 주입)
          if (typeof clone.플랫폼수수료적용여부 === 'undefined') clone.플랫폼수수료적용여부 = true;
          if (typeof clone.플랫폼수수료 === 'undefined') clone.플랫폼수수료 = 0.01;
          if (typeof clone.결제수수료적용여부 === 'undefined') clone.결제수수료적용여부 = true;
          if (typeof clone.결제수수료 === 'undefined') clone.결제수수료 = 0.03;
          // 기존데이터 업데이트 플래그
          clone.기존데이터업데이트여부 = true;
          // 커미션타입: 사용자 선택값 반영 (정률 또는 정액)
          clone.커미션타입 = commissionType || '정률';
          // 상품일정타입 / 코드 (가능할 때만 보강)
          if (!clone.상품일정타입 || !clone.상품일정타입코드) {
            try {
              const itinData2 = await getItineraryType({ productNo: Number(productId) });
              const r2 = (Array.isArray(itinData2?.table) && itinData2.table.length) ? itinData2.table[0] : null;
              const tName = r2 ? String(r2.일정타입명 ?? r2.itineraryTypeName ?? '대표일정') : undefined;
              const tCode = r2 ? (String(r2.일정타입코드 ?? r2.itineraryTypeCode ?? '')) : '';
              if (!clone.상품일정타입 && tName) clone.상품일정타입 = tName;
              if (!clone.상품일정타입코드 && tCode) clone.상품일정타입코드 = tCode;
            } catch (_) { }
          }
        }
      } catch (_) { }
      // 항상 현재 화면의 상품번호를 강제 반영하여 타 상품번호가 포함되는 것을 방지
      clone.상품번호 = Number(productId);
      // 저장 직전 선택요금제 ↔ 상품자원요금제 매핑 일관성 강제 + 불필요한 요금제 제거
      try {
        const selectedPlanNos = new Set((Array.isArray(clone.선택요금제) ? clone.선택요금제 : [])
          .map(it => Number(it?.가격옵션번호))
          .filter(Number.isFinite));
        if (Array.isArray(clone.상품자원요금제)) {
          clone.상품자원요금제 = clone.상품자원요금제.map(group => {
            let list = Array.isArray(group?.요금제리스트) ? [...group.요금제리스트] : [];
            // 보강: 누락된 선택 요금제 추가
            selectedPlanNos.forEach(no => {
              if (!list.some(x => Number(x?.가격옵션번호) === Number(no))) {
                list.push({ 가격옵션번호: Number(no), 가격옵션명: '기본요금제', 합산가격여부: 'N' });
              }
            });
            // 정리: 선택 요금제 외 항목 제거
            list = list.filter(x => selectedPlanNos.has(Number(x?.가격옵션번호)));
            return { ...group, 요금제리스트: list };
          });
        }
        // 요금제 목록도 선택 요금제만 남기기
        if (Array.isArray(clone.요금제)) {
          clone.요금제 = clone.요금제.filter(p => selectedPlanNos.has(Number(p?.요금제번호)));
        }
      } catch (_) { }
      const priceInfoJson = JSON.stringify(clone);
      const 합산가격여부 = !!clone.합산가격여부;
      await saveTourPriceInfo({ priceInfoJson, userNo, companyNo, 합산가격여부 });
      try {
        const pname = String(productName || '').trim();
        const tname = String(resolvedItineraryTypeName || '').trim();
        const shouldSync = Array.isArray(priceItineraryTypes) && priceItineraryTypes.length > 1 && pname && tname;
        if (shouldSync) {
          const priceNameToSave = `${pname} (${tname})`;
          const applied = new Set();
          datesToApply.forEach((d) => {
            if (!isSelectedDow(d)) return;
            applied.add(formatYMD(d));
          });
          const appliedDates = Array.from(applied).sort();
          if (appliedDates.length > 0 && Number.isFinite(Number(resolvedItineraryTypeNo)) && Number(resolvedItineraryTypeNo) > 0) {
            let selected = [];
            for (let attempt = 0; attempt < 3 && selected.length === 0; attempt += 1) {
              if (attempt > 0) {
                await new Promise((r) => setTimeout(r, 350));
              }
              const resp = await getPriceStockCalendar({
                productTypeName: '투어',
                productNo: Number(productId),
                companyNo: String(companyNo),
                startDate: appliedDates[0],
                endDate: appliedDates[appliedDates.length - 1],
                판매된리스트만보기: false,
                플랫폼수수료반영: true,
                결제수수료반영: true,
              }).catch(() => null);
              const list = Array.isArray(resp?.table)
                ? resp.table
                : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.table1) ? resp.table1 : (Array.isArray(resp?.Table1) ? resp.Table1 : [])));
              const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);
              const byNo = new Set();
              (Array.isArray(list) ? list : []).forEach((row) => {
                const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                const ymd = dateStr ? String(dateStr).slice(0, 10) : '';
                if (!ymd || !applied.has(ymd)) return;
                const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo', 'PlanNo', 'priceNo']);
                const optionNum = Number(optionNo);
                if (Number.isFinite(optionNum) && Number.isFinite(Number(planNoToUse)) && optionNum !== Number(planNoToUse)) return;
                if (!Number.isFinite(optionNum)) {
                  const pat = String(tryGet(row, ['항공패턴', '항공', '패턴명', 'pattern']) || '').trim();
                  const pln = String(tryGet(row, ['요금제', '가격명', 'pricePlan', 'PlanName']) || '').trim();
                  if (!pat || !pln) return;
                  if (String(pat) !== String(effectivePattern)) return;
                  if (String(pln) !== String(effectivePlan)) return;
                }
                const itinNo = tryGet(row, ['일정타입번호', 'itineraryTypeNo', 'ItineraryTypeNo']);
                const itinNum = Number(itinNo);
                if (!Number.isFinite(itinNum) || itinNum !== Number(resolvedItineraryTypeNo)) return;
                const priceNo = tryGet(row, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo', 'priceId', 'PriceId']);
                const n = Number(priceNo);
                if (Number.isFinite(n) && n > 0) byNo.add(n);
              });
              selected = Array.from(byNo).map((n) => ({ 가격번호: n }));
            }
            if (selected.length > 0) {
              await savePriceNameBulkByPriceNos({
                productNo: Number(productId),
                companyNo: String(companyNo),
                userNo: String(userNo),
                selected,
                priceName: priceNameToSave,
              });
            }
          }
        }
      } catch (e) {
        try { console.warn('[PriceStock] price name sync failed (bulk save)', e); } catch (_) { }
      }
      // Avoid immediate refresh to prevent overwriting optimistic values before server reflects
      setTimeout(() => { try { refreshCalendarForCurrentMonth(); } catch (_) { } }, 1200);
      alert(['저장되었습니다.', ...saveExtraMessages].filter(Boolean).join('\n\n'));
    } catch (err) {
      console.error('SP 저장 실패', err);
      alert('저장에 실패했습니다.');
    }
  };

  // ── AI 어시스턴트용 화면 도구 ──────────────────────────────
  // 이 화면의 저장은 SP 만으로 조립이 안 된다. 재고 SP 의 @선택행사 에는 자원번호가,
  // 가격 SP 의 @가격정보 에는 화면이 만드는 큰 JSON 이 들어가기 때문이다.
  // 그래서 AI 가 SP 를 손으로 짜게 두지 않고, 사람이 [일괄 적용] 버튼으로 쓰는 applyBulk 를
  // 그대로 부르게 한다. 자원번호·가격번호는 applyBulk 가 화면 상태에서 알아서 꺼내 쓴다.

  /**
   * applyBulk 는 실패를 alert 로 알리고 그냥 return 한다(반환값이 없다).
   * 그래서 실행하는 동안 alert 를 가로채 두고, 무슨 말이 나왔는지로 성공/실패를 판단한다.
   * 이렇게 하면 사용자가 화면에서 보게 될 문구가 그대로 AI 에게 전달된다.
   */
  const runApplyBulkCapturingAlerts = async (payload) => {
    const captured = [];
    const originalAlert = window.alert;
    window.alert = (message) => { captured.push(String(message ?? '')); };
    try {
      await applyBulk(payload);
    } finally {
      window.alert = originalAlert;
    }
    return captured;
  };

  const aiPlanList = (allPlansByPattern?.[selectedPattern] || Object.keys(dataByPattern?.[selectedPattern] || {}));
  const aiResourceNo = (
    planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.resourceNo ??
    resourceNoCache?.[selectedPattern]?.[selectedPlan]
  );

  useScreenTools(() => ({
    screen: '가격/재고 설정 (투어)',
    description:
      '한 투어 상품의 날짜별 판매가·재고를 관리하는 화면. 항공패턴 > 요금제 아래에 날짜별 가격과 재고가 달린다.',
    state: {
      상품번호: productId,
      상품명: selectedProduct?.상품명 || '',
      선택된항공패턴: selectedPattern || '(아직 선택 안 됨)',
      항공패턴목록: allPatterns,
      선택된요금제: selectedPlan || '(아직 선택 안 됨)',
      요금제목록: aiPlanList,
      자원번호: Number(aiResourceNo) > 0 ? aiResourceNo : '없음 (재고 저장 불가 — 요금제를 먼저 선택해야 함)',
    },
    actions: [
      {
        name: 'price_stock_read',
        description:
          '지금 선택된 항공패턴·요금제의 날짜별 가격/재고를 읽는다. 저장하기 전 현재 값을 확인할 때 쓴다.',
        parameters: {
          type: 'object',
          properties: {
            시작일: { type: 'string', description: 'YYYY-MM-DD' },
            종료일: { type: 'string', description: 'YYYY-MM-DD. 생략하면 시작일과 같은 날 하루만.' },
          },
          required: ['시작일'],
        },
        run: async ({ 시작일, 종료일 }) => {
          if (!selectedPattern || !selectedPlan) {
            return { 오류: '항공패턴과 요금제가 아직 선택되지 않았습니다. 사용자에게 화면에서 먼저 골라 달라고 안내하세요.' };
          }
          const from = String(시작일 || '').slice(0, 10);
          const to = String(종료일 || 시작일 || '').slice(0, 10);
          const days = dataByPattern?.[selectedPattern]?.[selectedPlan] || {};
          const rows = Object.keys(days)
            .filter((key) => key >= from && key <= to)
            .sort()
            .map((key) => {
              const d = days[key] || {};
              return {
                날짜: key,
                성인가: d.adult ?? null,
                어린이가: d.child ?? null,
                영유아가: d.infant ?? null,
                재고: d.stock ?? null,
                마감: !!d.closed,
              };
            });
          return {
            항공패턴: selectedPattern,
            요금제: selectedPlan,
            찾은개수: rows.length,
            목록: rows,
            ...(rows.length ? {} : { 안내: '그 기간에는 등록된 가격/재고가 없습니다.' }),
          };
        },
      },
      {
        name: 'price_stock_apply',
        description:
          '지금 선택된 항공패턴·요금제에 날짜별 가격과 재고를 일괄 적용한다. 화면의 [일괄 적용] 버튼과 똑같이 동작한다. ' +
          '값을 준 항목만 바뀌고, 안 준 항목은 건드리지 않는다. 실행 전에 사용자 확인을 받는다.',
        write: true,
        label: '가격/재고 화면 — 일괄 적용',
        parameters: {
          type: 'object',
          properties: {
            시작일: { type: 'string', description: '적용 시작일 YYYY-MM-DD' },
            종료일: { type: 'string', description: '적용 종료일 YYYY-MM-DD. 하루만 적용하면 시작일과 같게 쓴다.' },
            날짜목록: {
              type: 'array',
              items: { type: 'string' },
              description: '띄엄띄엄한 날짜들. 이걸 주면 시작일/종료일 대신 이 날짜들만 적용한다.',
            },
            요일: {
              type: 'array',
              items: { type: 'string' },
              description: '월, 수 처럼 특정 요일에만 적용. 생략하면 기간 내 모든 날.',
            },
            성인가: { type: 'string', description: '성인 판매가. 원 단위 숫자만 (예: 750000)' },
            어린이가: { type: 'string', description: '어린이 판매가' },
            영유아가: { type: 'string', description: '영유아 판매가' },
            성인제세공과금: { type: 'string' },
            어린이제세공과금: { type: 'string' },
            영유아제세공과금: { type: 'string' },
            성인원가: { type: 'string' },
            어린이원가: { type: 'string' },
            영유아원가: { type: 'string' },
            재고수: { type: 'string', description: '해당 날짜들에 설정할 재고 수량' },
            마감: { type: 'boolean', description: 'true 면 판매 마감 처리' },
            summary: { type: 'string', description: '사용자 확인 카드에 보여줄 한 줄 설명' },
          },
          required: ['summary'],
        },
        preview: (args) => ({
          항공패턴: selectedPattern,
          요금제: selectedPlan,
          기간: args.날짜목록?.length
            ? args.날짜목록.join(', ')
            : `${args.시작일 || '?'} ~ ${args.종료일 || args.시작일 || '?'}`,
          요일: args.요일?.length ? args.요일.join(', ') : '전체',
          성인가: args.성인가,
          어린이가: args.어린이가,
          영유아가: args.영유아가,
          재고수: args.재고수,
          마감: args.마감 ? '마감' : undefined,
        }),
        run: async (args) => {
          if (!selectedPattern || !selectedPlan) {
            return { 오류: '항공패턴과 요금제가 아직 선택되지 않았습니다. 사용자에게 화면에서 먼저 골라 달라고 안내하세요.' };
          }
          const dates = Array.isArray(args.날짜목록) ? args.날짜목록.filter(Boolean) : [];
          const start = String(args.시작일 || '').slice(0, 10);
          const end = String(args.종료일 || args.시작일 || '').slice(0, 10);
          if (!dates.length && !start) {
            return { 오류: '적용할 날짜가 없습니다. 시작일/종료일 또는 날짜목록이 필요합니다.' };
          }
          const wantsStock = String(args.재고수 ?? '').trim() !== '';
          if (wantsStock && !(Number(aiResourceNo) > 0)) {
            return {
              오류:
                '재고를 저장하려면 자원번호가 필요한데 지금 선택된 요금제에 자원번호가 없습니다. ' +
                '사용자에게 화면에서 요금제를 선택하거나 요금제·자원 매핑을 확인해 달라고 안내하세요.',
            };
          }

          const messages = await runApplyBulkCapturingAlerts({
            rangeStart: start || dates[0],
            rangeEnd: end || dates[dates.length - 1],
            selectedDates: dates.length ? dates : undefined,
            weekdays: Array.isArray(args.요일) && args.요일.length ? { names: args.요일 } : undefined,
            bulk: {
              pa: args.성인가 ?? '',
              pc: args.어린이가 ?? '',
              pi: args.영유아가 ?? '',
              ta: args.성인제세공과금 ?? '',
              tc: args.어린이제세공과금 ?? '',
              ti: args.영유아제세공과금 ?? '',
              ca: args.성인원가 ?? '',
              cc: args.어린이원가 ?? '',
              ci: args.영유아원가 ?? '',
              ma: '',
              mc: '',
              mi: '',
              stock: args.재고수 ?? '',
              closed: !!args.마감,
            },
            useSharedStock: false,
            sharedSeriesNo: null,
            sharedSeries: null,
          });

          const succeeded = messages.some((m) => m.startsWith('저장되었습니다.'));
          if (!succeeded) {
            return {
              오류: messages.length
                ? messages.join(' / ')
                : '적용되지 않았습니다. 화면 조건(패턴·요금제·기간)을 확인하세요.',
            };
          }
          return {
            결과: '적용했습니다.',
            항공패턴: selectedPattern,
            요금제: selectedPlan,
            ...(messages.length > 1 ? { 참고: messages.slice(1).join(' / ') } : {}),
          };
        },
      },
    ],
  }));

  const parseNum = (s) => {
    const n = Number(String(s ?? '').toString().replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const parseNonNegativeInt = (s) => {
    const n = Math.floor(parseNum(s));
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const loadBasePriceInfo = async (planNo) => {
    try {
      setBaseLoading(true);
      const { companyNo } = getUserInfo() || {};
      if (!companyNo || !productId || !planNo) return null;
      const data = await getPriceReservationInfo({ productNo: productId, companyNo, priceNo: planNo });
      const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
      const row = Array.isArray(tableAny) && tableAny.length ? tableAny[0] : null;
      const priceInfoStr = row?.가격정보 || null;
      // If no base price info, construct a sensible default so new entries can be saved
      if (!priceInfoStr) {
        // Try to prefill currency from selectedProduct or fetch details
        let defCode = String(selectedProduct?.통화코드 || '').trim();
        let defSymbol = String(selectedProduct?.통화 || '').trim();
        if (!defCode || !defSymbol) {
          try {
            const details = await getProductDetails(productId, companyNo).catch(() => null);
            if (details) {
              defCode = String(details.통화코드 || details.basic?.통화코드 || defCode || '').trim();
              defSymbol = String(details.통화 || details.basic?.통화 || defSymbol || '').trim();
            }
          } catch (_) { }
        }
        // Map 3-letter currency codes to display symbols when needed (e.g., KRW -> ￦)
        const toSymbol = (val) => {
          const v = String(val || '').toUpperCase();
          const map = { KRW: '￦', USD: '$', EUR: '€', JPY: '¥', CNY: '元', AUD: 'A$', NZD: 'NZ$', HKD: 'HK$', SGD: 'S$', GBP: '£', CAD: 'CA$', THB: '฿', VND: '₫', PHP: '₱' };
          return map[v] || val || '';
        };
        const displaySymbol = toSymbol(defSymbol);
        const defaultObj = {
          상품번호: Number(productId) || undefined,
          전일종가: 1,
          통화코드: defCode || '',
          통화기호: displaySymbol || '',
          적용환율: defCode ? [
            { 통화코드: defCode, 통화기호: displaySymbol || '', 적용환율: 1, 실시간환율: 1, 평균환율: 1 }
          ] : [],
          합산가격여부: false,
          가상모객사용: false,
          가상모객인원: 0,
          가상모객출발몇일전: 0,
          원가구분: '2',
          판매가: [],
          총원가: [
            { 구분: '총원가', 통화코드: defCode || '', 통화기호: displaySymbol || '', 성인: 0, 어린이: 0, 영유아: 0 },
          ],
          // 기본 세부 원가는 '통합원가(233013)'를 우선 적용
          세부원가: [
            {
              번호: 1,
              원가항목: '통합원가',
              원가항목코드: '233013',
              통화코드: defCode || '',
              통화기호: displaySymbol || '',
              환율적용방식: '전일종가',
              환율적용방식코드: '312001',
              적용환율: 1,
              성인: 0,
              어린이: 0,
              영유아: 0,
            },
          ],
          적용기간: [],
          선택요금제: [],
        };
        setBasePriceInfo(defaultObj);
        return defaultObj;
      }
      let arr = [];
      try { arr = JSON.parse(priceInfoStr); } catch (_) { arr = []; }
      const obj = Array.isArray(arr) && arr.length ? arr[0] : null;
      // If parsing produced no object, still return a default object
      if (!obj) {
        // Try to prefill currency from selectedProduct or fetch details
        let defCode = String(selectedProduct?.통화코드 || '').trim();
        let defSymbol = String(selectedProduct?.통화 || '').trim();
        if (!defCode || !defSymbol) {
          try {
            const details = await getProductDetails(productId, companyNo).catch(() => null);
            if (details) {
              defCode = String(details.통화코드 || details.basic?.통화코드 || defCode || '').trim();
              defSymbol = String(details.통화 || details.basic?.통화 || defSymbol || '').trim();
            }
          } catch (_) { }
        }
        const toSymbol = (val) => {
          const v = String(val || '').toUpperCase();
          const map = { KRW: '￦', USD: '$', EUR: '€', JPY: '¥', CNY: '元', AUD: 'A$', NZD: 'NZ$', HKD: 'HK$', SGD: 'S$', GBP: '£', CAD: 'CA$', THB: '฿', VND: '₫', PHP: '₱' };
          return map[v] || val || '';
        };
        const displaySymbol = toSymbol(defSymbol);
        const defaultObj = {
          상품번호: Number(productId) || undefined,
          전일종가: 1,
          통화코드: defCode || '',
          통화기호: displaySymbol || '',
          적용환율: defCode ? [
            { 통화코드: defCode, 통화기호: displaySymbol || '', 적용환율: 1, 실시간환율: 1, 평균환율: 1 }
          ] : [],
          합산가격여부: false,
          원가구분: '2',
          판매가: [],
          총원가: [
            { 구분: '총원가', 통화코드: defCode || '', 통화기호: displaySymbol || '', 성인: 0, 어린이: 0, 영유아: 0 },
          ],
          세부원가: [
            {
              번호: 1,
              원가항목: '통합원가',
              원가항목코드: '233013',
              통화코드: defCode || '',
              통화기호: displaySymbol || '',
              환율적용방식: '전일종가',
              환율적용방식코드: '312001',
              적용환율: 1,
              성인: 0,
              어린이: 0,
              영유아: 0,
            },
          ],
          적용기간: [],
          선택요금제: [],
        };
        setBasePriceInfo(defaultObj);
        return defaultObj;
      }
      setBasePriceInfo(obj || null);
      return obj || null;
    } catch (e) {
      console.warn('loadBasePriceInfo failed', e);
      return null;
    } finally {
      setBaseLoading(false);
    }
  };

  useEffect(() => {
    if (!basePriceInfo || typeof basePriceInfo !== 'object') return;
    setVirtualRecruit({
      use: !!basePriceInfo.가상모객사용,
      count: String(basePriceInfo.가상모객인원 ?? 0),
      daysBefore: String(basePriceInfo.가상모객출발몇일전 ?? 0),
    });
  }, [basePriceInfo]);

  const handleSavePriceChanges = async () => {
    try {
      if (!pendingChanges.length) {
        alert('저장할 변경사항이 없습니다.');
        return;
      }
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        alert('사용자 정보가 없습니다. 다시 로그인해주세요.');
        return;
      }
      // Aggregate by planNo to determine which base to load (assume same plan currently)
      const planNos = Array.from(new Set(pendingChanges.map(c => c.planNo).filter(Boolean)));
      const primaryPlanNo = planNos[0];
      let base = basePriceInfo;
      if (!base || !primaryPlanNo) {
        base = await loadBasePriceInfo(primaryPlanNo);
      }
      if (!base) {
        alert('기준 가격정보를 불러오지 못했습니다.');
        return;
      }

      // Deep clone base and merge all changes
      const clone = JSON.parse(JSON.stringify(base));

      try {
        const vr = (typeof virtualRecruit === 'object' && virtualRecruit) ? virtualRecruit : null;
        if (vr) {
          clone.가상모객사용 = !!vr.use;
          clone.가상모객인원 = parseNonNegativeInt(vr.count);
          clone.가상모객출발몇일전 = parseNonNegativeInt(vr.daysBefore);
        }
      } catch (_) { }

      // 합산가격여부는 base 값을 그대로 사용
      const 합산가격여부 = !!clone.합산가격여부;

      // 선택요금제: 변경 대상 planNos만 포함하도록 정리
      const 기존선택요금제2 = Array.isArray(clone.선택요금제) ? clone.선택요금제 : [];
      const 선택요금제Set = new Map();
      planNos.forEach(no => {
        if (no != null) 선택요금제Set.set(Number(no), { 가격옵션번호: Number(no), 합산가격여부: 'N' });
      });
      clone.선택요금제 = Array.from(선택요금제Set.values());

      // 요금제/상품자원요금제 구성 보강: 변경 대상 planNos 기준으로 재구성
      try {
        // 요금제 목록
        const mergedPlans2 = [];
        planNos.forEach(no => {
          if (no == null) return;
          // plan name best-effort from current selection if matches, else keep 번호만
          const name = (no === (planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.priceNo)) ? selectedPlan : '기본요금제';
          mergedPlans2.push({ 요금제번호: Number(no), 요금제명: String(name) });
        });
        clone.요금제 = mergedPlans2;

        // 상품자원요금제 목록
        const sry2 = [];
        planNos.forEach(no => {
          if (no == null) return;
          const pat = selectedPattern; // best-effort for current UI context
          const pln = selectedPlan;
          const rno = (
            planMetaByPattern?.[pat]?.[pln]?.resourceNo ??
            resourceNoCache?.[pat]?.[pln]
          );
          const resName = String(pat || '기초상품자원');
          let grp = sry2.find(x => Number(x?.자원번호) === Number(rno));
          if (!grp) {
            grp = { 자원번호: Number(rno || 0), 자원명: resName, 요금제리스트: [] };
            sry2.push(grp);
          }
          if (!grp.요금제리스트.some(x => Number(x?.가격옵션번호) === Number(no))) {
            grp.요금제리스트.push({ 가격옵션번호: Number(no), 가격옵션명: String(pln || '기본요금제'), 합산가격여부: 'N' });
          }
        });
        clone.상품자원요금제 = sry2;
      } catch (_) { }

      // 적용기간: 모든 변경 범위를 추가 (중복은 간단히 허용). 요일 선택값이 있으면 반영
      const 적용기간 = Array.isArray(clone.적용기간) ? clone.적용기간 : [];
      pendingChanges.forEach(ch => {
        const names = (ch?.weekdays && Array.isArray(ch.weekdays.names) && ch.weekdays.names.length)
          ? ch.weekdays.names
          : ['월', '화', '수', '목', '금', '토', '일'];
        const mask = (ch?.weekdays && typeof ch.weekdays.mask === 'string' && ch.weekdays.mask.length === 7)
          ? ch.weekdays.mask
          : 'YYYYYYY';
        적용기간.push({ 시작일: ch.rangeStart, 종료일: ch.rangeEnd, 요일: names, 요일2: mask });
      });
      clone.적용기간 = 적용기간;

      // 판매가: 항목 '판매가' 갱신 (마지막 변경 우선)
      const 판매가목록 = Array.isArray(clone.판매가) ? clone.판매가 : [];
      const 판매가Index = 판매가목록.findIndex(x => x.항목 === '판매가');
      const lastChange = pendingChanges[pendingChanges.length - 1];
      const currency = lastChange?.currency || clone.통화기호 || '';
      const 판매가Row = {
        ...(판매가Index >= 0 ? 판매가목록[판매가Index] : { 항목: '판매가', 번호: 1, 통화코드: clone.통화코드 || '', 통화기호: currency }),
        통화기호: currency || (판매가Index >= 0 ? 판매가목록[판매가Index].통화기호 : clone.통화기호 || ''),
        성인: parseNum(lastChange?.price?.pa),
        어린이: parseNum(lastChange?.price?.pc),
        영유아: parseNum(lastChange?.price?.pi),
      };
      if (판매가Index >= 0) 판매가목록[판매가Index] = 판매가Row; else 판매가목록.push(판매가Row);
      // 판매가: Tax및유류할증료 갱신/추가 (마지막 변경 기준)
      const 세금Index2 = 판매가목록.findIndex(x => x.항목 === 'Tax및유류할증료');
      const 세금Row2 = {
        ...(세금Index2 >= 0 ? 판매가목록[세금Index2] : { 항목: 'Tax및유류할증료', 번호: 2, 통화코드: clone.통화코드 || '', 통화기호: currency }),
        통화기호: currency || (세금Index2 >= 0 ? 판매가목록[세금Index2].통화기호 : clone.통화기호 || ''),
        성인: parseNum(lastChange?.tax?.ta),
        어린이: parseNum(lastChange?.tax?.tc),
        영유아: parseNum(lastChange?.tax?.ti),
      };
      if (세금Index2 >= 0) 판매가목록[세금Index2] = 세금Row2; else 판매가목록.push(세금Row2);
      // 판매가: 커미션 항목 갱신/추가 (원가 탭 커미션 입력 반영)
      const 커미션Index2 = 판매가목록.findIndex(x => x.항목 === '커미션');
      const hasCommissionInput2 = !!(bulk.ma || bulk.mc || bulk.mi);
      const 커미션Row2 = {
        ...(커미션Index2 >= 0 ? 판매가목록[커미션Index2] : { 항목: '커미션', 번호: 3, 통화코드: clone.통화코드 || '', 통화기호: currency || clone.통화기호 || '' }),
        // 커미션이 입력되어 있으면 해당 값 저장, 아니면 0
        // 정액 커미션일 때는 문자열로 저장, 정률일 때는 숫자로 저장
        성인: hasCommissionInput2 ? (commissionType === '정액' ? String(parseNum(bulk.ma)) : parseNum(bulk.ma)) : 0,
        어린이: hasCommissionInput2 ? (commissionType === '정액' ? String(parseNum(bulk.mc)) : parseNum(bulk.mc)) : 0,
        영유아: hasCommissionInput2 ? (commissionType === '정액' ? String(parseNum(bulk.mi)) : parseNum(bulk.mi)) : 0,
        통화코드: clone.통화코드 || '',
        통화기호: currency || clone.통화기호 || '',
      };
      if (커미션Index2 >= 0) 판매가목록[커미션Index2] = 커미션Row2; else 판매가목록.push(커미션Row2);
      clone.판매가 = 판매가목록;
      // 커미션타입: 사용자 선택값 반영 (정률 또는 정액)
      try { clone.커미션타입 = commissionType || '정률'; } catch (_) { }

      // 원가구분 / 총원가 / 세부원가 갱신 (마지막 변경 우선)
      // 세부원가 입력 유무에 따라 원가구분 동적 설정
      const hasDetailInput2 = Array.isArray(lastChange?.detailCosts) && lastChange.detailCosts.length > 0;
      clone.원가구분 = hasDetailInput2 ? '2' : '1';
      const 총원가목록 = [{ 구분: '총원가', 통화코드: clone.통화코드 || '', 통화기호: currency || clone.통화기호 || '', 성인: parseNum(lastChange?.cost?.ca), 어린이: parseNum(lastChange?.cost?.cc), 영유아: parseNum(lastChange?.cost?.ci) }];
      clone.총원가 = 총원가목록;
      const 세부원가목록 = Array.isArray(lastChange?.detailCosts)
        ? lastChange.detailCosts.map((it, idx) => ({
          번호: idx + 1,
          원가항목: String(it.원가항목 || ''),
          항목: String(it.원가항목 || ''),
          원가항목코드: String(it.원가항목코드 || ''),
          통화코드: clone.통화코드 || '',
          통화기호: it.통화기호 || currency || clone.통화기호 || '',
          성인: parseNum(it.성인),
          어린이: parseNum(it.어린이),
          영유아: parseNum(it.영유아),
        }))
        : (clone.세부원가 || []);
      clone.세부원가 = (세부원가목록 && 세부원가목록.length) ? 세부원가목록 : [{ 번호: 1, 원가항목: null, 통화코드: clone.통화코드 || '', 통화기호: currency || clone.통화기호 || '', 성인: parseNum(lastChange?.cost?.ca), 어린이: parseNum(lastChange?.cost?.cc), 영유아: parseNum(lastChange?.cost?.ci) }];

      // Optimistic local calendar/data update for immediate prefill
      try {
        const dowNames = ['일', '월', '화', '수', '목', '금', '토'];
        const isSelectedDow = (dateObj, ch) => {
          const w = ch?.weekdays;
          if (!w || !Array.isArray(w.names) || w.names.length === 0) return true;
          const name = dowNames[dateObj.getDay()];
          return w.names.includes(name);
        };
        const applyChangeDates = (ch) => {
          const dates = [];
          const s = new Date(ch.rangeStart + 'T00:00:00');
          const e = new Date(ch.rangeEnd + 'T00:00:00');
          if (isNaN(s) || isNaN(e)) return dates;
          for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
            const dd = new Date(d);
            if (isSelectedDow(dd, ch)) dates.push(formatYMD(dd));
          }
          return dates;
        };
        const pat = selectedPattern;
        const pln = selectedPlan;
        if (pat && pln) {
          setDataByPattern((prev) => {
            const cur = { ...((prev?.[pat]?.[pln]) || {}) };
            pendingChanges.forEach((ch) => {
              const dates = applyChangeDates(ch);
              dates.forEach((key) => {
                const price = {
                  adult: parseNum(ch?.price?.pa),
                  child: parseNum(ch?.price?.pc),
                  infant: parseNum(ch?.price?.pi),
                  taxAdult: parseNum(ch?.tax?.ta),
                  taxChild: parseNum(ch?.tax?.tc),
                  taxInfant: parseNum(ch?.tax?.ti),
                  costAdult: parseNum(ch?.cost?.ca),
                  costChild: parseNum(ch?.cost?.cc),
                  costInfant: parseNum(ch?.cost?.ci),
                  closed: !!ch?.closed,
                  currency: ch?.currency || (basePriceInfo?.통화기호 || ''),
                  costDivision: (Array.isArray(ch?.detailCosts) && ch.detailCosts.length > 0) ? '2' : '1',
                };
                cur[key] = { ...(cur[key] || {}), price };
              });
            });
            const next = { ...(prev || {}), [pat]: { ...(prev?.[pat] || {}), [pln]: cur } };
            return next;
          });
          // Nudge calendar to repaint
          setCalendarRerenderTick((t) => t + 1);
        }
      } catch (_) { }

      // 항상 현재 화면의 상품번호를 강제 반영
      clone.상품번호 = Number(productId);
      // 가격 저장 시 일정타입 정보를 사용자 선택값 우선 반영, 없으면 API에서 조회
      let resolvedItineraryTypeNo;
      let resolvedItineraryTypeName = '';
      try {
        let typeNo = selectedItineraryTypeNo;
        let typeName = '';
        if (typeNo && priceItineraryTypes.length > 0) {
          const found = priceItineraryTypes.find(t => Number(t.일정타입번호) === Number(typeNo));
          typeName = found ? String(found.일정타입명 || '대표일정') : '대표일정';
        }
        if (!Number.isFinite(typeNo)) {
          // fallback: API에서 조회
          const itinData = await getItineraryType({ productNo: Number(productId) });
          const row = (Array.isArray(itinData?.table) && itinData.table.length) ? itinData.table[0] : null;
          typeNo = row ? Number(row.일정타입번호 ?? row.itineraryTypeNo ?? row.번호) : undefined;
          typeName = row ? String(row.일정타입명 ?? row.itineraryTypeName ?? row.이름 ?? '대표일정') : '대표일정';
        }
        if (Number.isFinite(typeNo)) {
          clone.선택일정타입 = typeNo;
          clone.일정타입 = [{ 선택: false, 코드: typeNo, 코드명: typeName || '대표일정' }];
          resolvedItineraryTypeNo = Number(typeNo);
          resolvedItineraryTypeName = String(typeName || '대표일정');
        }
      } catch (_) {
        // ignore; keep existing if fetch fails
      }
      // 시작시간 정규화 및 매핑 일관성 강제 (정상 페이로드와 동일하게)
      if (!clone.행사시작시간 || (Array.isArray(clone.행사시작시간) && clone.행사시작시간.length === 0)) {
        clone.행사시작시간 = null;
      }
      if (!Array.isArray(clone.선택시작시간) || clone.선택시작시간.length === 0) {
        clone.선택시작시간 = [{ 시간: '00:00' }];
      }
      try {
        // 1) 현재 패턴의 유효 가격옵션번호 집합
        const metaForPattern2 = planMetaByPattern?.[selectedPattern] || {};
        const validPriceNos2 = new Set(Object.values(metaForPattern2).map(m => Number(m?.priceNo)).filter(Number.isFinite));
        // 2) 선택요금제에서 번호 수집 후 유효성 필터링
        let selectedPlanNos2 = new Set((Array.isArray(clone.선택요금제) ? clone.선택요금제 : [])
          .map(it => Number(it?.가격옵션번호))
          .filter(no => Number.isFinite(no) && (validPriceNos2.size === 0 || validPriceNos2.has(no))));
        // 3) 하나도 남지 않으면 첫 유효 번호로 보정
        if (selectedPlanNos2.size === 0 && validPriceNos2.size > 0) {
          selectedPlanNos2 = new Set([Array.from(validPriceNos2)[0]]);
        }
        // 4) 선택요금제 / 요금제 재구성
        clone.선택요금제 = Array.from(selectedPlanNos2).map(no => ({ 가격옵션번호: Number(no), 합산가격여부: 'N' }));
        clone.요금제 = Array.from(selectedPlanNos2).map(no => ({ 요금제번호: Number(no), 요금제명: String(selectedPlan || '기본요금제') }));
        // 5) 상품자원요금제 매핑 보강 + 정리
        if (Array.isArray(clone.상품자원요금제)) {
          clone.상품자원요금제 = clone.상품자원요금제.map(group => {
            let list = Array.isArray(group?.요금제리스트) ? [...group.요금제리스트] : [];
            // 누락된 선택 번호 보강
            selectedPlanNos2.forEach(no => {
              if (!list.some(x => Number(x?.가격옵션번호) === Number(no))) {
                list.push({ 가격옵션번호: Number(no), 가격옵션명: String(selectedPlan || '기본요금제'), 합산가격여부: 'N' });
              }
            });
            // 선택 번호 외 제거
            list = list.filter(x => selectedPlanNos2.has(Number(x?.가격옵션번호)));
            return { ...group, 요금제리스트: list };
          });
        }
      } catch (_) { }
      // 최종 JSON 문자열화 후 단일 호출
      const priceInfoJson = JSON.stringify(clone);
      await saveTourPriceInfo({ priceInfoJson, userNo, companyNo, 합산가격여부 });
      try {
        const pname = String(productName || '').trim();
        const tname = String(resolvedItineraryTypeName || '').trim();
        const shouldSync = Array.isArray(priceItineraryTypes) && priceItineraryTypes.length > 1 && pname && tname;
        if (shouldSync) {
          const priceNameToSave = `${pname} (${tname})`;
          const planNoToUseForSync = (
            planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.priceNo ??
            planNoCache?.[selectedPattern]?.[selectedPlan]
          );
          const applied = new Set();
          try {
            pendingChanges.forEach((ch) => {
              const s = new Date(ch.rangeStart + 'T00:00:00');
              const e = new Date(ch.rangeEnd + 'T00:00:00');
              if (isNaN(s) || isNaN(e)) return;
              const w = ch?.weekdays;
              const dowNames = ['일', '월', '화', '수', '목', '금', '토'];
              const isSelectedDow2 = (dateObj) => {
                if (!w || !Array.isArray(w.names) || w.names.length === 0) return true;
                const name = dowNames[dateObj.getDay()];
                return w.names.includes(name);
              };
              for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
                const dd = new Date(d);
                if (!isSelectedDow2(dd)) continue;
                applied.add(formatYMD(dd));
              }
            });
          } catch (_) { }
          const appliedDates = Array.from(applied).sort();
          if (appliedDates.length > 0 && Number.isFinite(Number(resolvedItineraryTypeNo)) && Number(resolvedItineraryTypeNo) > 0) {
            let selected = [];
            for (let attempt = 0; attempt < 3 && selected.length === 0; attempt += 1) {
              if (attempt > 0) {
                await new Promise((r) => setTimeout(r, 350));
              }
              const resp = await getPriceStockCalendar({
                productTypeName: '투어',
                productNo: Number(productId),
                companyNo: String(companyNo),
                startDate: appliedDates[0],
                endDate: appliedDates[appliedDates.length - 1],
                판매된리스트만보기: false,
                플랫폼수수료반영: true,
                결제수수료반영: true,
              }).catch(() => null);
              const list = Array.isArray(resp?.table)
                ? resp.table
                : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.table1) ? resp.table1 : (Array.isArray(resp?.Table1) ? resp.Table1 : [])));
              const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);
              const byNo = new Set();
              (Array.isArray(list) ? list : []).forEach((row) => {
                const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                const ymd = dateStr ? String(dateStr).slice(0, 10) : '';
                if (!ymd || !applied.has(ymd)) return;
                const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo', 'PlanNo', 'priceNo']);
                const optionNum = Number(optionNo);
                if (Number.isFinite(optionNum) && Number.isFinite(Number(planNoToUseForSync)) && optionNum !== Number(planNoToUseForSync)) return;
                if (!Number.isFinite(optionNum)) {
                  const pat = String(tryGet(row, ['항공패턴', '항공', '패턴명', 'pattern']) || '').trim();
                  const pln = String(tryGet(row, ['요금제', '가격명', 'pricePlan', 'PlanName']) || '').trim();
                  if (!pat || !pln) return;
                  if (String(pat) !== String(selectedPattern)) return;
                  if (String(pln) !== String(selectedPlan)) return;
                }
                const itinNo = tryGet(row, ['일정타입번호', 'itineraryTypeNo', 'ItineraryTypeNo']);
                const itinNum = Number(itinNo);
                if (!Number.isFinite(itinNum) || itinNum !== Number(resolvedItineraryTypeNo)) return;
                const priceNo = tryGet(row, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo', 'priceId', 'PriceId']);
                const n = Number(priceNo);
                if (Number.isFinite(n) && n > 0) byNo.add(n);
              });
              selected = Array.from(byNo).map((n) => ({ 가격번호: n }));
            }
            if (selected.length > 0) {
              await savePriceNameBulkByPriceNos({
                productNo: Number(productId),
                companyNo: String(companyNo),
                userNo: String(userNo),
                selected,
                priceName: priceNameToSave,
              });
            }
          }
        }
      } catch (e) {
        try { console.warn('[PriceStock] price name sync failed (pending changes save)', e); } catch (_) { }
      }
      // Avoid immediate refresh; schedule a delayed refresh to let backend settle
      setTimeout(() => { try { refreshCalendarForCurrentMonth(); } catch (_) { } }, 1200);
      alert('변경사항을 저장했습니다.');
      setPendingChanges([]);
    } catch (e) {
      console.error('가격 저장 실패:', e);
      alert('가격 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col space-y-4">
      {/* Header */}
      <ProductPageHeader
        productName={productName}
        productId={productId}
        currentPage="price"
        productType="tour"
        onProductNameClick={() => setSwitcherOpen(true)}
        onBasicSettingsClick={openBasicSettings}
        onBackClick={() => navigate(-1)}
        rightActions={
          <>
            {!multiMonthView && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
                onClick={() => setBulkImportOpen(true)}
                title="스마트 가격 등록"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                스마트 가격 등록
              </button>
            )}
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-500 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
              onClick={() => setHelpOpen(true)}
              title="도움말"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </>
        }
      />
      <ProductSwitcherModal
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        currentProductId={productId}
        onlyTour={true}
        onSelect={(id) => { setSwitcherOpen(false); navigate(`/price-stock/${id}`); }}
      />
      <PromotionMappingModal
        open={promoMappingOpen}
        onClose={() => setPromoMappingOpen(false)}
        productNo={productId}
        productName={productName}
        selection={promoSelection}
        onSaved={async (result) => {
          // 저장 직후 로컬에 반영해 새로고침 전에 캘린더/사이드 패널이 바로 보이게 한다.
          // (서버 refresh 는 아래에서도 돌리되, SP 반영 지연에 대비한 낙관적 업데이트)
          try {
            const dates = Array.isArray(result?.dates) ? result.dates : [];
            const promoStr = Array.isArray(result?.promoNames)
              ? result.promoNames.map((n) => String(n || '').trim()).filter(Boolean).join(',')
              : '';
            if (dates.length > 0 && selectedPattern && selectedPlan) {
              const dateSet = new Set(dates);
              setDataByPattern((prev) => {
                const next = { ...(prev || {}) };
                const pat = { ...(next[selectedPattern] || {}) };
                const plan = { ...(pat[selectedPlan] || {}) };
                dateSet.forEach((d) => {
                  if (!plan[d]) return;
                  plan[d] = { ...plan[d], promo: promoStr };
                });
                pat[selectedPlan] = plan;
                next[selectedPattern] = pat;
                return next;
              });
              try { setCalendarRerenderTick((t) => t + 1); } catch (_) { }
            }
          } catch (_) { }
          try { await refreshCalendarForCurrentMonth(); } catch (_) { }
        }}
      />
      {helpOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" onClick={() => setHelpOpen(false)}>
          <div
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-200 p-5 text-sm text-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-900">상품관리 - 투어 사용 안내</h2>
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => setHelpOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed">
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">1. 화면 개요</h3>
                <p className="text-gray-700">
                  이 화면은 <b>투어 상품의 가격/재고를 날짜별로 관리</b>하는 곳입니다. 상단에서 재고구분(항공패턴)과 요금제를 선택하면,
                  아래 캘린더에 해당 조합의 <b>가격·원가·재고·마감 상태</b>가 표시되고, 우측 패널에서 선택된 기간에 일괄 적용할 수 있습니다.
                </p>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">2. 상단 헤더 영역</h3>
                <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                  <li><b>상품명</b>을 클릭하면 다른 투어 상품으로 이동할 수 있습니다.</li>
                  <li><b>상품번호</b>는 시스템 내부 식별용 번호입니다.</li>
                  <li><b>← 목록으로 돌아가기</b> 버튼으로 이전 상품 목록으로 이동합니다.</li>
                  <li><b>일괄 업로드(텍스트/엑셀)</b> 버튼으로 텍스트/엑셀 데이터를 이용해 가격을 한 번에 등록할 수 있습니다.</li>
                  <li><b>기본설정</b> 버튼에서 상품의 기본정보 및 예약 정책 등을 설정합니다.</li>
                  <li><b>상세 페이지 설정</b>에서는 노출용 상세 페이지 구성을, <b>주문내역</b>에서는 해당 상품의 주문 목록을 확인합니다.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">3. 재고구분 / 요금제 선택</h3>
                <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                  <li><b>재고구분 선택(항공패턴)</b>에서 항공편/출발 패턴 등 재고를 구분하는 단위를 선택합니다.</li>
                  <li><b>요금제 선택</b>에서 같은 재고구분 내 다양한 가격 정책(기본요금, 프로모션 등)을 선택합니다.</li>
                  <li><b>재고구분 관리 / 요금제 관리</b> 버튼으로 재고구분·요금제를 추가/수정/삭제하고 순서를 조정할 수 있습니다.</li>
                  <li><b>간단히 보기</b>를 체크하면 캘린더의 표시 정보를 줄여 한 화면에 더 많은 날짜를 확인할 수 있습니다.</li>
                  <li>우측에 <b>원가방식 / 가격구분</b>이 표시되어 선택한 요금제의 가격 정책을 빠르게 확인할 수 있습니다.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">4. 캘린더 영역</h3>
                <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                  <li><b>캘린더 노출 기간</b>에서 시작월/종료월을 지정하여 한 번에 여러 달을 조회할 수 있습니다.</li>
                  <li>마우스로 드래그하여 연속된 날짜 범위를 선택하면, 선택 구간이 하이라이트 됩니다.</li>
                  <li>각 날짜 셀에는 가격, 재고, 마감 상태 등이 색상/배지로 표시되며, 마감일은 별도로 표기됩니다.</li>
                  <li>날짜 상단의 예약 버튼을 통해 해당 일자의 예약 관련 정보를 조회하거나 예약 화면으로 이동할 수 있습니다.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">5. 우측 편집 패널(일괄 적용)</h3>
                <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                  <li>캘린더에서 선택한 날짜 범위가 <b>적용기간</b>으로 표시되며, 필요 시 직접 시작일/종료일을 수정할 수 있습니다.</li>
                  <li><b>요일 선택</b> 체크박스로 월~일 중 적용할 요일만 골라서 일괄 적용할 수 있습니다.</li>
                  <li><b>판매가 탭</b>에서 성인/소아/유아 판매가와 Tax/유류할증료를 입력하면, 총 판매가가 자동 계산됩니다.</li>
                  <li><b>재고/마감</b> 영역에서 재고 수량을 입력하거나 해당 날짜를 마감 처리할 수 있습니다.</li>
                  <li><b>원가 탭</b>에서 항목별 원가(항공, 숙박, 기타 등)를 입력하면 합계가 총 원가에 자동 반영됩니다.</li>
                  <li><b>판매 커미션</b> 영역에서 정률(%) 또는 정액(금액) 커미션을 설정할 수 있습니다.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">6. 기본 사용 흐름</h3>
                <ol className="list-decimal pl-5 space-y-0.5 text-gray-700">
                  <li>상단에서 <b>재고구분</b>과 <b>요금제</b>를 선택합니다.</li>
                  <li>캘린더에서 가격/재고를 수정할 <b>날짜 범위</b>를 드래그하여 선택합니다.</li>
                  <li>우측 편집 패널에서 적용기간/요일을 확인하고, 판매가·원가·재고·마감·커미션을 입력합니다.</li>
                  <li>편집 패널의 <b>저장</b> 버튼을 눌러 선택된 날짜에 일괄 적용합니다.</li>
                  <li>필요 시 <b>공유재고 연동</b>이나 <b>일괄 업로드</b> 기능을 활용해 대량 데이터를 관리합니다.</li>
                </ol>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900 mb-1">7. 참고 사항</h3>
                <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                  <li>반드시 <b>재고구분·요금제 선택 후</b> 수정해야 해당 조합으로 정확히 저장됩니다.</li>
                  <li>캘린더 노출 기간은 <b>조회 범위</b>일 뿐이며, 실제 반영 범위는 우측 패널의 적용기간을 기준으로 합니다.</li>
                  <li>이미 값이 있는 날짜에 다시 저장하면 <b>새 값으로 덮어쓰기</b> 됩니다.</li>
                  <li>공유재고를 사용하는 경우, 다른 상품과 재고가 함께 움직이므로 변경 시 주의가 필요합니다.</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      )}
      {/* Main Work Area: Calendar (left) + Edit Panel (right) */}
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        {/* Calendar Area (left) */}
        <div className="order-2 flex-1 flex flex-col min-h-0">
          {/* 필터/컨트롤 영역 */}
          <div className="mb-4 p-3 bg-white rounded-xl">
            {/* 1행: 검색 조건 (항공패턴, 요금제, 간단히 보기) + 메타 */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2" data-tour="ps-pattern">
                <span className="text-xs text-gray-600">재고구분 선택</span>
                <select
                  className="px-2 py-1 text-sm border rounded"
                  value={selectedPattern}
                  onChange={(e) => setSelectedPattern(e.target.value)}
                >
                  {(() => {
                    if (listsLoading && (!patterns || patterns.length === 0)) {
                      return (<option value="">항공패턴 불러오는 중...</option>);
                    }
                    if (!patterns || patterns.length === 0) {
                      return (<option value="">항공패턴 없음</option>);
                    }
                    return patterns.map(p => (<option key={p} value={p}>{p}</option>));
                  })()}
                </select>
              </div>
              <div className="flex items-center gap-2" data-tour="ps-plan">
                <span className="text-xs text-gray-600">요금제 선택</span>
                <select
                  className="px-2 py-1 text-sm border rounded"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                >
                  {(() => {
                    if (listsLoading && (!plans || plans.length === 0)) {
                      return (<option value="">요금제 불러오는 중...</option>);
                    }
                    if (!plans || plans.length === 0) {
                      return (<option value="">요금제 없음</option>);
                    }
                    return plans.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ));
                  })()}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => setUnifiedManageOpen(true)}
                  data-tour="ps-manage"
                >재고구분 & 요금제 관리</button>
              </div>

              {selectedPlan && selectedPlanMeta && (
                <div className="ml-auto flex flex-wrap items-center gap-2 text-xs text-gray-700">
        
                  <div className="flex items-baseline gap-1">
                    <span className="text-gray-600">가격방식</span>
                    <span className="text-gray-400">:</span>
                    <span className="px-1.5 py-0.5 font-bold text-gray-500">{selectedPlanMeta.priceType || '-'}</span>
                  </div>
                      <label className="flex items-center gap-1 text-xs text-gray-700">
                <input type="checkbox" className="rounded border-gray-300" checked={showCompact} onChange={(e) => setShowCompact(e.target.checked)} />
                간단히 보기
              </label>
                </div>
              )}
            </div>

            {/* 2행: 조회 기간 + 선택된 날짜 */}
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {multiMonthView && (
                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-700">
                    연령구분: <span className={`font-semibold ${useAgeGroup === false ? 'text-gray-800' : 'text-blue-700'}`}>{useAgeGroup === false ? '미사용' : '사용'}</span>
                  </div>
                  <button
                    type="button"
                    className="text-[11px] px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                    disabled={!!savingAgeGroup}
                    onClick={() => {
                      setAgeGroupSettingsTemp(useAgeGroup !== false);
                      setAgeRangeInitial(null);
                      setAgeGroupSettingsOpen(true);
                    }}
                  >연령구분 변경</button>
                </div>
              )}
              {multiMonthView && (
                <div className="ml-auto flex items-center gap-2 text-xs text-gray-700" data-tour="ps-range">
                  <label className="flex items-center gap-1">
                    <span className="text-gray-700">캘린더 노출 기간</span>
                    <MonthPicker
                      className="w-[162px]"
                      value={visibleStartMM}
                      onChange={(yyyyMm) => {
                        const [y, m] = String(yyyyMm).split('-').map(Number);
                        if (y && m) setVisibleStartMM(new Date(y, m - 1, 1));
                      }}
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    <span>~</span>
                    <MonthPicker
                      className="w-[162px]"
                      value={visibleEndMM}
                      onChange={(yyyyMm) => {
                        const [y, m] = String(yyyyMm).split('-').map(Number);
                        if (y && m) setVisibleEndMM(endOfMonth(new Date(y, m - 1, 1)));
                      }}
                    />
                  </label>
                </div>
              )}

            </div>
            {multiMonthView && showItineraryLegend && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span className="text-[11px] font-medium text-gray-600">일정표 타입</span>
                {itineraryLegendItems.map((item) => {
                  const color = itineraryLegendMap.get(`${item.no == null ? 'null' : Number(item.no)}::${item.label}`);
                  if (!color) return null;
                  return (
                    <span
                      key={`${item.no == null ? 'null' : Number(item.no)}::${item.label}`}
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium"
                      style={{ backgroundColor: color.bg, borderColor: color.border, color: color.text }}
                      title={item.label}
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full border border-white/70"
                        style={{ backgroundColor: color.border }}
                      />
                      {item.label}
                    </span>
                  );
                })}
              </div>
            )}
            {/* 기획전 범례 — 뷰 전체 요약이므로 선택과 무관하게 툴바에 둔다.
                (매핑 액션과 선택 범위 현황은 좌측 설정 패널로 옮겼다) */}
            {promoSummary.mappedDays > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-purple-200 bg-purple-50/60 px-3 py-2">
                <span className="text-[11px] font-medium text-purple-800">기획전</span>
                {promoSummary.names.map((p) => (
                  <span
                    key={p.name}
                    className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-white px-2 py-1 text-[11px] font-medium text-purple-800"
                    title={`${p.name} — 이 요금제에서 ${p.days}일 매핑`}
                  >
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-purple-500" />
                    {p.name}
                    <span className="text-purple-500">{p.days}일</span>
                  </span>
                ))}
                <label className="ml-auto flex items-center gap-1.5 text-[11px] font-medium text-purple-800 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={promoHighlightOnly}
                    onChange={(e) => setPromoHighlightOnly(e.target.checked)}
                  />
                  기획전만 강조
                </label>
              </div>
            )}
          </div>




          {/* Compact calendar CSS overrides to remove events row and reduce vertical space */}
          <style>{`
          .fc .fc-daygrid-day-events { display: none !important; height: 0 !important; padding: 0 !important; margin: 0 !important; }
          .fc .fc-daygrid-day-bottom { display: none !important; }
          
          /* Hover styles for reservation button */
          .calendar-day-cell:hover .reservation-btn { opacity: 1 !important; }
          .fc-day-past .reservation-btn { display: none !important; }
          .ps-closed .reservation-btn { display: none !important; }
          /* Live preview highlight from 적용기간 (right panel) */
          .ps-preview { box-shadow: inset 0 0 0 2px rgba(59,130,246,0.9); background: linear-gradient(0deg, rgba(59,130,246,0.08), rgba(59,130,246,0.08)); }
          /* Closed day hatch background for better visibility */
          .ps-closed { background-image: repeating-linear-gradient(45deg, rgba(0,0,0,0.03) 0, rgba(0,0,0,0.03) 2px, transparent 2px, transparent 6px) !important; }
        
          /* Top header reservation button */
          .fc .fc-daygrid-day-top { position: relative; }
          .fc .fc-daygrid-day-top { display: block !important; padding-right: 0 !important; margin-right: 0 !important; }
          .fc .fc-daygrid-day .fc-daygrid-day-top .fc-daygrid-day-number { padding-right: 0 !important; margin-right: 0 !important; display: inline-block; float: none; }
          /* Ensure custom content below header takes full width */
          .fc .calendar-day-cell { clear: both; width: 100%; }
          .fc .fc-daygrid-day-top:hover .reservation-btn-top { opacity: 1 !important; }
          .fc .fc-daygrid-day:hover .reservation-btn-top { opacity: 1 !important; }
          .fc-day-past .reservation-btn-top { display: none !important; }
          .ps-closed .reservation-btn-top { display: none !important; }
          .reservation-btn-top button { cursor: pointer; }
          .reservation-btn-top button:hover { filter: brightness(0.95); }
          /* Multi-month drag selection highlight */
          .ps-selecting { box-shadow: inset 0 0 0 2px rgba(37,99,235,0.8); background: rgba(37,99,235,0.08); }
          /* Multi-date selection (Ctrl+Click) highlight */
          .ps-multi-selected { box-shadow: inset 0 0 0 3px rgba(16,185,129,0.9); background: rgba(16,185,129,0.12); }
          /* 기획전 매핑 표시: 셀 하단 컬러 바.
             FullCalendar의 td는 position이 보장되지 않으므로 명시한다.
             멀티월 셀(.ps-day-cell)은 이미 relative + overflow-hidden 이다. */
          .fc .fc-daygrid-day.ps-promo { position: relative; }
          .ps-promo::after {
            content: '';
            position: absolute;
            left: 0; right: 0; bottom: 0;
            height: 3px;
            background: #7c3aed; /* 단일 매핑: 보라 */
            pointer-events: none;
            z-index: 3;
          }
          /* 기획전 2개 이상: 하단 2줄 — 색을 확연히 다르게 */
          .ps-promo.ps-promo-multi::before {
            content: '';
            position: absolute;
            left: 0; right: 0; bottom: 4px;
            height: 3px;
            background: #ea580c; /* 주황 */
            pointer-events: none;
            z-index: 3;
          }
          .ps-promo.ps-promo-multi::after {
            background: #7c3aed; /* 보라 */
          }
          /* '기획전만 보기' 강조 모드에서 미매핑 날짜 흐리게 */
          .ps-promo-dim { opacity: 0.35; }

          /* Multi-month view: replace closed hatch with small red '마감' badge */
          .ps-mm .ps-closed { background-image: none !important; }
          .ps-mm .ps-closed-badge { position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); font-size: 10px; color: #6b7280; font-weight: 600; line-height: 1; pointer-events: none; white-space: nowrap; }
          
          /* Date input placeholder/hint appearance (empty) */
          .ps-date.ps-empty { font-size: 12px; color: #9CA3AF; }
          /* Chromium/WebKit date input internals */
          .ps-date.ps-empty::-webkit-datetime-edit { color: #9CA3AF; font-size: 12px; }
          .ps-date.ps-empty::-webkit-datetime-edit-text { color: #CBD5E1; }
          .ps-date.ps-empty::-webkit-datetime-edit-year-field,
          .ps-date.ps-empty::-webkit-datetime-edit-month-field,
          .ps-date.ps-empty::-webkit-datetime-edit-day-field {
            color: #9CA3AF;
          }
          .ps-date.ps-empty::-webkit-calendar-picker-indicator { opacity: 0.6; }
        `}</style>

          {!hasPlan ? (
            <div className="px-5 bg-gray-50 border border-dashed border-gray-300 rounded-xl flex items-center justify-center text-gray-500 text-sm py-16">
              요금제가 없습니다. 요금제 관리를 선택하여 요금제를 추가해주세요
            </div>
          ) : (multiMonthView ? (
            <div
              ref={containerRefMM}
              data-tour="ps-calendar"
              className="ps-mm space-y-6 overflow-y-auto flex-1"
              onScroll={() => {
                if (hoverTooltipMM) setHoverTooltipMM(null);
              }}
              onMouseMove={(e) => {
                if (!isDraggingMM) return;
                const el = containerRefMM.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const threshold = 40; // px
                const speed = 12; // scroll speed
                if (e.clientY - rect.top < threshold) {
                  el.scrollBy({ top: -speed, behavior: 'auto' });
                } else if (rect.bottom - e.clientY < threshold) {
                  el.scrollBy({ top: speed, behavior: 'auto' });
                }
              }}
              onMouseUp={() => {
                // If dragging produced a range, add it to selection and open modal listing all selected dates
                if (!isDraggingMM || !normalizedRangeMM) { setIsDraggingMM(false); return; }
                // If there was no movement, treat as simple click (handled by onDayClick)
                if (!dragMovedMM) { setIsDraggingMM(false); setDragStartMM(null); setDragCurrentMM(null); return; }
                const s = new Date(normalizedRangeMM.start);
                const e = new Date(normalizedRangeMM.end);
                const acc = new Set(Array.from(selectedDates));
                for (let d = new Date(s); d.getTime() <= e.getTime(); d.setDate(d.getDate() + 1)) {
                  acc.add(formatYMD(d));
                }
                const sortedDates = Array.from(acc).sort();
                setSelectedDates(acc);
                setBulkInitialRange({ selectedDates: sortedDates });
                setBulkInitialValues(null);
                setBulkOpen(true);
                setIsDraggingMM(false);
                setDragStartMM(null);
                setDragCurrentMM(null);
                setDragMovedMM(false);
                // mark drag ended to ignore immediate background click
                try { lastDragEndRef.current = Date.now(); } catch (_) { }
                // also suppress the next day click event for a short time
                try { suppressNextDayClickRef.current = Date.now(); } catch (_) { }
                // Prefill values using first selected date
                (async () => {
                  try {
                    setVirtualRecruit({ use: false, count: '0', daysBefore: '0' });
                    const first = sortedDates[0];
                    const key = String(first);
                    const day = (renderData && renderData[key]) || {};
                    const datePriceNo = day?.datePriceNo;
                    const { companyNo } = getUserInfo() || {};
                    
                    // Find max stock from all selected dates
                    let maxStockForDrag = 0;
                    for (const dateKey of sortedDates) {
                      const stockData = stockDataByDate[dateKey] || {};
                      const dayData = renderData?.[dateKey] || {};
                      const capacity = stockData?.보유석 ?? dayData?.capacity ?? 0;
                      const numCapacity = Number(String(capacity).replace(/[^0-9.-]/g, '')) || 0;
                      if (numCapacity > maxStockForDrag) maxStockForDrag = numCapacity;
                    }
                    const maxStockDisp = maxStockForDrag > 0 ? String(maxStockForDrag) : '';
                    
                    // 1) Try local prefill from renderData (optimistic state)
                    const localPrice = day?.price;
                    if (localPrice && (typeof localPrice === 'object')) {
                      setBulkInitialValues({
                        pa: localPrice.adult ?? '',
                        pc: localPrice.child ?? '',
                        pi: localPrice.infant ?? '',
                        ta: localPrice.taxAdult ?? '',
                        tc: localPrice.taxChild ?? '',
                        ti: localPrice.taxInfant ?? '',
                        ca: localPrice.costAdult ?? '',
                        cc: localPrice.costChild ?? '',
                        ci: localPrice.costInfant ?? '',
                        // Commission is not stored in local day.price; default to zero for prefill
                        ma: '0',
                        mc: '0',
                        mi: '0',
                        closed: !!localPrice.closed,
                        currency: localPrice.currency || '',
                        ...bulkInitialStockField(maxStockDisp),
                      });

                      // Even when using optimistic local values, virtual recruit is only available
                      // from RCT_S_가격예약등록정보_01. Fetch it separately without overwriting bulk fields.
                      try {
                        if (productId && companyNo && Number.isFinite(Number(datePriceNo))) {
                          const dataVr = await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: Number(datePriceNo) }).catch(() => null);
                          const tableAnyVr = (dataVr && (dataVr.table || dataVr.Table || dataVr.table1 || dataVr.Table1)) || [];
                          const rowVr = (Array.isArray(tableAnyVr) && tableAnyVr.length) ? tableAnyVr[0] : null;
                          const priceInfoStrVr = rowVr?.가격정보 || null;
                          if (priceInfoStrVr) {
                            let objVr = null;
                            try {
                              const arrVr = JSON.parse(priceInfoStrVr);
                              objVr = Array.isArray(arrVr) ? arrVr[0] : null;
                            } catch (_) { }
                            if (objVr) {
                              setVirtualRecruit({
                                use: !!objVr?.가상모객사용,
                                count: String(objVr?.가상모객인원 ?? 0),
                                daysBefore: String(objVr?.가상모객출발몇일전 ?? 0),
                              });
                            }
                          }
                        }
                      } catch (_) { }
                      return;
                    }
                    // 2) Fallback to API fetch if no local data yet
                    {
                      let priceNoToUse = Number(datePriceNo);
                      if (!Number.isFinite(priceNoToUse)) {
                        try {
                          const resp = await getPriceStockCalendar({
                            productTypeName: '투어',
                            productNo: Number(productId),
                            companyNo: String(companyNo),
                            startDate: key,
                            endDate: key,
                          }).catch(() => null);
                          const list = Array.isArray(resp?.table)
                            ? resp.table
                            : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.Table1) ? resp.Table1 : []));
                          const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);
                          const filtered = (list || []).filter((row) => {
                            const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                            if (String(dateStr).slice(0, 10) !== key) return false;
                            if (!selectedPlan) return true;
                            const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo']);
                            return optionNo == null || String(optionNo) === String(selectedPlan);
                          });
                          const targetRow = filtered[0] || (list && list[0]);
                          const datePriceNo2 = targetRow ? tryGet(targetRow, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']) : undefined;
                          if (Number.isFinite(Number(datePriceNo2))) priceNoToUse = Number(datePriceNo2);
                        } catch (_) { }
                      }
                      const data = Number.isFinite(priceNoToUse) ? await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: priceNoToUse }).catch(() => null) : null;
                      const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
                      const row = (Array.isArray(tableAny) && tableAny.length) ? tableAny[0] : null;
                      const priceInfoStr = row?.가격정보 || null;
                      if (priceInfoStr) {
                        let obj = null; try { const arr = JSON.parse(priceInfoStr); obj = Array.isArray(arr) ? arr[0] : null; } catch (_) { }
                        if (obj) {
                          const findItem = (list, name) => { const arr = Array.isArray(list) ? list : []; return arr.find(x => String(x?.항목) === String(name)); };
                          const sell = findItem(obj.판매가, '판매가') || {};
                          const tax = findItem(obj.판매가, 'Tax및유류할증료') || {};
                          const comm = findItem(obj.판매가, '커미션') || {};
                          const totalCost = (Array.isArray(obj.총원가) && obj.총원가[0]) ? obj.총원가[0] : {};
                          const details = Array.isArray(obj.세부원가) ? obj.세부원가.map(it => ({
                            원가항목: String(it?.원가항목 ?? it?.항목 ?? ''),
                            원가항목코드: String(it?.원가항목코드 ?? ''),
                            통화기호: String(it?.통화기호 ?? obj?.통화기호 ?? ''),
                            성인: Number(it?.성인 ?? 0),
                            어린이: Number(it?.어린이 ?? 0),
                            영유아: Number(it?.영유아 ?? 0),
                          })) : [];
                          const toDisp = (n) => { const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : ''; };
                          const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                          const pcur = String(obj?.통화기호 || '');
                          const ccur = String((details[0]?.통화기호) || obj?.통화기호 || '');

                          // Parse commission data from obj.커미션 string if available
                          const commissionStr = String(obj?.커미션 ?? '');
                          const commissionData = parseCommissionData(commissionStr);

                          setVirtualRecruit({
                            use: !!obj?.가상모객사용,
                            count: String(obj?.가상모객인원 ?? 0),
                            daysBefore: String(obj?.가상모객출발몇일전 ?? 0),
                          });
                          setBulkInitialValues({
                            pa: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.성인 ?? 0),
                            pc: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.어린이 ?? 0),
                            pi: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.영유아 ?? 0),
                            ta: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.성인 ?? 0),
                            tc: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.어린이 ?? 0),
                            ti: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.영유아 ?? 0),
                            // Use parsed commission values if available, otherwise fall back to individual fields
                            ma: commissionData.hasCommission ? commissionData.values.adult : toDisp(comm?.성인 ?? 0),
                            mc: commissionData.hasCommission ? commissionData.values.child : toDisp(comm?.어린이 ?? 0),
                            mi: commissionData.hasCommission ? commissionData.values.infant : toDisp(comm?.영유아 ?? 0),
                            ca: toDisp(totalCost?.성인 ?? 0),
                            cc: toDisp(totalCost?.어린이 ?? 0),
                            ci: toDisp(totalCost?.영유아 ?? 0),
                            pcur, ccur,
                            costDivision: String(obj?.원가구분 ?? ''),
                            detailCosts: (details && details.length) ? details : [{ 원가항목: null, 원가항목코드: '', 통화기호: ccur, 성인: 0, 어린이: 0, 영유아: 0 }],
                            ...bulkInitialStockField(maxStockDisp),
                            closed: !!day?.closed,
                            commissionType: commissionData.hasCommission ? commissionData.commissionType : String(obj?.커미션타입 ?? '정률'),
                            // Add commission checkbox state
                            showCommission: commissionData.hasCommission,
                          });
                          return;
                        }
                      }
                    }
                  } catch (e) {
                    try { console.warn('Multi-date prefill (range add) failed, fallback to zeros', e); } catch (_) { }
                  }
                  const firstKey = String(sortedDates[0] || '');
                  // Find max stock from selected dates
                  let maxStock = 0;
                  for (const dateKey of sortedDates) {
                    const stockData = stockDataByDate[dateKey] || {};
                    const dayData = renderData?.[dateKey] || {};
                    const capacity = stockData?.보유석 ?? dayData?.capacity ?? 0;
                    const numCapacity = Number(String(capacity).replace(/[^0-9.-]/g, '')) || 0;
                    if (numCapacity > maxStock) maxStock = numCapacity;
                  }
                  setBulkInitialValues({
                    pa: '0', pc: '0', pi: '0',
                    ta: '0', tc: '0', ti: '0',
                    ma: '0', mc: '0', mi: '0',
                    ca: '0', cc: '0', ci: '0',
                    pcur: productCurrency || '', ccur: productCurrency || '',
                    costDivision: '',
                    detailCosts: [{ 원가항목: null, 원가항목코드: '', 통화기호: productCurrency || '', 성인: 0, 어린이: 0, 영유아: 0 }],
                    ...bulkInitialStockField(maxStock > 0 ? String(maxStock) : ''),
                    closed: !!(renderData?.[firstKey]?.closed),
                  });
                })();
              }}
            >
              <CalendarMonthGrid
                key={`mm-${calendarRerenderTick}`}
                months={monthsMM}
                holidayMap={holidayMap}
                getMonthSelectionState={getMonthSelectionState}
                onToggleMonth={handleToggleMonth}
                getDayState={(d, mDate) => {
                  const month = mDate.getMonth();
                  const isOther = d.getMonth() !== month;
                  const key = formatYMD(d);
                  const v = renderData[key] || {};
                  const itineraryItems = Array.isArray(v?.itineraryTypes)
                    ? v.itineraryTypes
                      .map((item) => ({ ...item, label: normalizeItineraryLegendLabel(item?.label) }))
                      .filter((item) => item.label !== '')
                    : [];
                  const resolvedItineraries = itineraryItems.length > 0
                    ? itineraryItems
                    : (() => {
                      const normalizedLabel = normalizeItineraryLegendLabel(v?.itineraryLabel);
                      return normalizedLabel ? [{ no: v?.itineraryTypeNo ?? null, label: normalizedLabel }] : [];
                    })();
                  const itineraryColors = showItineraryLegend
                    ? resolvedItineraries
                      .map((item) => itineraryLegendMap.get(`${item.no == null ? 'null' : Number(item.no)}::${item.label}`))
                      .filter(Boolean)
                    : [];
                  const today = (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t.getTime() === d.getTime(); })();
                  const inPreview = (() => {
                    try {
                      if (!applyPreview || !applyPreview.weekdays) return false;

                      // If selectedDates array is provided (discontiguous mode), check if this date is in the filtered list
                      if (applyPreview.selectedDates && Array.isArray(applyPreview.selectedDates)) {
                        return applyPreview.selectedDates.includes(key);
                      }

                      // Otherwise, use start/end range (continuous mode)
                      if (!applyPreview.start || !applyPreview.end) return false;
                      const s = stripTime(applyPreview.start).getTime();
                      const e = stripTime(applyPreview.end).getTime();
                      const t = d.getTime();
                      return t >= s && t <= e && applyPreview.weekdays.has(d.getDay());
                    } catch (_) { return false; }
                  })();
                  const inDrag = normalizedRangeMM && d.getTime() >= normalizedRangeMM.start.getTime() && d.getTime() <= normalizedRangeMM.end.getTime();
                  // If a preview is active, only show selection for dates that match the preview filter.
                  // Also, avoid double borders by suppressing selection when preview is on the same day.
                  // Consider preview active if weekdays exist, even without range (for discontiguous selections)
                  const previewActive = !!(applyPreview && applyPreview.weekdays);
                  const suppressSelection = !!previewActive;
                  // When preview is active, do not show any selection borders at all.
                  // Only rely on inPreview for highlighting (either from selectedDates or range + weekday).
                  const baseSelected = selectedDates.has(key);
                  const filteredOut = suppressSelection ? !inPreview && baseSelected : false;
                  const isSelected = suppressSelection ? false : baseSelected;
                  const now = new Date(); now.setHours(0, 0, 0, 0);
                  const isPast = d.getTime() < now.getTime();
                  const isClosedDay = !!v?.closed;
                  let cellStyle = undefined;
                  /** 일정 타입색 → 날짜 원 배지에만 반영 */
                  let dateChipStyle = undefined;
                  if (!isClosedDay) {
                    if (itineraryColors.length === 1) {
                      const c = itineraryColors[0];
                      dateChipStyle = {
                        backgroundColor: c.bg,
                        color: c.text,
                        border: `1px solid ${c.border}`,
                      };
                    } else if (itineraryColors.length > 1) {
                      const gradientStops = itineraryColors
                        .slice(0, 3)
                        .map((color, idx, arr) => {
                          const start = Math.round((idx / arr.length) * 100);
                          const end = Math.round(((idx + 1) / arr.length) * 100);
                          return `${color.bg} ${start}% ${end}%`;
                        })
                        .join(', ');
                      const c0 = itineraryColors[0];
                      dateChipStyle = {
                        backgroundImage: `linear-gradient(135deg, ${gradientStops})`,
                        color: c0?.text || '#374151',
                        border: `1px solid ${c0?.border || '#d1d5db'}`,
                      };
                    }
                  } else {
                    // 마감일은 일정표 타입색 없이 셀 전체를 옅은 회색으로 통일
                    cellStyle = { backgroundColor: '#f3f4f6', backgroundImage: 'none' };
                    dateChipStyle = {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: '#9ca3af',
                      border: '1px solid #e5e7eb',
                    };
                  }
                  const stockFillPct = !isOther && !isClosedDay
                    ? getSellThroughPercent(stockDataByDate[key], v)
                    : null;
                  // 기획전 매핑 표시 — 단월(FullCalendar) dayCellClassNames와 같은 규칙
                  const promoNames = splitPromoNames(v?.promo);
                  const promoClassName = promoNames.length > 0
                    ? `ps-promo${promoNames.length > 1 ? ' ps-promo-multi' : ''}`
                    : (promoHighlightOnly ? 'ps-promo-dim' : '');
                  return {
                    isOther,
                    isToday: today,
                    isClosed: isClosedDay,
                    inPreview,
                    inDrag,
                    isSelected,
                    isPast,
                    filteredOut,
                    cellStyle,
                    dateChipStyle,
                    stockFillPct,
                    cellClassName: promoClassName,
                  };
                }}
                onDayMouseDown={(d, e) => {
                  const key = formatYMD(d);
                  const v = renderData[key] || {};
                  e.preventDefault();
                  try {
                    if (hoverTooltipTimerMMRef.current) clearTimeout(hoverTooltipTimerMMRef.current);
                  } catch (_) { }
                  hoverTooltipPendingMMRef.current = null;
                  if (hoverTooltipMM) setHoverTooltipMM(null);
                  if (e.ctrlKey || e.metaKey) {
                    setSelectedDates(prev => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      setTimeout(() => {
                        const sortedDates = Array.from(next).sort();
                        if (sortedDates.length > 0) {
                          setBulkInitialRange({ selectedDates: sortedDates });
                          setBulkInitialValues(null);
                          setBulkOpen(true);
                          (async () => {
                            try {
                              const first = sortedDates[0];
                              const key = String(first);
                              const day = (renderData && renderData[key]) || {};
                              const datePriceNo = day?.datePriceNo;
                              const { companyNo } = getUserInfo() || {};
                              {
                                let priceNoToUse = Number(datePriceNo);
                                if (!Number.isFinite(priceNoToUse)) {
                                  try {
                                    const resp = await getPriceStockCalendar({
                                      productTypeName: '투어',
                                      productNo: Number(productId),
                                      companyNo: String(companyNo),
                                      startDate: key,
                                      endDate: key,
                                    }).catch(() => null);
                                    const list = Array.isArray(resp?.table)
                                      ? resp.table
                                      : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.Table1) ? resp.Table1 : []));
                                    const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);
                                    const filtered = (list || []).filter((row) => {
                                      const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                                      if (String(dateStr).slice(0, 10) !== key) return false;
                                      if (!selectedPlan) return true;
                                      const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo']);
                                      return optionNo == null || String(optionNo) === String(selectedPlan);
                                    });
                                    const targetRow = filtered[0] || (list && list[0]);
                                    const datePriceNo2 = targetRow ? tryGet(targetRow, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']) : undefined;
                                    if (Number.isFinite(Number(datePriceNo2))) priceNoToUse = Number(datePriceNo2);
                                  } catch (_) { }
                                }
                                const data = Number.isFinite(priceNoToUse) ? await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: priceNoToUse }).catch(() => null) : null;
                                const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
                                const row = (Array.isArray(tableAny) && tableAny.length) ? tableAny[0] : null;
                                const priceInfoStr = row?.가격정보 || null;
                                if (priceInfoStr) {
                                  let obj = null; try { const arr = JSON.parse(priceInfoStr); obj = Array.isArray(arr) ? arr[0] : null; } catch (_) { }
                                  if (obj) {
                                    const findItem = (list, name) => { const arr = Array.isArray(list) ? list : []; return arr.find(x => String(x?.항목) === String(name)); };
                                    const sell = findItem(obj.판매가, '판매가') || {};
                                    const tax = findItem(obj.판매가, 'Tax및유류할증료') || {};
                                    const comm = findItem(obj.판매가, '커미션') || {};
                                    const totalCost = (Array.isArray(obj.총원가) && obj.총원가[0]) ? obj.총원가[0] : {};
                                    const details = Array.isArray(obj.세부원가) ? obj.세부원가.map(it => ({
                                      원가항목: String(it?.원가항목 ?? it?.항목 ?? ''),
                                      원가항목코드: String(it?.원가항목코드 ?? ''),
                                      통화기호: String(it?.통화기호 ?? obj?.통화기호 ?? ''),
                                      성인: Number(it?.성인 ?? 0),
                                      어린이: Number(it?.어린이 ?? 0),
                                      영유아: Number(it?.영유아 ?? 0),
                                    })) : [];
                                    const toDisp = (n) => { const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : ''; };
                                    const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                                    const pcur = String(obj?.통화기호 || '');
                                    const ccur = String((details[0]?.통화기호) || obj?.통화기호 || '');
                                    // Use stockDataByDate first, then fall back to day.capacity
                                    const stockDataForKey = stockDataByDate[key] || {};
                                    const capacityFromStock = stockDataForKey?.보유석;
                                    const capacityDisp = (capacityFromStock != null && capacityFromStock !== 0) ? String(capacityFromStock) : (day?.capacity ? String(day.capacity) : '');

                                    // Parse commission data from obj.커미션 string if available
                                    const commissionStr = String(obj?.커미션 ?? '');
                                    const commissionData = parseCommissionData(commissionStr);

                                    // Determine if any commission exists either from obj.커미션 or 판매가 커미션 row
                                    const hasCommissionAny = commissionData.hasCommission || [comm?.성인, comm?.어린이, comm?.영유아].some(v => {
                                      const n = Number(v);
                                      return Number.isFinite(n) && n !== 0;
                                    });

                                    setVirtualRecruit({
                                      use: !!obj?.가상모객사용,
                                      count: String(obj?.가상모객인원 ?? 0),
                                      daysBefore: String(obj?.가상모객출발몇일전 ?? 0),
                                    });
                                    setBulkInitialValues({
                                      pa: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.성인 ?? 0),
                                      pc: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.어린이 ?? 0),
                                      pi: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.영유아 ?? 0),
                                      ta: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.성인 ?? 0),
                                      tc: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.어린이 ?? 0),
                                      ti: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.영유아 ?? 0),
                                      // Use parsed commission values if available, otherwise fall back to individual fields
                                      ma: commissionData.hasCommission ? commissionData.values.adult : toDisp(comm?.성인 ?? 0),
                                      mc: commissionData.hasCommission ? commissionData.values.child : toDisp(comm?.어린이 ?? 0),
                                      mi: commissionData.hasCommission ? commissionData.values.infant : toDisp(comm?.영유아 ?? 0),
                                      ca: toDisp(totalCost?.성인 ?? 0),
                                      cc: toDisp(totalCost?.어린이 ?? 0),
                                      ci: toDisp(totalCost?.영유아 ?? 0),
                                      pcur, ccur,
                                      costDivision: String(obj?.원가구분 ?? ''),
                                      detailCosts: (details && details.length) ? details : [{ 원가항목: null, 원가항목코드: '', 통화기호: ccur, 성인: 0, 어린이: 0, 영유아: 0 }],
                                      ...bulkInitialStockField(capacityDisp),
                                      closed: !!day?.closed,
                                      commissionType: commissionData.hasCommission ? commissionData.commissionType : String(obj?.커미션타입 ?? '정률'),
                                      // Add commission checkbox state (auto-enable if any commission exists)
                                      showCommission: hasCommissionAny,
                                    });
                                    return;
                                  }
                                }
                              }
                            } catch (e) {
                              try { console.warn('Multi-date prefill (multi-month) failed, fallback to zeros', e); } catch (_) { }
                            }
                            const firstKey = String((Array.isArray(sortedDates) && sortedDates[0]) || '');
                            // Find max stock from selected dates
                            let maxStock = 0;
                            for (const dateKey of sortedDates) {
                              const stockData = stockDataByDate[dateKey] || {};
                              const dayData = renderData?.[dateKey] || {};
                              const capacity = stockData?.보유석 ?? dayData?.capacity ?? 0;
                              const numCapacity = Number(String(capacity).replace(/[^0-9.-]/g, '')) || 0;
                              if (numCapacity > maxStock) maxStock = numCapacity;
                            }
                            setBulkInitialValues({
                              pa: '0', pc: '0', pi: '0',
                              ta: '0', tc: '0', ti: '0',
                              ma: '0', mc: '0', mi: '0',
                              ca: '0', cc: '0', ci: '0',
                              pcur: productCurrency || '', ccur: productCurrency || '',
                              costDivision: '',
                              detailCosts: [{ 원가항목: null, 원가항목코드: '', 통화기호: productCurrency || '', 성인: 0, 어린이: 0, 영유아: 0 }],
                              ...bulkInitialStockField(maxStock > 0 ? String(maxStock) : ''),
                              closed: !!(renderData?.[firstKey]?.closed),
                            });
                          })();
                        }
                      }, 0);
                      return next;
                    });
                    return;
                  }
                  setIsDraggingMM(true);
                  setDragStartMM(d);
                  setDragCurrentMM(d);
                  setDragMovedMM(false);
                }}
onDayMouseEnter={(d, e) => {
  if (isDraggingMM) {
    setDragCurrentMM(prev => {
      const changed = !prev || prev.getTime() !== d.getTime();
      if (changed) setDragMovedMM(true);
      return d;
    });
    return;
  }

  if ((e?.buttons ?? 0) & 1) return;

  const key = formatYMD(d);
  try {
    if (hoverTooltipTimerMMRef.current) clearTimeout(hoverTooltipTimerMMRef.current);
  } catch (_) { }
  hoverTooltipPendingMMRef.current = {
    dateKey: key,
    x: e?.clientX ?? 0,
    y: e?.clientY ?? 0,
  };
  setHoverTooltipMM(null);
  hoverTooltipTimerMMRef.current = setTimeout(() => {
    const pending = hoverTooltipPendingMMRef.current;
    if (!pending || String(pending.dateKey || '') !== String(key)) return;
    setHoverTooltipMM({
      dateKey: String(pending.dateKey || ''),
      x: pending.x ?? 0,
      y: pending.y ?? 0,
    });
  }, 500);
}}
                onDayMouseLeave={() => {
                  if (isDraggingMM) return;
                  try {
                    if (hoverTooltipTimerMMRef.current) clearTimeout(hoverTooltipTimerMMRef.current);
                  } catch (_) { }
                  hoverTooltipPendingMMRef.current = null;
                  setHoverTooltipMM(null);
                }}
                onDayClick={(d, e) => {
                  if (isDraggingMM) return;
                  if (hoverTooltipMM) setHoverTooltipMM(null);
                  try { if (Date.now() - (suppressNextDayClickRef.current || 0) < 350) return; } catch (_) { }
                  const key = formatYMD(d);
                  setSelectedDates(prev => {
                    const willBeEmpty = prev.has(key) && prev.size === 1;
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key); else next.add(key);
                    const sortedDates = Array.from(next).sort();
                    setTimeout(() => {
                      if (willBeEmpty) {
                        setApplyPreview(null);
                        setBulkInitialRange(null);
                        setBulkInitialValues(null);
                        setBulkOpen(false);
                        setDragStartMM(null);
                        setDragCurrentMM(null);
                        setDragMovedMM(false);
                        return;
                      }
                      if (sortedDates.length > 0) {
                        setBulkInitialRange({ selectedDates: sortedDates });
                        setBulkInitialValues(null);
                        setBulkOpen(true);
                        (async () => {
                          try {
                            setVirtualRecruit({ use: false, count: '0', daysBefore: '0' });
                            const first = sortedDates[0];
                            const dkey = String(first);
                            const day = (renderData && renderData[dkey]) || {};
                            const datePriceNo = day?.datePriceNo;
                            const { companyNo } = getUserInfo() || {};
                            {
                              let priceNoToUse = Number(datePriceNo);
                              if (!Number.isFinite(priceNoToUse)) {
                                try {
                                  const resp = await getPriceStockCalendar({
                                    productTypeName: '투어',
                                    productNo: Number(productId),
                                    companyNo: String(companyNo),
                                    startDate: dkey,
                                    endDate: dkey,
                                  }).catch(() => null);
                                  const list = Array.isArray(resp?.table)
                                    ? resp.table
                                    : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.Table1) ? resp.Table1 : []));
                                  const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);
                                  const filtered = (list || []).filter((row) => {
                                    const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                                    if (String(dateStr).slice(0, 10) !== dkey) return false;
                                    if (!selectedPlan) return true;
                                    const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo']);
                                    return optionNo == null || String(optionNo) === String(selectedPlan);
                                  });
                                  const targetRow = filtered[0] || (list && list[0]);
                                  const datePriceNo2 = targetRow ? tryGet(targetRow, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']) : undefined;
                                  if (Number.isFinite(Number(datePriceNo2))) priceNoToUse = Number(datePriceNo2);
                                } catch (_) { }
                              }
                              const data = Number.isFinite(priceNoToUse) ? await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: priceNoToUse }).catch(() => null) : null;
                              const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
                              const row = (Array.isArray(tableAny) && tableAny.length) ? tableAny[0] : null;
                              const priceInfoStr = row?.가격정보 || null;
                              if (priceInfoStr) {
                                let obj = null; try { const arr = JSON.parse(priceInfoStr); obj = Array.isArray(arr) ? arr[0] : null; } catch (_) { }
                                if (obj) {
                                  const findItem = (list, name) => { const arr = Array.isArray(list) ? list : []; return arr.find(x => String(x?.항목) === String(name)); };
                                  const sell = findItem(obj.판매가, '판매가') || {};
                                  const tax = findItem(obj.판매가, 'Tax및유류할증료') || {};
                                  const comm = findItem(obj.판매가, '커미션') || {};
                                  const totalCost = (Array.isArray(obj.총원가) && obj.총원가[0]) ? obj.총원가[0] : {};
                                  const details = Array.isArray(obj.세부원가) ? obj.세부원가.map(it => ({
                                    원가항목: String(it?.원가항목 ?? it?.항목 ?? ''),
                                    원가항목코드: String(it?.원가항목코드 ?? ''),
                                    통화기호: String(it?.통화기호 ?? obj?.통화기호 ?? ''),
                                    성인: Number(it?.성인 ?? 0),
                                    어린이: Number(it?.어린이 ?? 0),
                                    영유아: Number(it?.영유아 ?? 0),
                                  })) : [];
                                  const toDisp = (n) => { const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : ''; };
                                  const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                                  const pcur = String(obj?.통화기호 || '');
                                  const ccur = String((details[0]?.통화기호) || obj?.통화기호 || '');
                                  // Use stockDataByDate first, then fall back to day.capacity
                                  const stockDataForKey = stockDataByDate[dkey] || {};
                                  const capacityFromStock = stockDataForKey?.보유석;
                                  const capacityDisp = (capacityFromStock != null && capacityFromStock !== 0) ? String(capacityFromStock) : (day?.capacity ? String(day.capacity) : '');

                                  // Parse commission data from obj.커미션 string if available
                                  const commissionStr = String(obj?.커미션 ?? '');
                                  const commissionData = parseCommissionData(commissionStr);

                                  // Determine if any commission exists either from obj.커미션 or 판매가 커미션 row
                                  const hasCommissionAny = commissionData.hasCommission || [comm?.성인, comm?.어린이, comm?.영유아].some(v => {
                                    const n = Number(v);
                                    return Number.isFinite(n) && n !== 0;
                                  });

                                  setVirtualRecruit({
                                    use: !!obj?.가상모객사용,
                                    count: String(obj?.가상모객인원 ?? 0),
                                    daysBefore: String(obj?.가상모객출발몇일전 ?? 0),
                                  });
                                  setBulkInitialValues({
                                    pa: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.성인 ?? 0),
                                    pc: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.어린이 ?? 0),
                                    pi: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.영유아 ?? 0),
                                    ta: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.성인 ?? 0),
                                    tc: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.어린이 ?? 0),
                                    ti: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.영유아 ?? 0),
                                    // Use parsed commission values if available, otherwise fall back to individual fields
                                    ma: commissionData.hasCommission ? commissionData.values.adult : toDisp(comm?.성인 ?? 0),
                                    mc: commissionData.hasCommission ? commissionData.values.child : toDisp(comm?.어린이 ?? 0),
                                    mi: commissionData.hasCommission ? commissionData.values.infant : toDisp(comm?.영유아 ?? 0),
                                    ca: toDisp(totalCost?.성인 ?? 0),
                                    cc: toDisp(totalCost?.어린이 ?? 0),
                                    ci: toDisp(totalCost?.영유아 ?? 0),
                                    pcur, ccur,
                                    costDivision: String(obj?.원가구분 ?? ''),
                                    detailCosts: (details && details.length) ? details : [{ 원가항목: null, 원가항목코드: '', 통화기호: ccur, 성인: 0, 어린이: 0, 영유아: 0 }],
                                    ...bulkInitialStockField(capacityDisp),
                                    closed: !!day?.closed,
                                    commissionType: commissionData.hasCommission ? commissionData.commissionType : String(obj?.커미션타입 ?? '정률'),
                                    // Add commission checkbox state (auto-enable if any commission exists)
                                    showCommission: hasCommissionAny,
                                  });
                                  return;
                                }
                              }
                            }
                          } catch (e) {
                            try { console.warn('Multi-date prefill (click add) failed, fallback to zeros', e); } catch (_) { }
                          }
                          const firstKey = String(sortedDates[0] || '');
                          // Find max stock from selected dates
                          let maxStock = 0;
                          for (const dateKey of sortedDates) {
                            const stockData = stockDataByDate[dateKey] || {};
                            const dayData = renderData?.[dateKey] || {};
                            const capacity = stockData?.보유석 ?? dayData?.capacity ?? 0;
                            const numCapacity = Number(String(capacity).replace(/[^0-9.-]/g, '')) || 0;
                            if (numCapacity > maxStock) maxStock = numCapacity;
                          }
                          setBulkInitialValues({
                            pa: '0', pc: '0', pi: '0',
                            ta: '0', tc: '0', ti: '0',
                            ma: '0', mc: '0', mi: '0',
                            ca: '0', cc: '0', ci: '0',
                            pcur: productCurrency || '', ccur: productCurrency || '',
                            costDivision: '',
                            detailCosts: [{ 원가항목: null, 원가항목코드: '', 통화기호: productCurrency || '', 성인: 0, 어린이: 0, 영유아: 0 }],
                            ...bulkInitialStockField(maxStock > 0 ? String(maxStock) : ''),
                            closed: !!(renderData?.[firstKey]?.closed),
                          });
                        })();
                      }
                    }, 0);
                    return next;
                  });
                }}
                onBackgroundClick={(e) => {
                  // Clicked on empty area inside the multi-month grid: clear selection
                  if (isDraggingMM) return;
                  // Ignore the synthetic click that follows a drag mouseup
                  try { if (Date.now() - (lastDragEndRef.current || 0) < 300) return; } catch (_) { }
                  if (hoverTooltipMM) setHoverTooltipMM(null);
                  if (selectedDates && selectedDates.size > 0) {
                    setSelectedDates(new Set());
                    setApplyPreview(null);
                    setBulkInitialRange(null);
                    setBulkInitialValues(null);
                    setBulkOpen(false);
                    setDragStartMM(null);
                    setDragCurrentMM(null);
                    setDragMovedMM(false);
                  }
                }}
                renderDayContent={(d, ctx = {}) => {
                  const key = formatYMD(d);
                  const v = renderData[key] || {};
                  const closed = !!ctx?.isClosed;
                  // Use currency from price data or fallback to product default currency
                  const currencySymbol = v?.currency || v?.price?.currency || productCurrency || '';
                  const priceA = v?.price?.adult;
                  const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                  // Format price without currency symbol (will be added separately)
                  const formatPrice = (p) => {
                    if (!p) return '';
                    // Remove any existing currency symbols and format
                    const numStr = String(p).replace(/[^\d.,]/g, '');
                    return numStr || String(p);
                  };
                  const toNum = (x) => { const s = String(x ?? '').replace(/[^0-9.-]/g, ''); return s ? Number(s) : NaN; };
                  const nStockApi = (val) => {
                    if (val == null || val === '') return NaN;
                    const s = String(val).replace(/[^0-9.-]/g, '');
                    if (s === '') return NaN;
                    const n = Number(s);
                    return Number.isFinite(n) ? n : NaN;
                  };
                  const stockData = stockDataByDate[key] || {};
                  const apiCapacity = stockData?.보유석;
                  const apiRemain = stockData?.잔여석;
                  const apiOwnSold = stockData?.판매석;
                  const apiSharedSold = stockData?.공유판매석;
                  const capParsed = nStockApi(apiCapacity);
                  const remParsed = nStockApi(apiRemain);
                  const capacityNum = !Number.isNaN(capParsed) ? capParsed : toNum(v?.capacity);
                  const remainNum = !Number.isNaN(remParsed) ? remParsed : toNum(v?.stock);
                  const ownSoldParsed = nStockApi(apiOwnSold);
                  const ownSoldNum = !Number.isNaN(ownSoldParsed)
                    ? ownSoldParsed
                    : (!Number.isNaN(capacityNum) && !Number.isNaN(remainNum) ? Math.max(0, capacityNum - remainNum) : undefined);
                  const sharedSoldParsed = nStockApi(apiSharedSold);
                  const sharedSoldNum = !Number.isNaN(sharedSoldParsed)
                    ? sharedSoldParsed
                    : ownSoldNum;
                  const capacityDisp = !Number.isNaN(capacityNum) ? capacityNum : (v?.capacity || '-');
                  const remainDisp = !Number.isNaN(remainNum) ? remainNum : (v?.stock || '-');
                  const isShared = !!(sharedFromApiDates?.has?.(key) || sharedLinkedDates?.has?.(key) || v?.공유재고사용 || v?.shared || v?.isShared);
                  const hasPrice = !!priceA;
                  const stockSoldOut = typeof sharedSoldNum !== 'undefined' && !Number.isNaN(Number(sharedSoldNum))
                    ? isCapacityFullySold(capacityNum, sharedSoldNum)
                    : false;
                  const bothZeroSeats = !Number.isNaN(capacityNum) && !Number.isNaN(remainNum)
                    && capacityNum === 0 && remainNum === 0;
                  const stockSuffix = (stockSoldOut || bothZeroSeats)
                    ? (waitlistReservationAllowed ? ' (대기)' : ' (마감)')
                    : '';
                  const stockLineClass = closed
                    ? 'text-gray-500'
                    : (stockSoldOut || bothZeroSeats
                      ? 'text-red-600'
                      : (isShared ? 'text-purple-600' : 'text-gray-500'));
                  const ownSoldLineClass = closed ? 'text-gray-500' : 'text-gray-600';
                  
                  return (
                    <div className="space-y-0.5">
                      {/* Price info */}
                      {hasPrice && (
                        (isTotal || showCompact) ? (
                          <div className={`text-[11px] font-medium ${closed ? 'text-gray-500' : 'text-blue-600'}`}>
                            {currencySymbol} {formatPrice(priceA)}
                          </div>
                        ) : (
                          <div className="text-[10px] leading-tight">
                            <div className={closed ? 'text-gray-500' : 'text-blue-600'}>성인 <span className="font-medium">{formatPrice(v.price.adult)}</span></div>
                            <div className={closed ? 'text-gray-500' : 'text-emerald-600'}>소아 <span className="font-medium">{formatPrice(v.price.child)}</span></div>
                            <div className={closed ? 'text-gray-500' : 'text-amber-600'}>유아 <span className="font-medium">{formatPrice(v.price.infant)}</span></div>
                          </div>
                        )
                      )}
                      {/* Stock info */}
                      {isShared ? (
                        <>
                          <div className={`text-[10px] leading-tight ${stockLineClass}`}>
                            보유 : {capacityDisp}
                          </div>
                          <div className={`text-[10px] leading-tight ${stockLineClass}`}>
                            판매 : {typeof ownSoldNum !== 'undefined' ? ownSoldNum : '-'}
                            {typeof sharedSoldNum !== 'undefined' ? `(공유판매 ${sharedSoldNum})` : ''}
                          </div>
                          <div className={`text-[10px] leading-tight ${stockLineClass}`}>
                            잔여 : {remainDisp}{stockSuffix}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={`text-[10px] leading-tight ${stockLineClass}`}>
                            보유 : {capacityDisp}
                          </div>
                          <div className={`text-[10px] leading-tight ${ownSoldLineClass}`}>
                            판매 : {typeof ownSoldNum !== 'undefined' ? ownSoldNum : '-'}
                          </div>
                          <div className={`text-[10px] leading-tight ${stockLineClass}`}>
                            잔여 : {remainDisp}{stockSuffix}
                          </div>
                        </>
                      )}
                      {Number.isFinite(Number(v?.virtualGuest)) && Number(v.virtualGuest) > 0 && (
                        <div className={`text-[10px] ${closed ? 'text-gray-400' : 'text-gray-500'}`}>
                          가상: {Number(v.virtualGuest)}
                        </div>
                      )}
                    </div>
                  );
                }}
              />

              {hoverTooltipMM && createPortal((() => {
                const dateKey = String(hoverTooltipMM.dateKey || '');
                const day = renderData?.[dateKey] || {};
                const stockData = stockDataByDate?.[dateKey] || {};
                const toNum = (x) => {
                  const s = String(x ?? '').replace(/[^0-9.-]/g, '');
                  return s ? Number(s) : NaN;
                };
                const cap = (stockData?.보유석 != null && stockData?.보유석 !== 0) ? Number(stockData.보유석) : toNum(day?.capacity);
                const rem = (stockData?.잔여석 != null && stockData?.잔여석 !== 0) ? Number(stockData.잔여석) : toNum(day?.stock);
                const ownSold = (stockData?.판매석 != null && stockData?.판매석 !== 0)
                  ? Number(stockData.판매석)
                  : (!Number.isNaN(cap) && !Number.isNaN(rem) ? Math.max(0, cap - rem) : NaN);
                const sharedSoldRaw = stockData?.공유판매석;
                const sharedSold = (sharedSoldRaw != null && sharedSoldRaw !== '')
                  ? Number(sharedSoldRaw)
                  : ownSold;
                const sold = sharedSold;
                const sellThrough = (!Number.isNaN(cap) && cap > 0 && !Number.isNaN(sold)) ? Math.round((sold / cap) * 100) : null;
                const isSharedDay = !!(sharedFromApiDates?.has?.(dateKey) || sharedLinkedDates?.has?.(dateKey) || day?.공유재고사용 || day?.shared || day?.isShared);

                const disp = (v) => {
                  if (v == null) return '-';
                  const s = String(v).trim();
                  return s === '' ? '-' : s;
                };
                const tri = (obj) => ({
                  adult: disp(obj?.adult),
                  child: disp(obj?.child),
                  infant: disp(obj?.infant),
                });

                const sellT = tri(day?.sell);
                const taxT = tri(day?.tax);
                const totalT = tri(day?.price);
                const commT = tri(day?.commission);

                const dObj = (() => { try { return new Date(`${dateKey}T00:00:00`); } catch (_) { return null; } })();
                const dateLabel = (() => {
                  try {
                    if (!dObj || Number.isNaN(dObj.getTime())) return dateKey;
                    return new Intl.DateTimeFormat('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short' }).format(dObj);
                  } catch (_) {
                    return dateKey;
                  }
                })();

                let left = Number(hoverTooltipMM.x) + 12;
                let top = Number(hoverTooltipMM.y) + 12;
                try {
                  const maxLeft = window.innerWidth - 420;
                  const maxTop = window.innerHeight - 260;
                  if (Number.isFinite(maxLeft)) left = Math.min(left, Math.max(12, maxLeft));
                  if (Number.isFinite(maxTop)) top = Math.min(top, Math.max(12, maxTop));
                } catch (_) { }

                return (
                  <div
                    className="fixed z-[9999] pointer-events-none"
                    style={{ left, top, width: 408 }}
                  >
                    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3">
                      <div className="text-[13px] font-semibold text-gray-900 mb-2">{dateLabel}</div>
                      <div className="grid grid-cols-[84px_1fr_1fr_1fr] gap-x-2 gap-y-1 text-[11px] text-gray-700">
                        <div></div>
                        <div className="text-gray-500 text-right">성인</div>
                        <div className="text-gray-500 text-right">소아</div>
                        <div className="text-gray-500 text-right">유아</div>

                        <div className="text-gray-500">판매가</div>
                        <div className="text-right tabular-nums">{sellT.adult}</div>
                        <div className="text-right tabular-nums">{sellT.child}</div>
                        <div className="text-right tabular-nums">{sellT.infant}</div>

                        <div className="text-gray-500">택스/유류</div>
                        <div className="text-right tabular-nums">{taxT.adult}</div>
                        <div className="text-right tabular-nums">{taxT.child}</div>
                        <div className="text-right tabular-nums">{taxT.infant}</div>

                        <div className="text-gray-500">총판매가</div>
                        <div className="text-right tabular-nums font-semibold text-blue-700">{totalT.adult}</div>
                        <div className="text-right tabular-nums font-semibold text-blue-700">{totalT.child}</div>
                        <div className="text-right tabular-nums font-semibold text-blue-700">{totalT.infant}</div>

                        <div className="text-gray-500">커미션</div>
                        <div className="text-right tabular-nums">{commT.adult}</div>
                        <div className="text-right tabular-nums">{commT.child}</div>
                        <div className="text-right tabular-nums">{commT.infant}</div>
                      </div>

                      <div className="mt-3 pt-2 border-t border-gray-100 grid grid-cols-3 gap-2 text-[11px]">
                        <div className="text-gray-600">보유석 <span className="font-semibold text-gray-900 tabular-nums">{!Number.isNaN(cap) ? cap : '-'}</span></div>
                        <div className="text-gray-600">잔여석 <span className="font-semibold text-gray-900 tabular-nums">{!Number.isNaN(rem) ? rem : '-'}</span></div>
                        <div className="text-gray-600">소진율 <span className="font-semibold text-gray-900 tabular-nums">{sellThrough != null ? `${sellThrough}%` : '-'}</span></div>
                        {isSharedDay && (
                          <>
                            <div className="text-gray-600">공유판매 <span className="font-semibold text-purple-700 tabular-nums">{!Number.isNaN(sharedSold) ? sharedSold : '-'}</span></div>
                            <div className="text-gray-600">상품판매 <span className="font-semibold text-gray-900 tabular-nums">{!Number.isNaN(ownSold) ? ownSold : '-'}</span></div>
                          </>
                        )}
                      </div>

                      {splitPromoNames(day?.promo).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-1 text-[11px]">
                          <span className="text-gray-500 mr-0.5">기획전</span>
                          {splitPromoNames(day.promo).map((n) => (
                            <span key={n} className="inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2 py-0.5 font-medium text-purple-800">
                              {n}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })(), document.body)}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto" style={{ '--fc-daygrid-row-min-height': '52px' }}>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={false}
                contentHeight="auto"
                expandRows={false}
                fixedWeekCount={false}
                showNonCurrentDates={false}
                dayHeaderFormat={{ weekday: 'short' }}
                dayHeaderContent={(arg) => {
                  const dow = arg.date.getDay();
                  const cls = dow === 0 ? 'text-red-600' : (dow === 6 ? 'text-blue-600' : 'text-gray-700');
                  return { html: `<div class="text-[12px] font-medium ${cls}">${arg.text}</div>` };
                }}
                dayCellDidMount={(info) => {
                  // Dim days from other months
                  if (info.isOther) {
                    info.el.classList.add('opacity-60');
                  } else {
                    // Dim past dates (before today) similar to other-month days
                    try {
                      const cell = new Date(info.date);
                      const today = new Date();
                      cell.setHours(0, 0, 0, 0);
                      today.setHours(0, 0, 0, 0);
                      if (cell.getTime() < today.getTime()) {
                        info.el.classList.add('opacity-60');
                      }
                    } catch (_) { }
                  }
                  // 기획전 매핑 날짜는 셀 title에 기획전명을 노출 (하단 컬러 바 보조 설명)
                  try {
                    const pKey = formatYMD(info.date);
                    const pNames = splitPromoNames(((renderData && renderData[pKey]) || {}).promo);
                    if (pNames.length > 0) info.el.setAttribute('title', `기획전: ${pNames.join(', ')}`);
                    else info.el.removeAttribute('title');
                  } catch (_) { }
                  // Mark closed days with hatch background
                  try {
                    const key = formatYMD(info.date);
                    const v = (renderData && renderData[key]) || {};
                    if (v?.closed) {
                      info.el.classList.add('ps-closed');
                      info.el.setAttribute('title', (info.el.getAttribute('title') || '') + ' 마감');
                    }
                  } catch (_) { }
                  // Inject reservation button into the day header (top-right corner)
                  try {
                    const topEl = info.el.querySelector('.fc-daygrid-day-top');
                    if (topEl) {
                      // Ensure relative positioning for absolute child
                      const cs = window.getComputedStyle(topEl);
                      if (cs && cs.position === 'static') {
                        topEl.style.position = 'relative';
                      }
                      // Clear any right space reserved by theme/styles
                      topEl.style.paddingRight = '0px';
                      topEl.style.marginRight = '0px';
                      // Remove any right padding/margin from the day number so content uses full width
                      const numberEl = topEl.querySelector('.fc-daygrid-day-number');
                      if (numberEl) {
                        numberEl.style.paddingRight = '0px';
                        numberEl.style.marginRight = '0px';
                      }
                      const dateStr = formatYMD(info.date);
                      const v2 = (renderData && renderData[dateStr]) || {};
                      const datePriceNo = v2?.datePriceNo || '';
                      // Avoid duplicate injection on rerenders
                      if (!topEl.querySelector('.reservation-btn-top')) {
                        const wrap = document.createElement('div');
                        wrap.className = 'reservation-btn-top absolute top-1 right-1 z-10 opacity-0 transition-opacity duration-200 ease-in-out';
                        const btn = document.createElement('button');
                        btn.type = 'button';
                        btn.className = 'px-2 py-0.5 bg-primary-600 text-white text-[10px] rounded shadow hover:bg-blue-700 focus:outline-none';
                        btn.textContent = '예약하기';
                        btn.addEventListener('click', (e) => {
                          e.stopPropagation();
                          if (window && typeof window.handleReservationClick === 'function') {
                            window.handleReservationClick(dateStr, String(datePriceNo || ''));
                          }
                        });
                        wrap.appendChild(btn);
                        topEl.appendChild(wrap);
                      }
                    }
                  } catch (_) { }
                  // Subtle hover
                  info.el.classList.add('hover:bg-gray-50');
                }}
                selectable={true}
                selectMirror={true}
                longPressDelay={100}
                locales={[koLocale]}
                locale="ko"
                dayCellClassNames={(arg) => {
                  const key = formatYMD(arg.date);
                  const v = renderData[key] || {};
                  const classes = [];
                  if (v?.closed) classes.push('ps-closed');
                  // 기획전 매핑 표시 (셀 하단 컬러 바). 강조 모드에서는 미매핑 날짜를 흐리게.
                  if (v?.promo) {
                    classes.push('ps-promo');
                    if (splitPromoNames(v.promo).length > 1) classes.push('ps-promo-multi');
                  } else if (promoHighlightOnly) {
                    classes.push('ps-promo-dim');
                  }
                  // Mark today
                  try {
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const d0 = new Date(arg.date); d0.setHours(0, 0, 0, 0);
                    if (d0.getTime() === today.getTime()) classes.push('ps-today');
                  } catch (_) { }
                  // Highlight when in live preview range and weekday selected
                  try {
                    // Discontiguous selection preview (filtered by weekday in the panel already)
                    if (applyPreview && Array.isArray(applyPreview.selectedDates) && applyPreview.selectedDates.length > 0) {
                      if (applyPreview.selectedDates.includes(key)) {
                        classes.push('ps-preview');
                      }
                    }
                    if (applyPreview && applyPreview.start && applyPreview.end && applyPreview.weekdays) {
                      const d = new Date(arg.date);
                      d.setHours(0, 0, 0, 0);
                      const s = new Date(applyPreview.start);
                      const e = new Date(applyPreview.end);
                      s.setHours(0, 0, 0, 0); e.setHours(0, 0, 0, 0);
                      if (d.getTime() >= s.getTime() && d.getTime() <= e.getTime()) {
                        const dow = d.getDay();
                        if (applyPreview.weekdays.has(dow)) {
                          classes.push('ps-preview');
                        }
                      }
                    }
                  } catch (_) { }
                  // Multi-date selection highlight
                  if (selectedDates.has(key)) classes.push('ps-multi-selected');
                  return classes;
                }}
                datesSet={(arg) => {
                  // Use the midpoint of the visible range to determine the displayed month
                  const start = arg.start;
                  const end = arg.end; // exclusive
                  const midTime = start.getTime() + Math.floor((end.getTime() - start.getTime()) / 2);
                  const mid = new Date(midTime);
                  const next = startOfMonth(new Date(mid.getFullYear(), mid.getMonth(), 1));
                  // Store visible range for holiday calc (only if actually changed)
                  const prevStart = lastRangeRef.current.start;
                  const prevEnd = lastRangeRef.current.end;
                  const changed = !prevStart || !prevEnd || prevStart.getTime() !== start.getTime() || prevEnd.getTime() !== end.getTime();
                  if (changed) {
                    lastRangeRef.current = { start, end };
                    setVisibleRange({ start, end });
                  }
                  setCursor(prev => {
                    if (prev && prev.getFullYear() === next.getFullYear() && prev.getMonth() === next.getMonth()) {
                      return prev; // no change -> avoid re-render loop
                    }
                    return next;
                  });
                }}
                select={async (info) => {
                  // Multi-day drag selection (info.end is exclusive)
                  const start = new Date(info.start);
                  const end = new Date(info.end);
                  setBulkInitialRange({ start, end, startStr: formatYMD(start), endStr: formatYMD(end) });
                  // Open first so that async prefill can update fields via effect
                  setBulkInitialValues(null);
                  setBulkOpen(true);
                  // Try to prefill from SP "DBO.RCT_S_가격예약등록정보_01" using the start date's 가격번호
                  (async () => {
                    try {
                      setVirtualRecruit({ use: false, count: '0', daysBefore: '0' });
                      const key = formatYMD(start);
                      const day = (renderData && renderData[key]) || {};
                      const datePriceNo = day?.datePriceNo;
                      const { companyNo } = getUserInfo() || {};
                      if (productId && companyNo && Number.isFinite(Number(datePriceNo))) {
                        const data = await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: Number(datePriceNo) }).catch(() => null);
                        const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
                        const row = (Array.isArray(tableAny) && tableAny.length) ? tableAny[0] : null;
                        const priceInfoStr = row?.가격정보 || null;
                        if (priceInfoStr) {
                          let obj = null; try { const arr = JSON.parse(priceInfoStr); obj = Array.isArray(arr) ? arr[0] : null; } catch (_) { }
                          if (obj) {
                            const findItem = (list, name) => {
                              const arr = Array.isArray(list) ? list : []; return arr.find(x => String(x?.항목) === String(name));
                            };
                            // 판매가 / Tax / 커미션
                            const sell = findItem(obj.판매가, '판매가') || {};
                            const tax = findItem(obj.판매가, 'Tax및유류할증료') || {};
                            const comm = findItem(obj.판매가, '커미션') || {};
                            // 총원가 (첫 행)
                            const totalCost = (Array.isArray(obj.총원가) && obj.총원가[0]) ? obj.총원가[0] : {};
                            // 세부원가
                            const details = Array.isArray(obj.세부원가) ? obj.세부원가.map(it => ({
                              원가항목: String(it?.원가항목 ?? it?.항목 ?? ''),
                              원가항목코드: String(it?.원가항목코드 ?? ''),
                              통화기호: String(it?.통화기호 ?? obj?.통화기호 ?? ''),
                              성인: Number(it?.성인 ?? 0),
                              어린이: Number(it?.어린이 ?? 0),
                              영유아: Number(it?.영유아 ?? 0),
                            })) : [];
                            const toDisp = (n) => {
                              const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : '';
                            };
                            const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                            const pcur = String(obj?.통화기호 || '');
                            const ccur = String((details[0]?.통화기호) || obj?.통화기호 || '');
                            // Use stockDataByDate first, then fall back to day.capacity
                            const stockDataForKey = stockDataByDate[key] || {};
                            const capacityFromStock = stockDataForKey?.보유석;
                            const capacityDisp = (capacityFromStock != null && capacityFromStock !== 0) ? String(capacityFromStock) : (day?.capacity ? String(day.capacity) : '');

                            // Parse commission data from obj.커미션 string if available
                            const commissionStr = String(obj?.커미션 ?? '');
                            const commissionData = parseCommissionData(commissionStr);

                            setVirtualRecruit({
                              use: !!obj?.가상모객사용,
                              count: String(obj?.가상모객인원 ?? 0),
                              daysBefore: String(obj?.가상모객출발몇일전 ?? 0),
                            });
                            setBulkInitialValues({
                              pa: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.성인 ?? 0),
                              pc: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.어린이 ?? 0),
                              pi: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.영유아 ?? 0),
                              ta: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.성인 ?? 0),
                              tc: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.어린이 ?? 0),
                              ti: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.영유아 ?? 0),
                              // Use parsed commission values if available, otherwise fall back to individual fields
                              ma: commissionData.hasCommission ? commissionData.values.adult : toDisp(comm?.성인 ?? 0),
                              mc: commissionData.hasCommission ? commissionData.values.child : toDisp(comm?.어린이 ?? 0),
                              mi: commissionData.hasCommission ? commissionData.values.infant : toDisp(comm?.영유아 ?? 0),
                              ca: toDisp(totalCost?.성인 ?? 0),
                              cc: toDisp(totalCost?.어린이 ?? 0),
                              ci: toDisp(totalCost?.영유아 ?? 0),
                              pcur, ccur,
                              costDivision: String(obj?.원가구분 ?? ''),
                              detailCosts: (details && details.length) ? details : [{ 원가항목: null, 원가항목코드: '', 통화기호: ccur, 성인: 0, 어린이: 0, 영유아: 0 }],
                              ...bulkInitialStockField(capacityDisp),
                              closed: !!day?.closed,
                              commissionType: commissionData.hasCommission ? commissionData.commissionType : String(obj?.커미션타입 ?? '정률'),
                              // Add commission checkbox state
                              showCommission: commissionData.hasCommission,
                            });
                            return;
                          }
                        }
                      }
                    } catch (e) {
                      try { console.warn('Range prefill failed, fallback to zeros', e); } catch (_) { }
                    }
                    // Fallback: zeros but with product currency
                    const key2 = formatYMD(start);
                    // Find max stock from date range (start to end exclusive)
                    let maxStock = 0;
                    const rangeEnd = new Date(end);
                    for (let d = new Date(start); d < rangeEnd; d.setDate(d.getDate() + 1)) {
                      const dateKey = formatYMD(d);
                      const stockData = stockDataByDate[dateKey] || {};
                      const dayData = renderData?.[dateKey] || {};
                      const capacity = stockData?.보유석 ?? dayData?.capacity ?? 0;
                      const numCapacity = Number(String(capacity).replace(/[^0-9.-]/g, '')) || 0;
                      if (numCapacity > maxStock) maxStock = numCapacity;
                    }
                    setBulkInitialValues({
                      pa: '0', pc: '0', pi: '0',
                      ta: '0', tc: '0', ti: '0',
                      ca: '0', cc: '0', ci: '0',
                      pcur: productCurrency || '', ccur: productCurrency || '',
                      costDivision: '',
                      detailCosts: [{ 원가항목: null, 원가항목코드: '', 통화기호: productCurrency || '', 성인: 0, 어린이: 0, 영유아: 0 }],
                      ...bulkInitialStockField(maxStock > 0 ? String(maxStock) : ''),
                      closed: !!(renderData?.[key2]?.closed),
                    });
                  })();
                }}
                dateClick={async (info) => {
                  // Ctrl+Click: toggle multi-selection and open modal immediately
                  if (info.jsEvent && (info.jsEvent.ctrlKey || info.jsEvent.metaKey)) {
                    const key = formatYMD(info.date);
                    setSelectedDates(prev => {
                      const next = new Set(prev);
                      if (next.has(key)) {
                        next.delete(key);
                      } else {
                        next.add(key);
                      }
                      // Open modal immediately with updated selection
                      setTimeout(() => {
                        const sortedDates = Array.from(next).sort();
                        if (sortedDates.length > 0) {
                          setBulkInitialRange({ selectedDates: sortedDates });
                          setBulkInitialValues(null);
                          setBulkOpen(true);
                          // Prefill values for multi-date selection (FullCalendar)
                          (async () => {
                            try {
                              setVirtualRecruit({ use: false, count: '0', daysBefore: '0' });
                              const first = sortedDates[0];
                              const key = String(first);
                              const day = (renderData && renderData[key]) || {};
                              const datePriceNo = day?.datePriceNo;
                              const { companyNo } = getUserInfo() || {};
                              if (productId && companyNo && Number.isFinite(Number(datePriceNo))) {
                                const data = await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: Number(datePriceNo) }).catch(() => null);
                                const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
                                const row = (Array.isArray(tableAny) && tableAny.length) ? tableAny[0] : null;
                                const priceInfoStr = row?.가격정보 || null;
                                if (priceInfoStr) {
                                  let obj = null; try { const arr = JSON.parse(priceInfoStr); obj = Array.isArray(arr) ? arr[0] : null; } catch (_) { }
                                  if (obj) {
                                    const findItem = (list, name) => { const arr = Array.isArray(list) ? list : []; return arr.find(x => String(x?.항목) === String(name)); };
                                    const sell = findItem(obj.판매가, '판매가') || {};
                                    const tax = findItem(obj.판매가, 'Tax및유류할증료') || {};
                                    const totalCost = (Array.isArray(obj.총원가) && obj.총원가[0]) ? obj.총원가[0] : {};
                                    const details = Array.isArray(obj.세부원가) ? obj.세부원가.map(it => ({
                                      원가항목: String(it?.원가항목 ?? it?.항목 ?? ''),
                                      원가항목코드: String(it?.원가항목코드 ?? ''),
                                      통화기호: String(it?.통화기호 ?? obj?.통화기호 ?? ''),
                                      성인: Number(it?.성인 ?? 0),
                                      어린이: Number(it?.어린이 ?? 0),
                                      영유아: Number(it?.영유아 ?? 0),
                                    })) : [];
                                    const toDisp = (n) => { const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : ''; };
                                    const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                                    const pcur = String(obj?.통화기호 || '');
                                    const ccur = String((details[0]?.통화기호) || obj?.통화기호 || '');
                                    // Use stockDataByDate first, then fall back to day.capacity
                                    const stockDataForKey = stockDataByDate[key] || {};
                                    const capacityFromStock = stockDataForKey?.보유석;
                                    const capacityDisp = (capacityFromStock != null && capacityFromStock !== 0) ? String(capacityFromStock) : (day?.capacity ? String(day.capacity) : '');
                                    setVirtualRecruit({
                                      use: !!obj?.가상모객사용,
                                      count: String(obj?.가상모객인원 ?? 0),
                                      daysBefore: String(obj?.가상모객출발몇일전 ?? 0),
                                    });
                                    setBulkInitialValues({
                                      pa: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.성인 ?? 0),
                                      pc: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.어린이 ?? 0),
                                      pi: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.영유아 ?? 0),
                                      ta: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.성인 ?? 0),
                                      tc: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.어린이 ?? 0),
                                      ti: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.영유아 ?? 0),
                                      ca: toDisp(totalCost?.성인 ?? 0),
                                      cc: toDisp(totalCost?.어린이 ?? 0),
                                      ci: toDisp(totalCost?.영유아 ?? 0),
                                      pcur, ccur,
                                      costDivision: String(obj?.원가구분 ?? ''),
                                      detailCosts: (details && details.length) ? details : [{ 원가항목: null, 원가항목코드: '', 통화기호: ccur, 성인: 0, 어린이: 0, 영유아: 0 }],
                                      ...bulkInitialStockField(capacityDisp),
                                      closed: !!day?.closed,
                                    });
                                    return;
                                  }
                                }
                              }
                            } catch (e) {
                              try { console.warn('Multi-date prefill (FullCalendar) failed, fallback to zeros', e); } catch (_) { }
                            }
                            // Fallback: zeros but with product currency
                            const firstKey = String((Array.isArray(sortedDates) && sortedDates[0]) || '');
                            // Find max stock from selected dates
                            let maxStock = 0;
                            for (const dateKey of sortedDates) {
                              const stockData = stockDataByDate[dateKey] || {};
                              const dayData = renderData?.[dateKey] || {};
                              const capacity = stockData?.보유석 ?? dayData?.capacity ?? 0;
                              const numCapacity = Number(String(capacity).replace(/[^0-9.-]/g, '')) || 0;
                              if (numCapacity > maxStock) maxStock = numCapacity;
                            }
                            setBulkInitialValues({
                              pa: '0', pc: '0', pi: '0',
                              ta: '0', tc: '0', ti: '0',
                              ca: '0', cc: '0', ci: '0',
                              pcur: productCurrency || '', ccur: productCurrency || '',
                              costDivision: '',
                              detailCosts: [{ 원가항목: null, 원가항목코드: '', 통화기호: productCurrency || '', 성인: 0, 어린이: 0, 영유아: 0 }],
                              ...bulkInitialStockField(maxStock > 0 ? String(maxStock) : ''),
                              closed: !!(renderData?.[firstKey]?.closed),
                            });
                          })();
                        }
                      }, 0);
                      return next;
                    });
                    return;
                  }
                  // Single-day selection
                  const start = new Date(info.date);
                  const end = new Date(info.date);
                  end.setDate(end.getDate() + 1); // exclusive end
                  setBulkInitialRange({ start, end, startStr: formatYMD(start), endStr: formatYMD(end) });
                  // Open first so that async prefill can update fields via effect
                  setBulkInitialValues(null);
                  setBulkOpen(true);
                  // Try to prefill from SP "DBO.RCT_S_가격예약등록정보_01" using the date's 가격번호
                  (async () => {
                    try {
                      setVirtualRecruit({ use: false, count: '0', daysBefore: '0' });
                      const key = formatYMD(start);
                      const day = (renderData && renderData[key]) || {};
                      const datePriceNo = day?.datePriceNo;
                      const { companyNo } = getUserInfo() || {};
                      if (productId && companyNo && Number.isFinite(Number(datePriceNo))) {
                        const data = await getPriceReservationInfo({ productNo: Number(productId), companyNo: String(companyNo), priceNo: Number(datePriceNo) }).catch(() => null);
                        const tableAny = (data && (data.table || data.Table || data.table1 || data.Table1)) || [];
                        const row = (Array.isArray(tableAny) && tableAny.length) ? tableAny[0] : null;
                        const priceInfoStr = row?.가격정보 || null;
                        if (priceInfoStr) {
                          let obj = null; try { const arr = JSON.parse(priceInfoStr); obj = Array.isArray(arr) ? arr[0] : null; } catch (_) { }
                          if (obj) {
                            const findItem = (list, name) => {
                              const arr = Array.isArray(list) ? list : []; return arr.find(x => String(x?.항목) === String(name));
                            };
                            // 판매가 / Tax
                            const sell = findItem(obj.판매가, '판매가') || {};
                            const tax = findItem(obj.판매가, 'Tax및유류할증료') || {};
                            // 총원가 (첫 행)
                            const totalCost = (Array.isArray(obj.총원가) && obj.총원가[0]) ? obj.총원가[0] : {};
                            // 세부원가
                            const details = Array.isArray(obj.세부원가) ? obj.세부원가.map(it => ({
                              원가항목: String(it?.원가항목 ?? it?.항목 ?? ''),
                              원가항목코드: String(it?.원가항목코드 ?? ''),
                              통화기호: String(it?.통화기호 ?? obj?.통화기호 ?? ''),
                              성인: Number(it?.성인 ?? 0),
                              어린이: Number(it?.어린이 ?? 0),
                              영유아: Number(it?.영유아 ?? 0),
                            })) : [];
                            const toDisp = (n) => {
                              const v = Number(n); return Number.isFinite(v) ? v.toLocaleString() : '';
                            };
                            const isTotal = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                            const pcur = String(obj?.통화기호 || '');
                            const ccur = String((details[0]?.통화기호) || obj?.통화기호 || '');
                            // Use stockDataByDate first, then fall back to day.capacity
                            const stockDataForKey = stockDataByDate[key] || {};
                            const capacityFromStock = stockDataForKey?.보유석;
                            const capacityDisp = (capacityFromStock != null && capacityFromStock !== 0) ? String(capacityFromStock) : (day?.capacity ? String(day.capacity) : '');
                            setVirtualRecruit({
                              use: !!obj?.가상모객사용,
                              count: String(obj?.가상모객인원 ?? 0),
                              daysBefore: String(obj?.가상모객출발몇일전 ?? 0),
                            });
                            setBulkInitialValues({
                              pa: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.성인 ?? 0),
                              pc: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.어린이 ?? 0),
                              pi: isTotal ? toDisp(sell?.성인 ?? 0) : toDisp(sell?.영유아 ?? 0),
                              ta: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.성인 ?? 0),
                              tc: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.어린이 ?? 0),
                              ti: isTotal ? toDisp(tax?.성인 ?? 0) : toDisp(tax?.영유아 ?? 0),
                              ca: toDisp(totalCost?.성인 ?? 0),
                              cc: toDisp(totalCost?.어린이 ?? 0),
                              ci: toDisp(totalCost?.영유아 ?? 0),
                              pcur, ccur,
                              costDivision: String(obj?.원가구분 ?? ''),
                              detailCosts: (details && details.length) ? details : [{ 원가항목: null, 원가항목코드: '', 통화기호: ccur, 성인: 0, 어린이: 0, 영유아: 0 }],
                              ...bulkInitialStockField(capacityDisp),
                              closed: !!day?.closed,
                            });
                            return;
                          }
                        }
                      }
                    } catch (e) {
                      try { console.warn('Single-day prefill failed, fallback to zeros', e); } catch (_) { }
                    }
                    // Fallback: zeros but with product currency
                    const key2 = formatYMD(start);
                    // Get stock from stockDataByDate or renderData for single date
                    const stockData2 = stockDataByDate[key2] || {};
                    const dayData2 = renderData?.[key2] || {};
                    const capacity2 = stockData2?.보유석 ?? dayData2?.capacity ?? 0;
                    const stockDisp = (capacity2 != null && capacity2 !== '' && Number(String(capacity2).replace(/[^\d.-]/g, '') || '0') > 0)
                      ? String(capacity2)
                      : '';
                    setBulkInitialValues({
                      pa: '0', pc: '0', pi: '0',
                      ta: '0', tc: '0', ti: '0',
                      ca: '0', cc: '0', ci: '0',
                      pcur: productCurrency || '', ccur: productCurrency || '',
                      costDivision: '',
                      detailCosts: [{ 원가항목: null, 원가항목코드: '', 통화기호: productCurrency || '', 성인: 0, 어린이: 0, 영유아: 0 }],
                      ...bulkInitialStockField(stockDisp),
                      closed: !!(renderData?.[key2]?.closed),
                    });
                  })();
                }}
                dayCellContent={(arg) => {
                  const key = formatYMD(arg.date);
                  const v = renderData[key] || {};
                  // Use currency from price data or fallback to product default currency
                  const currencySymbol = v?.currency || v?.price?.currency || productCurrency || '';
                  const hasPriceData = v?.price?.adult != null && v?.price?.adult !== '-';
                  const hasCostData = v?.cost?.adult != null && v?.cost?.adult !== '-';
                  const priceA = hasPriceData ? v.price.adult : (currencySymbol ? `${currencySymbol} -` : '-');
                  const priceC = hasPriceData ? v.price.child : (currencySymbol ? `${currencySymbol} -` : '-');
                  const priceI = hasPriceData ? v.price.infant : (currencySymbol ? `${currencySymbol} -` : '-');
                  const costA = hasCostData ? v.cost.adult : (currencySymbol ? `${currencySymbol} -` : '-');
                  const costC = hasCostData ? v.cost.child : (currencySymbol ? `${currencySymbol} -` : '-');
                  const costI = hasCostData ? v.cost.infant : (currencySymbol ? `${currencySymbol} -` : '-');
                  const isTotalPrice = String(selectedPlanMeta?.priceType || '').trim() === '합계요금';
                  const priceDisp = isTotalPrice || showCompact ? priceA : `${priceA}/${priceC}/${priceI}`;
                  const priceTitle = isTotalPrice || showCompact ? priceA : `${priceA} / ${priceC} / ${priceI}`;
                  const costDisp = isTotalPrice ? costA : `${costA}/${costC}/${costI}`;
                  const costTitle = isTotalPrice ? costA : `${costA} / ${costC} / ${costI}`;
                  const dow = arg.date.getDay();
                  const holidayName = holidayMap.get(key);
                  const isHoliday = !!holidayName;
                  const dowClass = (isHoliday || dow === 0) ? 'text-red-600' : (dow === 6 ? 'text-blue-600' : 'text-gray-700');
                  const toNum = (x) => {
                    const s = String(x ?? '').replace(/[^0-9.-]/g, '');
                    return s ? Number(s) : NaN;
                  };

                  // Get stock data from API (보유석, 판매석, 잔여석)
                  const stockData = stockDataByDate[key] || {};
                  const apiCapacity = stockData?.보유석;
                  const apiRemain = stockData?.잔여석;
                  const apiSold = stockData?.판매석;

                  // Use API stock data if available, otherwise fall back to calendar data
                  const capacityNum = (apiCapacity != null && apiCapacity !== 0) ? apiCapacity : toNum(v?.capacity);
                  const remainNum = (apiRemain != null && apiRemain !== 0) ? apiRemain : toNum(v?.stock);
                  let guestNum = (apiSold != null && apiSold !== 0) ? apiSold : toNum(v?.guest);
                  if (Number.isNaN(guestNum) && !Number.isNaN(capacityNum) && !Number.isNaN(remainNum)) {
                    guestNum = Math.max(0, capacityNum - remainNum);
                  }
                  const sellThrough = (!Number.isNaN(capacityNum) && capacityNum > 0 && !Number.isNaN(guestNum))
                    ? Math.round((guestNum / capacityNum) * 100)
                    : null;
                  const capacityDisp = !Number.isNaN(capacityNum) ? capacityNum : (v?.capacity || '-');
                  const remainDisp = !Number.isNaN(remainNum) ? remainNum : (v?.stock || '-');
                  const guestDisp = !Number.isNaN(guestNum) ? guestNum : (v?.guest || '-');
                  const stockSoldOutFc = !Number.isNaN(Number(guestNum)) && isCapacityFullySold(capacityNum, guestNum);
                  const stockSuffixFc = stockSoldOutFc
                    ? (waitlistReservationAllowed ? ' (대기)' : ' (마감)')
                    : '';
                  // Add the data attributes for reservation
                  const dateStr = formatYMD(arg.date);
                  const datePriceNo = v?.datePriceNo || '';

                  const html = `
              <div class="relative calendar-day-cell" data-date="${dateStr}" data-price-no="${datePriceNo}">
                <!-- top-left badges: 마감 + (줄·확 / 일·확) -->
                ${v?.closed ? '<span class="ps-closed-badge">마감</span>' : ''}
                <div class="absolute top-1 left-8 flex gap-1">
                  ${v?.departConfirmed ? '<span class="px-1 py-0.5 text-[10px] rounded bg-emerald-600 text-white">줄·확</span>' : ''}
                  ${v?.scheduleConfirmed ? '<span class="px-1 py-0.5 text-[10px] rounded bg-sky-600 text-white">일·확</span>' : ''}
                </div>
                <!-- Reservation button moved to fc-daygrid-day-top via dayCellDidMount -->
                <div class="mx-0.5 mb-0.5 flex items-center gap-0.5">
                  <span class="inline-flex items-center justify-center w-5 h-5 rounded-md bg-gray-50 ${dowClass} text-[14px] font-bold">${arg.date.getDate()}</span>
                  ${holidayName ? `<span class="text-[10px] text-red-600 font-medium leading-none">${holidayName}</span>` : ''}
                </div>
                <div class="space-y-0.5 text-[10px] leading-tight pb-2">
                  <!-- 총판매가 -->
                  <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                    <span class="text-gray-600 whitespace-nowrap">총판매가</span>
                    <span class="text-[11px] text-blue-800 font-semibold tabular-nums text-right truncate" title="${priceTitle}">${priceDisp}</span>
                  </div>
                  <!-- divider -->
                  ${showCompact ? '' : '<div class="mx-1 h-px bg-gray-200/70"></div>'}
                  <!-- 원가 (compact에선 생략) -->
                  ${showCompact ? '' : `
                  <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                    <span class="text-gray-600 whitespace-nowrap">원가</span>
                    <span class="text-[10px] text-gray-600 font-medium tabular-nums text-right truncate" title="${costTitle}">${costDisp}</span>
                  </div>`}
                  <!-- 재고/좌석 정보 -->
                  ${showCompact ? (stockSoldOutFc ? `
                    <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                      <span class="text-red-600 font-semibold whitespace-nowrap">재고</span>
                      <span class="text-[10px] font-semibold text-red-600 tabular-nums text-right">
                        <span class="inline-flex items-center px-1 py-0.5 rounded-full bg-red-100 text-red-800">${capacityDisp}/${guestDisp}${stockSuffixFc}</span>
                      </span>
                    </div>
                  ` : `
                    <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                      <span class="text-gray-700 whitespace-nowrap">잔여석</span>
                      <span class="text-[10px] font-semibold text-gray-800 tabular-nums text-right">
                        <span class="inline-flex items-center px-1 py-0.5 rounded-full bg-red-100 text-gray-800">${remainDisp}</span>
                      </span>
                    </div>
                  `) : (stockSoldOutFc ? `
                    <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                      <span class="text-red-600 font-semibold whitespace-nowrap">재고</span>
                      <span class="text-[10px] font-semibold text-red-600 tabular-nums text-right">
                        <span class="inline-flex items-center px-1 py-0.5 rounded-full bg-red-100 text-red-800">${capacityDisp}/${guestDisp}${stockSuffixFc}</span>
                      </span>
                    </div>
                  ` : `
                    <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                      <span class="text-gray-700 whitespace-nowrap">보유석</span>
                      <span class="text-[10px] font-semibold text-gray-800 tabular-nums text-right">
                        <span class="inline-flex items-center px-1 py-0.5 rounded-full bg-green-100 text-gray-800">${capacityDisp}</span>
                      </span>
                    </div>
                    <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                      <span class="text-gray-700 whitespace-nowrap">잔여석</span>
                      <span class="text-[10px] font-semibold text-gray-800 tabular-nums text-right">
                        <span class="inline-flex items-center px-1 py-0.5 rounded-full bg-red-100 text-gray-800">${remainDisp}</span>
                      </span>
                    </div>
                    <div class="mx-1 grid grid-cols-[auto_1fr] items-center gap-2">
                      <span class="text-gray-700 whitespace-nowrap">모객수</span>
                      <span class="text-[10px] font-semibold text-gray-800 tabular-nums text-right">
                        <span class="inline-flex items-center px-1 py-0.5 rounded-full bg-gray-100 text-gray-800">${guestDisp}</span>
                      </span>
                    </div>
                  `)}
                  
                </div>
              `;
                  return { html };
                }}
              />
            </div>
          ))}
        </div>
        {/* Right fixed edit panel */}
        <div
          ref={rightPanelRef}
          data-tour="ps-panel"
          className="order-1 w-full lg:w-80 shrink-0 lg:sticky lg:self-start"
          style={rightPanelStickyTop != null ? { top: `${rightPanelStickyTop}px` } : undefined}
        >
          {bulkOpen ? (
            <BulkApplySideModal
              inline
              open={bulkOpen}
              onClose={() => setBulkOpen(false)}
              multiMonthView={multiMonthView}
              itineraryTypes={priceItineraryTypes}
              selectedItineraryTypeNo={selectedItineraryTypeNo}
              onSelectedItineraryTypeNoChange={setSelectedItineraryTypeNo}
              virtualRecruitInitial={virtualRecruit}
              onVirtualRecruitChange={(next) => {
                try { setVirtualRecruit(next); } catch (_) { }
              }}
              onClearSelection={() => {
                try {
                  setSelectedDates(new Set());
                  setBulkInitialRange(null);
                  setApplyPreview(null);
                  // reset drag/preview states for multi-month view
                  try { setIsDraggingMM(false); } catch (_) { }
                  try { setDragStartMM(null); } catch (_) { }
                  try { setDragCurrentMM(null); } catch (_) { }
                  try { setDragMovedMM(false); } catch (_) { }
                } catch (_) { }
                try { setCalendarRerenderTick((t) => t + 1); } catch (_) { }
                setBulkOpen(false);
              }}
              onPreviewChange={setApplyPreview}
              promoSelection={promoSelection}
              onOpenPromoMapping={() => setPromoMappingOpen(true)}
              hasSelectablePromos={hasSelectablePromos}
              productId={productId}
              sharedSeriesNoByDate={sharedSeriesNoByDate}
              resourceNo={(planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.resourceNo ?? resourceNoCache?.[selectedPattern]?.[selectedPlan])}
              onRequestRefresh={refreshCalendarForCurrentMonth}
              onChangeUseAgeGroup={handleChangeUseAgeGroup}
              savingAgeGroup={savingAgeGroup}
              onSyncSelection={(list) => {
                try {
                  const arr = Array.isArray(list) ? list.slice().sort() : [];
                  setSelectedDates(new Set(arr));
                  setBulkInitialRange({ selectedDates: arr });
                } catch (_) { }
              }}
              onApply={applyBulk}
              onDelete={async ({ rangeStart, rangeEnd, weekdays, selectedDates: _selectedDates }) => {
                try {
                  const dowNames = ['일', '월', '화', '수', '목', '금', '토'];
                  const isSelectedDow = (dateObj) => {
                    if (!weekdays || !Array.isArray(weekdays.names) || weekdays.names.length === 0) return true;
                    const name = dowNames[dateObj.getDay()];
                    return weekdays.names.includes(name);
                  };

                  let startDate;
                  let endDate;
                  let targetDates = [];
                  if (_selectedDates && Array.isArray(_selectedDates) && _selectedDates.length > 0) {
                    const uniq = [...new Set(_selectedDates.map(s => String(s).slice(0, 10)))].sort();
                    startDate = uniq[0];
                    endDate = uniq[uniq.length - 1];
                    targetDates = uniq.filter((ymd) => {
                      const d = new Date(`${ymd}T00:00:00`);
                      return !Number.isNaN(d.getTime()) && isSelectedDow(d);
                    });
                  } else {
                    if (!rangeStart || !rangeEnd) { alert('삭제할 기간을 선택하세요.'); return; }
                    startDate = String(rangeStart).slice(0, 10);
                    endDate = String(rangeEnd).slice(0, 10);
                    const s = new Date(startDate + 'T00:00:00');
                    const e = new Date(endDate + 'T00:00:00');
                    for (let d = new Date(s); d.getTime() <= e.getTime(); d.setDate(d.getDate() + 1)) {
                      if (isSelectedDow(d)) targetDates.push(formatYMD(d));
                    }
                  }

                  if (!startDate || !endDate) { alert('삭제할 기간을 선택하세요.'); return; }
                  if (targetDates.length === 0) {
                    alert('선택한 조건에 해당하는 날짜가 없습니다. 요일 필터를 확인해주세요.');
                    return;
                  }

                  const { userNo, companyNo } = getUserInfo() || {};
                  if (!productId || !companyNo || !userNo) { alert('필수 정보(상품/업체/유저)가 없습니다.'); return; }
                  const planNoPref = (
                    planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.priceNo ??
                    planNoCache?.[selectedPattern]?.[selectedPlan]
                  );
                  const resp = await getPriceStockCalendar({
                    productTypeName: '투어',
                    productNo: Number(productId),
                    companyNo: String(companyNo),
                    startDate,
                    endDate,
                    판매된리스트만보기: false,
                    플랫폼수수료반영: true,
                    결제수수료반영: true,
                  });
                  const list = Array.isArray(resp?.table) ? resp.table : Array.isArray(resp?.Table) ? resp.Table : [];
                  const tryGet = (row, keys) => keys.reduce((acc, k) => acc ?? row[k], undefined);
                  const normalizeYmd = (v) => {
                    const s = String(v ?? '').trim();
                    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                    const d = s.replace(/[^0-9]/g, '');
                    if (/^\d{8}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
                    const t = new Date(s);
                    if (!isNaN(t)) { const mm = String(t.getMonth() + 1).padStart(2, '0'); const dd = String(t.getDate()).padStart(2, '0'); return `${t.getFullYear()}-${mm}-${dd}`; }
                    return s.slice(0, 10);
                  };
                  const map = {};
                  for (const row of list) {
                    const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                    const priceNo = tryGet(row, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']);
                    const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo']);
                    if (!dateStr || !priceNo) continue;
                    if (Number.isFinite(Number(planNoPref)) && optionNo && Number(optionNo) !== Number(planNoPref)) continue;
                    const ymd = normalizeYmd(dateStr);
                    map[ymd] = Number(priceNo);
                  }
                  const selected = [];
                  for (const ymd of targetDates) {
                    const priceNo = map[ymd];
                    if (priceNo) {
                      selected.push({ 가격번호: String(priceNo) });
                    }
                  }

                  let deletedCount = 0;

                  // 1) 가격 숨김 처리 (가격이 있는 경우만)
                  if (selected.length > 0) {
                    await hideProductPricesByPriceNos({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), selected });
                    deletedCount += selected.length;
                  }

                  // 2) 재고(세션) 삭제: 저장(벌크 적용)과 동일하게 요일·멀티선택이 반영된 날짜만
                  let stockDeletedCount = 0;
                  try {
                    const rno = (
                      planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.resourceNo ??
                      resourceNoCache?.[selectedPattern]?.[selectedPlan]
                    );
                    if (Number.isFinite(Number(rno)) && Number(rno) > 0) {
                      const events = targetDates.map(d => ({ 날짜: d, 시간: '00:00', 자원번호: Number(rno) }));
                      if (events.length) {
                        await deleteTourSelectedEvents({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), events, calendarUse: true });
                        stockDeletedCount = events.length;
                      }
                    } else {
                      console.warn('재고 삭제 건너뜀: 자원번호를 확인할 수 없습니다.');
                    }
                  } catch (ex) {
                    console.error('재고 삭제 처리 중 오류', ex);
                  }

                  // Optimistic UI update: remove deleted dates from local calendar maps so UI updates immediately
                  try {
                    // 1) Remove from price/stock day data for current pattern/plan
                    setDataByPattern(prev => {
                      try {
                        const next = { ...(prev || {}) };
                        const pat = String(selectedPattern || '');
                        const plan = String(selectedPlan || '');
                        if (next[pat] && next[pat][plan]) {
                          const days = { ...(next[pat][plan] || {}) };
                          for (const k of targetDates) {
                            if (Object.prototype.hasOwnProperty.call(days, k)) delete days[k];
                          }
                          next[pat] = { ...(next[pat] || {}), [plan]: days };
                        }
                        return next;
                      } catch (_) {
                        return prev;
                      }
                    });
                    // 2) Remove from stock map
                    setStockDataByDate(prev => {
                      try {
                        const next = { ...(prev || {}) };
                        for (const k of targetDates) {
                          if (Object.prototype.hasOwnProperty.call(next, k)) delete next[k];
                        }
                        return next;
                      } catch (_) {
                        return prev;
                      }
                    });
                    // 3) Clear any shared-inventory flags for those dates
                    setSharedFromApiDates(prev => {
                      try {
                        const s = new Set(prev instanceof Set ? Array.from(prev) : []);
                        for (const k of targetDates) s.delete(String(k));
                        return s;
                      } catch (_) {
                        return prev;
                      }
                    });
                    setSharedSeriesNoByDate(prev => {
                      try {
                        const next = { ...(prev || {}) };
                        for (const k of targetDates) { if (Object.prototype.hasOwnProperty.call(next, k)) delete next[k]; }
                        return next;
                      } catch (_) { return prev; }
                    });
                  } catch (_) { }

                  await refreshCalendarForCurrentMonth();
                  // Clear selections and force calendar repaint so latest data is visible immediately
                  try {
                    setSelectedDates(new Set());
                    setBulkInitialRange(null);
                    setApplyPreview(null);
                  } catch (_) { }
                  try { setIsDraggingMM(false); } catch (_) { }
                  try { setDragStartMM(null); } catch (_) { }
                  try { setDragCurrentMM(null); } catch (_) { }
                  try { setDragMovedMM(false); } catch (_) { }
                  try { setCalendarRerenderTick((t) => t + 1); } catch (_) { }
                  setBulkOpen(false);

                  if (deletedCount === 0 && stockDeletedCount === 0) {
                    alert('삭제할 대상이 없습니다.');
                  } else {
                    const messages = [];
                    if (deletedCount > 0) messages.push(`가격 ${deletedCount}건`);
                    if (stockDeletedCount > 0) messages.push(`재고 ${stockDeletedCount}건`);
                    alert(`삭제 완료: ${messages.join(', ')}`);
                  }
                } catch (e) {
                  console.error('Bulk delete failed', e);
                  alert('선택 기간 삭제 처리 중 오류가 발생했습니다.');
                }
              }}
              initialRange={bulkInitialRange}
              initialBulk={bulkInitialValues}
              priceType={selectedPlanMeta?.priceType}
              useAgeGroup={useAgeGroup}
            />
          ) : (
            <div className="px-5 bg-gray-50 border border-gray-200 rounded-xl flex flex-col items-center justify-center text-gray-700 text-sm py-16 text-center space-y-4">
              <p className="text-[13px] leading-relaxed">
                달력에서 날짜를 선택하세요.
                <br />
                <br />
                또는 스마트 가격 등록을 통해 엑셀·한글·워드 일정표의 가격 정보를 복사해 자동으로 일괄 등록할 수 있습니다.
              </p>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={() => setBulkImportOpen(true)}
                title="스마트 가격 등록"
                data-tour="ps-smart-price"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                스마트 가격 등록
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Bulk Price Import Modal */}
      <BulkPriceImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        initialText={bulkImportInitialText}
        initialInputTab={bulkImportInitialInputTab}
        enableSharedStock={true}
        productNo={productId}
        onApply={async ({ rows, mode, stockCount, useSharedStock, sharedSeriesNo, showCommission, commissionType, ma, mc, mi }) => {
          try {
            if (!rows || rows.length === 0) return;
            if (!selectedPattern || !selectedPlan) {
              alert('가격을 적용할 재고구분(패턴)과 요금제를 먼저 선택하세요.');
              return;
            }
            const { userNo, companyNo } = getUserInfo() || {};
            if (!productId || !companyNo || !userNo) { alert('필수 정보(상품/업체/유저)가 없습니다.'); return; }
            // Map visible plan data: date -> datePriceNo
            const map = {};
            const planData = (dataByPattern?.[selectedPattern]?.[selectedPlan]) || {};
            Object.keys(planData).forEach(dateKey => {
              const day = planData[dateKey];
              if (day && day.datePriceNo) map[dateKey] = Number(day.datePriceNo);
            });
            // Prefetch 1-year calendar from this month's 1st to (1 year - 1 day)
            try {
              // Determine current selected 가격옵션번호(요금제번호) if available
              const planNoPref = (
                planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.priceNo ??
                planNoCache?.[selectedPattern]?.[selectedPlan]
              );
              const today = new Date();
              const yStart = new Date(today.getFullYear(), today.getMonth(), 1);
              const yEnd = new Date(yStart);
              yEnd.setFullYear(yEnd.getFullYear() + 1);
              yEnd.setDate(yEnd.getDate() - 1);
              const pad = (n) => String(n).padStart(2, '0');
              const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
              const respYear = await getPriceStockCalendar({
                productTypeName: '투어',
                productNo: Number(productId),
                companyNo: String(companyNo),
                startDate: toYmd(yStart),
                endDate: toYmd(yEnd),
                판매된리스트만보기: false,
                플랫폼수수료반영: true,
                결제수수료반영: true,
              });
              const listYear = Array.isArray(respYear?.table) ? respYear.table : Array.isArray(respYear?.Table) ? respYear.Table : [];
              const tryGet = (row, keys) => keys.reduce((acc, k) => acc ?? row[k], undefined);
              const normalizeYmd = (v) => {
                const s = String(v ?? '').trim();
                // already formatted YYYY-MM-DD
                if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                // digits only like 20251011
                const d = s.replace(/[^0-9]/g, '');
                if (/^\d{8}$/.test(d)) {
                  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
                }
                // fallback: try Date parse
                const t = new Date(s);
                if (!isNaN(t)) {
                  const mm = String(t.getMonth() + 1).padStart(2, '0');
                  const dd = String(t.getDate()).padStart(2, '0');
                  return `${t.getFullYear()}-${mm}-${dd}`;
                }
                return s.slice(0, 10);
              };
              for (const row of listYear) {
                const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                const priceNo = tryGet(row, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']);
                const optionNo = tryGet(row, ['가격옵션번호', '요금제번호', 'priceOptionNo', 'optionNo']);
                if (!dateStr || !priceNo) continue;
                // If we know the selected planNo, only map rows that belong to that option
                if (Number.isFinite(Number(planNoPref)) && optionNo && Number(optionNo) !== Number(planNoPref)) continue;
                const ymd = normalizeYmd(dateStr);
                map[ymd] = Number(priceNo);
              }
              try {
                const resolved = (rows || []).filter(r => map[r.date]).length;
                console.debug('[BulkImport] Year-prefetch mapped dates:', resolved, '/', rows?.length, 'planNo:', planNoPref);
              } catch (_) { }
            } catch (e) {
              try { console.warn('연단위 캘린더 사전 조회 실패', e); } catch (_) { }
            }
            let hit = 0; let miss = 0;
            const toNumStr = (v) => {
              const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
              return String(Number.isFinite(n) ? n : 0);
            };
            const selected = [];
            for (const r of rows) {
              const priceId = map[r.date];
              if (!priceId) { miss++; continue; }
              hit++;
              if (mode === 'total') {
                const v = toNumStr(r.adult);
                selected.push({ 가격번호: String(priceId), 성인: v, 어린이: v, 영유아: v });
              } else {
                selected.push({ 가격번호: String(priceId), 성인: toNumStr(r.adult), 어린이: toNumStr(r.child), 영유아: toNumStr(r.infant) });
              }
            }
            // If some dates are missing, attempt to auto-create date price records for those dates
            if (miss > 0) {
              try {
                // Determine planNo and resourceNo
                const planNo = (
                  planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.priceNo ??
                  planNoCache?.[selectedPattern]?.[selectedPlan]
                );
                if (planNo) {
                  // Load base price info for selected plan
                  let base = basePriceInfo;
                  if (!base) base = await loadBasePriceInfo(planNo);
                  if (base) {
                    const clone = JSON.parse(JSON.stringify(base));
                    try {
                      const vr = (typeof virtualRecruit === 'object' && virtualRecruit) ? virtualRecruit : null;
                      if (vr) {
                        clone.가상모객사용 = !!vr.use;
                        clone.가상모객인원 = parseNonNegativeInt(vr.count);
                        clone.가상모객출발몇일전 = parseNonNegativeInt(vr.daysBefore);
                      }
                    } catch (_) { }
                    // Always ensure 상품번호 반영
                    clone.상품번호 = Number(productId);
                    // Ensure 선택요금제/요금제에 현재 plan만 포함되도록 보정
                    clone.선택요금제 = [{ 가격옵션번호: Number(planNo), 합산가격여부: 'N' }];
                    clone.요금제 = [{ 요금제번호: Number(planNo), 요금제명: String(selectedPlan || '기본요금제') }];
                    // 상품자원요금제 보강 (best-effort)
                    try {
                      const rno = (
                        planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.resourceNo ??
                        resourceNoCache?.[selectedPattern]?.[selectedPlan]
                      );
                      if (Number.isFinite(Number(rno)) && Number(rno) > 0) {
                        clone.상품자원요금제 = [
                          { 자원번호: Number(rno), 자원명: String(selectedPattern || '기초상품자원'), 요금제리스트: [{ 가격옵션번호: Number(planNo), 가격옵션명: String(selectedPlan || '기본요금제'), 합산가격여부: 'N' }] }
                        ];
                      } else {
                        // If resourceNo unknown, do not send 상품자원요금제 to avoid invalid FK
                        delete clone.상품자원요금제;
                      }
                    } catch (_) { delete clone.상품자원요금제; }
                    // 안정성: 일정타입 정보를 사용자 선택값 우선 반영
                    try {
                      let typeNo = selectedItineraryTypeNo;
                      let typeName = '';
                      if (typeNo && priceItineraryTypes.length > 0) {
                        const found = priceItineraryTypes.find(t => Number(t.일정타입번호) === Number(typeNo));
                        typeName = found ? String(found.일정타입명 || '대표일정') : '대표일정';
                      }
                      if (!Number.isFinite(typeNo)) {
                        const itin = await getItineraryType({ productNo: Number(productId) });
                        const row = (Array.isArray(itin?.table) && itin.table.length) ? itin.table[0] : null;
                        typeNo = row ? Number(row.일정타입번호 ?? row.itineraryTypeNo ?? row.번호) : undefined;
                        typeName = row ? String(row.일정타입명 ?? row.itineraryTypeName ?? row.이름 ?? '대표일정') : '대표일정';
                      }
                      if (Number.isFinite(typeNo)) {
                        clone.선택일정타입 = typeNo;
                        clone.일정타입 = [{ 선택: false, 코드: typeNo, 코드명: typeName || '대표일정' }];
                      }
                    } catch (_) { }
                    // 적용기간에 누락 날짜들을 1일 범위로 추가
                    const missingDates = rows.map(r => r.date).filter(d => !map[d]);
                    const periods = missingDates.map(d => ({ 시작일: d, 종료일: d, 요일: ['월', '화', '수', '목', '금', '토', '일'], 요일2: 'YYYYYYY' }));
                    clone.적용기간 = Array.isArray(clone.적용기간) ? [...clone.적용기간, ...periods] : periods;
                    // 숫자 필드 null 방지: 판매가/총원가 기본값 보정
                    try {
                      const toNum = (x) => { const n = Number(x); return Number.isFinite(n) ? n : 0; };
                      if (Array.isArray(clone.판매가)) {
                        clone.판매가 = clone.판매가.map(row => ({
                          ...row,
                          성인: toNum(row.성인), 어린이: toNum(row.어린이), 영유아: toNum(row.영유아)
                        }));
                      }
                      if (Array.isArray(clone.총원가)) {
                        clone.총원가 = clone.총원가.map(row => ({
                          ...row,
                          성인: toNum(row.성인), 어린이: toNum(row.어린이), 영유아: toNum(row.영유아)
                        }));
                      }
                    } catch (_) { }
                    // 정상 payload와 동일한 메타 필드/기본 판매가/시간값 보강
                    try {
                      const currencyCode = String(clone?.통화코드 ?? '');
                      const currencySymbol = String(clone?.통화기호 ?? '￦');

                      if (!Array.isArray(clone.노출채널) || clone.노출채널.length === 0) {
                        clone.노출채널 = [
                          { 선택: true, 코드: '웹노출여부', 코드명: '웹노출여부' },
                          { 선택: true, 코드: '대리점노출여부', 코드명: '대리점노출여부' },
                        ];
                      }

                      if (typeof clone.플랫폼수수료적용여부 === 'undefined') clone.플랫폼수수료적용여부 = true;
                      if (typeof clone.플랫폼수수료 === 'undefined') clone.플랫폼수수료 = 0.01;
                      if (typeof clone.결제수수료적용여부 === 'undefined') clone.결제수수료적용여부 = true;
                      if (typeof clone.결제수수료 === 'undefined') clone.결제수수료 = 0.03;

                      if (!clone.행사시작시간 || (Array.isArray(clone.행사시작시간) && clone.행사시작시간.length === 0)) {
                        clone.행사시작시간 = null;
                      }
                      if (!Array.isArray(clone.선택시작시간) || clone.선택시작시간.length === 0) {
                        clone.선택시작시간 = [{ 시간: '00:00' }];
                      }

                      if (!clone.상품일정타입) {
                        const name = (Array.isArray(clone.일정타입) && clone.일정타입.length)
                          ? (clone.일정타입[0]?.코드명 ?? clone.일정타입[0]?.코드)
                          : null;
                        clone.상품일정타입 = String(name || '대표일정');
                      }

                      if (!Array.isArray(clone.판매가) || clone.판매가.length === 0) {
                        clone.판매가 = [
                          { 항목: '판매가', 번호: 1, 통화코드: currencyCode, 통화기호: currencySymbol, 성인: '0', 어린이: 0, 영유아: 0 },
                          { 항목: 'Tax및유류할증료', 번호: 2, 통화코드: currencyCode, 통화기호: currencySymbol, 성인: 0, 어린이: 0, 영유아: 0 },
                          { 항목: '커미션', 번호: 3, 통화코드: currencyCode, 통화기호: currencySymbol, 성인: 0, 어린이: 0, 영유아: 0 },
                        ];
                      }
                      if (Array.isArray(clone.판매가)) {
                        clone.판매가 = clone.판매가.map(row => {
                          if (row?.항목 === '판매가') return { ...row, 성인: String(row.성인 ?? '0') };
                          return row;
                        });
                      }

                      if (Array.isArray(clone.세부원가)) {
                        clone.세부원가 = clone.세부원가.map(row => ({
                          ...row,
                          항목: (typeof row?.항목 === 'undefined' ? (row?.원가항목 ?? null) : row?.항목),
                        }));
                      }
                    } catch (_) { }
                    // 서버가 날짜별 레코드를 생성하도록만 요청
                    // 업데이트/머지 허용 플래그 보강
                    clone.기존데이터업데이트여부 = true;
                    const priceInfoJson = JSON.stringify(clone);
                    try { console.debug('[AutoCreate] payload', clone); } catch (_) { }
                    const 합산가격여부 = !!clone.합산가격여부;
                    await saveTourPriceInfo({ priceInfoJson, userNo, companyNo, 합산가격여부 });
                    // After creation, fetch exact date range to obtain price IDs (SP: RCT_S_가격예약_02)
                    const datesAll = rows.map(r => r.date).sort();
                    const startDate = datesAll[0];
                    const endDate = datesAll[datesAll.length - 1];
                    const { companyNo: cNo } = getUserInfo() || {};
                    try {
                      const resp = await getPriceStockCalendar({
                        productTypeName: '투어',
                        productNo: Number(productId),
                        companyNo: String(cNo),
                        startDate,
                        endDate,
                        판매된리스트만보기: false,
                        플랫폼수수료반영: true,
                        결제수수료반영: true,
                      });
                      const list = Array.isArray(resp?.table) ? resp.table : Array.isArray(resp?.Table) ? resp.Table : [];
                      const tryGet = (row, keys) => keys.reduce((acc, k) => acc ?? row[k], undefined);
                      for (const row of list) {
                        const dateStr = tryGet(row, ['가격일자', '일자', '날짜', '판매일자', '행사시작일시', 'Date', 'DATE']);
                        const priceNo = tryGet(row, ['가격번호', 'datePriceNo', '가격ID', 'PriceNo']);
                        if (!dateStr || !priceNo) continue;
                        // normalize date format to YYYY-MM-DD to match parser keys
                        const d = String(dateStr).trim();
                        let ymd = d;
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
                          const only = d.replace(/[^0-9]/g, '');
                          if (/^\d{8}$/.test(only)) {
                            ymd = `${only.slice(0, 4)}-${only.slice(4, 6)}-${only.slice(6, 8)}`;
                          } else {
                            const t = new Date(d);
                            if (!isNaN(t)) {
                              const mm = String(t.getMonth() + 1).padStart(2, '0');
                              const dd = String(t.getDate()).padStart(2, '0');
                              ymd = `${t.getFullYear()}-${mm}-${dd}`;
                            } else {
                              ymd = d.slice(0, 10);
                            }
                          }
                        }
                        map[ymd] = Number(priceNo);
                      }
                    } catch (e) {
                      console.warn('Exact-range calendar fetch failed, fallback to current month map', e);
                      await refreshCalendarForCurrentMonth();
                      await new Promise(res => setTimeout(res, 600));
                      const updatedPlanData = (dataByPattern?.[selectedPattern]?.[selectedPlan]) || {};
                      Object.keys(updatedPlanData).forEach(dateKey => {
                        const day = updatedPlanData[dateKey];
                        if (day && day.datePriceNo) map[dateKey] = Number(day.datePriceNo);
                      });
                    }
                    // No zero-initialization; directly build final selection using mapped price IDs
                    // Rebuild final selection from latest map to ensure no miss due to earlier filtering
                    const finalSelected = [];
                    for (const r of rows) {
                      const priceId = map[r.date];
                      if (!priceId) continue;
                      if (mode === 'total') {
                        const v = toNumStr(r.adult);
                        finalSelected.push({ 가격번호: String(priceId), 성인: v, 어린이: v, 영유아: v });
                      } else {
                        finalSelected.push({ 가격번호: String(priceId), 성인: toNumStr(r.adult), 어린이: toNumStr(r.child), 영유아: toNumStr(r.infant) });
                      }
                    }
                    // Merge with any previously selected (existing dates before creation)
                    const seen = new Set();
                    const merged = [];
                    for (const it of [...selected, ...finalSelected]) {
                      const key = String(it.가격번호);
                      if (seen.has(key)) continue;
                      seen.add(key);
                      merged.push(it);
                    }
                    // Update counters
                    hit = merged.length;
                    miss = rows.length - hit;
                    selected.length = 0; // clear and replace
                    merged.forEach(x => selected.push(x));
                  }
                }
              } catch (e) {
                console.warn('누락 날짜 자동 생성 실패', e);
              }
            }
            if (selected.length === 0) {
              alert('적용할 대상이 없습니다. (가격번호가 존재하지 않거나 생성에 실패했습니다)');
              return;
            }

            let commissionSaveOk = true;
            try {
              const shouldApplyCommission = !!showCommission;
              const planNoForComm = (
                planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.priceNo ??
                planNoCache?.[selectedPattern]?.[selectedPlan]
              );
              if (shouldApplyCommission && planNoForComm) {
                const appliedDates = Array.from(
                  new Set((rows || []).map(r => String(r?.date || '').slice(0, 10)).filter(Boolean).filter(d => !!map[d]))
                );

                if (appliedDates.length > 0) {
                  const parseCommNum = (v) => {
                    const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
                    return Number.isFinite(n) ? n : 0;
                  };
                  const type = (commissionType === '정액') ? '정액' : '정률';

                  let baseForComm = basePriceInfo;
                  if (!baseForComm && planNoForComm) {
                    try { baseForComm = await loadBasePriceInfo(planNoForComm); } catch (_) { baseForComm = null; }
                  }
                  if (baseForComm) {
                    const vMa = parseCommNum(ma);
                    const vMc = parseCommNum(mc);
                    const vMi = parseCommNum(mi);
                    const hasCommissionInput = !!(String(ma || '') || String(mc || '') || String(mi || ''));

                    const dowAll = ['월', '화', '수', '목', '금', '토', '일'];
                    const periodsAll = appliedDates.map(d => ({ 시작일: d, 종료일: d, 요일: dowAll, 요일2: 'YYYYYYY' }));

                    const chunkSize = 60;
                    for (let i = 0; i < periodsAll.length; i += chunkSize) {
                      const periods = periodsAll.slice(i, i + chunkSize);
                      const clone = JSON.parse(JSON.stringify(baseForComm));
                      try {
                        const vr = (typeof virtualRecruit === 'object' && virtualRecruit) ? virtualRecruit : null;
                        if (vr) {
                          clone.가상모객사용 = !!vr.use;
                          clone.가상모객인원 = parseNonNegativeInt(vr.count);
                          clone.가상모객출발몇일전 = parseNonNegativeInt(vr.daysBefore);
                        }
                      } catch (_) { }
                      clone.상품번호 = Number(productId);
                      clone.기존데이터업데이트여부 = true;
                      try { clone.커미션타입 = type || '정률'; } catch (_) { }
                      clone.선택요금제 = [{ 가격옵션번호: Number(planNoForComm), 합산가격여부: 'N' }];
                      clone.요금제 = [{ 요금제번호: Number(planNoForComm), 요금제명: String(selectedPlan || '기본요금제') }];
                      clone.적용기간 = periods;

                      const currencyCode = String(clone?.통화코드 ?? '');
                      const currencySymbol = String(clone?.통화기호 ?? '￦');
                      const 판매가목록 = Array.isArray(clone.판매가) ? [...clone.판매가] : [];
                      const commIdx = 판매가목록.findIndex(x => x?.항목 === '커미션');
                      const baseRow = (commIdx >= 0 && 판매가목록[commIdx])
                        ? 판매가목록[commIdx]
                        : { 항목: '커미션', 번호: 3, 통화코드: currencyCode, 통화기호: currencySymbol };
                      const commRow = {
                        ...baseRow,
                        통화코드: currencyCode,
                        통화기호: currencySymbol,
                        성인: hasCommissionInput ? (type === '정액' ? String(vMa) : vMa) : 0,
                        어린이: hasCommissionInput ? (type === '정액' ? String(vMc) : vMc) : 0,
                        영유아: hasCommissionInput ? (type === '정액' ? String(vMi) : vMi) : 0,
                      };
                      if (commIdx >= 0) 판매가목록[commIdx] = commRow; else 판매가목록.push(commRow);
                      clone.판매가 = 판매가목록;

                      const 합산가격여부 = !!clone.합산가격여부;
                      await saveTourPriceInfo({
                        priceInfoJson: JSON.stringify(clone),
                        userNo,
                        companyNo,
                        합산가격여부,
                      });
                    }
                  }
                }
              }
            } catch (e) {
              commissionSaveOk = false;
              try { console.warn('커미션 일괄 저장 실패(스마트 가격 등록)', e); } catch (_) { }
            }

            try {
              const rno = (
                planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.resourceNo ??
                resourceNoCache?.[selectedPattern]?.[selectedPlan]
              );
              if (Number.isFinite(Number(rno)) && Number(rno) > 0) {
                const uniqDates = Array.from(new Set((rows || []).map(r => String(r.date).slice(0, 10)))).filter(Boolean);
                const events = uniqDates.map(d => ({ 날짜: d, 시간: '00:00', 자원번호: Number(rno) }));
                if (events.length > 0) {
                  if (useSharedStock && sharedSeriesNo) {
                    await linkSharedSeriesToSelectedEvents({
                      productNo: Number(productId),
                      companyNo: String(companyNo),
                      userNo: String(userNo),
                      events,
                      sharedSeriesNo: Number(sharedSeriesNo),
                      calendarUse: true,
                    });
                    setSharedLinkedDates(prev => {
                      const next = new Set(prev);
                      events.forEach(ev => { if (ev?.날짜) next.add(String(ev.날짜)); });
                      return next;
                    });
                  }

                  if (!useSharedStock) {
                    const needUnlink = events.some(ev => (
                      ev?.날짜 && (sharedFromApiDates?.has?.(String(ev.날짜)) || sharedLinkedDates?.has?.(String(ev.날짜)))
                    ));
                    if (needUnlink) {
                      await unlinkSharedSeriesFromSelectedEvents({
                        productNo: Number(productId),
                        companyNo: String(companyNo),
                        userNo: String(userNo),
                        events,
                        calendarUse: true,
                      });
                    }
                  }
                }
              }
            } catch (e) {
              try { console.warn('공유재고 연동 처리 실패(스마트 가격 등록)', e); } catch (_) { }
            }

            await saveSalePriceBulkByPriceNos({ productNo: Number(productId), companyNo: String(companyNo), userNo: String(userNo), selected });
            try {
              const pname = String(productName || '').trim();
              let typeNo = selectedItineraryTypeNo;
              let typeName = '';
              if (typeNo && Array.isArray(priceItineraryTypes) && priceItineraryTypes.length > 0) {
                const found = priceItineraryTypes.find(t => Number(t.일정타입번호) === Number(typeNo));
                typeName = found ? String(found.일정타입명 || '대표일정') : '대표일정';
              }
              const tname = String(typeName || '').trim();
              const shouldSync = Array.isArray(priceItineraryTypes) && priceItineraryTypes.length > 1 && pname && tname;
              if (shouldSync) {
                const priceNameToSave = `${pname} (${tname})`;
                const onlyPriceNos = (Array.isArray(selected) ? selected : [])
                  .map((x) => ({ 가격번호: Number(x?.가격번호) }))
                  .filter((x) => Number.isFinite(x.가격번호) && x.가격번호 > 0);
                if (onlyPriceNos.length > 0) {
                  await savePriceNameBulkByPriceNos({
                    productNo: Number(productId),
                    companyNo: String(companyNo),
                    userNo: String(userNo),
                    selected: onlyPriceNos,
                    priceName: priceNameToSave,
                  });
                }
              }
            } catch (e) {
              try { console.warn('[PriceStock] price name sync failed (smart bulk)', e); } catch (_) { }
            }
            // If the modal provided a default stock value, register it for the parsed dates as well
            try {
              const stockStr = String(stockCount || '').trim();
              if (stockStr && !(useSharedStock && sharedSeriesNo)) {
                const rno = (
                  planMetaByPattern?.[selectedPattern]?.[selectedPlan]?.resourceNo ??
                  resourceNoCache?.[selectedPattern]?.[selectedPlan]
                );
                const uniqDates = Array.from(new Set((rows || []).map(r => String(r.date).slice(0, 10))));
                const events = uniqDates.map(d => ({ 날짜: d, 시간: '', 자원번호: Number(rno) || 0 }));
                if (events.length > 0) {
                  await saveTourSelectedStock({
                    productNo: Number(productId),
                    companyNo: String(companyNo),
                    userNo: String(userNo),
                    stockCount: stockStr,
                    events,
                    calendarUse: true,
                    seriesUpdate: false,
                  });
                }
              }
            } catch (e) {
              try { console.warn('Bulk stock registration failed (continuing)', e); } catch (_) { }
            }
            await refreshCalendarForCurrentMonth();
            setBulkImportOpen(false);
            alert(`일괄 저장 완료\n적용: ${hit}건, 누락(가격번호 없음): ${miss}건${showCommission ? `\n커미션 저장: ${commissionSaveOk ? '완료' : '실패(판매가 저장은 완료)'}` : ''}`);
          } catch (e) {
            console.error('Bulk save failed', e);
            alert('일괄 저장 처리 중 오류가 발생했습니다.');
          }
        }}
        defaultMode="total"
      />

      {/* 통합 모달: 재고구분 & 요금제 관리 */}
      {unifiedManageOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setUnifiedManageOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-[1080px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-blue-600">📦</span> 재고구분 & 요금제 관리
              </h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-sm px-2"
                onClick={() => setUnifiedManageOpen(false)}
              >닫기</button>
            </div>

            {/* 본문: 좌측 트리 + 우측 상세 */}
            <div className="flex flex-1 min-h-0">
              {/* 좌측: 재고구분 + 요금제 트리 */}
              <div className="w-[380px] border-r flex flex-col min-h-0">
                {/* 새 재고구분 추가 - 상단 배치 */}
                <div className="border-b p-3 bg-white">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="새 재고구분명 입력..."
                      value={tempPatternName}
                      onChange={(e) => setTempPatternName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && tempPatternName.trim()) handleAddPattern(); }}
                    />
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      disabled={!tempPatternName.trim()}
                      onClick={handleAddPattern}
                    >+ 재고구분</button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {(Array.isArray(patterns) ? patterns : []).map((pat) => {
                    const planList = (Array.isArray(allPlansByPattern?.[pat]) && allPlansByPattern[pat].length)
                      ? allPlansByPattern[pat]
                      : Object.keys(dataByPattern?.[pat] || {});
                    const isExpanded = expandedPatterns.has(pat);
                    const isSelectedPattern = planManageSelectedPattern === pat;

                    return (
                      <div key={pat} className={`border rounded-lg overflow-hidden ${isSelectedPattern ? 'border-blue-400 shadow-sm' : 'border-gray-200'}`}>
                        {/* 재고구분 헤더 */}
                        <div
                          className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${isSelectedPattern ? 'bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'}`}
                          onClick={() => {
                            setPlanManageSelectedPattern(pat);
                            setExpandedPatterns(prev => {
                              const next = new Set(prev);
                              if (next.has(pat)) next.delete(pat);
                              else next.add(pat);
                              return next;
                            });
                          }}
                        >
                          <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                          {renamingPattern === pat ? (
                            <input
                              type="text"
                              className="flex-1 px-2 py-0.5 text-sm border rounded"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') handleConfirmRenamePattern();
                                if (e.key === 'Escape') handleCancelRenamePattern();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <span className="flex-1 text-sm font-medium text-gray-800 truncate">{pat}</span>
                          )}
                          <span className="text-[10px] text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">{planList.length}개</span>
                          {renamingPattern === pat ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-600 text-white hover:bg-emerald-700" onClick={handleConfirmRenamePattern}>저장</button>
                              <button type="button" className="px-1.5 py-0.5 text-[10px] rounded border hover:bg-gray-50" onClick={handleCancelRenamePattern}>취소</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="p-1 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                                title="요금제 추가"
                                onClick={() => {
                                  setPlanManageSelectedPattern(pat);
                                  setPlanManageMode('create');
                                  setRenamingPlan({ pattern: pat, plan: '' });
                                  setRenamePlanValue('');
                                  setPlanManageCreatePriceMethod('');
                                  setPlanManageSelectedPlan('');
                                  setExpandedPatterns(prev => new Set(prev).add(pat));
                                }}
                              >➕</button>
                              <button
                                type="button"
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="이름 변경"
                                onClick={() => handleStartRenamePattern(pat)}
                              >✏️</button>
                              <button
                                type="button"
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="삭제"
                                onClick={() => handleDeletePattern(pat)}
                              >🗑️</button>
                            </div>
                          )}
                        </div>

                        {/* 요금제 목록 (펼쳐진 경우) */}
                        {isExpanded && (
                          <div className="bg-white divide-y divide-gray-100">
                            {planList.map((pl, idx) => {
                              const isSelectedPlan = isSelectedPattern && planManageSelectedPlan === pl;
                              return (
                                <div
                                  key={pl}
                                  className={`flex items-center gap-2 px-3 py-2 pl-8 cursor-pointer transition-colors ${isSelectedPlan ? 'bg-blue-100' : 'hover:bg-gray-50'}`}
                                  draggable
                                  onDragStart={() => handlePlanDragStart(pat, idx)}
                                  onDragOver={handlePlanDragOver}
                                  onDrop={() => handlePlanDrop(pat, idx)}
                                  onClick={() => {
                                    setPlanManageMode('edit');
                                    setPlanManageSelectedPattern(pat);
                                    setPlanManageSelectedPlan(pl);
                                  }}
                                >
                                  <span className="cursor-grab select-none text-gray-300 text-xs">≡</span>
                                  <span className={`flex-1 text-sm truncate ${isSelectedPlan ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{pl}</span>
                                  <button
                                    type="button"
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100"
                                    style={{ opacity: isSelectedPlan ? 1 : undefined }}
                                    title="삭제"
                                    onClick={(e) => { e.stopPropagation(); handleDeletePlanWithin(pat, pl); }}
                                  >🗑️</button>
                                </div>
                              );
                            })}
                            {planList.length === 0 && (
                              <div className="px-3 py-4 pl-8 text-xs text-gray-400 text-center">요금제가 없습니다</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {(!patterns || patterns.length === 0) && (
                    <div className="px-3 py-12 text-sm text-gray-500 text-center border border-dashed rounded-lg">
                      재고구분이 없습니다.<br />상단에서 추가해주세요.
                    </div>
                  )}
                </div>
              </div>

              {/* 우측: 요금제 상세 설정 */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto p-4">
                  {!planManageSelectedPattern ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <div className="text-4xl mb-3">📋</div>
                        <div className="text-sm">좌측에서 재고구분을 선택하세요</div>
                      </div>
                    </div>
                  ) : planManageMode === 'edit' && !planManageSelectedPlan ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <div className="text-4xl mb-3">📝</div>
                        <div className="text-sm">좌측에서 요금제를 선택하거나<br />상단의 "요금제 추가" 버튼을 클릭하세요</div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* 선택 정보 */}
                      <div className="flex items-center gap-2 text-xs text-gray-600 pb-2 border-b">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{planManageSelectedPattern}</span>
                        {planManageMode === 'edit' && planManageSelectedPlan && (
                          <>
                            <span>→</span>
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded">{planManageSelectedPlan}</span>
                          </>
                        )}
                        {planManageMode === 'create' && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">새 요금제 추가</span>
                        )}
                      </div>

                      {/* 요금제명 */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">요금제명</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={planManageMode === 'create' ? '요금제명을 입력하세요' : ''}
                          value={renamePlanValue}
                          onChange={(e) => setRenamePlanValue(e.target.value)}
                        />
                      </div>

                      {/* 생성 모드: 가격방식 선택 */}
                      {planManageMode === 'create' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">가격방식 <span className="text-red-500">*</span></label>
                          <select
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={planManageCreatePriceMethod}
                            onChange={(e) => setPlanManageCreatePriceMethod(e.target.value)}
                          >
                            <option value="">선택하세요</option>
                            <option value="1인가격">1인가격</option>
                            <option value="합산가격">합산가격</option>
                            <option value="단품">단품</option>
                          </select>
                        </div>
                      )}

                      {/* 편집 모드: 상세 설정 */}
                      {planManageMode === 'edit' && planManageSelectedPlan && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">가격방식</label>
                              <div className="px-3 py-2 text-sm bg-gray-100 rounded-lg text-gray-700">
                                {getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).priceMethod || '-'}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">예약 인원수 제어</label>
                              <div className="px-3 py-2 flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveControl}
                                  onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveControl: !!e.target.checked })}
                                />
                                <span className="text-sm text-gray-700">사용</span>
                              </div>
                            </div>
                          </div>

                          {getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveControl && (
                            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">예약 최소인원</label>
                                <input
                                  type="number"
                                  min="0"
                                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMin ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') {
                                      updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMin: '' });
                                      return;
                                    }
                                    const newMin = Number(v);
                                    updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMin: newMin });
                                    const curMax = getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMax;
                                    if (curMax !== '' && curMax != null && Number(curMax) < newMin) {
                                      updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMax: newMin });
                                    }
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">예약 최대인원</label>
                                <input
                                  type="number"
                                  min={getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMin ?? 0}
                                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMax ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') {
                                      updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMax: '' });
                                      return;
                                    }
                                    const num = Number(v);
                                    const minVal = Number(getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMin ?? 0);
                                    updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMax: num < minVal ? minVal : num });
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <label className="block text-xs font-medium text-gray-700 mb-2">환불 불가여부</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                  checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).nonRefundable}
                                  onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { nonRefundable: !!e.target.checked })}
                                />
                                <span className="text-sm text-gray-700">환불불가</span>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <label className="block text-xs font-medium text-gray-700 mb-2">자동확정여부</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                  checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).autoConfirm}
                                  onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { autoConfirm: !!e.target.checked })}
                                />
                                <span className="text-sm text-gray-700">자동확정</span>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <label className="block text-xs font-medium text-gray-700 mb-2">프라이빗(단독)</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).isPrivate}
                                  onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { isPrivate: !!e.target.checked })}
                                />
                                <span className="text-sm text-gray-700">단독 진행</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 액션 버튼 */}
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        {planManageMode === 'create' ? (
                          <>
                            <button
                              type="button"
                              className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50"
                              onClick={() => {
                                setPlanManageMode('edit');
                                setRenamePlanValue('');
                                setPlanManageCreatePriceMethod('');
                              }}
                            >취소</button>
                            <button
                              type="button"
                              className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                              disabled={!planManageSelectedPattern || !renamePlanValue.trim() || !planManageCreatePriceMethod}
                              onClick={handleCreateNewPlan}
                            >요금제 생성</button>
                          </>
                        ) : planManageSelectedPlan && (
                          <button
                            type="button"
                            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                            disabled={planManageSaving}
                            onClick={handleSavePlanManage}
                          >{planManageSaving ? '저장중...' : '설정 저장'}</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 항공패턴 관리 모달 */}
      {patternManageOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPatternManageOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-[680px] max-w-[90vw] max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold text-gray-800">재고구분 관리</h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-sm"
                onClick={() => setPatternManageOpen(false)}
              >닫기</button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
              <div>
                <label className="block text-xs text-gray-600 mb-1">새 재고 구분 추가</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 px-2 py-1 text-sm border rounded"
                    placeholder="예: 기초 재고"
                    value={tempPatternName}
                    onChange={(e) => setTempPatternName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddPattern(); }}
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={handleAddPattern}
                  >추가</button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">재고구분 목록</span>
                  <span className="text-xs text-gray-500">{Array.isArray(patterns) ? patterns.length : 0}개</span>
                </div>
                <div className="border rounded divide-y">
                  {(Array.isArray(patterns) ? patterns : []).map((name) => (
                    <div key={name} className="flex items-center gap-2 px-3 py-2 bg-white">
                      {renamingPattern === name ? (
                        <>
                          <input
                            type="text"
                            className="flex-1 px-2 py-1 text-sm border rounded"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmRenamePattern(); if (e.key === 'Escape') handleCancelRenamePattern(); }}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
                            onClick={handleConfirmRenamePattern}
                          >저장</button>
                          <button
                            type="button"
                            className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                            onClick={handleCancelRenamePattern}
                          >취소</button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-800 truncate">{name}</div>

                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                              onClick={() => handleStartRenamePattern(name)}
                            >이름변경</button>
                            <button
                              type="button"
                              className="px-2 py-1 text-xs rounded border text-red-600 hover:bg-red-50"
                              onClick={() => handleDeletePattern(name)}
                            >삭제</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {(!patterns || patterns.length === 0) && (
                    <div className="px-3 py-8 text-sm text-gray-500 text-center">항공패턴이 없습니다. 상단에서 추가하세요.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {planManageOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPlanManageOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-[980px] max-w-[95vw] max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold text-gray-800">요금제 관리</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50 disabled:opacity-40"
                  disabled={!planManageSelectedPattern}
                  onClick={startCreatePlan}
                >신규</button>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700 text-sm"
                  onClick={() => setPlanManageOpen(false)}
                >닫기</button>
              </div>
            </div>
            <div className="flex divide-x" style={{ maxHeight: '75vh' }}>
              <div className="w-[42%] p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">항공패턴/요금제</span>
                  <span className="text-xs text-gray-500">{Array.isArray(patterns) ? patterns.length : 0}패턴</span>
                </div>
                <div className="space-y-3">
                  {(Array.isArray(patterns) ? patterns : []).map((pat) => {
                    const planList = (Array.isArray(allPlansByPattern?.[pat]) && allPlansByPattern[pat].length)
                      ? allPlansByPattern[pat]
                      : Object.keys(dataByPattern?.[pat] || {});
                    return (
                      <div key={pat} className="border rounded">
                        <div className={`flex items-center justify-between px-3 py-2 cursor-pointer ${planManageSelectedPattern === pat ? 'bg-gray-200' : 'bg-gray-200'}`} onClick={() => { setPlanManageSelectedPattern(pat); if (!planManageSelectedPlan && planList[0]) setPlanManageSelectedPlan(planList[0]); }}>
                          <div className="text-sm font-medium text-gray-800 truncate">{pat}</div>
                          <div className="text-[11px] text-gray-500">{planList.length}개</div>
                        </div>
                        <div className="divide-y">
                          {planList.map((pl, idx) => (
                            <div
                              key={pl}
                              className={`flex items-center gap-2 px-3 py-2 ${planManageSelectedPattern === pat && planManageSelectedPlan === pl ? 'bg-blue-50' : 'bg-white'}`}
                              draggable
                              onDragStart={() => handlePlanDragStart(pat, idx)}
                              onDragOver={handlePlanDragOver}
                              onDrop={() => handlePlanDrop(pat, idx)}
                            >
                              <span className="cursor-grab select-none text-gray-400 pr-1">≡</span>
                              <button type="button" className="flex-1 text-left text-sm text-gray-800 truncate" onClick={() => { setPlanManageMode('edit'); setPlanManageSelectedPattern(pat); setPlanManageSelectedPlan(pl); }}>{pl}</button>
                              <button type="button" className="px-2 py-1 text-xs rounded border text-red-600 hover:bg-red-50" onClick={() => handleDeletePlanWithin(pat, pl)}>삭제</button>
                            </div>
                          ))}
                          {planList.length === 0 && (
                            <div className="px-3 py-6 text-xs text-gray-500">요금제가 없습니다.</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(!patterns || patterns.length === 0) && (
                    <div className="px-3 py-8 text-sm text-gray-500 text-center border rounded">항공패턴이 없습니다.</div>
                  )}
                </div>
              </div>
              <div className="w-[58%] p-4 overflow-y-auto">
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-xs text-gray-600">선택 패턴: <span className="font-medium text-gray-800">{planManageSelectedPattern || '-'}</span> {planManageMode === 'create' && (<span className="ml-2 text-[11px] text-emerald-700">새 요금제 추가 모드</span>)}</div>
                </div>
                {!planManageSelectedPattern ? (
                  <div className="px-3 py-16 text-sm text-gray-500 text-center border border-dashed rounded">좌측에서 항공패턴을 선택하세요.</div>
                ) : (planManageMode === 'edit' && !planManageSelectedPlan) ? (
                  <div className="px-3 py-16 text-sm text-gray-500 text-center border border-dashed rounded">좌측에서 요금제를 선택하거나 상단의 신규 버튼을 클릭하세요.</div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="font-bold block text-xs text-gray-600 mb-1">요금제명</label>
                      <div className="flex gap-2">
                        <input type="text" className="flex-1 px-2 py-1 text-sm border rounded" placeholder={planManageMode === 'create' ? '요금제명을 입력하세요' : ''} value={renamePlanValue} onChange={(e) => setRenamePlanValue(e.target.value)} />
                      </div>
                    </div>
                    {planManageMode === 'create' && (
                      <div>
                        <label className="font-bold block text-xs text-gray-600 mb-1">가격방식</label>
                        <select className="w-full px-2 py-1 text-sm border rounded" value={planManageCreatePriceMethod} onChange={(e) => setPlanManageCreatePriceMethod(e.target.value)}>
                          <option value="">선택</option>
                          <option value="1인가격">1인가격</option>
                          <option value="합산가격">합산가격</option>
                          <option value="단품">단품</option>
                        </select>
                      </div>
                    )}
                    {planManageMode === 'edit' && planManageSelectedPlan && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="font-bold block text-xs text-gray-600 mb-1">가격방식</label>
                          <div className="w-full px-2 py-1 text-xs rounded text-gray-700">
                            {getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).priceMethod || '-'}
                          </div>
                        </div>
                        <div>
                          <label className="font-bold block text-xs text-gray-600 mb-1">예약 인원수 제어</label>
                          <div className="px-2 mt-3 flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300 disabled:opacity-50" disabled={planManageMode === 'create'} checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveControl} onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveControl: !!e.target.checked })} />
                            <span className="text-xs text-gray-700">사용</span>
                          </div>
                        </div>
                        {getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveControl && (
                          <>
                            <div>
                              <label className="font-bold block text-xs text-gray-600 mb-1">예약 최소인원</label>
                              <input
                                type="number"
                                min="0"
                                className="w-full px-2 py-1 text-sm border rounded disabled:opacity-50"
                                disabled={planManageMode === 'create'}
                                value={getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMin ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') {
                                    updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMin: '' });
                                    return;
                                  }
                                  const newMin = Number(v);
                                  updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMin: newMin });
                                  const curMax = getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMax;
                                  if (curMax !== '' && curMax != null && Number(curMax) < newMin) {
                                    updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMax: newMin });
                                    alert('최대인원이 최소인원보다 작을 수 없어 최대인원을 최소인원으로 변경했습니다.');
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <label className="font-bold block text-xs text-gray-600 mb-1">예약 최대인원</label>
                              <input
                                type="number"
                                min={getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMin ?? 0}
                                className="w-full px-2 py-1 text-sm border rounded disabled:opacity-50"
                                disabled={planManageMode === 'create'}
                                value={getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMax ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') {
                                    updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMax: '' });
                                    return;
                                  }
                                  const num = Number(v);
                                  const minVal = Number(getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).reserveMin ?? 0);
                                  if (num < minVal) {
                                    alert('최대인원은 최소인원보다 작을 수 없습니다. 최소인원 이상으로 설정됩니다.');
                                  }
                                  updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { reserveMax: num < minVal ? minVal : num });
                                }}
                              />
                            </div>
                          </>
                        )}
                        <div>
                          <label className="font-bold block text-xs text-gray-600 mb-1">환불 불가여부</label>
                          <div className="px-2 flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300 disabled:opacity-50" disabled={planManageMode === 'create'} checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).nonRefundable} onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { nonRefundable: !!e.target.checked })} />
                            <span className="text-xs text-gray-700">환불불가</span>
                          </div>
                        </div>
                        <div>
                          <label className="font-bold block text-xs text-gray-600 mb-1">자동확정여부</label>
                          <div className="px-2 flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300 disabled:opacity-50" disabled={planManageMode === 'create'} checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).autoConfirm} onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { autoConfirm: !!e.target.checked })} />
                            <span className="text-xs text-gray-700">자동확정</span>
                          </div>
                        </div>
                        <div>
                          <label className="font-bold block text-xs text-gray-600 mb-1">프라이빗(단독)</label>
                          <div className="px-2 flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300 disabled:opacity-50" disabled={planManageMode === 'create'} checked={!!getPlanMeta(planManageSelectedPattern, planManageSelectedPlan).isPrivate} onChange={(e) => updatePlanMeta(planManageSelectedPattern, planManageSelectedPlan, { isPrivate: !!e.target.checked })} />
                            <span className="text-xs text-gray-700">단독 진행</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Actions under inputs */}
                    {planManageMode === 'create' ? (
                      <div className="flex justify-end pt-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={!planManageSelectedPattern || !renamePlanValue.trim() || !planManageCreatePriceMethod}
                          onClick={handleCreateNewPlan}
                        >생성</button>
                      </div>
                    ) : (planManageSelectedPlan && (
                      <div className="flex justify-end pt-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={planManageSaving}
                          onClick={handleSavePlanManage}
                        >{planManageSaving ? '저장중...' : '설정 저장'}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Shared product settings modal */}
      {isModalOpen && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={modalLoading}
          hideBasicDuration
          onPreviewRefresh={async () => {
            // 상세 설정 저장 후 연령구분여부 및 캘린더 데이터 갱신
            try {
              const res = await getProductDetailExtra(productId);
              const detail = res?.detail || {};
              setUseAgeGroup(detail.연령구분여부 !== false);
            } catch (_) {}
            try {
              await refreshCalendarForCurrentMonth();
            } catch (_) {}
          }}
        />
      )}

      {/* Reservation Modal */}
      <ReservationModalComponent
        open={reservationModalOpen}
        onClose={() => setReservationModalOpen(false)}
        priceNo={selectedPriceNo}
        datePriceNo={selectedPriceNo} /* Using selectedPriceNo as datePriceNo since it's actually the date-specific price record ID */
        date={selectedDate}
        productName={productName}
      />

      {ageGroupModalMain}
    </div>
  );
};

export default PriceStockSettings;
